import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Button, Typography, Space, Tabs, Input } from 'antd';
import {
  FullscreenOutlined,
  FullscreenExitOutlined,
  RocketOutlined,
  AppstoreOutlined,
  ToolOutlined,
  LineChartOutlined,
  ThunderboltOutlined,
  CrownOutlined,
  MessageOutlined,
  QrcodeOutlined,
  NodeIndexOutlined,
  SearchOutlined,
  SafetyCertificateOutlined,
  BarcodeOutlined,
} from '@ant-design/icons';
import { ReactFlow, Background, Controls, Node, Edge } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import ArchFlow, { ArchGroup, ArchNode, ArchEdge } from '../components/ArchFlow';
import { font } from '../theme/tokens';

interface Slide {
  title: string;
  content?: string;
  component?: React.ReactNode;
}

const ArchitectureFlow = () => {
  const nodes: Node[] = [
    { id: 'user', data: { label: '👤 User (Browser)' }, position: { x: 0, y: 150 }, type: 'input', style: { background: '#eef2ff', border: '1px solid #4f46e5', borderRadius: '8px', padding: '10px' } },
    { id: 'npm', data: { label: '🔀 Nginx Proxy Manager' }, position: { x: 200, y: 150 }, style: { background: '#f0f9ff', border: '1px solid #0284c7', borderRadius: '8px', padding: '10px' } },
    { id: 'fe', data: { label: '🌐 React Frontend (k3s)' }, position: { x: 450, y: 50 }, style: { background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '8px', padding: '10px' } },
    { id: 'be', data: { label: '⚡ FastAPI Backend (k3s)' }, position: { x: 450, y: 250 }, style: { background: '#fff7ed', border: '1px solid #ea580c', borderRadius: '8px', padding: '10px' } },
    { id: 'db', data: { label: '🗄️ PostgreSQL' }, position: { x: 700, y: 150 }, type: 'output', style: { background: '#fef2f2', border: '1px solid #dc2626', borderRadius: '8px', padding: '10px' } },
    { id: 'obs', data: { label: '☁️ OCI Object Storage' }, position: { x: 700, y: 250 }, type: 'output', style: { background: '#faf5ff', border: '1px solid #7c3aed', borderRadius: '8px', padding: '10px' } },
    { id: 'redis', data: { label: '🔴 Redis Cache' }, position: { x: 700, y: 50 }, type: 'output', style: { background: '#fff1f2', border: '1px solid #e11d48', borderRadius: '8px', padding: '10px' } },
  ];
  const edges: Edge[] = [
    { id: 'e1-2', source: 'user', target: 'npm', animated: true, label: 'HTTPS' },
    { id: 'e2-3', source: 'npm', target: 'fe', label: 'Proxy' },
    { id: 'e2-4', source: 'npm', target: 'be', label: 'Proxy' },
    { id: 'e3-4', source: 'fe', target: 'be', animated: true, label: 'API Call' },
    { id: 'e4-db', source: 'be', target: 'db', label: 'SQL' },
    { id: 'e4-obs', source: 'be', target: 'obs', label: 'OCI SDK' },
    { id: 'e4-redis', source: 'be', target: 'redis', label: 'Cache' },
  ];
  return (
    <div style={{ height: 500, width: '100%', background: '#fff', border: '1px solid #f1f5f9', borderRadius: 16 }}>
      <ReactFlow nodes={nodes} edges={edges} fitView>
        <Background color="#f8fafc" />
        <Controls />
      </ReactFlow>
    </div>
  );
};

const TapoApiFlow = () => {
  const nodes: Node[] = [
    // 센서 3개
    { id: 'doc', data: { label: '📁 T315-DOC\n문서보관실\n18–22°C / 40–55%' }, position: { x: 0, y: 20 }, type: 'input', style: { background: '#eff6ff', border: '2px solid #60a5fa', borderRadius: '10px', padding: '8px 12px', fontSize: '11px', whiteSpace: 'pre-line', textAlign: 'center' } },
    { id: 'lab', data: { label: '🧬 T315-LAB\n사육실\n20–26°C / 30–70%' }, position: { x: 0, y: 140 }, type: 'input', style: { background: '#f0fdf4', border: '2px solid #34d399', borderRadius: '10px', padding: '8px 12px', fontSize: '11px', whiteSpace: 'pre-line', textAlign: 'center' } },
    { id: 'chm', data: { label: '🧪 T315-CHM\n시약실\n15–25°C / 40–60%' }, position: { x: 0, y: 260 }, type: 'input', style: { background: '#faf5ff', border: '2px solid #a78bfa', borderRadius: '10px', padding: '8px 12px', fontSize: '11px', whiteSpace: 'pre-line', textAlign: 'center' } },
    // H200 허브
    { id: 'hub', data: { label: '🌐 H200 Hub\nTP-Link 허브\nRF 868MHz' }, position: { x: 210, y: 130 }, style: { background: '#fff7ed', border: '2px solid #fb923c', borderRadius: '10px', padding: '8px 12px', fontSize: '11px', whiteSpace: 'pre-line', textAlign: 'center' } },
    // NAS
    { id: 'nas', data: { label: '💾 NAS\nDS423+\ncollector + uploader\nSQLite WAL 큐 14일' }, position: { x: 410, y: 110 }, style: { background: '#fefce8', border: '2px solid #facc15', borderRadius: '10px', padding: '8px 12px', fontSize: '11px', whiteSpace: 'pre-line', textAlign: 'center' } },
    // FastAPI
    { id: 'api', data: { label: '⚡ FastAPI\ntapo-api.sappt.co.kr\nOCI 춘천 · k8s' }, position: { x: 640, y: 110 }, style: { background: '#ecfdf5', border: '2px solid #10b981', borderRadius: '10px', padding: '8px 12px', fontSize: '11px', whiteSpace: 'pre-line', textAlign: 'center' } },
    // TimescaleDB
    { id: 'tsdb', data: { label: '🗄️ TimescaleDB\nHypertable\n5분 연속집계' }, position: { x: 870, y: 60 }, type: 'output', style: { background: '#fef2f2', border: '2px solid #f87171', borderRadius: '10px', padding: '8px 12px', fontSize: '11px', whiteSpace: 'pre-line', textAlign: 'center' } },
    // 대시보드
    { id: 'dash', data: { label: '📊 대시보드\nECharts 실시간\n+ Grafana 통합' }, position: { x: 870, y: 200 }, type: 'output', style: { background: '#eef2ff', border: '2px solid #818cf8', borderRadius: '10px', padding: '8px 12px', fontSize: '11px', whiteSpace: 'pre-line', textAlign: 'center' } },
  ];
  const edges: Edge[] = [
    { id: 'doc-hub', source: 'doc', target: 'hub', label: 'RF', style: { stroke: '#60a5fa' } },
    { id: 'lab-hub', source: 'lab', target: 'hub', label: 'RF', style: { stroke: '#34d399' } },
    { id: 'chm-hub', source: 'chm', target: 'hub', label: 'RF', style: { stroke: '#a78bfa' } },
    { id: 'hub-nas', source: 'hub', target: 'nas', label: 'LAN 2min', animated: true, style: { stroke: '#fb923c' } },
    { id: 'nas-api', source: 'nas', target: 'api', label: 'HTTPS Bearer\n30s batch', animated: true, style: { stroke: '#facc15' }, labelStyle: { fontSize: 10 } },
    { id: 'api-tsdb', source: 'api', target: 'tsdb', label: 'asyncpg SQL', style: { stroke: '#10b981' }, labelStyle: { fontSize: 10 } },
    { id: 'api-dash', source: 'api', target: 'dash', label: 'GET /api/*', animated: true, style: { stroke: '#818cf8' }, labelStyle: { fontSize: 10 } },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
        {[
          { color: '#60a5fa', label: '문서보관실 · doc' },
          { color: '#34d399', label: '사육실 · lab' },
          { color: '#a78bfa', label: '시약실 · chm' },
          { color: '#fb923c', label: 'H200 RF 허브' },
          { color: '#facc15', label: 'NAS 엣지 (SQLite 버퍼)' },
          { color: '#10b981', label: 'FastAPI (OCI)' },
          { color: '#f87171', label: 'TimescaleDB' },
          { color: '#818cf8', label: '대시보드' },
        ].map(({ color, label }) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f8fafc', padding: '3px 10px', borderRadius: 20, border: `1px solid ${color}30` }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
            {label}
          </span>
        ))}
      </div>
      <div style={{ height: 380, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16 }}>
        <ReactFlow nodes={nodes} edges={edges} fitView>
          <Background color="#f1f5f9" />
          <Controls />
        </ReactFlow>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, fontSize: 12 }}>
        {[
          { label: '폴링 주기', value: '2분 / 실' },
          { label: '일일 수집', value: '2,160행' },
          { label: '오프라인 버퍼', value: 'NAS 14일' },
          { label: '5년 보존', value: '~95 MB' },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: '#f8fafc', padding: '10px 14px', borderRadius: 10, border: '1px solid #e2e8f0', textAlign: 'center' }}>
            <div style={{ color: '#64748b', fontSize: 11 }}>{label}</div>
            <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 14, marginTop: 3 }}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════
//  ArchFlow 기반 인터랙티브 시스템 구성도 (기존 Mermaid graph → React Flow)
// ════════════════════════════════════════════════════════════════════

// ── 하이브리드 인프라 구성 (k3s + Docker Compose) ──
const HybridInfraFlow: React.FC = () => {
  const groups: ArchGroup[] = [
    { id: 'g_inet', label: '🌐 외부 인터넷 (도메인)', x: 0, y: 30, w: 195, h: 470, tone: 'slate' },
    { id: 'g_npm', label: 'Nginx Proxy Manager', x: 250, y: 150, w: 175, h: 110, tone: 'orange' },
    { id: 'g_okta', label: '🔐 Okta SSO', x: 250, y: 385, w: 175, h: 95, tone: 'indigo' },
    { id: 'g_k3s', label: '☸️ k3s Cluster (무중단 배포)', x: 480, y: 20, w: 360, h: 250, tone: 'green' },
    { id: 'g_docker', label: '🐳 Docker Compose (인프라)', x: 480, y: 300, w: 660, h: 320, tone: 'blue' },
  ];
  const nodes: ArchNode[] = [
    // 외부 도메인
    { id: 'lab', group: 'g_inet', x: 14, y: 36, label: 'lab', sub: 'lab.sappt.co.kr', tone: 'slate' },
    { id: 'api', group: 'g_inet', x: 14, y: 96, label: 'api', sub: 'api.sappt.co.kr', tone: 'slate' },
    { id: 'partner', group: 'g_inet', x: 14, y: 156, label: 'partner', sub: 'partner.sappt.co.kr', tone: 'slate' },
    { id: 'logd', group: 'g_inet', x: 14, y: 216, label: 'log', sub: 'log.sappt.co.kr', tone: 'slate' },
    { id: 'npmd', group: 'g_inet', x: 14, y: 276, label: 'npm', sub: 'npm.sappt.co.kr', tone: 'slate' },
    { id: 'tapod', group: 'g_inet', x: 14, y: 336, label: 'tapo-api', sub: 'tapo-api.sappt.co.kr', tone: 'slate' },
    { id: 'ssod', group: 'g_inet', x: 14, y: 396, label: '🔒 SSO 보호', sub: 'headlamp/prom/term/k3s', tone: 'indigo' },
    // NPM
    { id: 'npm2', group: 'g_npm', x: 14, y: 42, label: 'NPM', sub: ':80/:443 · Let\'s Encrypt', emoji: '🔀', tone: 'orange' },
    // Okta
    { id: 'okta2', group: 'g_okta', x: 14, y: 38, label: 'Okta OIDC', sub: 'auth.sappt.co.kr', tone: 'indigo' },
    // k3s
    { id: 'svc_fe', group: 'g_k3s', x: 16, y: 48, label: 'Service :30080', tone: 'green' },
    { id: 'svc_be', group: 'g_k3s', x: 16, y: 113, label: 'Service :30800', tone: 'green' },
    { id: 'svc_pfe', group: 'g_k3s', x: 16, y: 178, label: 'Service :30081', tone: 'green' },
    { id: 'fe_pods', group: 'g_k3s', x: 195, y: 48, label: 'frontend', sub: 'Pod ×2 · nginx', tone: 'green' },
    { id: 'be_pods', group: 'g_k3s', x: 195, y: 113, label: 'backend', sub: 'Pod ×2 · FastAPI', tone: 'green' },
    { id: 'pfe_pods', group: 'g_k3s', x: 195, y: 178, label: 'partner-fe', sub: 'Pod ×2 · nginx', tone: 'green' },
    // Docker Compose
    { id: 'pg', group: 'g_docker', x: 16, y: 42, label: 'PostgreSQL 17', sub: ':5432', emoji: '🗄️', tone: 'blue' },
    { id: 'obs', group: 'g_docker', x: 175, y: 42, label: 'OCI Object Storage', sub: 'ap-chuncheon-1', emoji: '☁️', tone: 'purple' },
    { id: 'osearch', group: 'g_docker', x: 355, y: 42, label: 'OpenSearch', sub: '전문검색 + nori', emoji: '🔎', tone: 'blue' },
    { id: 'gha', group: 'g_docker', x: 510, y: 42, label: 'GitHub Actions', sub: '→ GHCR', emoji: '⚙️', tone: 'purple' },
    { id: 'grafana', group: 'g_docker', x: 16, y: 130, label: 'Grafana', sub: ':3000', emoji: '📊', tone: 'amber' },
    { id: 'loki', group: 'g_docker', x: 175, y: 130, label: 'Loki + Promtail', emoji: '📜', tone: 'amber' },
    { id: 'prom', group: 'g_docker', x: 355, y: 130, label: 'Prometheus', sub: '+ cAdvisor', emoji: '📈', tone: 'amber' },
    { id: 'tapoapi', group: 'g_docker', x: 16, y: 218, label: 'tapo-api', sub: 'TAPO 온습도 수집', emoji: '🌡️', tone: 'green' },
    { id: 'tsdb', group: 'g_docker', x: 175, y: 218, label: 'TimescaleDB', sub: '온습도 시계열', emoji: '🗄️', tone: 'red' },
    { id: 'oauth2', group: 'g_docker', x: 355, y: 218, label: 'oauth2-proxy', sub: ':4180', emoji: '🔐', tone: 'indigo' },
    { id: 'bridge', group: 'g_docker', x: 510, y: 218, label: 'npm-sso-bridge', sub: ':8080', emoji: '🌉', tone: 'indigo' },
  ];
  const edges: ArchEdge[] = [
    ...['lab', 'api', 'partner', 'logd', 'npmd', 'tapod', 'ssod'].map(
      (s): ArchEdge => ({ source: s, target: 'npm2', tone: 'slate' }),
    ),
    { source: 'npm2', target: 'svc_fe', label: '30080', tone: 'orange' },
    { source: 'npm2', target: 'svc_be', label: '30800', tone: 'orange' },
    { source: 'npm2', target: 'svc_pfe', label: '30081', tone: 'orange' },
    { source: 'npm2', target: 'tapoapi', label: 'proxy', tone: 'orange' },
    { source: 'npm2', target: 'grafana', label: 'Okta→Grafana', tone: 'orange' },
    { source: 'npm2', target: 'oauth2', label: 'Okta→oauth2', tone: 'orange' },
    { source: 'svc_fe', target: 'fe_pods', tone: 'green' },
    { source: 'svc_be', target: 'be_pods', tone: 'green' },
    { source: 'svc_pfe', target: 'pfe_pods', tone: 'green' },
    { source: 'be_pods', target: 'pg', label: 'DB', tone: 'blue', animated: true },
    { source: 'be_pods', target: 'obs', label: 'Storage', tone: 'purple', animated: true },
    { source: 'gha', target: 'be_pods', label: 'ghcr pull', tone: 'purple', dashed: true },
    { source: 'tapoapi', target: 'tsdb', label: 'asyncpg', tone: 'green', animated: true },
    { source: 'oauth2', target: 'bridge', label: 'proxy', tone: 'indigo' },
    { source: 'bridge', target: 'npm2', label: 'proxy', tone: 'indigo' },
    { source: 'okta2', target: 'grafana', label: 'OIDC', tone: 'indigo', dashed: true },
    { source: 'okta2', target: 'oauth2', label: 'OIDC', tone: 'indigo', dashed: true },
  ];
  return <ArchFlow groups={groups} nodes={nodes} edges={edges} height={560} />;
};

// ── 무중단 배포 구성도 (GitHub Actions → GHCR → k3s) ──
const DeployArchFlow: React.FC = () => {
  const groups: ArchGroup[] = [
    { id: 'g_inet', label: '🌐 외부 (인터넷)', x: 0, y: 40, w: 185, h: 185, tone: 'slate' },
    { id: 'g_npm', label: 'Nginx Proxy Manager', x: 235, y: 70, w: 165, h: 95, tone: 'orange' },
    { id: 'g_k3s', label: '☸️ k3s Cluster · ns: sappt', x: 450, y: 20, w: 365, h: 275, tone: 'green' },
    { id: 'g_compose', label: '🐳 Docker Compose (인프라)', x: 450, y: 330, w: 365, h: 185, tone: 'blue' },
    { id: 'g_ghcr', label: '📦 GHCR', x: 880, y: 235, w: 185, h: 130, tone: 'cyan' },
    { id: 'g_gha', label: '⚙️ GitHub Actions', x: 880, y: 410, w: 185, h: 95, tone: 'purple' },
  ];
  const nodes: ArchNode[] = [
    { id: 'user', group: 'g_inet', x: 14, y: 36, label: '사용자 브라우저', sub: 'lab/partner.sappt.co.kr', emoji: '👤', tone: 'slate' },
    { id: 'gh', group: 'g_inet', x: 14, y: 110, label: 'GitHub', sub: 'github.com/sappt', emoji: '🐙', tone: 'slate' },
    { id: 'npm', group: 'g_npm', x: 14, y: 40, label: 'Reverse Proxy', sub: 'SSL 종단 · Let\'s Encrypt', emoji: '🔀', tone: 'orange' },
    { id: 'svc_fe', group: 'g_k3s', x: 16, y: 48, label: 'Service: frontend', sub: 'NodePort :30080', tone: 'green' },
    { id: 'svc_be', group: 'g_k3s', x: 16, y: 120, label: 'Service: backend', sub: 'NodePort :30800', tone: 'green' },
    { id: 'svc_pfe', group: 'g_k3s', x: 16, y: 192, label: 'Service: partner', sub: 'NodePort :30081', tone: 'green' },
    { id: 'fe_pods', group: 'g_k3s', x: 205, y: 48, label: 'frontend', sub: 'Pod ×2 · nginx:alpine', tone: 'green' },
    { id: 'be_pods', group: 'g_k3s', x: 205, y: 120, label: 'backend', sub: 'Pod ×2 · python:3.11', tone: 'green' },
    { id: 'pfe_pods', group: 'g_k3s', x: 205, y: 192, label: 'partner-fe', sub: 'Pod ×2 · nginx:alpine', tone: 'green' },
    { id: 'pg', group: 'g_compose', x: 16, y: 42, label: 'PostgreSQL 17', emoji: '🗄️', tone: 'blue' },
    { id: 'obs', group: 'g_compose', x: 195, y: 42, label: 'OCI Object Storage', sub: 'ap-chuncheon-1', emoji: '☁️', tone: 'purple' },
    { id: 'redis', group: 'g_compose', x: 16, y: 115, label: 'Redis', emoji: '🔴', tone: 'red' },
    { id: 'osearch', group: 'g_compose', x: 195, y: 115, label: 'OpenSearch 2.17', emoji: '🔎', tone: 'blue' },
    { id: 'img', group: 'g_ghcr', x: 14, y: 40, label: 'sappt-*:latest', sub: 'backend / frontend / partner', emoji: '📦', tone: 'cyan' },
    { id: 'ci', group: 'g_gha', x: 14, y: 38, label: 'ARM64 빌드', sub: 'push to main 트리거', emoji: '🏗️', tone: 'purple' },
    { id: 'containerd', x: 905, y: 150, label: 'containerd', sub: 'imagePullPolicy:Always', emoji: '📥', tone: 'slate' },
  ];
  const edges: ArchEdge[] = [
    { source: 'user', target: 'npm', label: 'HTTPS', tone: 'slate', animated: true },
    { source: 'npm', target: 'svc_fe', label: ':30080', tone: 'orange' },
    { source: 'npm', target: 'svc_be', label: ':30800', tone: 'orange' },
    { source: 'npm', target: 'svc_pfe', label: ':30081', tone: 'orange' },
    { source: 'svc_fe', target: 'fe_pods', tone: 'green' },
    { source: 'svc_be', target: 'be_pods', tone: 'green' },
    { source: 'svc_pfe', target: 'pfe_pods', tone: 'green' },
    { source: 'be_pods', target: 'pg', label: 'SQL', tone: 'blue', animated: true },
    { source: 'be_pods', target: 'obs', label: 'Storage', tone: 'purple', animated: true },
    { source: 'gh', target: 'ci', label: 'push main', tone: 'purple' },
    { source: 'ci', target: 'img', label: 'push', tone: 'cyan' },
    { source: 'img', target: 'containerd', label: 'pull', tone: 'cyan' },
    { source: 'containerd', target: 'be_pods', label: 'rolling update', tone: 'green', dashed: true },
  ];
  return <ArchFlow groups={groups} nodes={nodes} edges={edges} height={520} />;
};

const slides: Slide[] = [
  // ── Slide 0: 표지 ──
  {
    title: '',
    content: `
      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;text-align:center">
        <div style="font-size:18px;color:#667eea;font-weight:600;letter-spacing:6px;margin-bottom:16px">SYSTEM ARCHITECTURE GUIDE</div>
        <h1 style="font-size:52px;font-weight:800;margin:0;background:linear-gradient(135deg,#667eea,#764ba2);-webkit-background-clip:text;-webkit-text-fill-color:transparent">SAPPT</h1>
        <p style="font-size:22px;color:#555;margin:12px 0 32px">농약 시험계획서 관리 시스템</p>
        <div style="width:120px;height:4px;background:linear-gradient(90deg,#667eea,#764ba2);border-radius:2px;margin-bottom:32px"></div>
        <p style="font-size:15px;color:#888;line-height:1.8">
          Pesticide Test Protocol Management System<br/>
          FastAPI + React + PostgreSQL + HWPX Engine
        </p>
        <div style="margin-top:40px;padding:16px 32px;background:rgba(102,126,234,0.08);border-radius:12px;border:1px solid rgba(102,126,234,0.15)">
          <span style="color:#667eea;font-size:13px">v2.1.0 &nbsp;|&nbsp; 2026 &nbsp;|&nbsp; lab.sappt.co.kr</span>
        </div>
      </div>
    `,
  },
  // ── Slide 1: 시스템 개요 ──
  {
    title: '1. 시스템 개요 (System Overview)',
    component: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div dangerouslySetInnerHTML={{ __html: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:8px">
        <div style="background:linear-gradient(135deg,#667eea15,#764ba215);padding:24px;border-radius:16px;border:1px solid #667eea22">
          <h3 style="color:#667eea;margin-top:0">목적 (Purpose)</h3>
          <p style="color:#444;line-height:1.8;margin:0">농약 품목등록을 위한 <strong>약제방제 효과시험 계획서</strong>를 웹 기반으로 작성, 관리하고 <strong>HWPX 한글 문서</strong>로 자동 생성합니다. <strong>시험일정총괄표</strong>를 정규화 DB로 관리하며, 의뢰사/장소/구분/포장주 마스터 기반의 체계적 시험 업무를 지원합니다.</p>
        </div>
        <div style="background:linear-gradient(135deg,#f093fb15,#f5576c15);padding:24px;border-radius:16px;border:1px solid #f093fb22">
          <h3 style="color:#f5576c;margin-top:0">핵심 가치 (Core Value)</h3>
          <ul style="color:#444;line-height:2;margin:0;padding-left:18px">
            <li>수기 작성 대비 <strong>80% 시간 절감</strong></li>
            <li>양식 표준화 및 오류 방지</li>
            <li>DB 정규화(3NF) 기반 <strong>마스터 관리</strong></li>
            <li>Excel 업로드 &harr; 마스터 자동 매핑</li>
            <li>데이터 기반 통계 분석 &amp; 캘린더 입력</li>
            <li>HWPX 자동 생성 엔진</li>
          </ul>
        </div>
      </div>
` }} />
        <ArchFlow
          height={260}
          nodes={[
            { id: 'a', x: 0, y: 90, label: '사용자', emoji: '👤', tone: 'indigo' },
            { id: 'b', x: 175, y: 90, label: 'React Frontend', emoji: '🌐', tone: 'blue' },
            { id: 'c', x: 370, y: 90, label: 'FastAPI Backend', emoji: '⚡', tone: 'green' },
            { id: 'd', x: 595, y: 30, label: 'PostgreSQL DB', emoji: '🗄️', tone: 'red' },
            { id: 'e', x: 595, y: 150, label: 'HWPX Engine', emoji: '📄', tone: 'orange' },
          ]}
          edges={[
            { source: 'a', target: 'b', label: '로그인', tone: 'indigo' },
            { source: 'b', target: 'c', label: 'REST API', tone: 'blue', animated: true },
            { source: 'c', target: 'd', label: 'ORM', tone: 'green' },
            { source: 'c', target: 'e', label: '생성', tone: 'orange' },
            { source: 'e', target: 'a', label: '다운로드', tone: 'slate', dashed: true },
          ]}
        />
      </div>
    ),
  },
  // ── Slide 2: 기술 아키텍처 ──
  {
    title: '2. 기술 아키텍처 (Technical Architecture)',
    component: (
      <ArchFlow
        height={500}
        groups={[
          { id: 'g_client', label: '🖥️ Client Layer', x: 0, y: 0, w: 560, h: 100, tone: 'indigo' },
          { id: 'g_proxy', label: '🔀 Reverse Proxy Layer', x: 130, y: 140, w: 300, h: 95, tone: 'orange' },
          { id: 'g_k3s', label: '☸️ k3s (Rolling Update)', x: 70, y: 275, w: 420, h: 100, tone: 'green' },
          { id: 'g_data', label: '💾 Data Layer (Docker Compose)', x: 100, y: 415, w: 360, h: 100, tone: 'blue' },
        ]}
        nodes={[
          { id: 'browser', group: 'g_client', x: 16, y: 40, label: 'Browser', sub: 'React 19 + TS', tone: 'indigo' },
          { id: 'antd', group: 'g_client', x: 210, y: 40, label: 'Ant Design 6', sub: 'UI Components', tone: 'indigo' },
          { id: 'echarts', group: 'g_client', x: 380, y: 40, label: 'Apache ECharts', sub: 'Data Viz', tone: 'indigo' },
          { id: 'npm', group: 'g_proxy', x: 16, y: 38, label: 'Nginx Proxy Manager', sub: "lab/api/partner · Let's Encrypt", tone: 'orange' },
          { id: 'nginx', group: 'g_k3s', x: 16, y: 40, label: 'Nginx Pod ×2', sub: 'Static + Proxy', tone: 'green' },
          { id: 'fastapi', group: 'g_k3s', x: 215, y: 40, label: 'FastAPI Pod ×2', sub: 'REST API', tone: 'green' },
          { id: 'pg', group: 'g_data', x: 16, y: 40, label: 'PostgreSQL 17', sub: 'Structured Data', emoji: '🗄️', tone: 'blue' },
          { id: 'obs', group: 'g_data', x: 195, y: 40, label: 'OCI Object Storage', sub: 'ap-chuncheon-1', emoji: '☁️', tone: 'purple' },
        ]}
        edges={[
          { source: 'browser', target: 'npm', label: 'HTTPS', tone: 'indigo', animated: true },
          { source: 'npm', target: 'nginx', label: 'Internal', tone: 'orange' },
          { source: 'npm', target: 'fastapi', label: 'Internal', tone: 'orange' },
          { source: 'fastapi', target: 'pg', label: 'ORM', tone: 'blue', animated: true },
          { source: 'fastapi', target: 'obs', label: 'OCI SDK', tone: 'purple', animated: true },
        ]}
      />
    ),
  },
  // ── Slide 3: 인터랙티브 인프라 맵 (React Flow) ──
  {
    title: '3. 인터랙티브 인프라 맵 (Interactive Infrastructure)',
    component: <ArchitectureFlow />,
  },
  // ── Slide 4: 하이브리드 인프라 구성 (k3s + Docker Compose) ──
  {
    title: '3. 하이브리드 인프라 구성 (k3s + Docker Compose)',
    component: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          🖥️ 전체 스택은 단일 Oracle Cloud ARM64 VM(4 vCPU / 24GB RAM)에서 구동 · 노드 드래그 / 휠 줌 가능
        </div>
        <HybridInfraFlow />
        <div
          dangerouslySetInnerHTML={{
            __html: `
      <div style="display:grid;grid-template-columns:repeat(6,1fr);gap:12px;margin-top:8px">
        <div style="background:#e8f5e9;padding:14px;border-radius:12px;text-align:center;border:1px solid #c8e6c9">
          <div style="font-size:24px">☸️</div>
          <strong style="font-size:13px">k3s Rolling</strong><br/><span style="color:#888;font-size:11px">무중단 배포 (0s downtime)</span>
        </div>
        <div style="background:#e3f2fd;padding:14px;border-radius:12px;text-align:center;border:1px solid #bbdefb">
          <div style="font-size:24px">📦</div>
          <strong style="font-size:13px">GHCR</strong><br/><span style="color:#888;font-size:11px">ghcr.io/sappt/* (Public)</span>
        </div>
        <div style="background:#f8f9ff;padding:14px;border-radius:12px;text-align:center;border:1px solid #e8eaff">
          <div style="font-size:24px">🔒</div>
          <strong style="font-size:13px">SSL/TLS</strong><br/><span style="color:#888;font-size:11px">Let's Encrypt 자동갱신</span>
        </div>
        <div style="background:#fff8f8;padding:14px;border-radius:12px;text-align:center;border:1px solid #ffe8e8">
          <div style="font-size:24px">💾</div>
          <strong style="font-size:13px">Persistent</strong><br/><span style="color:#888;font-size:11px">Docker Volume 보존</span>
        </div>
        <div style="background:#f8f0ff;padding:14px;border-radius:12px;text-align:center;border:1px solid #e8d8ff">
          <div style="font-size:24px">🔄</div>
          <strong style="font-size:13px">Auto Rollback</strong><br/><span style="color:#888;font-size:11px">deploy.sh rollback</span>
        </div>
        <div style="background:#fffbeb;padding:14px;border-radius:12px;text-align:center;border:1px solid #fde68a">
          <div style="font-size:24px">📊</div>
          <strong style="font-size:13px">모니터링</strong><br/><span style="color:#888;font-size:11px">Grafana + Loki</span>
        </div>
      </div>`,
          }}
        />
      </div>
    ),
  },
  // ── Slide 4: 데이터 모델 (ERD) ──
  {
    title: '4. 데이터 모델 (Entity Relationship)',
    content: `
      <div class="mermaid">
erDiagram
    USER {
        int id PK
        string username UK
        string hashed_password
        string full_name
        datetime created_at
    }
    TEST_PROTOCOL {
        int id PK
        string protocol_code
        string title
        string institution
        string test_year
        string manager
        string planned_start
        string planned_end
        string test_location
        text purpose
        string target_disease
        string test_crop
        string plot_design
        int replications
        text survey_items
        int created_by FK
        datetime created_at
    }
    TREATMENT {
        int id PK
        int protocol_id FK
        string pesticide_name
        string active_ingredient_pct
        string dilution_rate
        string treatment_timing
        string standard_amount
        string double_amount
        int test_year_number
        string client_company
        int sort_order
    }
    PLOT_DESIGN {
        int id PK
        int protocol_id FK
        string category
        string treatment_count
        int replication_count
        int total_plots
        string trees_per_plot
        string required_trees
    }

    MST_COMPANY {
        int id PK
        string name UK
        string code
        string contact_name
        string contact_phone
        int is_active
    }
    MST_LOCATION {
        int id PK
        string region
        string city
        string full_name UK
        int is_active
    }
    MST_CATEGORY {
        int id PK
        string name UK
        int sort_order
        int is_active
    }
    MST_FIELD_OWNER {
        int id PK
        string name
        string phone
        int location_id FK
        int is_active
    }
    TEST_SCHEDULE {
        int id PK
        string test_number UK
        string title
        string test_year
        int company_id FK
        int category_id FK
        int location_id FK
        int field_owner_id FK
        string start_date
        string status
    }
    SCHEDULE_TREATMENT {
        int id PK
        int schedule_id FK
        int round_no
        string treatment_date
    }
    SCHEDULE_SURVEY {
        int id PK
        int schedule_id FK
        int round_no
        string survey_date
    }

    USER ||--o{ TEST_PROTOCOL : "creates"
    TEST_PROTOCOL ||--o{ TREATMENT : "has"
    TEST_PROTOCOL ||--o{ PLOT_DESIGN : "has"
    MST_COMPANY ||--o{ TEST_SCHEDULE : "ref"
    MST_CATEGORY ||--o{ TEST_SCHEDULE : "ref"
    MST_LOCATION ||--o{ TEST_SCHEDULE : "ref"
    MST_FIELD_OWNER ||--o{ TEST_SCHEDULE : "ref"
    MST_LOCATION ||--o{ MST_FIELD_OWNER : "ref"
    TEST_SCHEDULE ||--o{ SCHEDULE_TREATMENT : "has"
    TEST_SCHEDULE ||--o{ SCHEDULE_SURVEY : "has"
      </div>
    `,
  },
  // ── Slide 5: API 설계 ──
  {
    title: '5. REST API 설계 (API Endpoints)',
    component: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <ArchFlow
          height={300}
          groups={[
            { id: 'g_auth', label: '🔐 Authentication', x: 0, y: 0, w: 250, h: 95, tone: 'green' },
            { id: 'g_file', label: '📄 File Export', x: 290, y: 0, w: 280, h: 95, tone: 'orange' },
            { id: 'g_stat', label: '📊 Statistics', x: 610, y: 0, w: 250, h: 95, tone: 'purple' },
            { id: 'g_crud', label: '📋 Protocol CRUD', x: 0, y: 135, w: 860, h: 115, tone: 'blue' },
          ]}
          nodes={[
            { id: 'a1', group: 'g_auth', x: 16, y: 38, label: 'POST /login', sub: 'JWT 발급', tone: 'green' },
            { id: 'c1', group: 'g_file', x: 16, y: 38, label: 'GET /:id/hwpx', sub: 'HWPX 다운로드', tone: 'orange' },
            { id: 'd1', group: 'g_stat', x: 16, y: 38, label: 'GET /dashboard', sub: '통계 데이터', tone: 'purple' },
            { id: 'b1', group: 'g_crud', x: 16, y: 42, label: 'GET /protocols', sub: '목록', tone: 'blue' },
            { id: 'b2', group: 'g_crud', x: 180, y: 42, label: 'GET /:id', sub: '상세', tone: 'blue' },
            { id: 'b3', group: 'g_crud', x: 340, y: 42, label: 'POST /protocols', sub: '등록', tone: 'blue' },
            { id: 'b4', group: 'g_crud', x: 520, y: 42, label: 'PUT /:id', sub: '수정', tone: 'blue' },
            { id: 'b5', group: 'g_crud', x: 680, y: 42, label: 'DELETE /:id', sub: '삭제', tone: 'red' },
          ]}
          edges={[]}
        />
        <div dangerouslySetInnerHTML={{ __html: `
      <div style="margin-top:20px;overflow-x:auto">
        <table style="width:100%;border-collapse:collapse;font-size:14px">
          <thead>
            <tr style="background:linear-gradient(135deg,#667eea,#764ba2);color:#fff">
              <th style="padding:10px 16px;text-align:left;border-radius:8px 0 0 0">Method</th>
              <th style="padding:10px 16px;text-align:left">Endpoint</th>
              <th style="padding:10px 16px;text-align:left">Auth</th>
              <th style="padding:10px 16px;text-align:left;border-radius:0 8px 0 0">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr style="background:#f8f9ff"><td style="padding:8px 16px"><code style="background:#43e97b33;padding:2px 8px;border-radius:4px;color:#2e7d32">POST</code></td><td><code>/api/auth/login</code></td><td>-</td><td>로그인 (JWT 발급)</td></tr>
            <tr><td style="padding:8px 16px"><code style="background:#4facfe33;padding:2px 8px;border-radius:4px;color:#1565c0">GET</code></td><td><code>/api/protocols</code></td><td>Bearer</td><td>전체 목록</td></tr>
            <tr style="background:#f8f9ff"><td style="padding:8px 16px"><code style="background:#4facfe33;padding:2px 8px;border-radius:4px;color:#1565c0">GET</code></td><td><code>/api/protocols/:id</code></td><td>Bearer</td><td>상세 조회</td></tr>
            <tr><td style="padding:8px 16px"><code style="background:#43e97b33;padding:2px 8px;border-radius:4px;color:#2e7d32">POST</code></td><td><code>/api/protocols</code></td><td>Bearer</td><td>신규 등록</td></tr>
            <tr style="background:#f8f9ff"><td style="padding:8px 16px"><code style="background:#f7971e33;padding:2px 8px;border-radius:4px;color:#e65100">PUT</code></td><td><code>/api/protocols/:id</code></td><td>Bearer</td><td>수정</td></tr>
            <tr><td style="padding:8px 16px"><code style="background:#fa709a33;padding:2px 8px;border-radius:4px;color:#c62828">DEL</code></td><td><code>/api/protocols/:id</code></td><td>Bearer</td><td>삭제</td></tr>
            <tr style="background:#f8f9ff"><td style="padding:8px 16px"><code style="background:#4facfe33;padding:2px 8px;border-radius:4px;color:#1565c0">GET</code></td><td><code>/api/protocols/:id/hwpx</code></td><td>Bearer</td><td>HWPX 파일 생성/다운로드</td></tr>
            <tr><td style="padding:8px 16px"><code style="background:#4facfe33;padding:2px 8px;border-radius:4px;color:#1565c0">GET</code></td><td><code>/api/dashboard</code></td><td>Bearer</td><td>통계 데이터</td></tr>
            <tr style="background:#f0fdf4"><td colspan="4" style="padding:8px 16px;font-weight:700;color:#16a34a">시험일정 API (정규화)</td></tr>
            <tr><td style="padding:8px 16px"><code style="background:#4facfe33;padding:2px 8px;border-radius:4px;color:#1565c0">GET</code></td><td><code>/api/schedules</code></td><td>Bearer</td><td>일정 목록 (검색/필터)</td></tr>
            <tr style="background:#f8f9ff"><td style="padding:8px 16px"><code style="background:#43e97b33;padding:2px 8px;border-radius:4px;color:#2e7d32">POST</code></td><td><code>/api/schedules</code></td><td>Bearer</td><td>일정 등록 (마스터FK+1:N)</td></tr>
            <tr><td style="padding:8px 16px"><code style="background:#f7971e33;padding:2px 8px;border-radius:4px;color:#e65100">PUT</code></td><td><code>/api/schedules/:id</code></td><td>Bearer</td><td>일정 수정</td></tr>
            <tr style="background:#f8f9ff"><td style="padding:8px 16px"><code style="background:#fa709a33;padding:2px 8px;border-radius:4px;color:#c62828">DEL</code></td><td><code>/api/schedules/:id</code></td><td>Bearer</td><td>일정 삭제 (자식 연쇄)</td></tr>
            <tr><td style="padding:8px 16px"><code style="background:#43e97b33;padding:2px 8px;border-radius:4px;color:#2e7d32">POST</code></td><td><code>/api/schedules/bulk</code></td><td>Bearer</td><td>n건 일괄등록</td></tr>
            <tr style="background:#f8f9ff"><td style="padding:8px 16px"><code style="background:#43e97b33;padding:2px 8px;border-radius:4px;color:#2e7d32">POST</code></td><td><code>/api/schedules/upload-excel</code></td><td>Bearer</td><td>Excel 업로드 (마스터 자동생성)</td></tr>
            <tr><td style="padding:8px 16px"><code style="background:#4facfe33;padding:2px 8px;border-radius:4px;color:#1565c0">GET</code></td><td><code>/api/schedules/export/excel</code></td><td>Bearer</td><td>Excel 다운로드</td></tr>
            <tr style="background:#fef3c7"><td colspan="4" style="padding:8px 16px;font-weight:700;color:#d97706">마스터 API (CRUD x4)</td></tr>
            <tr style="background:#f8f9ff"><td style="padding:8px 16px"><code style="background:#4facfe33;padding:2px 8px;border-radius:4px;color:#1565c0">CRUD</code></td><td><code>/api/master/companies</code></td><td>Bearer</td><td>의뢰사 마스터</td></tr>
            <tr><td style="padding:8px 16px"><code style="background:#4facfe33;padding:2px 8px;border-radius:4px;color:#1565c0">CRUD</code></td><td><code>/api/master/locations</code></td><td>Bearer</td><td>시험장소 마스터</td></tr>
            <tr style="background:#f8f9ff"><td style="padding:8px 16px"><code style="background:#4facfe33;padding:2px 8px;border-radius:4px;color:#1565c0">CRUD</code></td><td><code>/api/master/categories</code></td><td>Bearer</td><td>시험구분 마스터</td></tr>
            <tr><td style="padding:8px 16px"><code style="background:#4facfe33;padding:2px 8px;border-radius:4px;color:#1565c0">CRUD</code></td><td><code>/api/master/field-owners</code></td><td>Bearer</td><td>포장주 마스터</td></tr>
          </tbody>
        </table>
      </div>
` }} />
      </div>
    ),
  },
  // ── Slide 6: 인증 흐름 ──
  {
    title: '6. 인증 흐름 (Authentication Flow)',
    content: `
      <div class="mermaid">
sequenceDiagram
    participant U as 👤 사용자
    participant R as 🌐 React
    participant F as ⚡ FastAPI
    participant DB as 🗄️ PostgreSQL

    U->>R: ID/PW 입력
    R->>F: POST /api/auth/login
    F->>DB: SELECT user WHERE username=?
    DB-->>F: User Record
    F->>F: bcrypt.verify(password)
    alt 인증 성공
        F->>F: JWT 생성 (자동로그인 ON 30일 / OFF 8시간)
        F-->>R: {access_token, username}
        R->>R: 토큰 저장 (ON localStorage / OFF sessionStorage)
        R-->>U: 대시보드로 이동
    else 인증 실패
        F-->>R: 401 Unauthorized
        R-->>U: 오류 메시지 표시
    end

    Note over R,F: 이후 모든 API 요청에<br/>Authorization: Bearer {token} 포함

    U->>R: 보호된 페이지 접근
    R->>F: GET /api/protocols (Bearer token)
    F->>F: JWT 검증 (jose)
    alt 토큰 유효
        F->>DB: Query Data
        DB-->>F: Results
        F-->>R: 200 OK + Data
    else 토큰 만료/무효
        F-->>R: 401 Unauthorized
        R->>R: 토큰 삭제
        R-->>U: 로그인 페이지로 리다이렉트
    end
      </div>
    `,
  },
  // ── Slide 6-A: 자동로그인 (Remember Me) ──
  {
    title: '6-A. 자동로그인 (Remember Me) — 토큰 수명 분기',
    component: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ArchFlow
          height={400}
          groups={[
            { id: 'g_on', label: '☑ 자동로그인 ON', x: 410, y: 20, w: 640, h: 110, tone: 'green' },
            { id: 'g_off', label: '☐ 자동로그인 OFF (기본)', x: 410, y: 250, w: 640, h: 110, tone: 'slate' },
          ]}
          nodes={[
            { id: 'login', x: 0, y: 175, label: '로그인', sub: '자동로그인 체크박스', emoji: '🔐', tone: 'indigo' },
            { id: 'check', x: 215, y: 178, label: '체크 여부', sub: 'remember 플래그', emoji: '❓', tone: 'amber' },
            { id: 'on_token', group: 'g_on', x: 20, y: 42, label: '30일 토큰', sub: 'REMEMBER_TOKEN', tone: 'green' },
            { id: 'on_store', group: 'g_on', x: 220, y: 42, label: 'localStorage', sub: '지속 저장', tone: 'green' },
            { id: 'on_result', group: 'g_on', x: 430, y: 42, label: '재시작해도 유지', sub: '30일 자동 로그인', tone: 'green' },
            { id: 'off_token', group: 'g_off', x: 20, y: 42, label: '8시간 토큰', sub: 'ACCESS_TOKEN', tone: 'slate' },
            { id: 'off_store', group: 'g_off', x: 220, y: 42, label: 'sessionStorage', sub: '탭 종료 시 소멸', tone: 'slate' },
            { id: 'off_result', group: 'g_off', x: 430, y: 42, label: '닫으면 로그아웃', sub: '공용 PC 안전', tone: 'blue' },
          ]}
          edges={[
            { source: 'login', target: 'check', tone: 'indigo' },
            { source: 'check', target: 'on_token', label: 'ON ☑', tone: 'green', animated: true },
            { source: 'check', target: 'off_token', label: 'OFF ☐', tone: 'slate' },
            { source: 'on_token', target: 'on_store', tone: 'green' },
            { source: 'on_store', target: 'on_result', tone: 'green', animated: true },
            { source: 'off_token', target: 'off_store', tone: 'slate' },
            { source: 'off_store', target: 'off_result', tone: 'slate' },
          ]}
        />
        <div dangerouslySetInnerHTML={{ __html: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
        <div style="background:linear-gradient(135deg,#ecfdf5,#d1fae5);border:1px solid #6ee7b7;border-radius:14px;padding:18px">
          <h4 style="color:#059669;margin:0 0 10px;font-size:14px">☑ 자동로그인 ON</h4>
          <table style="width:100%;font-size:12.5px;border-collapse:collapse">
            <tr><td style="padding:4px 0;color:#64748b;width:38%">토큰 수명</td><td style="color:#1e293b"><strong>30일</strong> (REMEMBER_TOKEN_EXPIRE)</td></tr>
            <tr><td style="padding:4px 0;color:#64748b">저장소</td><td style="color:#1e293b"><strong>localStorage</strong> (영구)</td></tr>
            <tr><td style="padding:4px 0;color:#64748b">지속성</td><td style="color:#1e293b">브라우저 재시작해도 유지</td></tr>
            <tr><td style="padding:4px 0;color:#64748b">용도</td><td style="color:#1e293b">개인 PC · 매일 사용</td></tr>
          </table>
        </div>
        <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:14px;padding:18px">
          <h4 style="color:#475569;margin:0 0 10px;font-size:14px">☐ 자동로그인 OFF (기본값)</h4>
          <table style="width:100%;font-size:12.5px;border-collapse:collapse">
            <tr><td style="padding:4px 0;color:#64748b;width:38%">토큰 수명</td><td style="color:#1e293b"><strong>8시간</strong> (ACCESS_TOKEN_EXPIRE)</td></tr>
            <tr><td style="padding:4px 0;color:#64748b">저장소</td><td style="color:#1e293b"><strong>sessionStorage</strong></td></tr>
            <tr><td style="padding:4px 0;color:#64748b">지속성</td><td style="color:#1e293b">브라우저/탭 닫으면 로그아웃</td></tr>
            <tr><td style="padding:4px 0;color:#64748b">용도</td><td style="color:#1e293b">공용 PC · 보안 우선</td></tr>
          </table>
        </div>
      </div>
      <div style="margin-top:16px;display:grid;grid-template-columns:1.3fr 1fr;gap:16px">
        <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:14px;padding:16px">
          <h4 style="color:#b45309;margin:0 0 8px;font-size:13px">🔒 보안 모델 & 메모</h4>
          <ul style="margin:0;padding-left:16px;color:#555;font-size:12px;line-height:1.85">
            <li>두 저장소 모두 <code>getToken()</code>이 조회 → 어디 있든 인증 동작</li>
            <li>로그아웃 · 401 응답 시 <code>clearAuth()</code>가 <strong>양쪽 저장소 모두 정리</strong></li>
            <li>스테이트리스 JWT — 서버측 강제 폐기 불가 (SECRET 교체로만 전체 무효화)</li>
            <li>30일 토큰은 XSS 노출 창이 길다 → 사내 도구 수준에서 수용</li>
            <li>적용 범위: <strong>관리자(lab) + 파트너 포털</strong> · 각 토큰(token / partner_token) 독립 관리</li>
          </ul>
        </div>
        <div style="background:#eef2ff;border:1px solid #c7d2fe;border-radius:14px;padding:16px">
          <h4 style="color:#4f46e5;margin:0 0 8px;font-size:13px">🛠️ 구현 위치</h4>
          <table style="width:100%;font-size:11.5px;border-collapse:collapse;line-height:1.5">
            <tr><td style="padding:3px 0;color:#64748b">백엔드</td><td style="color:#1e293b"><code>auth.py</code> · <code>schemas.py</code> · <code>main.py</code></td></tr>
            <tr><td style="padding:3px 0;color:#64748b">토큰 헬퍼</td><td style="color:#1e293b"><code>services/authToken.ts</code></td></tr>
            <tr><td style="padding:3px 0;color:#64748b">로그인 UI</td><td style="color:#1e293b"><code>pages/LoginPage.tsx</code></td></tr>
            <tr><td style="padding:3px 0;color:#64748b">선택 기억</td><td style="color:#1e293b"><code>localStorage('sappt_auto_login')</code></td></tr>
          </table>
        </div>
      </div>
        ` }} />
      </div>
    ),
  },
  // ── Slide 7: HWPX 생성 엔진 ──
  {
    title: '7. HWPX 생성 엔진 (Document Engine)',
    component: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ArchFlow
          height={540}
          groups={[
            { id: 'g_input', label: '📥 Input', x: 0, y: 0, w: 560, h: 95, tone: 'green' },
            { id: 'g_engine', label: '⚙️ HWPX Engine', x: 120, y: 135, w: 320, h: 135, tone: 'orange' },
            { id: 'g_struct', label: '📦 HWPX Structure (ZIP)', x: 0, y: 310, w: 560, h: 110, tone: 'blue' },
            { id: 'g_output', label: '📤 Output', x: 175, y: 460, w: 220, h: 90, tone: 'purple' },
          ]}
          nodes={[
            { id: 'p', group: 'g_input', x: 16, y: 38, label: 'Protocol Data', tone: 'green' },
            { id: 't', group: 'g_input', x: 200, y: 38, label: 'Treatments[]', tone: 'green' },
            { id: 'pd', group: 'g_input', x: 380, y: 38, label: 'PlotDesigns[]', tone: 'green' },
            { id: 'try', group: 'g_engine', x: 40, y: 34, label: 'python-hwpx', sub: 'Primary Engine', tone: 'orange' },
            { id: 'fallback', group: 'g_engine', x: 40, y: 82, label: 'Manual XML Builder', sub: 'Fallback Engine', tone: 'amber' },
            { id: 'm', group: 'g_struct', x: 16, y: 40, label: 'mimetype', tone: 'blue' },
            { id: 'meta', group: 'g_struct', x: 150, y: 40, label: 'META-INF/', sub: 'container.xml', tone: 'blue' },
            { id: 'cont', group: 'g_struct', x: 320, y: 40, label: 'Contents/', sub: 'section0 · header · content.hpf', tone: 'blue' },
            { id: 'file', group: 'g_output', x: 16, y: 38, label: '*.hwpx', sub: '≈10KB', emoji: '📄', tone: 'purple' },
          ]}
          edges={[
            { source: 'p', target: 'try', tone: 'green' },
            { source: 't', target: 'try', tone: 'green' },
            { source: 'pd', target: 'try', tone: 'green' },
            { source: 'try', target: 'fallback', label: '예외 발생 시', tone: 'orange', dashed: true },
            { source: 'try', target: 'cont', label: '조립', tone: 'blue', animated: true },
            { source: 'fallback', target: 'cont', tone: 'amber', dashed: true },
            { source: 'cont', target: 'file', label: 'ZIP 패키징', tone: 'purple', animated: true },
          ]}
        />
        <div dangerouslySetInnerHTML={{ __html: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:20px">
        <div style="background:#f8f9ff;padding:20px;border-radius:12px;border:1px solid #e0e4ff">
          <h4 style="color:#667eea;margin-top:0">Primary: python-hwpx</h4>
          <ul style="color:#555;line-height:1.9;font-size:13px;margin:0;padding-left:16px">
            <li>HwpxDocument.new() 빈 문서 생성</li>
            <li>add_paragraph() 단락 추가</li>
            <li>add_table(rows, cols) 테이블 생성</li>
            <li>set_cell_text() 셀 텍스트 입력</li>
            <li>save_to_path() 파일 저장</li>
          </ul>
        </div>
        <div style="background:#fff8f0;padding:20px;border-radius:12px;border:1px solid #ffe0c0">
          <h4 style="color:#f7971e;margin-top:0">Fallback: Manual XML</h4>
          <ul style="color:#555;line-height:1.9;font-size:13px;margin:0;padding-left:16px">
            <li>OXML(ZIP) 구조 직접 생성</li>
            <li>XML 요소로 단락/테이블 구성</li>
            <li>mimetype 비압축 저장 (규격 준수)</li>
            <li>환경 독립적 (의존성 최소)</li>
            <li>Docker 호환성 보장</li>
          </ul>
        </div>
      </div>
` }} />
      </div>
    ),
  },
  // ── Slide 8: 프론트엔드 컴포넌트 ──
  {
    title: '8. 프론트엔드 컴포넌트 구조 (Frontend)',
    component: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ArchFlow
          height={460}
          groups={[
            { id: 'g_app', label: 'App.tsx', x: 0, y: 40, w: 230, h: 200, tone: 'green' },
            { id: 'g_layout', label: 'MainLayout.tsx', x: 290, y: 60, w: 200, h: 170, tone: 'blue' },
            { id: 'g_pages', label: 'Pages', x: 550, y: 0, w: 410, h: 410, tone: 'orange' },
            { id: 'g_services', label: 'Services', x: 1010, y: 160, w: 180, h: 110, tone: 'purple' },
          ]}
          nodes={[
            { id: 'router', group: 'g_app', x: 16, y: 36, label: 'BrowserRouter', tone: 'green' },
            { id: 'login', group: 'g_app', x: 16, y: 90, label: 'LoginPage', sub: '인증 폼', tone: 'green' },
            { id: 'private', group: 'g_app', x: 16, y: 144, label: 'PrivateRoute', sub: '토큰 검증', tone: 'green' },
            { id: 'sider', group: 'g_layout', x: 16, y: 36, label: 'Sider', sub: '메뉴 네비', tone: 'blue' },
            { id: 'header', group: 'g_layout', x: 16, y: 84, label: 'Header', sub: '사용자/로그아웃', tone: 'blue' },
            { id: 'content', group: 'g_layout', x: 16, y: 132, label: 'Content', sub: 'Outlet', tone: 'blue' },
            { id: 'dash', group: 'g_pages', x: 16, y: 40, label: 'DashboardPage', sub: 'ECharts + Leaflet', tone: 'orange' },
            { id: 'list', group: 'g_pages', x: 215, y: 40, label: 'ProtocolListPage', sub: '시험계획서', tone: 'orange' },
            { id: 'sched', group: 'g_pages', x: 16, y: 130, label: 'SchedulePage', sub: '시험일정총괄표', tone: 'orange' },
            { id: 'master', group: 'g_pages', x: 215, y: 130, label: 'MasterPage', sub: '마스터 데이터', tone: 'orange' },
            { id: 'server', group: 'g_pages', x: 16, y: 220, label: 'ServerPage', sub: '모니터링/로그', tone: 'orange' },
            { id: 'ops', group: 'g_pages', x: 215, y: 220, label: 'OpsPage', sub: '활동로그/파트너', tone: 'orange' },
            { id: 'guide', group: 'g_pages', x: 16, y: 310, label: 'SystemGuidePage', sub: '시스템 가이드', tone: 'orange' },
            { id: 'api', group: 'g_services', x: 16, y: 40, label: 'api.ts', sub: 'Axios + 인터셉터', tone: 'purple' },
          ]}
          edges={[
            { source: 'router', target: 'private', tone: 'green' },
            { source: 'router', target: 'login', label: 'public', tone: 'slate' },
            { source: 'private', target: 'content', label: '인증 후', tone: 'blue' },
            { source: 'content', target: 'dash', label: 'Outlet', tone: 'orange', animated: true },
            { source: 'dash', target: 'api', label: 'Axios', tone: 'purple', animated: true },
            { source: 'list', target: 'api', tone: 'purple' },
            { source: 'sched', target: 'api', tone: 'purple' },
          ]}
        />
        <div dangerouslySetInnerHTML={{ __html: `
      <div style="margin-top:20px;background:#f8fafc;padding:16px;border-radius:12px;border:1px solid #e2e8f0">
        <h4 style="margin:0 0 12px;color:#334155">메뉴 구조 (5개 그룹)</h4>
        <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:10px;font-size:12px">
          <div style="background:#eef2ff;padding:12px;border-radius:10px;border:1px solid #c7d2fe">
            <strong style="color:#4f46e5">시험 관리</strong><br/>
            <span style="color:#555">대시보드<br/>시험계획서<br/>시험일정총괄표</span>
          </div>
          <div style="background:#f0fdf4;padding:12px;border-radius:10px;border:1px solid #bbf7d0">
            <strong style="color:#16a34a">데이터 관리</strong><br/>
            <span style="color:#555">의뢰사 관리<br/>시험장소 관리<br/>구분/포장주 관리</span>
          </div>
          <div style="background:#fff7ed;padding:12px;border-radius:10px;border:1px solid #fed7aa">
            <strong style="color:#ea580c">서버 관리</strong><br/>
            <span style="color:#555">서버 모니터링<br/>서버 로그</span>
          </div>
          <div style="background:#fef3c7;padding:12px;border-radius:10px;border:1px solid #fde68a">
            <strong style="color:#d97706">운영 관리</strong><br/>
            <span style="color:#555">활동 로그<br/>파트너 계정</span>
          </div>
          <div style="background:#faf5ff;padding:12px;border-radius:10px;border:1px solid #e9d5ff">
            <strong style="color:#7c3aed">시스템</strong><br/>
            <span style="color:#555">시스템 가이드</span>
          </div>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-top:12px">
        <div style="background:#f0fdf4;padding:14px;border-radius:10px;text-align:center;border:1px solid #bbf7d0">
          <div style="font-size:24px">🎨</div>
          <strong style="font-size:13px">Ant Design 6</strong><br/><span style="color:#888;font-size:11px">UI 컴포넌트</span>
        </div>
        <div style="background:#eff6ff;padding:14px;border-radius:10px;text-align:center;border:1px solid #bfdbfe">
          <div style="font-size:24px">📊</div>
          <strong style="font-size:13px">ECharts</strong><br/><span style="color:#888;font-size:11px">Bar/Pie 차트</span>
        </div>
        <div style="background:#fefce8;padding:14px;border-radius:10px;text-align:center;border:1px solid #fef08a">
          <div style="font-size:24px">🔗</div>
          <strong style="font-size:13px">React Router</strong><br/><span style="color:#888;font-size:11px">SPA 라우팅</span>
        </div>
        <div style="background:#fdf2f8;padding:14px;border-radius:10px;text-align:center;border:1px solid #fbcfe8">
          <div style="font-size:24px">📡</div>
          <strong style="font-size:13px">Axios</strong><br/><span style="color:#888;font-size:11px">HTTP + 인터셉터</span>
        </div>
      </div>
` }} />
      </div>
    ),
  },
  // ── Slide 9: 데이터 흐름 ──
  {
    title: '9. 시험계획서 작성 데이터 흐름 (Data Flow)',
    content: `
      <div class="mermaid">
sequenceDiagram
    participant U as 👤 사용자
    participant FORM as 📝 ProtocolForm
    participant API as 📡 Axios
    participant SRV as ⚡ FastAPI
    participant DB as 🗄️ PostgreSQL
    participant HWP as 📄 HWPX Engine

    U->>FORM: 양식 입력<br/>(기본정보 + 약제 + 시험구)
    FORM->>FORM: Form.List로 동적 약제 추가
    U->>FORM: 저장 버튼 클릭
    FORM->>API: POST /api/protocols
    API->>SRV: JSON Payload

    SRV->>DB: INSERT test_protocols
    SRV->>DB: INSERT treatments (N건)
    SRV->>DB: INSERT plot_designs (N건)
    DB-->>SRV: Commit OK
    SRV-->>API: ProtocolResponse
    API-->>FORM: 등록 완료
    FORM-->>U: 목록 페이지로 이동

    Note over U,HWP: HWPX 다운로드 시

    U->>API: HWPX 다운로드 클릭
    API->>SRV: GET /api/protocols/:id/hwpx
    SRV->>DB: SELECT protocol + treatments + plots
    DB-->>SRV: Full Data
    SRV->>HWP: generate_hwpx(data)
    HWP->>HWP: 문서 구조 생성
    HWP-->>SRV: 임시 파일 경로
    SRV-->>API: FileResponse (HWPX Blob)
    API-->>U: 파일 다운로드 트리거
      </div>
    `,
  },
  // ── Slide 10: 보안 아키텍처 ──
  {
    title: '10. 보안 아키텍처 (Security)',
    component: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ArchFlow
          height={660}
          groups={[
            { id: 'g_net', label: '🔥 네트워크 계층 (Fail2Ban)', x: 0, y: 0, w: 580, h: 95, tone: 'red' },
            { id: 'g_transport', label: '🔐 전송 계층', x: 130, y: 135, w: 320, h: 95, tone: 'green' },
            { id: 'g_auth', label: '🛡️ 인증 계층', x: 0, y: 270, w: 660, h: 95, tone: 'blue' },
            { id: 'g_app', label: '🏰 애플리케이션 계층', x: 60, y: 405, w: 460, h: 95, tone: 'orange' },
            { id: 'g_data', label: '💾 데이터 계층', x: 140, y: 540, w: 320, h: 95, tone: 'purple' },
          ]}
          nodes={[
            { id: 'f2b_ssh', group: 'g_net', x: 16, y: 38, label: 'SSH Jail', sub: '3회→24h 차단', tone: 'red' },
            { id: 'f2b_http', group: 'g_net', x: 200, y: 38, label: 'HTTP Jail', sub: '20회/60s→1h', tone: 'red' },
            { id: 'nft', group: 'g_net', x: 400, y: 38, label: 'nftables', sub: 'IP 방화벽', tone: 'red' },
            { id: 'ssl', group: 'g_transport', x: 16, y: 38, label: 'TLS 1.3', sub: "Let's Encrypt", tone: 'green' },
            { id: 'hsts', group: 'g_transport', x: 175, y: 38, label: 'HSTS', sub: '강제 HTTPS', tone: 'green' },
            { id: 'jwt', group: 'g_auth', x: 16, y: 40, label: 'JWT (HS256)', sub: '8h 만료', tone: 'blue' },
            { id: 'bcrypt', group: 'g_auth', x: 140, y: 40, label: 'bcrypt', sub: '패스워드', tone: 'blue' },
            { id: 'oauth', group: 'g_auth', x: 270, y: 40, label: 'OAuth2 Bearer', tone: 'blue' },
            { id: 'okta', group: 'g_auth', x: 410, y: 40, label: 'Okta SSO', sub: 'OIDC 통제', tone: 'indigo' },
            { id: 'proxy', group: 'g_auth', x: 540, y: 40, label: 'oauth2-proxy', sub: 'NPM Admin', tone: 'indigo' },
            { id: 'cors', group: 'g_app', x: 16, y: 38, label: 'CORS', sub: '교차 출처', tone: 'orange' },
            { id: 'valid', group: 'g_app', x: 165, y: 38, label: 'Pydantic 검증', tone: 'orange' },
            { id: 'exploit', group: 'g_app', x: 320, y: 38, label: 'Block Exploits', sub: 'NPM 필터', tone: 'orange' },
            { id: 'orm', group: 'g_data', x: 16, y: 38, label: 'SQLAlchemy ORM', sub: 'SQLi 방어', tone: 'purple' },
            { id: 'param', group: 'g_data', x: 180, y: 38, label: 'Param Query', sub: '바인딩', tone: 'purple' },
          ]}
          edges={[
            { source: 'f2b_ssh', target: 'nft', tone: 'red' },
            { source: 'f2b_http', target: 'nft', tone: 'red' },
            { source: 'nft', target: 'ssl', tone: 'green', animated: true },
            { source: 'ssl', target: 'jwt', tone: 'blue', animated: true },
            { source: 'okta', target: 'proxy', tone: 'indigo' },
            { source: 'jwt', target: 'cors', tone: 'orange', animated: true },
            { source: 'cors', target: 'orm', tone: 'purple', animated: true },
          ]}
        />
        <div dangerouslySetInnerHTML={{ __html: `
      <div style="margin-top:20px;background:linear-gradient(135deg,#667eea08,#764ba208);padding:20px;border-radius:12px;border:1px solid #667eea15">
        <h4 style="color:#667eea;margin-top:0">보안 체크리스트</h4>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:13px;color:#555">
          <div>&#10003; 비밀번호 bcrypt 단방향 해싱</div>
          <div>&#10003; JWT 토큰 만료 시간 제한 (8h)</div>
          <div>&#10003; SQLAlchemy ORM으로 SQL Injection 방어</div>
          <div>&#10003; HTTPS 강제 리다이렉트 (HSTS)</div>
          <div>&#10003; Pydantic 스키마 기반 입력 검증</div>
          <div>&#10003; NPM Block Exploits 활성화</div>
          <div>&#10003; 401 시 자동 토큰 삭제 및 리다이렉트</div>
          <div>&#10003; 내부 네트워크 격리 (Docker Network)</div>
          <div style="color:#e11d48">&#10003; Fail2Ban SSH 브루트포스 차단 (3회→24h)</div>
          <div style="color:#e11d48">&#10003; Fail2Ban HTTP 스캔 차단 (20회/60s→1h)</div>
          <div style="color:#e11d48">&#10003; nftables IP 방화벽 자동 적용</div>
          <div style="color:#e11d48">&#10003; Discord 보안 알림 (즉시 차단 + 06:30 일일 보고)</div>
          <div style="color:#4f46e5">&#10003; Okta SSO — 인프라 접속 통합 인증 (log/npm)</div>
          <div style="color:#4f46e5">&#10003; oauth2-proxy — NPM Admin Okta OIDC 보호</div>
        </div>
      </div>
` }} />
      </div>
    ),
  },
  // ── Slide 11: 배포 파이프라인 (GitHub Actions + GHCR + k3s) ──
  {
    title: '11. 무중단 배포 파이프라인 — 전체 흐름 (Zero-Downtime Deployment Pipeline)',
    component: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h3 style={{ color: '#4f46e5', margin: 0, fontSize: 16 }}>
          ① 전체 배포 흐름도 <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>· 노드 드래그 / 휠 줌 가능</span>
        </h3>
        <ArchFlow
          height={400}
          groups={[
            { id: 'g_dev', label: '🛠️ 개발 (Local)', x: 0, y: 20, w: 185, h: 165, tone: 'purple' },
            { id: 'g_ci', label: '⚙️ GitHub Actions', x: 235, y: 0, w: 200, h: 225, tone: 'green' },
            { id: 'g_ghcr', label: '📦 GHCR (Public)', x: 485, y: 20, w: 190, h: 205, tone: 'blue' },
            { id: 'g_deploy', label: '🚀 k3s Rolling Update', x: 725, y: 0, w: 200, h: 385, tone: 'orange' },
            { id: 'g_users', label: '👥 사용자', x: 975, y: 40, w: 185, h: 205, tone: 'red' },
          ]}
          nodes={[
            { id: 'code', group: 'g_dev', x: 16, y: 36, label: '코드 수정', sub: 'feature branch', tone: 'purple' },
            { id: 'pr', group: 'g_dev', x: 16, y: 100, label: 'Pull Request', sub: '→ main merge', tone: 'purple' },
            { id: 'qemu', group: 'g_ci', x: 16, y: 36, label: 'QEMU 설정', sub: 'ARM64 크로스', tone: 'green' },
            { id: 'bake', group: 'g_ci', x: 16, y: 100, label: 'buildx bake', sub: '3 services 병렬', tone: 'green' },
            { id: 'push_ghcr', group: 'g_ci', x: 16, y: 164, label: 'ghcr.io push', sub: ':latest · :sha · :ts', tone: 'green' },
            { id: 'img_be', group: 'g_ghcr', x: 16, y: 36, label: 'sappt-backend', tone: 'blue' },
            { id: 'img_fe', group: 'g_ghcr', x: 16, y: 94, label: 'sappt-frontend', tone: 'blue' },
            { id: 'img_pfe', group: 'g_ghcr', x: 16, y: 152, label: 'sappt-partner-fe', tone: 'blue' },
            { id: 'anno', group: 'g_deploy', x: 16, y: 36, label: 'kubectl patch', sub: 'restartedAt', tone: 'orange' },
            { id: 'pull', group: 'g_deploy', x: 16, y: 96, label: 'containerd pull', sub: 'Always', tone: 'orange' },
            { id: 'pod_new', group: 'g_deploy', x: 16, y: 156, label: '새 Pod 생성', sub: '+1 surge', tone: 'orange' },
            { id: 'ready', group: 'g_deploy', x: 16, y: 216, label: 'readinessProbe', sub: '/api/storage/health', tone: 'orange' },
            { id: 'svc', group: 'g_deploy', x: 16, y: 276, label: 'Service 전환', sub: '새 Pod로', tone: 'orange' },
            { id: 'pod_old', group: 'g_deploy', x: 16, y: 330, label: '구 Pod 종료', sub: 'drain', tone: 'slate' },
            { id: 'u1', group: 'g_users', x: 16, y: 36, label: '관리자', sub: 'lab.sappt.co.kr', tone: 'red' },
            { id: 'u2', group: 'g_users', x: 16, y: 100, label: '파트너', sub: 'partner.sappt.co.kr', tone: 'red' },
            { id: 'u3', group: 'g_users', x: 16, y: 156, label: 'API', sub: 'api.sappt.co.kr', tone: 'red' },
          ]}
          edges={[
            { source: 'code', target: 'pr', tone: 'purple' },
            { source: 'pr', target: 'qemu', label: 'git push main', tone: 'green', animated: true },
            { source: 'qemu', target: 'bake', tone: 'green' },
            { source: 'bake', target: 'push_ghcr', tone: 'green' },
            { source: 'push_ghcr', target: 'img_be', label: 'push', tone: 'blue', animated: true },
            { source: 'img_be', target: 'anno', label: './deploy.sh all', tone: 'orange', animated: true },
            { source: 'anno', target: 'pull', tone: 'orange' },
            { source: 'pull', target: 'pod_new', tone: 'orange' },
            { source: 'pod_new', target: 'ready', tone: 'orange' },
            { source: 'ready', target: 'svc', tone: 'orange' },
            { source: 'svc', target: 'pod_old', tone: 'slate', dashed: true },
            { source: 'svc', target: 'u1', label: '무중단 서비스', tone: 'red', animated: true },
          ]}
        />
        <div dangerouslySetInnerHTML={{ __html: `
      <h3 style="color:#4f46e5;margin-top:20px;font-size:16px">② Rolling Update 단계별 Pod 상태 전이</h3>
      <div class="mermaid">
sequenceDiagram
    participant OPS as 운영자 (deploy.sh)
    participant K8S as k3s API Server
    participant GHCR as GHCR Registry
    participant POD_A as Pod-A (구버전)
    participant POD_B as Pod-B (신버전)
    participant LB as Service (LoadBalancer)

    Note over POD_A,LB: 배포 전: Pod-A ×2 운영 중 (트래픽 수신)
    OPS->>K8S: kubectl patch (restartedAt + image)
    K8S->>GHCR: pull sappt-*:latest
    GHCR-->>K8S: 이미지 반환
    K8S->>POD_B: 신규 Pod 생성 (maxSurge:1)
    Note over POD_B: readinessProbe 대기<br/>/api/storage/health → 200
    POD_B-->>K8S: ✅ Ready
    K8S->>LB: 트래픽 → Pod-B 추가
    Note over POD_A,POD_B: 과도기: 구·신 Pod 동시 트래픽 수신
    K8S->>POD_A: Terminating (maxUnavailable:0)
    POD_A-->>LB: 트래픽 제거 → 커넥션 드레인
    Note over POD_B,LB: 배포 후: Pod-B ×2 운영 중 (무중단)
      </div>

      <div style="margin-top:20px;background:#1a1a2e;color:#e0e0e0;padding:20px;border-radius:12px;font-family:monospace;font-size:13px;line-height:2">
        <span style="color:#667eea"># ① GitHub Actions 자동 빌드/푸시 (push to main 트리거)</span><br/>
        <span style="color:#888"># .github/workflows/build-and-push.yml → ghcr.io/sappt/sappt-*:latest</span><br/>
        <br/>
        <span style="color:#667eea"># ② 수동 Rolling Update (GHCR 최신 이미지 적용)</span><br/>
        <span style="color:#43e97b">$</span> ./deploy.sh all &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#888"># 전체 서비스 (backend + frontend + partner-frontend)</span><br/>
        <span style="color:#43e97b">$</span> ./deploy.sh backend &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#888"># Backend만 rolling update</span><br/>
        <span style="color:#43e97b">$</span> ./deploy.sh rollback backend <span style="color:#888"># 이전 revision으로 즉시 롤백</span><br/>
        <span style="color:#43e97b">$</span> ./deploy.sh status &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#888"># Pod 상태 + 이미지 확인</span>
      </div>
` }} />
      </div>
    ),
  },
  // ── Slide 11-A: 무중단 배포 구성도 & 구성 요소 ──
  {
    title: '11-A. 무중단 배포 구성도 & 구성 요소 (Architecture & Components)',
    component: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h3 style={{ color: '#4f46e5', margin: 0, fontSize: 16 }}>
          ① 시스템 구성도 <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>· 노드 드래그 / 휠 줌 가능</span>
        </h3>
        <DeployArchFlow />
        <div dangerouslySetInnerHTML={{ __html: `
      <h3 style="color:#4f46e5;margin-top:20px;font-size:16px">② 핵심 구성 요소</h3>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:8px">
        <div style="background:#f0f4ff;border:1px solid #c7d2fe;border-radius:12px;padding:16px">
          <div style="font-weight:700;color:#4f46e5;margin-bottom:10px;font-size:14px">⚙️ GitHub Actions</div>
          <table style="width:100%;font-size:12px;border-collapse:collapse">
            <tr><td style="padding:3px 0;color:#64748b;width:40%">트리거</td><td style="color:#1e293b"><strong>push to main</strong></td></tr>
            <tr><td style="padding:3px 0;color:#64748b">빌드 방식</td><td style="color:#1e293b">docker buildx (QEMU + ARM64)</td></tr>
            <tr><td style="padding:3px 0;color:#64748b">이미지 태그</td><td style="color:#1e293b">:latest / :sha-XXXXXXX / :타임스탬프</td></tr>
            <tr><td style="padding:3px 0;color:#64748b">병렬 빌드</td><td style="color:#1e293b">3 서비스 동시 빌드</td></tr>
            <tr><td style="padding:3px 0;color:#64748b">워크플로</td><td style="color:#1e293b">.github/workflows/build-and-push.yml</td></tr>
          </table>
        </div>
        <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:12px;padding:16px">
          <div style="font-weight:700;color:#15803d;margin-bottom:10px;font-size:14px">📦 GHCR (GitHub Container Registry)</div>
          <table style="width:100%;font-size:12px;border-collapse:collapse">
            <tr><td style="padding:3px 0;color:#64748b;width:40%">레지스트리</td><td style="color:#1e293b"><strong>ghcr.io/sappt/*</strong></td></tr>
            <tr><td style="padding:3px 0;color:#64748b">공개 설정</td><td style="color:#1e293b">Public (인증 없이 pull 가능)</td></tr>
            <tr><td style="padding:3px 0;color:#64748b">이미지 수</td><td style="color:#1e293b">3개 (backend/frontend/partner)</td></tr>
            <tr><td style="padding:3px 0;color:#64748b">보관 정책</td><td style="color:#1e293b">latest + 버전 태그 유지</td></tr>
            <tr><td style="padding:3px 0;color:#64748b">Pull 방식</td><td style="color:#1e293b">k3s containerd 직접 pull</td></tr>
          </table>
        </div>
        <div style="background:#fff7ed;border:1px solid #fed7aa;border-radius:12px;padding:16px">
          <div style="font-weight:700;color:#c2410c;margin-bottom:10px;font-size:14px">🚀 k3s Rolling Update</div>
          <table style="width:100%;font-size:12px;border-collapse:collapse">
            <tr><td style="padding:3px 0;color:#64748b;width:40%">전략</td><td style="color:#1e293b"><strong>RollingUpdate</strong></td></tr>
            <tr><td style="padding:3px 0;color:#64748b">maxUnavailable</td><td style="color:#1e293b"><strong>0</strong> (서비스 중단 없음)</td></tr>
            <tr><td style="padding:3px 0;color:#64748b">maxSurge</td><td style="color:#1e293b"><strong>1</strong> (신규 Pod 먼저 생성)</td></tr>
            <tr><td style="padding:3px 0;color:#64748b">replicas</td><td style="color:#1e293b">2 (서비스별)</td></tr>
            <tr><td style="padding:3px 0;color:#64748b">revisionHistory</td><td style="color:#1e293b">3 (즉시 롤백 가능)</td></tr>
          </table>
        </div>
        <div style="background:#fdf4ff;border:1px solid #e9d5ff;border-radius:12px;padding:16px">
          <div style="font-weight:700;color:#7c3aed;margin-bottom:10px;font-size:14px">❤️ Health Check (readinessProbe)</div>
          <table style="width:100%;font-size:12px;border-collapse:collapse">
            <tr><td style="padding:3px 0;color:#64748b;width:40%">Backend 경로</td><td style="color:#1e293b"><strong>GET /api/storage/health</strong></td></tr>
            <tr><td style="padding:3px 0;color:#64748b">Frontend 경로</td><td style="color:#1e293b"><strong>GET /</strong></td></tr>
            <tr><td style="padding:3px 0;color:#64748b">초기 대기</td><td style="color:#1e293b">Backend 10s / Frontend 5s</td></tr>
            <tr><td style="padding:3px 0;color:#64748b">주기</td><td style="color:#1e293b">5초마다 체크</td></tr>
            <tr><td style="padding:3px 0;color:#64748b">실패 임계값</td><td style="color:#1e293b">3회 연속 실패 시 재시작</td></tr>
          </table>
        </div>
      </div>

      <div style="margin-top:14px;display:grid;grid-template-columns:repeat(5,1fr);gap:10px">
        <div style="background:#e8f5e9;padding:10px;border-radius:10px;text-align:center;border:1px solid #c8e6c9">
          <div style="font-size:20px">🔄</div>
          <strong style="color:#2e7d32;font-size:12px;display:block;margin-top:4px">Zero Downtime</strong>
          <span style="color:#666;font-size:10px">maxUnavailable: 0</span>
        </div>
        <div style="background:#e3f2fd;padding:10px;border-radius:10px;text-align:center;border:1px solid #bbdefb">
          <div style="font-size:20px">⚡</div>
          <strong style="color:#1565c0;font-size:12px;display:block;margin-top:4px">Auto Build</strong>
          <span style="color:#666;font-size:10px">push → 자동 빌드</span>
        </div>
        <div style="background:#f0fdf4;padding:10px;border-radius:10px;text-align:center;border:1px solid #bbf7d0">
          <div style="font-size:20px">📦</div>
          <strong style="color:#15803d;font-size:12px;display:block;margin-top:4px">GHCR Public</strong>
          <span style="color:#666;font-size:10px">인증 없이 pull</span>
        </div>
        <div style="background:#fff3e0;padding:10px;border-radius:10px;text-align:center;border:1px solid #ffe0b2">
          <div style="font-size:20px">↩️</div>
          <strong style="color:#e65100;font-size:12px;display:block;margin-top:4px">Instant Rollback</strong>
          <span style="color:#666;font-size:10px">3 revision 보관</span>
        </div>
        <div style="background:#f3e5f5;padding:10px;border-radius:10px;text-align:center;border:1px solid #e1bee7">
          <div style="font-size:20px">🖥️</div>
          <strong style="color:#6a1b9a;font-size:12px;display:block;margin-top:4px">ARM64</strong>
          <span style="color:#666;font-size:10px">OCI 아키텍처 맞춤</span>
        </div>
      </div>
        ` }} />
      </div>
    ),
  },
  // ── Slide 11-B: 무중단 배포 검증 결과 (실측) ──
  {
    title: '11-B. 무중단 배포 검증 결과 — 실측 (Zero-Downtime Verification Report)',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px">
        <div style="background:linear-gradient(135deg,#f0fdf4,#dcfce7);border:1px solid #86efac;border-radius:14px;padding:20px">
          <div style="font-size:28px;text-align:center;margin-bottom:8px">✅</div>
          <h3 style="text-align:center;color:#15803d;margin:0 0 12px;font-size:18px">무중단 배포 확인됨</h3>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
            <div style="background:#fff;border-radius:8px;padding:10px;text-align:center;border:1px solid #bbf7d0">
              <div style="font-size:22px;font-weight:800;color:#15803d">228</div>
              <div style="font-size:11px;color:#64748b">총 HTTP 체크</div>
            </div>
            <div style="background:#fff;border-radius:8px;padding:10px;text-align:center;border:1px solid #bbf7d0">
              <div style="font-size:22px;font-weight:800;color:#dc2626">0</div>
              <div style="font-size:11px;color:#64748b">실패 건수</div>
            </div>
            <div style="background:#fff;border-radius:8px;padding:10px;text-align:center;border:1px solid #bbf7d0">
              <div style="font-size:22px;font-weight:800;color:#15803d">100%</div>
              <div style="font-size:11px;color:#64748b">가용성</div>
            </div>
            <div style="background:#fff;border-radius:8px;padding:10px;text-align:center;border:1px solid #bbf7d0">
              <div style="font-size:22px;font-weight:800;color:#0369a1">~0.01s</div>
              <div style="font-size:11px;color:#64748b">평균 응답</div>
            </div>
          </div>
        </div>
        <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:14px;padding:20px">
          <h4 style="color:#334155;margin:0 0 12px;font-size:14px">📋 테스트 조건</h4>
          <table style="width:100%;font-size:12px;border-collapse:collapse">
            <tr style="border-bottom:1px solid #f1f5f9"><td style="padding:5px 0;color:#64748b;width:35%">테스트 일시</td><td style="color:#1e293b"><strong>2026-04-17 20:41~20:43</strong></td></tr>
            <tr style="border-bottom:1px solid #f1f5f9"><td style="padding:5px 0;color:#64748b">모니터링 도구</td><td style="color:#1e293b">외부 curl (2초 간격)</td></tr>
            <tr style="border-bottom:1px solid #f1f5f9"><td style="padding:5px 0;color:#64748b">폴링 라운드</td><td style="color:#1e293b">76라운드 × 3 엔드포인트</td></tr>
            <tr style="border-bottom:1px solid #f1f5f9"><td style="padding:5px 0;color:#64748b">동시 배포 서비스</td><td style="color:#1e293b">backend + frontend + partner-frontend</td></tr>
            <tr style="border-bottom:1px solid #f1f5f9"><td style="padding:5px 0;color:#64748b">배포 소요 시간</td><td style="color:#1e293b">2분 11초 (3 서비스 동시)</td></tr>
            <tr><td style="padding:5px 0;color:#64748b">관찰자 위치</td><td style="color:#1e293b">서버 외부 (실 사용자 관점)</td></tr>
          </table>
        </div>
      </div>

      <h3 style="color:#4f46e5;font-size:15px;margin-bottom:12px">② 모니터링 대상 엔드포인트 전구간 HTTP 200 유지</h3>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:16px">
        <div style="background:#f0f4ff;border:1px solid #c7d2fe;border-radius:10px;padding:14px">
          <div style="font-weight:700;color:#4f46e5;font-size:13px;margin-bottom:8px">🔷 Admin Frontend</div>
          <div style="font-size:11px;color:#64748b;margin-bottom:6px">https://lab.sappt.co.kr</div>
          <div style="display:flex;justify-content:space-between;font-size:12px">
            <span>체크: <strong>76회</strong></span>
            <span>실패: <strong style="color:#dc2626">0</strong></span>
          </div>
          <div style="margin-top:6px;height:6px;background:#e2e8f0;border-radius:3px">
            <div style="height:100%;width:100%;background:#4f46e5;border-radius:3px"></div>
          </div>
          <div style="font-size:10px;color:#64748b;margin-top:4px">가용성 100.0%</div>
        </div>
        <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:14px">
          <div style="font-weight:700;color:#15803d;font-size:13px;margin-bottom:8px">🔶 Backend API</div>
          <div style="font-size:11px;color:#64748b;margin-bottom:6px">https://api.sappt.co.kr/api/storage/health</div>
          <div style="display:flex;justify-content:space-between;font-size:12px">
            <span>체크: <strong>76회</strong></span>
            <span>실패: <strong style="color:#dc2626">0</strong></span>
          </div>
          <div style="margin-top:6px;height:6px;background:#e2e8f0;border-radius:3px">
            <div style="height:100%;width:100%;background:#15803d;border-radius:3px"></div>
          </div>
          <div style="font-size:10px;color:#64748b;margin-top:4px">가용성 100.0%</div>
        </div>
        <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:14px">
          <div style="font-weight:700;color:#b45309;font-size:13px;margin-bottom:8px">🔸 Partner Frontend</div>
          <div style="font-size:11px;color:#64748b;margin-bottom:6px">https://partner.sappt.co.kr</div>
          <div style="display:flex;justify-content:space-between;font-size:12px">
            <span>체크: <strong>76회</strong></span>
            <span>실패: <strong style="color:#dc2626">0</strong></span>
          </div>
          <div style="margin-top:6px;height:6px;background:#e2e8f0;border-radius:3px">
            <div style="height:100%;width:100%;background:#f59e0b;border-radius:3px"></div>
          </div>
          <div style="font-size:10px;color:#64748b;margin-top:4px">가용성 100.0%</div>
        </div>
      </div>

      <h3 style="color:#4f46e5;font-size:15px;margin-bottom:12px">③ 배포 타임라인 (실측 로그)</h3>
      <div style="background:#0f172a;border-radius:12px;padding:18px;font-family:monospace;font-size:12px;line-height:1.8;overflow-x:auto">
        <div style="color:#94a3b8;margin-bottom:8px"># 모니터링 스크립트: 외부 curl 2초 간격 × 3 엔드포인트</div>
        <div><span style="color:#64748b">20:41:22</span> <span style="color:#60a5fa">[START]</span> <span style="color:#e2e8f0">모니터링 시작 — 배포 전 베이스라인 측정</span></div>
        <div><span style="color:#64748b">20:41:22~39</span> <span style="color:#34d399">lab:✓0.01s  api:✓0.01s  partner:✓0.01s</span> <span style="color:#64748b">| OK:27 FAIL:0</span></div>
        <div style="color:#fbbf24;margin:4px 0">━━━━━━━━━━━━━━━━━ 20:41:40  배포 시작 (kubectl patch ×3 동시) ━━━━━━━━━━━━━━━━━</div>
        <div><span style="color:#64748b">20:41:40~51</span> <span style="color:#34d399">lab:✓0.01s  api:✓0.02s  partner:✓0.01s</span> <span style="color:#64748b">| OK:60 FAIL:0  (신 Pod 생성 중)</span></div>
        <div><span style="color:#64748b">20:41:52~02</span> <span style="color:#34d399">lab:✓0.01s  api:✓0.01s  partner:✓0.01s</span> <span style="color:#64748b">| OK:108 FAIL:0 (readinessProbe 통과 중)</span></div>
        <div><span style="color:#64748b">20:43:03~51</span> <span style="color:#34d399">lab:✓0.01s  api:✓0.01s  partner:✓0.01s</span> <span style="color:#64748b">| OK:213 FAIL:0 (구 Pod 종료 중)</span></div>
        <div style="color:#fbbf24;margin:4px 0">━━━━━━━━━━━━━━━━━ 20:43:51  배포 완료 (전체 2분 11초 소요) ━━━━━━━━━━━━━━━━━</div>
        <div><span style="color:#64748b">20:43:52~58</span> <span style="color:#34d399">lab:✓0.01s  api:✓0.01s  partner:✓0.01s</span> <span style="color:#64748b">| OK:228 FAIL:0 (배포 후 안정 확인)</span></div>
        <div style="margin-top:8px;padding-top:8px;border-top:1px solid #1e293b">
          <span style="color:#34d399;font-weight:bold">최종: HTTP 체크 228회 / 실패 0회 / 가용성 100.0%</span>
          <span style="color:#64748b;margin-left:16px">평균 응답 ~0.01s / 최대 0.03s</span>
        </div>
      </div>

      <h3 style="color:#4f46e5;font-size:15px;margin:16px 0 10px">④ Rolling Update 중 Pod 생존 타임라인</h3>
      <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:16px">
        <div style="display:grid;grid-template-columns:100px 1fr;gap:8px;font-size:12px">
          <div style="color:#64748b;font-weight:600;padding:4px 0">서비스</div>
          <div style="color:#64748b;font-weight:600;padding:4px 0;display:grid;grid-template-columns:repeat(14,1fr);gap:2px">
            <span style="font-size:10px">:40</span><span style="font-size:10px">:45</span><span style="font-size:10px">:50</span><span style="font-size:10px">:55</span><span style="font-size:10px">:00</span><span style="font-size:10px">:05</span><span style="font-size:10px">:10</span><span style="font-size:10px">:15</span><span style="font-size:10px">:20</span><span style="font-size:10px">:25</span><span style="font-size:10px">:30</span><span style="font-size:10px">:35</span><span style="font-size:10px">:40</span><span style="font-size:10px">:51</span>
          </div>

          <div style="color:#4f46e5;font-weight:600;align-self:center">frontend</div>
          <div style="display:flex;gap:3px;align-items:center">
            <div style="height:18px;background:#4f46e5;border-radius:3px;flex:7;opacity:0.9" title="구버전 Pod 운영"></div>
            <div style="height:18px;background:#a5b4fc;border-radius:3px;flex:4;border:1px dashed #4f46e5" title="신/구 동시 (surge)"></div>
            <div style="height:18px;background:#4f46e5;border-radius:3px;flex:3;opacity:0.5" title="신버전 안정화"></div>
            <div style="font-size:10px;color:#4f46e5;margin-left:4px;white-space:nowrap">완료 ~1m50s</div>
          </div>

          <div style="color:#15803d;font-weight:600;align-self:center">backend</div>
          <div style="display:flex;gap:3px;align-items:center">
            <div style="height:18px;background:#15803d;border-radius:3px;flex:5;opacity:0.9" title="구버전 Pod 운영"></div>
            <div style="height:18px;background:#86efac;border-radius:3px;flex:5;border:1px dashed #15803d" title="신/구 동시 (surge)"></div>
            <div style="height:18px;background:#15803d;border-radius:3px;flex:4;opacity:0.5" title="신버전 안정화"></div>
            <div style="font-size:10px;color:#15803d;margin-left:4px;white-space:nowrap">완료 ~2m11s</div>
          </div>

          <div style="color:#b45309;font-weight:600;align-self:center">partner</div>
          <div style="display:flex;gap:3px;align-items:center">
            <div style="height:18px;background:#f59e0b;border-radius:3px;flex:5;opacity:0.9" title="구버전 Pod 운영"></div>
            <div style="height:18px;background:#fde68a;border-radius:3px;flex:5;border:1px dashed #f59e0b" title="신/구 동시 (surge)"></div>
            <div style="height:18px;background:#f59e0b;border-radius:3px;flex:4;opacity:0.5" title="신버전 안정화"></div>
            <div style="font-size:10px;color:#b45309;margin-left:4px;white-space:nowrap">완료 ~2m11s</div>
          </div>
        </div>
        <div style="display:flex;gap:16px;margin-top:12px;font-size:11px;color:#64748b">
          <span><span style="display:inline-block;width:12px;height:10px;background:#4f46e5;border-radius:2px;margin-right:4px;opacity:0.9"></span>구버전 운영</span>
          <span><span style="display:inline-block;width:12px;height:10px;background:#a5b4fc;border-radius:2px;border:1px dashed #4f46e5;margin-right:4px"></span>신/구 동시 (Surge 구간)</span>
          <span><span style="display:inline-block;width:12px;height:10px;background:#4f46e5;border-radius:2px;opacity:0.5;margin-right:4px"></span>신버전 안정화</span>
        </div>
      </div>
    `,
  },
  // ── Slide 12: 문서 양식 매핑 ──
  {
    title: '12. 문서 양식 매핑 (Form → HWPX)',
    component: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ArchFlow
          height={520}
          groups={[
            { id: 'g_form', label: '📝 입력 폼 (React)', x: 0, y: 0, w: 300, h: 480, tone: 'blue' },
            { id: 'g_doc', label: '📄 HWPX 문서', x: 420, y: 0, w: 320, h: 480, tone: 'orange' },
          ]}
          nodes={[
            { id: 'f1', group: 'g_form', x: 16, y: 40, label: '기본 정보', sub: '코드·항목명·기관·년도·장소', tone: 'blue' },
            { id: 'f2', group: 'g_form', x: 16, y: 118, label: '시험목적', sub: 'TextArea', tone: 'blue' },
            { id: 'f3', group: 'g_form', x: 16, y: 196, label: '시험방법', sub: '대상병해·작물·품종', tone: 'blue' },
            { id: 'f4', group: 'g_form', x: 16, y: 274, label: '처리내용', sub: 'Form.List × N 약제', tone: 'blue' },
            { id: 'f5', group: 'g_form', x: 16, y: 352, label: '시험구 배치', sub: 'Form.List × N 구', tone: 'blue' },
            { id: 'f6', group: 'g_form', x: 16, y: 425, label: '조사 항목', sub: 'TextArea', tone: 'blue' },
            { id: 'd1', group: 'g_doc', x: 16, y: 40, label: '헤더 영역', sub: 'SR24F-AEVC02 · 항목명', tone: 'orange' },
            { id: 'd2', group: 'g_doc', x: 16, y: 118, label: '○ 메타 정보', sub: '기관/년도/담당자/장소', tone: 'orange' },
            { id: 'd3', group: 'g_doc', x: 16, y: 196, label: '1. 시험목적', sub: '본문 단락', tone: 'orange' },
            { id: 'd4', group: 'g_doc', x: 16, y: 274, label: '2. 시험방법', sub: '가.나.다.라.마.', tone: 'orange' },
            { id: 'd5', group: 'g_doc', x: 16, y: 352, label: '처리내용 테이블', sub: '8열 × N행', tone: 'orange' },
            { id: 'd6', group: 'g_doc', x: 16, y: 425, label: '시험구 배치 테이블', sub: '7열 × N행', tone: 'orange' },
          ]}
          edges={[
            { source: 'f1', target: 'd1', tone: 'blue', animated: true },
            { source: 'f1', target: 'd2', tone: 'blue' },
            { source: 'f2', target: 'd3', tone: 'blue' },
            { source: 'f3', target: 'd4', tone: 'blue' },
            { source: 'f4', target: 'd5', tone: 'blue', animated: true },
            { source: 'f5', target: 'd6', tone: 'blue', animated: true },
          ]}
        />
        <div dangerouslySetInnerHTML={{ __html: `
      <div style="margin-top:20px;padding:16px;background:#f8f9ff;border-radius:12px;border:1px solid #e0e4ff">
        <h4 style="margin:0 0 8px;color:#667eea">처리내용 테이블 구조</h4>
        <table style="width:100%;border-collapse:collapse;font-size:12px;text-align:center">
          <tr style="background:#667eea;color:#fff">
            <th style="padding:6px;border:1px solid #5568d4">시험약제</th>
            <th style="padding:6px;border:1px solid #5568d4">주성분함량(%)</th>
            <th style="padding:6px;border:1px solid #5568d4">희석배수</th>
            <th style="padding:6px;border:1px solid #5568d4">처리시기</th>
            <th style="padding:6px;border:1px solid #5568d4">기준량</th>
            <th style="padding:6px;border:1px solid #5568d4">배량</th>
            <th style="padding:6px;border:1px solid #5568d4">시험년차</th>
            <th style="padding:6px;border:1px solid #5568d4">의뢰회사</th>
          </tr>
          <tr style="background:#f8f9ff">
            <td style="padding:6px;border:1px solid #e0e4ff">이미녹타딘트리스알베실레이트</td>
            <td style="padding:6px;border:1px solid #e0e4ff">30</td>
            <td style="padding:6px;border:1px solid #e0e4ff">500배</td>
            <td style="padding:6px;border:1px solid #e0e4ff">월동전후 수관처리</td>
            <td style="padding:6px;border:1px solid #e0e4ff">500배</td>
            <td style="padding:6px;border:1px solid #e0e4ff">250배</td>
            <td style="padding:6px;border:1px solid #e0e4ff">2</td>
            <td style="padding:6px;border:1px solid #e0e4ff">팜한농</td>
          </tr>
        </table>
      </div>
` }} />
      </div>
    ),
  },
  // ── Slide 13: 기술 스택 요약 ──
  {
    title: '13. 기술 스택 요약 (Tech Stack Summary)',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div>
          <div style="background:linear-gradient(135deg,#43e97b20,#38f9d720);padding:20px;border-radius:16px;border:1px solid #43e97b33;margin-bottom:16px">
            <h3 style="color:#2e7d32;margin:0 0 14px">⚙️ Backend</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Python 3.11</strong></div>
                <div style="color:#666;margin-top:3px">최신 안정 런타임 · 비동기(async/await) 완전 지원</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>FastAPI 0.115</strong></div>
                <div style="color:#666;margin-top:3px">타입 힌트 기반 자동 라우팅 · Swagger/ReDoc 문서 자동 생성</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Uvicorn (ASGI)</strong></div>
                <div style="color:#666;margin-top:3px">비동기 I/O 웹서버 · SSE 스트리밍 · 고성능 요청 처리</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>SQLAlchemy 2.0</strong></div>
                <div style="color:#666;margin-top:3px">Python ORM · 30개 테이블 모델 관리 · 비동기 쿼리 지원</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Pydantic 2.9</strong></div>
                <div style="color:#666;margin-top:3px">요청/응답 스키마 자동 검증 · 직렬화 · FastAPI 통합</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>python-jose + passlib</strong></div>
                <div style="color:#666;margin-top:3px">JWT(HS256) 토큰 발급·검증 · bcrypt 비밀번호 단방향 해싱</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>python-hwpx 2.8</strong></div>
                <div style="color:#666;margin-top:3px">한글 HWPX 문서 생성 · ZIP+XML 구조 파싱 · 시험계획서 자동화</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>openpyxl 3.1</strong></div>
                <div style="color:#666;margin-top:3px">Excel 파일 읽기/쓰기 · 시험일정총괄표 업로드·다운로드 처리</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Claude API (Anthropic)</strong></div>
                <div style="color:#666;margin-top:3px">AI 챗봇 · Sonnet 4 모델 · SSE 스트리밍 응답</div>
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fa709a20,#fee14020);padding:20px;border-radius:16px;border:1px solid #fa709a33">
            <h3 style="color:#c62828;margin:0 0 14px">🗄️ Database &amp; Storage</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>PostgreSQL 17</strong></div>
                <div style="color:#666;margin-top:3px">메인 RDBMS · 30개 테이블 · ACID 트랜잭션 · Docker 컨테이너 운영</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>OCI Object Storage</strong></div>
                <div style="color:#666;margin-top:3px">관리형 오브젝트 스토리지(ap-chuncheon-1) · 첨부파일·문서·이미지 저장 · 버전관리 활성화 · PAR URL 발급</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Redis</strong></div>
                <div style="color:#666;margin-top:3px">인메모리 캐시 · 세션 관리 · k3s 클러스터 내 배포</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>OpenSearch</strong></div>
                <div style="color:#666;margin-top:3px">전문(Full-text) 검색 엔진 · 시험계획서·일정 인덱싱 · 인기 검색어 집계</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Docker Volume</strong></div>
                <div style="color:#666;margin-top:3px">컨테이너 재시작 후에도 데이터 영구 보존 · 호스트 바인드 마운트</div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div style="background:linear-gradient(135deg,#4facfe20,#00f2fe20);padding:20px;border-radius:16px;border:1px solid #4facfe33;margin-bottom:16px">
            <h3 style="color:#1565c0;margin:0 0 14px">🖥️ Frontend</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>React 19 + TypeScript</strong></div>
                <div style="color:#666;margin-top:3px">최신 Concurrent 모드 · 컴포넌트 기반 SPA · 타입 안전 개발</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Ant Design 6</strong></div>
                <div style="color:#666;margin-top:3px">엔터프라이즈 UI 컴포넌트 라이브러리 · 동적 테마(관리자 Indigo / 파트너 Amber)</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>ECharts 6</strong></div>
                <div style="color:#666;margin-top:3px">대시보드 데이터 시각화 · 바·파이·라인·게이지 등 다양한 차트</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>React Router 7</strong></div>
                <div style="color:#666;margin-top:3px">SPA 클라이언트 라우팅 · 탭 기반 레이아웃 · 권한별 가드</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Axios</strong></div>
                <div style="color:#666;margin-top:3px">HTTP 클라이언트 · 인터셉터로 JWT 자동 첨부 · 오류 핸들링</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Mermaid.js</strong></div>
                <div style="color:#666;margin-top:3px">마크다운 기반 다이어그램 렌더링 · 시퀀스·플로우차트 · 시스템 가이드</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Pretendard Variable 폰트</strong></div>
                <div style="color:#666;margin-top:3px">한글 최적화 가변 폰트 · 100~900 웨이트 단일 파일 · CDN 적용</div>
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#764ba220,#667eea20);padding:20px;border-radius:16px;border:1px solid #764ba233">
            <h3 style="color:#4527a0;margin:0 0 14px">🏗️ Infrastructure &amp; DevOps</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>k3s (Kubernetes)</strong></div>
                <div style="color:#666;margin-top:3px">경량 Kubernetes 클러스터 · Rolling Update 무중단 배포 · replicas:2</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Docker + Docker Compose</strong></div>
                <div style="color:#666;margin-top:3px">인프라 컨테이너화 · postgres·로깅/모니터링 스택 통합 관리</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>GitHub Actions</strong></div>
                <div style="color:#666;margin-top:3px">CI/CD 파이프라인 · ARM64 멀티아키 빌드 → GHCR 자동 Push</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Nginx Proxy Manager</strong></div>
                <div style="color:#666;margin-top:3px">리버스 프록시 · 서브도메인 라우팅 · GUI 기반 SSL 인증서 관리</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Let's Encrypt SSL</strong></div>
                <div style="color:#666;margin-top:3px">무료 TLS 인증서 · 90일 자동 갱신 · HTTPS 전 도메인 적용</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Grafana + Loki + Promtail</strong></div>
                <div style="color:#666;margin-top:3px">컨테이너 로그 수집·집계·시각화 · 커스텀 대시보드 제공</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Prometheus + cAdvisor</strong></div>
                <div style="color:#666;margin-top:3px">서버·컨테이너 메트릭 수집 · CPU·메모리·네트워크 실시간 모니터링</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>오프사이트 백업 (OCI + Backblaze B2)</strong></div>
                <div style="color:#666;margin-top:3px">DB·소스코드 3시간 간격 자동 백업 · OCI/B2 이중 원격 복제 · Discord 알림</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide 13-T: TAPO API — 데이터 수집 아키텍처 (ReactFlow) ──
  {
    title: '🌡️ TAPO 온습도 수집 아키텍처 — tapo-api.sappt.co.kr',
    component: <TapoApiFlow />,
  },
  // ── Slide 13-T-B: TAPO API 엔드포인트 가이드 (mermaid) ──
  {
    title: '🌡️ TAPO API 엔드포인트 & 데이터 흐름 가이드',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:20px">
        <div>
          <div style="background:linear-gradient(135deg,#ddd6fe20,#bae6fd20);padding:18px;border-radius:14px;border:1px solid #818cf833">
            <h3 style="color:#3730a3;margin:0 0 12px;font-size:14px">🔌 API 엔드포인트 — tapo-api.sappt.co.kr</h3>
            <div style="display:flex;flex-direction:column;gap:6px;font-size:12px">
              <div style="background:#fff;padding:8px 12px;border-radius:8px">
                <span style="background:#dcfce7;color:#166534;padding:1px 6px;border-radius:4px;font-size:10.5px;font-weight:700">GET</span>
                &nbsp;<strong>/health</strong>
                <div style="color:#666;margin-top:2px">DB 연결 포함 헬스체크 (NPM/Docker 프로브)</div>
              </div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px">
                <span style="background:#fef9c3;color:#854d0e;padding:1px 6px;border-radius:4px;font-size:10.5px;font-weight:700">POST</span>
                &nbsp;<strong>/ingest</strong>
                <div style="color:#666;margin-top:2px">NAS → 서버 Push · <code style="background:#f8fafc;font-size:10.5px">Bearer INGEST_TOKEN</code> 필수</div>
              </div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px">
                <span style="background:#dcfce7;color:#166534;padding:1px 6px;border-radius:4px;font-size:10.5px;font-weight:700">GET</span>
                &nbsp;<strong>/api/current</strong>
                <div style="color:#666;margin-top:2px">전체 실 최신값 (최근 24h)</div>
              </div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px">
                <span style="background:#dcfce7;color:#166534;padding:1px 6px;border-radius:4px;font-size:10.5px;font-weight:700">GET</span>
                &nbsp;<strong>/api/current/{'{room_id}'}</strong>
                <div style="color:#666;margin-top:2px">특정 실 최신값 (최근 1h) · doc / lab / chm</div>
              </div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px">
                <span style="background:#dcfce7;color:#166534;padding:1px 6px;border-radius:4px;font-size:10.5px;font-weight:700">GET</span>
                &nbsp;<strong>/api/readings</strong>
                <div style="color:#666;margin-top:2px"><code style="background:#f8fafc;font-size:10.5px">?room_id=doc&amp;since=7d&amp;bucket=1h&amp;limit=2000</code></div>
                <div style="color:#94a3b8;margin-top:2px;font-size:10.5px">since: 24h · 7d · 1w · 1m &nbsp;|&nbsp; bucket: raw · 5min · 1h · 1d</div>
              </div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px">
                <span style="background:#dcfce7;color:#166534;padding:1px 6px;border-radius:4px;font-size:10.5px;font-weight:700">GET</span>
                &nbsp;<strong>/api/alerts</strong>
                <div style="color:#666;margin-top:2px">미해결 경보 목록 (온도·습도 임계 초과)</div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div style="background:linear-gradient(135deg,#0ea5e920,#38bdf820);padding:18px;border-radius:14px;border:1px solid #0ea5e933;margin-bottom:12px">
            <h3 style="color:#0369a1;margin:0 0 12px;font-size:14px">📡 3개 실 기준</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12px">
              <div style="background:#fff;padding:8px 12px;border-radius:8px;border-left:4px solid #60a5fa">
                <strong>문서보관실 <code style="font-size:10.5px;background:#eff6ff;padding:1px 5px;border-radius:3px">doc</code></strong>
                <div style="color:#666;margin-top:2px">18–22°C / 40–55% RH · 경보 ≥24°C</div>
              </div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;border-left:4px solid #34d399">
                <strong>사육실 <code style="font-size:10.5px;background:#f0fdf4;padding:1px 5px;border-radius:3px">lab</code></strong>
                <div style="color:#666;margin-top:2px">20–26°C / 30–70% RH · 경보 ≥26°C</div>
              </div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;border-left:4px solid #a78bfa">
                <strong>시약실 <code style="font-size:10.5px;background:#faf5ff;padding:1px 5px;border-radius:3px">chm</code></strong>
                <div style="color:#666;margin-top:2px">15–25°C / 40–60% RH · 경보 ≥28°C</div>
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fde68a20,#fbbf2420);padding:14px 18px;border-radius:14px;border:1px solid #fbbf2433;font-size:12px">
            <strong style="color:#92400e">🔑 인증</strong>
            <div style="margin-top:6px;color:#444;line-height:1.8">
              Ingest: <code style="background:#fef9c3;padding:1px 6px;border-radius:4px">Bearer INGEST_TOKEN</code><br/>
              조회 API (/api/*): 인증 불필요 (공개)<br/>
              Admin (/admin/*): <code style="background:#fee2e2;padding:1px 6px;border-radius:4px">Bearer ADMIN_TOKEN</code>
            </div>
          </div>
        </div>
      </div>
      <div class="mermaid">
sequenceDiagram
    participant T as 📡 T315 센서×3
    participant NAS as 💾 NAS (collector/uploader)
    participant API as ⚡ tapo-api.sappt.co.kr
    participant DB as 🗄️ TimescaleDB
    participant CLIENT as 🖥️ 브라우저/Grafana

    loop 2분마다
        T->>NAS: RF 868MHz → H200 Hub → LAN
        NAS->>NAS: SQLite WAL 큐에 적재
    end

    loop 30초마다
        NAS->>API: POST /ingest (Bearer INGEST_TOKEN)
        API->>DB: INSERT sensors.readings
        DB-->>API: ok
        API-->>NAS: {"ok":true, "inserted":3}
    end

    CLIENT->>API: GET /api/current
    API->>DB: SELECT DISTINCT ON (room_id) latest
    DB-->>API: [{room_id, temp_c, humidity, ...}]
    API-->>CLIENT: JSON 응답

    CLIENT->>API: GET /api/readings?since=7d&bucket=1h
    API->>DB: time_bucket('1 hour', ts) GROUP BY
    DB-->>API: 집계 시계열 데이터
    API-->>CLIENT: ECharts 렌더링
      </div>
    `,
  },
  // ── Slide 14: TAPO 온습도 모니터링 — 개요 & 아키텍처 ──
  {
    title: '14. TAPO 온습도 모니터링 — 시스템 개요 & 아키텍처',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div>
          <div style="background:linear-gradient(135deg,#60a5fa20,#a78bfa20);padding:20px;border-radius:16px;border:1px solid #60a5fa33;margin-bottom:16px">
            <h3 style="color:#1e3a8a;margin:0 0 14px">📡 모니터링 대상 — 3개 지점</h3>
            <div style="display:flex;flex-direction:column;gap:10px;font-size:12.5px">
              <div style="background:#fff;padding:10px 14px;border-radius:8px;border-left:4px solid #60a5fa">
                <div><strong>📁 문서보관실 · T315-DOC</strong></div>
                <div style="color:#666;margin-top:3px">국가기록원 보존환경 — 온도 18~22°C / 습도 40~55% / 경보 ≥24°C</div>
              </div>
              <div style="background:#fff;padding:10px 14px;border-radius:8px;border-left:4px solid #34d399">
                <div><strong>🧬 사육실 · T315-LAB</strong></div>
                <div style="color:#666;margin-top:3px">GLP/AAALAC 가이드 — 온도 20~26°C / 습도 30~70% / 경보 ≥26°C</div>
              </div>
              <div style="background:#fff;padding:10px 14px;border-radius:8px;border-left:4px solid #a78bfa">
                <div><strong>🧪 시약실 · T315-CHM</strong></div>
                <div style="color:#666;margin-top:3px">KOSHA/MSDS 요건 — 온도 15~25°C / 습도 40~60% / 경보 ≥28°C</div>
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fde68a20,#fbbf2420);padding:20px;border-radius:16px;border:1px solid #fbbf2433">
            <h3 style="color:#92400e;margin:0 0 14px">🎯 핵심 지표</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12.5px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>폴링 주기</strong> &mdash; 2분</div>
                <div style="color:#666;margin-top:3px">3센서 × 720회/일 = 2,160행/일 → 연 ~789K행</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>5년 보존 + TimescaleDB 압축</strong></div>
                <div style="color:#666;margin-top:3px">전체 ~95MB(압축) · 안전마진 150MB · 7일 후 자동 압축</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>무중단 배포</strong></div>
                <div style="color:#666;margin-top:3px">기존 sappt 13개 컨테이너 영향 0건 · 별도 컨테이너로 격리 추가</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>월 운영비</strong></div>
                <div style="color:#666;margin-top:3px">₩0 (OCI Always Free + 기존 인프라 재활용)</div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div style="background:linear-gradient(135deg,#ddd6fe20,#bae6fd20);padding:20px;border-radius:16px;border:1px solid #818cf833;margin-bottom:16px">
            <h3 style="color:#3730a3;margin:0 0 14px">🏗️ End-to-End 아키텍처</h3>
            <div style="background:#fff;padding:14px;border-radius:10px;font-size:11.5px;line-height:1.9;color:#444;font-family:Consolas,monospace">
              <div><span style="color:#be185d">📡 T315 ×3</span> &mdash;RF(868MHz)&rarr; <span style="color:#b45309">🌐 H200 Hub</span></div>
              <div style="margin-left:18px">&darr; LAN (192.168.10.0/24)</div>
              <div><span style="color:#1d4ed8">💾 NAS Container Manager</span></div>
              <div style="margin-left:18px">├─ tapo-collector (2분 폴링)</div>
              <div style="margin-left:18px">├─ tapo-uploader (30초 batch)</div>
              <div style="margin-left:18px">└─ SQLite WAL 큐 (14일 버퍼)</div>
              <div style="margin-left:18px">&darr; HTTPS Bearer Push</div>
              <div><span style="color:#15803d">☁️ OCI ap-chuncheon-1 (sappt)</span></div>
              <div style="margin-left:18px">├─ NPM (Let's Encrypt)</div>
              <div style="margin-left:18px">├─ FastAPI (Ingest API)</div>
              <div style="margin-left:18px">└─ TimescaleDB (Hypertable + 5분 집계)</div>
              <div style="margin-left:18px">&darr;</div>
              <div><span style="color:#0891b2">📊 https://tapo-api.sappt.co.kr/</span></div>
              <div style="margin-left:18px">└─ ECharts 실시간 + Grafana 통합</div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fecaca20,#fed7aa20);padding:20px;border-radius:16px;border:1px solid #f8717133">
            <h3 style="color:#9f1239;margin:0 0 14px">🛡️ 오프라인 14일 무손실 버퍼</h3>
            <div style="font-size:12.5px;color:#444;line-height:1.8">
              인터넷 단절 시 NAS의 <code style="background:#fff;padding:2px 6px;border-radius:4px">queue.db</code>(SQLite WAL)에
              누적되어 <strong>14일치까지 무손실 보존</strong>됩니다. 복구 시 uploader가 누적분을
              자동 batch 백필 → 데이터 결손 0건.
              <br/><br/>
              <strong>병렬 분리 설계</strong>: collector / uploader 프로세스 분리로 한쪽 장애가 다른 쪽에 영향 X.
              NAS 재부팅 시 <code style="background:#fff;padding:2px 6px;border-radius:4px">restart: unless-stopped</code>로 자동 복구.
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide 14-A: 운영 인프라 통합 (sappt 무중단 추가) ──
  {
    title: '14-A. 운영 인프라 통합 — sappt 무중단 추가',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div>
          <div style="background:linear-gradient(135deg,#43e97b20,#38f9d720);padding:20px;border-radius:16px;border:1px solid #43e97b33;margin-bottom:16px">
            <h3 style="color:#2e7d32;margin:0 0 14px">✅ 신규 추가 컨테이너 (격리)</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12.5px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>sappt-tapo-postgres</strong></div>
                <div style="color:#666;margin-top:3px">timescale/timescaledb-ha:pg17 · 127.0.0.1:5433 · 5GB 한도 · sappt_tapo_pgdata 볼륨</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>sappt-tapo-api</strong></div>
                <div style="color:#666;margin-top:3px">FastAPI 0.115 + Python 3.12 · 512MB 한도 · NPM 백엔드 연결</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>tapo_net 격리 네트워크</strong></div>
                <div style="color:#666;margin-top:3px">전용 docker network · sappt_sappt-net에도 연결(Grafana 도달)</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>tapo_monitoring DB</strong></div>
                <div style="color:#666;margin-top:3px">tapo_app / tapo_readonly role · PUBLIC 권한 회수 · CONNECTION LIMIT 20</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>리소스 사용</strong></div>
                <div style="color:#666;margin-top:3px">PG 43MB + API 114MB = 157MB / 24GB (0.65%) · 디스크 ~10MB</div>
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fef3c720,#fde68a20);padding:20px;border-radius:16px;border:1px solid #fbbf2433">
            <h3 style="color:#92400e;margin:0 0 14px">♻️ 기존 sappt 인프라 재활용</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12.5px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Nginx Proxy Manager</strong></div>
                <div style="color:#666;margin-top:3px">호스트 1개 추가로 SSL + 라우팅 자동 (Nginx 직접 운영 X)</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Promtail → Loki</strong></div>
                <div style="color:#666;margin-top:3px">Docker socket 자동 발견 · sappt-tapo-* 컨테이너 로그 즉시 수집</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Grafana 11 (provisioning)</strong></div>
                <div style="color:#666;margin-top:3px">PG datasource + 자동 import 대시보드 (3실 비교 + 로그 통합)</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>OAuth2 Proxy (가능 시)</strong></div>
                <div style="color:#666;margin-top:3px">기존 SSO와 연동 가능 (대시보드 접근 통제)</div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div style="background:linear-gradient(135deg,#4facfe20,#00f2fe20);padding:20px;border-radius:16px;border:1px solid #4facfe33;margin-bottom:16px">
            <h3 style="color:#1565c0;margin:0 0 14px">🌐 운영 엔드포인트</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>대시보드 (브라우저)</strong></div>
                <div style="color:#0891b2;margin-top:3px;font-family:Consolas,monospace">https://tapo-api.sappt.co.kr/</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>POST /ingest</strong> (Bearer 인증)</div>
                <div style="color:#666;margin-top:3px;font-family:Consolas,monospace">엣지 수집기 → 측정값 수신</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>GET /api/current</strong></div>
                <div style="color:#666;margin-top:3px;font-family:Consolas,monospace">실별 최신값 (room_id=doc/lab/chm)</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>GET /api/readings</strong></div>
                <div style="color:#666;margin-top:3px;font-family:Consolas,monospace">시계열 (bucket=raw/5min/1h/1d, since=24h/7d…)</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>GET /api/alerts</strong></div>
                <div style="color:#666;margin-top:3px;font-family:Consolas,monospace">미해결 알람 (severity 컬러 매핑)</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>GET /health</strong></div>
                <div style="color:#666;margin-top:3px;font-family:Consolas,monospace">헬스체크 (NPM/모니터링용)</div>
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fce7f320,#fbcfe820);padding:20px;border-radius:16px;border:1px solid #ec489933">
            <h3 style="color:#9d174d;margin:0 0 14px">🔒 보안 강화 (운영 적용)</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12.5px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>OpenAPI 비활성화</strong></div>
                <div style="color:#666;margin-top:3px">/docs · /redoc · /openapi.json 모두 404 (스키마 노출 차단)</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>NPM Access List</strong></div>
                <div style="color:#666;margin-top:3px">IP 화이트리스트 + Basic Auth 이중 (/ingest는 Bearer만)</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Bearer Token (64자)</strong></div>
                <div style="color:#666;margin-top:3px">.env(chmod 600) · 잘못된 토큰 401 즉시 거부</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>localhost-only DB 포트</strong></div>
                <div style="color:#666;margin-top:3px">127.0.0.1:5433 (외부 노출 차단 · NPM/내부망만)</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide 14-B: 백업 · 관측 · 자동화 ──
  {
    title: '14-B. 백업 · 관측 · 운영 자동화',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div>
          <div style="background:linear-gradient(135deg,#a7f3d020,#6ee7b720);padding:20px;border-radius:16px;border:1px solid #34d39933;margin-bottom:16px">
            <h3 style="color:#065f46;margin:0 0 14px">💾 백업 자동화 (현행)</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12.5px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>3시간 간격 cron · <code style="font-size:11px;background:#f0fdf4;padding:1px 5px;border-radius:3px">30 */3 * * * UTC</code></strong></div>
                <div style="color:#666;margin-top:3px">KST 기준 9:30 / 12:30 / 15:30 / 18:30 / 21:30 / 0:30 / 3:30 / 6:30 — 하루 8회</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>일별 14일 보관</strong></div>
                <div style="color:#666;margin-top:3px">pg_dump | gzip + <code style="font-size:11px">gzip -t</code> 무결성 검증 → <code style="font-size:11px">/opt/tapo/backups/tapo_YYYYMMDD_HHMMSS.sql.gz</code></div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>주간 스냅샷 8주 보관</strong></div>
                <div style="color:#666;margin-top:3px">일요일 UTC 0시대 <strong>첫 회(0:30)만</strong> weekly/ 복사 — 주당 정확히 1개</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>이중 클라우드 업로드</strong></div>
                <div style="color:#666;margin-top:3px">
                  ☁️ OCI Object Storage <code style="font-size:11px">sappt-backup/tapo/daily/YYYY/MM/</code><br/>
                  ☁️ Backblaze B2 <code style="font-size:11px">sappt-backup/tapo/daily/YYYY/MM/</code> (미국 서부)
                </div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>TimescaleDB 5년 보존</strong></div>
                <div style="color:#666;margin-top:3px">retention policy 5y · 압축 정책 7d · 1일 청크 단위</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>5분 연속집계</strong></div>
                <div style="color:#666;margin-top:3px">sensors.readings_5m view · 대시보드 쿼리 가속</div>
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fed7aa20,#fdba7420);padding:20px;border-radius:16px;border:1px solid #fb923c33">
            <h3 style="color:#9a3412;margin:0 0 14px">🚨 알람 정책 (실별 차등)</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>📁 문서</strong> &mdash; ≥24°C 경고 / 35%↓·60%↑ 경고 / 28°C·70% 3회연속 Critical</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>🧬 사육</strong> &mdash; ≥26°C 즉시통보 / ≥28°C Critical+SMS</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>🧪 시약</strong> &mdash; ≥28°C 경고 / 65%↑ 경고 / ≥32°C Critical</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>다중 채널 디스패치</strong></div>
                <div style="color:#666;margin-top:3px">Slack / Email / SMS(Solapi) / KakaoTalk 알림톡 · 야간모드 + 쿨다운 30분</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>에스컬레이션</strong> &mdash; 1차(즉시) → 2차(15분) → 3차(1시간 당직)</div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div style="background:linear-gradient(135deg,#fcd34d20,#fde68a20);padding:20px;border-radius:16px;border:1px solid #fbbf2433;margin-bottom:16px">
            <h3 style="color:#78350f;margin:0 0 14px">📊 Grafana 통합 대시보드</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12.5px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>TAPO PostgreSQL Datasource</strong></div>
                <div style="color:#666;margin-top:3px">provisioning yaml로 자동 등록 · tapo_readonly 권한 격리</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>대시보드 7 패널 자동 import</strong></div>
                <div style="color:#666;margin-top:3px">3실 stat · 24h 온/습도 시계열 · 알람 테이블 · TAPO 로그(Loki)</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Loki 자동 수집</strong></div>
                <div style="color:#666;margin-top:3px">Promtail docker_sd → container_name=~&quot;sappt-tapo-.*&quot;</div>
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#dbeafe20,#bfdbfe20);padding:20px;border-radius:16px;border:1px solid #60a5fa33">
            <h3 style="color:#1e40af;margin:0 0 14px">🖥️ 자체 ECharts 대시보드 (실시간)</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12.5px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>4 패널 풀스크린</strong></div>
                <div style="color:#666;margin-top:3px">24h 추이 · 실별 게이지(습도 메인) · 히트맵 · 안전구간 산점도</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>실시간 갱신 주기</strong></div>
                <div style="color:#666;margin-top:3px">현재값 30초 · 시계열/산점도 5분 · 헤더 pill 즉시</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>다크 모드 + 실별 색상</strong></div>
                <div style="color:#666;margin-top:3px">문서(파랑) · 사육(녹색) · 시약(보라) 일관 코드</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>FastAPI StaticFiles 통합</strong></div>
                <div style="color:#666;margin-top:3px">/ → 대시보드 · /api/* → REST · 단일 도메인 (CORS 무관)</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide 14-C: 운영 데이터 & 5년 로드맵 ──
  {
    title: '14-C. 운영 데이터 산정 & NAS Edge 패키지',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div>
          <div style="background:linear-gradient(135deg,#fae8ff20,#f5d0fe20);padding:20px;border-radius:16px;border:1px solid #c084fc33;margin-bottom:16px">
            <h3 style="color:#6b21a8;margin:0 0 14px">📈 5년 보존 용량 산정</h3>
            <div style="background:#fff;padding:12px;border-radius:8px;font-size:12px;font-family:Consolas,monospace;line-height:1.8;color:#444">
              <div>3센서 × 2분 = 2,160 행/일</div>
              <div>2,160 × 365 = ~789K 행/년</div>
              <div>2,160 × 1,826 = ~3.94M 행 (5년)</div>
              <div style="margin-top:6px;border-top:1px solid #eee;padding-top:6px">
                행 크기: ~120 B (tz + text×4 + real×2 + ...)<br/>
                연 raw + 인덱스: ~160 MB/년
              </div>
              <div style="margin-top:6px;border-top:1px solid #eee;padding-top:6px;color:#15803d">
                <strong>TimescaleDB 압축 (10~20× ↓):</strong><br/>
                연 ~12 MB · 5년 ~95 MB<br/>
                안전마진 30% 포함 = 150 MB<br/>
                <span style="color:#999">→ OCI 200GB 대비 0.075%</span>
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#cffafe20,#a5f3fc20);padding:20px;border-radius:16px;border:1px solid #22d3ee33">
            <h3 style="color:#0e7490;margin:0 0 14px">🛠️ 기술 스택 (TAPO 추가분)</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12.5px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>TimescaleDB 2.27 on PostgreSQL 17</strong></div>
                <div style="color:#666;margin-top:3px">시계열 최적화 · hypertable + 압축 + 연속집계</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>FastAPI 0.115 + asyncpg</strong></div>
                <div style="color:#666;margin-top:3px">비동기 Pool · Pydantic 검증 · CORS 미들웨어</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>tapo (Rust 기반 Python lib)</strong></div>
                <div style="color:#666;margin-top:3px">KLAP v2 인증 · H200/H100 Hub 자식 디바이스 조회</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Apache ECharts 5.5</strong></div>
                <div style="color:#666;margin-top:3px">실시간 차트 + 게이지 + 히트맵 + 산점도</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>Tailscale (선택)</strong></div>
                <div style="color:#666;margin-top:3px">NAS ↔ OCI WireGuard mesh · 옵션 B 직접 폴링용</div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div style="background:linear-gradient(135deg,#d1fae520,#a7f3d020);padding:20px;border-radius:16px;border:1px solid #10b98133;margin-bottom:16px">
            <h3 style="color:#065f46;margin:0 0 14px">📦 NAS Edge 수집기 패키지 (배포 준비 완료)</h3>
            <div style="font-size:12.5px;color:#444;line-height:1.8;margin-bottom:10px">
              H200/NAS 도착 시 즉시 배포 가능한 완전 패키지. ZIP 13.6KB · 10개 파일.
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px;font-size:11.5px;font-family:Consolas,monospace;line-height:1.7;color:#444">
              <div>nas_edge_package/</div>
              <div>├─ README.md · INSTALL.md</div>
              <div>├─ docker-compose.yml (3 svc)</div>
              <div>├─ Dockerfile · requirements.txt</div>
              <div>├─ collector.py (H200 폴링)</div>
              <div>├─ uploader.py (큐 → OCI Push)</div>
              <div>├─ config.yaml (3실 매핑)</div>
              <div>├─ .env.template (시크릿)</div>
              <div>└─ healthcheck.sh</div>
            </div>
            <div style="margin-top:10px;font-size:12px;color:#666;line-height:1.7">
              <strong>배포 절차</strong>: NAS의 /volume1/docker/tapo/에 풀기 → .env 작성 → <code style="background:#fff;padding:2px 6px;border-radius:4px">docker compose up -d</code> → healthcheck.sh 검증
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fef3c720,#fde68a20);padding:20px;border-radius:16px;border:1px solid #f59e0b33">
            <h3 style="color:#92400e;margin:0 0 14px">🔗 라이브 링크</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12.5px">
              <div style="background:#fff;padding:10px 14px;border-radius:8px">
                <div><strong>실시간 대시보드</strong></div>
                <a href="https://tapo-api.sappt.co.kr/" target="_blank" style="color:#0891b2;text-decoration:none;font-family:Consolas,monospace;font-size:11px">https://tapo-api.sappt.co.kr/</a>
              </div>
              <div style="background:#fff;padding:10px 14px;border-radius:8px">
                <div><strong>Grafana 대시보드</strong></div>
                <div style="color:#666;margin-top:3px;font-size:11px">sappt-grafana → Dashboards → TAPO 온습도 모니터링 (3개 지점)</div>
              </div>
              <div style="background:#fff;padding:10px 14px;border-radius:8px">
                <div><strong>로그 통합</strong></div>
                <div style="color:#666;margin-top:3px;font-family:Consolas,monospace;font-size:11px">{container_name=~&quot;sappt-tapo-.*&quot;}</div>
              </div>
              <div style="background:#fff;padding:10px 14px;border-radius:8px">
                <div><strong>운영 상태</strong></div>
                <div style="color:#15803d;margin-top:3px"><strong>✅ Phase 1~3 완료</strong> · NAS Edge 패키지 대기 중</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide 14-D: Admin TAPO 관리 화면 ──
  {
    title: '14-D. Admin TAPO 관리 화면 — 수집 모니터링 & 운영',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div>
          <div style="background:linear-gradient(135deg,#dbeafe20,#bfdbfe20);padding:20px;border-radius:16px;border:1px solid #60a5fa33;margin-bottom:16px">
            <h3 style="color:#1e40af;margin:0 0 14px">🖥️ 화면 구성 (SAPPT 관리자 → TAPO 관리)</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12.5px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px;border-left:4px solid #3b82f6">
                <div><strong>① 실시간 현재값 KPI</strong></div>
                <div style="color:#666;margin-top:3px">3개 실 최신 온도·습도 카드 · <strong>15초 자동 폴링</strong> · 지연 5분 이상 시 적색 경고</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px;border-left:4px solid #8b5cf6">
                <div><strong>② 24시간 추이 차트</strong></div>
                <div style="color:#666;margin-top:3px">ECharts 라인 차트 · 5분 평균 버킷 · 온도(실선) / 습도(점선) 이중 Y축</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px;border-left:4px solid #f59e0b">
                <div><strong>③ 최근 알림 패널</strong></div>
                <div style="color:#666;margin-top:3px">미해결 알람 최대 8건 · severity 컬러 태그 (critical/warning)</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px;border-left:4px solid #10b981">
                <div><strong>④ 운영 정보 (API · DB 상태)</strong></div>
                <div style="color:#666;margin-top:3px">TimescaleDB 버전 · 청크 수 · 연속집계 행 수 · 자동화 잡 목록</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px;border-left:4px solid #ec4899">
                <div><strong>⑤ 통계 · 실별 분포 · 디바이스별</strong></div>
                <div style="color:#666;margin-top:3px">전체 행수 / 실데이터 / 테스트 데이터 비율 · 실별 건수 · 디바이스 테이블</div>
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fde68a20,#fbbf2420);padding:20px;border-radius:16px;border:1px solid #fbbf2433">
            <h3 style="color:#92400e;margin:0 0 14px">❓ 도움말 팝오버 (? 아이콘)</h3>
            <div style="font-size:12.5px;color:#444;line-height:1.8">
              페이지 제목 옆 <strong>? 아이콘</strong>을 클릭하면 팝오버가 열립니다.<br/>
              실시간으로 다음 정보를 표시합니다:
            </div>
            <div style="display:flex;flex-direction:column;gap:5px;margin-top:8px;font-size:12px">
              <div style="background:#fff;padding:7px 12px;border-radius:8px">
                <span style="color:#1e40af">📅 수집 시작일시</span> — DB의 <code style="background:#eff6ff;font-size:11px;padding:1px 5px;border-radius:3px">stats.oldest</code> 실시간 반영
              </div>
              <div style="background:#fff;padding:7px 12px;border-radius:8px">
                <span style="color:#1e40af">🕐 최신 수집일시</span> — DB의 <code style="background:#eff6ff;font-size:11px;padding:1px 5px;border-radius:3px">stats.latest</code> 실시간 반영
              </div>
              <div style="background:#fff;padding:7px 12px;border-radius:8px">
                <span style="color:#1e40af">⏱ UI 갱신 주기</span> — 현재값·알림 15초 / 추이 차트 수동
              </div>
              <div style="background:#fff;padding:7px 12px;border-radius:8px">
                <span style="color:#1e40af">⚙️ DB 자동화 잡</span> — TimescaleDB <code style="background:#eff6ff;font-size:11px;padding:1px 5px;border-radius:3px">health.jobs</code> 목록
              </div>
            </div>
          </div>
        </div>
        <div>
          <div style="background:linear-gradient(135deg,#fce7f320,#fbcfe820);padding:20px;border-radius:16px;border:1px solid #ec489933;margin-bottom:16px">
            <h3 style="color:#9d174d;margin:0 0 14px">📊 수집주기 & 데이터 흐름 요약</h3>
            <div style="background:#fff;padding:14px;border-radius:10px;font-size:12px;line-height:2;color:#444;font-family:Consolas,monospace">
              <div><span style="color:#be185d">📡 T315 센서 × 3실</span></div>
              <div style="margin-left:16px">↓ RF 868MHz → H200 Hub</div>
              <div><span style="color:#b45309">💾 NAS collector</span> — <strong>2분 폴링</strong></div>
              <div style="margin-left:16px">↓ SQLite WAL 큐 (오프라인 14일 버퍼)</div>
              <div><span style="color:#b45309">💾 NAS uploader</span> — <strong>30초 batch</strong></div>
              <div style="margin-left:16px">↓ HTTPS Bearer Push</div>
              <div><span style="color:#15803d">⚡ FastAPI /ingest</span> — <strong>OCI 춘천</strong></div>
              <div style="margin-left:16px">↓ asyncpg INSERT</div>
              <div><span style="color:#dc2626">🗄️ TimescaleDB</span> — Hypertable</div>
              <div style="margin-left:16px">↓ 연속집계 (readings_5m)</div>
              <div><span style="color:#4f46e5">🖥️ SAPPT Admin</span> — <strong>15초 폴링</strong></div>
            </div>
            <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:8px;margin-top:12px;font-size:12px">
              <div style="background:#fff;padding:8px;border-radius:8px;text-align:center;border:1px solid #e2e8f0">
                <div style="color:#64748b;font-size:10.5px">센서 수집</div>
                <div style="font-weight:700;color:#1e293b;margin-top:2px">2분/실</div>
              </div>
              <div style="background:#fff;padding:8px;border-radius:8px;text-align:center;border:1px solid #e2e8f0">
                <div style="color:#64748b;font-size:10.5px">서버 Push</div>
                <div style="font-weight:700;color:#1e293b;margin-top:2px">30초 batch</div>
              </div>
              <div style="background:#fff;padding:8px;border-radius:8px;text-align:center;border:1px solid #e2e8f0">
                <div style="color:#64748b;font-size:10.5px">Admin 갱신</div>
                <div style="font-weight:700;color:#1e293b;margin-top:2px">15초 폴링</div>
              </div>
              <div style="background:#fff;padding:8px;border-radius:8px;text-align:center;border:1px solid #e2e8f0">
                <div style="color:#64748b;font-size:10.5px">추이 버킷</div>
                <div style="font-weight:700;color:#1e293b;margin-top:2px">5분 평균</div>
              </div>
              <div style="background:#fff;padding:8px;border-radius:8px;text-align:center;border:1px solid #e2e8f0">
                <div style="color:#64748b;font-size:10.5px">일 수집량</div>
                <div style="font-weight:700;color:#1e293b;margin-top:2px">2,160행</div>
              </div>
              <div style="background:#fff;padding:8px;border-radius:8px;text-align:center;border:1px solid #e2e8f0">
                <div style="color:#64748b;font-size:10.5px">5년 저장</div>
                <div style="font-weight:700;color:#1e293b;margin-top:2px">~95 MB</div>
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#d1fae520,#a7f3d020);padding:20px;border-radius:16px;border:1px solid #10b98133">
            <h3 style="color:#065f46;margin:0 0 14px">🔧 테스트 데이터 관리</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12.5px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>테스트 데이터 입력</strong></div>
                <div style="color:#666;margin-top:3px"><code style="font-size:11px">device_id</code>에 <code style="font-size:11px">TEST</code> 접미사 자동 부여 · 일중 사인파 패턴으로 자연스러운 더미 생성</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>테스트 데이터 삭제</strong></div>
                <div style="color:#666;margin-top:3px"><code style="font-size:11px">device_id LIKE '%TEST%' OR '%SIM%' OR '%API%'</code> 패턴 일괄 삭제 · 실 센서 데이터 보호</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px">
                <div><strong>시뮬레이터 컨테이너</strong> (운영 시 중단)</div>
                <div style="color:#666;margin-top:3px"><code style="font-size:11px">tapo-sim-uploader</code> / <code style="font-size:11px">tapo-sim-tailscale</code> — 실 센서 운영 전 테스트용 · 운영 시 <code style="font-size:11px">docker stop</code> 처리</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide 14-E: TAPO 수집 중단 사고 & 감시 격상 ──
  {
    title: '14-E. TAPO 수집 중단 사고 대응 — 원인 · 진단 절차 · 감시 격상 (2026-08-31)',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div>
          <div style="background:linear-gradient(135deg,#fee2e220,#fecaca20);padding:20px;border-radius:16px;border:1px solid #f8717133;margin-bottom:16px">
            <h3 style="color:#991b1b;margin:0 0 14px">🔴 사고 요약 — 11일 20시간 수집 중단</h3>
            <div style="display:flex;flex-direction:column;gap:8px;font-size:12.5px">
              <div style="background:#fff;padding:10px 14px;border-radius:8px;border-left:4px solid #ef4444">
                <div><strong>2026-08-19 14:40:42</strong> — 마지막 /ingest push</div>
                <div style="color:#666;margin-top:3px">사무실 정전으로 collector · uploader 동시 정지</div>
              </div>
              <div style="background:#fff;padding:10px 14px;border-radius:8px;border-left:4px solid #f59e0b">
                <div><strong>08-19 ~ 08-31</strong> — 감시는 정상 작동, 조치는 없음</div>
                <div style="color:#666;margin-top:3px">6시간마다 CRIT 약 44회 발송 · Discord · 알림센터 모두 도착</div>
              </div>
              <div style="background:#fff;padding:10px 14px;border-radius:8px;border-left:4px solid #10b981">
                <div><strong>2026-08-31 10:29:57</strong> — 수집 재개</div>
                <div style="color:#666;margin-top:3px">공백 구간 측정값 <strong>0건 · 소실 확정</strong> (엣지 큐에도 안 쌓임)</div>
              </div>
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#fef3c720,#fde68a20);padding:20px;border-radius:16px;border:1px solid #f59e0b33;margin-bottom:16px">
            <h3 style="color:#92400e;margin:0 0 12px">🔍 진짜 원인 — 정전이 아니라 DHCP 재할당</h3>
            <div style="font-size:12.5px;color:#444;line-height:1.75">
              정전은 방아쇠였고, <strong>재개를 막은 것은 허브 IP 변경</strong>이다.
            </div>
            <div style="background:#fff;padding:10px 14px;border-radius:8px;margin-top:10px;font-size:12.5px">
              <div>H200 허브 <code style="font-size:11px">192.168.10.110</code> → <strong style="color:#b91c1c">192.168.10.109</strong></div>
              <div style="margin-top:5px">NAS 도 가이드의 <code style="font-size:11px">.20</code> 이 아닌 <strong>.2</strong> — 이 망은 IP 가 고정돼 있지 않다</div>
            </div>
            <div style="background:#fffbeb;padding:10px 14px;border-radius:8px;margin-top:10px;font-size:12px;color:#78350f;border-left:4px solid #f59e0b">
              <strong>결정적 단서</strong>: /ingest 에 401 도 timeout 도 아닌 <strong>요청 자체가 0건</strong>.
              업로더가 거절당한 게 아니라 <strong>보낼 데이터가 없다</strong> = collector 가 허브를 못 읽는다.
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#ede9fe20,#ddd6fe20);padding:20px;border-radius:16px;border:1px solid #a78bfa33">
            <h3 style="color:#5b21b6;margin:0 0 12px">⚠️ 함정 — .env 수정 + 재시작은 반영되지 않는다</h3>
            <div style="font-size:12.5px;color:#444;line-height:1.75">
              <code style="font-size:11px">env_file</code> 은 <strong>컨테이너를 만들 때 한 번</strong> 읽힌다.
              이미 만들어진 컨테이너는 옛 값을 갖고 있어 <strong>restart 로는 바뀌지 않는다</strong>
              (DSM Container Manager 의 '재시작' 버튼도 동일 — 실제로 한 바퀴 헛돌았다).
            </div>
            <div style="background:#0f172a;color:#e2e8f0;padding:12px 14px;border-radius:8px;margin-top:10px;font-family:Consolas,monospace;font-size:11px;line-height:1.7">
              <div style="color:#94a3b8"># 반영 여부 확인</div>
              <div>docker exec tapo-collector printenv HUB_IP</div>
              <div style="color:#94a3b8;margin-top:6px"># 재생성만이 반영된다</div>
              <div>docker compose up -d --force-recreate</div>
            </div>
          </div>
        </div>

        <div>
          <div style="background:linear-gradient(135deg,#dcfce720,#bbf7d020);padding:20px;border-radius:16px;border:1px solid #34d39933;margin-bottom:16px">
            <h3 style="color:#065f46;margin:0 0 12px">🔔 연속 CRIT 재통지 격상 (신규)</h3>
            <div style="font-size:12.5px;color:#444;line-height:1.7;margin-bottom:10px">
              감시는 11일간 정확히 동작했다. <strong>부족했던 건 빈도가 아니라 '얼마나 오래 방치됐는지'의 가시성</strong> —
              44개 통지가 서로 똑같아 6시간 된 장애와 11일 된 장애가 구별되지 않았다.
            </div>
            <table style="width:100%;border-collapse:collapse;font-size:11.5px;background:#fff;border-radius:8px;overflow:hidden">
              <thead>
                <tr style="background:#f0fdf4">
                  <th style="padding:7px 9px;text-align:left">지속</th>
                  <th style="padding:7px 9px;text-align:left">주기</th>
                  <th style="padding:7px 9px;text-align:left">제목</th>
                </tr>
              </thead>
              <tbody>
                <tr><td style="padding:7px 9px;border-top:1px solid #e5e7eb">~24h</td><td style="padding:7px 9px;border-top:1px solid #e5e7eb">6시간</td><td style="padding:7px 9px;border-top:1px solid #e5e7eb">🔴 TAPO CRIT</td></tr>
                <tr><td style="padding:7px 9px;border-top:1px solid #e5e7eb">24~72h</td><td style="padding:7px 9px;border-top:1px solid #e5e7eb"><strong>3시간</strong></td><td style="padding:7px 9px;border-top:1px solid #e5e7eb">🔴🔴 장기 미조치</td></tr>
                <tr><td style="padding:7px 9px;border-top:1px solid #e5e7eb">72h~</td><td style="padding:7px 9px;border-top:1px solid #e5e7eb"><strong>1시간</strong></td><td style="padding:7px 9px;border-top:1px solid #e5e7eb">🚨 심각 + @everyone</td></tr>
              </tbody>
            </table>
            <div style="background:#fff;padding:10px 14px;border-radius:8px;margin-top:10px;font-size:12px;color:#444">
              모든 통지에 <strong>지속 시간 · 최초 감지 · 통지 회차</strong>를 적는다.
              상태파일은 4필드(<code style="font-size:11px">LEVEL 마지막통지 연속시작 회차</code>)이며
              구 2필드도 읽되 <strong>없는 과거는 지어내지 않는다</strong>.
            </div>
            <div style="background:#fff;padding:10px 14px;border-radius:8px;margin-top:8px;font-size:12px;color:#444">
              알림 본문도 정비 — <code style="font-size:11px">lab: 고온 28.600000381469727</code> →
              <strong>사육실: 고온 28.6°C (임계 26°C)</strong>
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#dbeafe20,#bfdbfe20);padding:20px;border-radius:16px;border:1px solid #60a5fa33;margin-bottom:16px">
            <h3 style="color:#1e40af;margin:0 0 12px">🛠️ 진단 순서 (수집 중단 신고 시)</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px;border-left:4px solid #3b82f6">
                <div><strong>① 서버 — 마지막 측정값</strong></div>
                <div style="color:#666;margin-top:3px;font-family:Consolas,monospace;font-size:10.5px">SELECT room_id, max(ts) FROM sensors.readings GROUP BY room_id;</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px;border-left:4px solid #6366f1">
                <div><strong>② 서버 — 마지막 push · 실패 여부</strong></div>
                <div style="color:#666;margin-top:3px">NPM proxy-host-15 로그. 실패 없이 끊겼으면 NAS 쪽</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px;border-left:4px solid #8b5cf6">
                <div><strong>③ 서버 — 사무실 회선 생사</strong></div>
                <div style="color:#666;margin-top:3px">같은 공인 IP 의 lab 접속 유무(proxy-host-2)로 판정</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px;border-left:4px solid #ec4899">
                <div><strong>④ NAS — 환경변수 → 로그 → 허브 도달성</strong></div>
                <div style="color:#666;margin-top:3px">DSM 로그 탭이 비어도 판단 금지 — <code style="font-size:10.5px">logs/edge.log</code> 수정일시가 더 확실</div>
              </div>
            </div>
            <div style="background:#eff6ff;padding:10px 14px;border-radius:8px;margin-top:10px;font-size:12px;color:#1e3a8a;border-left:4px solid #3b82f6">
              <strong>원격 접속 경로 있음</strong> — 사무실 공인 IP 의 <strong>5001(DSM HTTPS) · 5000 · 443</strong> 개방
              (22/SSH 는 닫힘). 이전 기록의 '원격 접근 불가'는 사실이 아니었다.
              단, DSM 이 인터넷에 직접 노출된 상태이므로 2FA · 자동차단 점검 필요.
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#fef3c720,#fed7aa20);padding:20px;border-radius:16px;border:1px solid #fb923c33">
            <h3 style="color:#9a3412;margin:0 0 12px">✅ 재발 방지 체크리스트</h3>
            <div style="font-size:12.5px;color:#444;line-height:1.9">
              <div>☐ 공유기 DHCP 예약 — H200 <strong>.109</strong> · NAS <strong>.2</strong> (MAC 바인딩)</div>
              <div>☐ DSM → 하드웨어 및 전원 → <strong>전원 복구 후 자동 재시작</strong></div>
              <div>☐ 컨테이너 2개 자동 재시작(unless-stopped) 확인</div>
              <div>☐ DSM 작업 스케줄러에 healthcheck.sh 등록</div>
              <div>☐ .env 의 HUB_IP 확인 (GUI 로만 고치면 파일은 옛 값)</div>
            </div>
            <div style="background:#fff7ed;padding:10px 14px;border-radius:8px;margin-top:10px;font-size:12px;color:#7c2d12;border-left:4px solid #ea580c">
              <strong>격상으로도 못 막는 것</strong> — 이번 알림은 정상 도착해 있었다.
              <strong>통지 채널 하나에만 의존하는 구조</strong>가 실제 약점이며,
              3일 이상 미조치 시 다른 경로(이메일 등) 승격이 다음 과제다.
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide 14-F: 하우스 온습도로거 ① 요구 · 하드웨어 · 통신 방식 비교 ──
  {
    title: '14-F. 하우스 온습도로거 ① 요구 · 하드웨어 · 통신 방식 비교 — Dragino S31-CB (초안 2026-11-03)',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div>
          <div style="background:linear-gradient(135deg,#bbf7d020,#86efac20);padding:20px;border-radius:16px;border:1px solid #22c55e33;margin-bottom:16px">
            <h3 style="color:#14532d;margin:0 0 12px">🎯 요구사항 — 시험 주요 하우스 온습도 기록</h3>
            <div style="font-size:12.5px;color:#444;line-height:1.85">
              <div><strong>대상</strong> — 시험 포장의 주요 하우스(현장: 상용 전원·인터넷·공유기 없음)</div>
              <div><strong>측정</strong> — 1시간 단위 온도·습도 (시험 기간 환경조건 기록 → 성적서 근거)</div>
              <div><strong>전송</strong> — 1일 1~2회 서버 수신 (실시간 불필요 · 배터리 우선)</div>
              <div><strong>관리</strong> — lab 에서 장치 ↔ 하우스 ↔ 시험번호 연결, 결손·배터리·수신지연 감시</div>
            </div>
            <div style="background:#fff;padding:10px 14px;border-radius:8px;margin-top:10px;font-size:12px;color:#166534;border-left:4px solid #22c55e">
              <strong>TAPO(사무실 3실)와 다른 점</strong> — TAPO 는 WiFi 허브 + NAS 수집기를 둘 수 있는 실내였다.
              하우스에는 허브·NAS 를 둘 수 없으므로 <strong>단말이 셀룰러로 서버에 직접 올리는 구조</strong>가 필요하다.
              수집 중단을 11일 몰랐던 TAPO 교훈(값이 '안 들어오는 것'을 감시)은 그대로 가져온다.
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#e0e7ff20,#c7d2fe20);padding:20px;border-radius:16px;border:1px solid #6366f133">
            <h3 style="color:#3730a3;margin:0 0 12px">📦 Dragino S31-CB / S31B-CB 사양 (공식 위키 기준)</h3>
            <table style="width:100%;font-size:12px;border-collapse:collapse">
              <tr style="background:#fff"><td style="padding:6px 10px;color:#666;width:34%">통신 모듈</td><td style="padding:6px 10px"><strong>Quectel BG95-NGFF</strong> — LTE-M(Cat.M1) + NB-IoT 겸용, 나노 SIM</td></tr>
              <tr><td style="padding:6px 10px;color:#666">대역</td><td style="padding:6px 10px">NB-IoT B1/2/3/4/5/8/12/13/17/18/19/20/25/28/66/70/85 · LTE-M B1/2/3/4/5/8/12/13/18/19/20/25/26/27/28/66/85</td></tr>
              <tr style="background:#fff"><td style="padding:6px 10px;color:#666">센서</td><td style="padding:6px 10px">SHT31 — -40~80℃ ±0.2℃ · 0~99.9%RH ±2% · <strong>S31-CB 외장 프로브 3m</strong> / S31B-CB 내장</td></tr>
              <tr><td style="padding:6px 10px;color:#666">전원</td><td style="padding:6px 10px">Li/SOCl2 <strong>8,500mAh 비충전</strong>(ER26500+SPC1520) · 자가방전 &lt;1%/년</td></tr>
              <tr style="background:#fff"><td style="padding:6px 10px;color:#666">데이터로그</td><td style="padding:6px 10px"><code>AT+CLOCKLOG=1,65535,TR분,N</code> — TR 분마다 저장, 업링크마다 최근 <strong>N개(최대 32)</strong> 동봉</td></tr>
              <tr><td style="padding:6px 10px;color:#666">프로토콜</td><td style="padding:6px 10px">CoAP · UDP · MQTT · MQTTS · TCP (<code>AT+PRO</code>) / 페이로드 HEX 또는 <strong>JSON</strong></td></tr>
              <tr style="background:#fff"><td style="padding:6px 10px;color:#666">설정·보수</td><td style="padding:6px 10px">BLE 앱 설정 + OTA · 다운링크로 TDC·CLOCKLOG·임계 원격 변경 · 방수 케이스</td></tr>
            </table>
          </div>
        </div>
        <div>
          <div style="background:linear-gradient(135deg,#fef3c720,#fde68a20);padding:20px;border-radius:16px;border:1px solid #f59e0b33;margin-bottom:16px">
            <h3 style="color:#92400e;margin:0 0 12px">📡 통신 방식 비교 — 현장 하우스 기준</h3>
            <table style="width:100%;font-size:11.5px;border-collapse:collapse">
              <tr style="background:#fff7ed;color:#7c2d12"><th style="padding:6px 8px;text-align:left">방식</th><th style="padding:6px 8px;text-align:left">현장 요건</th><th style="padding:6px 8px;text-align:left">요금/배터리</th><th style="padding:6px 8px;text-align:left">판정</th></tr>
              <tr style="background:#fff"><td style="padding:6px 8px"><strong>WiFi</strong> (TAPO 방식)</td><td style="padding:6px 8px">하우스에 공유기·전원·인터넷 필요</td><td style="padding:6px 8px">회선비 · 상시 전원</td><td style="padding:6px 8px;color:#b91c1c">✗ 현장 불가</td></tr>
              <tr><td style="padding:6px 8px"><strong>LoRaWAN</strong> (S31-LB + 게이트웨이)</td><td style="padding:6px 8px">게이트웨이 1대에 전원·인터넷, 반경 수 km</td><td style="padding:6px 8px">회선비 0 · 배터리 최장</td><td style="padding:6px 8px;color:#b45309">△ 하우스가 한 곳에 모여 있을 때 후보</td></tr>
              <tr style="background:#fff"><td style="padding:6px 8px"><strong>NB-IoT</strong> (KT · LGU+)</td><td style="padding:6px 8px">SIM 만 · 비닐/철골 투과 유리 · 저속</td><td style="padding:6px 8px"><strong>330원/월(100KB)</strong> · 저전력 최적</td><td style="padding:6px 8px;color:#15803d"><strong>◎ 1순위</strong></td></tr>
              <tr><td style="padding:6px 8px"><strong>LTE-M</strong> (SKT · KT · LGU+)</td><td style="padding:6px 8px">SIM 만 · 빠름 · 이동성</td><td style="padding:6px 8px">1,100원/월(5MB) · 세션 짧아 전력 양호</td><td style="padding:6px 8px;color:#15803d">○ 대안 (같은 단말, SIM 교체)</td></tr>
            </table>
            <div style="font-size:12px;color:#444;line-height:1.8;margin-top:10px">
              하나의 단말(BG95)이 NB-IoT·LTE-M 을 모두 지원하므로 <strong>현장 신호 측정 뒤 SIM 으로 결정</strong>한다.
              국내 저전력 IoT 망은 SKT LTE Cat.M1(B5) · KT NB-IoT/eMTC · LGU+ NB-IoT/LTE-M1 — 모두 BG95 지원 대역(B3/B5/B8)에 있다.
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#fecaca20,#fed7aa20);padding:20px;border-radius:16px;border:1px solid #f8717133">
            <h3 style="color:#9f1239;margin:0 0 12px">⚠️ 구매 전 확인 항목 (하드웨어 발주 게이트)</h3>
            <div style="font-size:12.5px;color:#444;line-height:1.9">
              <div>☐ <strong>KC 전파인증</strong> 여부 — 해외 직구 단말은 국내 사용 요건 확인(판매처·Dragino 문의)</div>
              <div>☐ <strong>통신사 단말 등록</strong> — IoT 요금제는 단말 모델(IMEI) 등록이 필요한 경우가 있어 개통 가능 여부 선확인</div>
              <div>☐ <strong>현장 NB-IoT 신호</strong> — 하우스 내부 CSQ 측정(첫 1대로), 약하면 LTE-M SIM</div>
              <div>☐ <strong>요금 재확인</strong> — 아래 요금은 2018 상용화 자료·현행 상품페이지 혼재, 가입 시점 기준 재확인</div>
              <div>☐ <strong>Phase 0 시뮬레이터 통과</strong> — 수신 체인 성립 ✅ · <strong>Phase 0.5 운영 서버 외부 수신</strong> ✅ 2026-11-03 완료</div>
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide 14-G: 하우스 온습도로거 ② 수신 아키텍처 · 요금 · 배터리 ──
  {
    title: '14-G. 하우스 온습도로거 ② 수신 아키텍처 · 최저 요금 · 배터리 (2026-11-03 실측 갱신 · 8시간 권장)',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div>
          <div style="background:linear-gradient(135deg,#ddd6fe20,#bae6fd20);padding:20px;border-radius:16px;border:1px solid #818cf833;margin-bottom:16px">
            <h3 style="color:#3730a3;margin:0 0 12px">🏗️ End-to-End 흐름 (TAPO 스택 재활용)</h3>
            <div style="background:#fff;padding:14px;border-radius:10px;font-size:11.5px;line-height:1.9;color:#444;font-family:var(--font-mono)">
              <div><span style="color:#be185d">🌡️ S31-CB ×N (하우스)</span> — 1h 측정 → 플래시 로그(CLOCKLOG)</div>
              <div style="margin-left:18px">&darr; NB-IoT · 8시간마다(00·08·16시) · UDP/JSON 1 패킷(~0.9KB) + ACK (+ 원격 설정 다운링크)</div>
              <div><span style="color:#b45309">☁️ OCI sappt 호스트</span> — 보안목록 + 호스트 UDP 포트 1개 개방</div>
              <div style="margin-left:18px">├─ <strong>sappt-thlogger-receiver</strong> (Docker, tapo 스택) — 디코드 · IMEI 허용목록 · ACK</div>
              <div style="margin-left:18px">├─ tapo-api <code>POST /ingest/logger</code> — (imei, ts) 중복 제거 · 업링크 이력</div>
              <div style="margin-left:18px">└─ TimescaleDB <code>sensors.readings</code>(site=house) + <code>logger_devices</code> · <code>logger_uplinks</code></div>
              <div style="margin-left:18px">&darr;</div>
              <div><span style="color:#0891b2">🖥️ lab.sappt.co.kr</span> — 온습도로거 메뉴(장치 대장·하우스 차트) · 시험일정 상세 환경 탭 · Grafana · 챗봇</div>
              <div style="margin-left:18px">감시: <code>tapo_health_check.sh</code> 확장 — 마지막 업링크 &gt; TDC×2+1h → CRIT (Discord + 알림센터)</div>
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#fef3c720,#fde68a20);padding:20px;border-radius:16px;border:1px solid #f59e0b33">
            <h3 style="color:#92400e;margin:0 0 12px">🔌 전송 프로토콜 선택 — 세션 비용이 요금과 배터리를 정한다</h3>
            <table style="width:100%;font-size:11.5px;border-collapse:collapse">
              <tr style="background:#fff7ed;color:#7c2d12"><th style="padding:6px 8px;text-align:left">방식</th><th style="padding:6px 8px;text-align:right">월 데이터<br/>(8h·N16, 31일)</th><th style="padding:6px 8px;text-align:left">특징</th></tr>
              <tr style="background:#fff"><td style="padding:6px 8px"><strong>UDP + JSON</strong> <code>AT+PRO=2,5</code></td><td style="padding:6px 8px;text-align:right"><strong>85.6KB 실측</strong></td><td style="padding:6px 8px">1패킷 + ACK(<code>Confirm=200</code>) · 세션 최단 · 서버: IMEI 허용목록·범위검증</td></tr>
              <tr><td style="padding:6px 8px">MQTT QoS1 <code>AT+PRO=3,5</code></td><td style="padding:6px 8px;text-align:right">~151KB 계산</td><td style="padding:6px 8px">TCP 세션 · 계정 인증 · 브로커 필요</td></tr>
              <tr style="background:#fff"><td style="padding:6px 8px">MQTTS <code>AT+TLSMOD=1,1</code></td><td style="padding:6px 8px;text-align:right">~605KB 계산</td><td style="padding:6px 8px">TLS 핸드셰이크 ~5KB/회 · 전력 2~3배 · 암호화</td></tr>
            </table>
            <div style="background:#fff;padding:10px 14px;border-radius:8px;margin-top:10px;font-size:12px;color:#7c2d12;border-left:4px solid #ea580c">
              <strong>권고: UDP+JSON 으로 시작</strong> — 온습도는 민감 데이터가 아니고, 보안은 서버측(등록 IMEI 만 저장·값 범위·중복) 으로 충분하다.
              요구가 바뀌면 <strong>AT 명령 몇 줄(BLE 또는 다운링크)로 MQTTS 전환</strong>이 가능해 되돌릴 수 있는 결정이다.
            </div>
          </div>
        </div>
        <div>
          <div style="background:linear-gradient(135deg,#bbf7d020,#86efac20);padding:20px;border-radius:16px;border:1px solid #22c55e33;margin-bottom:16px">
            <h3 style="color:#14532d;margin:0 0 12px">💰 통신요금 최저가 — 100KB 플랜에 들어간다</h3>
            <table style="width:100%;font-size:11.5px;border-collapse:collapse">
              <tr style="background:#f0fdf4;color:#14532d"><th style="padding:6px 8px;text-align:left">요금제</th><th style="padding:6px 8px;text-align:right">월</th><th style="padding:6px 8px;text-align:right">데이터</th><th style="padding:6px 8px;text-align:left">비고</th></tr>
              <tr style="background:#fff"><td style="padding:6px 8px"><strong>KT NB-IoT 3</strong></td><td style="padding:6px 8px;text-align:right"><strong>330원</strong></td><td style="padding:6px 8px;text-align:right">100KB</td><td style="padding:6px 8px">초과 0.22원/0.5KB · 장기·다회선 최대 20% 할인</td></tr>
              <tr><td style="padding:6px 8px"><strong>LGU+ LPWA 33</strong></td><td style="padding:6px 8px;text-align:right"><strong>330원</strong></td><td style="padding:6px 8px;text-align:right">100KB</td><td style="padding:6px 8px">NB-IoT/LTE-M1</td></tr>
              <tr style="background:#fff"><td style="padding:6px 8px">KT NB-IoT 11 / eMTC</td><td style="padding:6px 8px;text-align:right">1,100원</td><td style="padding:6px 8px;text-align:right">2MB / 5MB</td><td style="padding:6px 8px">월 1,850KB 넘을 때만 유리 — 이 용도엔 불필요</td></tr>
              <tr><td style="padding:6px 8px">SKT LTE-M 10</td><td style="padding:6px 8px;text-align:right">1,100원</td><td style="padding:6px 8px;text-align:right">5MB</td><td style="padding:6px 8px">Cat.M1 전용</td></tr>
              <tr style="background:#fff"><td style="padding:6px 8px">1NCE (글로벌 IoT SIM)</td><td style="padding:6px 8px;text-align:right">$14~15 / <strong>10년</strong></td><td style="padding:6px 8px;text-align:right">500MB</td><td style="padding:6px 8px">월 ~190원 환산 · <strong>한국 NB-IoT/LTE-M 커버리지 확인 필요</strong></td></tr>
            </table>
            <div style="font-size:12px;color:#444;line-height:1.8;margin-top:10px">
              31일 실측(1시간 측정): 8h·N16 <strong>85.6KB</strong> · 12h·N24 <strong>76.2KB</strong> · 24h·N24 <strong>38.3KB</strong> · 6h·N12 95.0KB · 4h·N8 113.8KB(초과)
              → 권장안은 100KB 안. 넘쳐도 초과 0.5KB 당 0.22원이라 <strong>월 1,850KB 까지 100KB 플랜이 2MB 플랜보다 싸다</strong>(MQTTS 도 330원+초과분). <strong>20대 월 6,600원</strong>.
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#e0f2fe20,#bae6fd20);padding:20px;border-radius:16px;border:1px solid #0ea5e933">
            <h3 style="color:#0c4a6e;margin:0 0 12px">🔋 배터리 최장 설정 — 측정 주기가 아니라 '세션 수'를 줄인다</h3>
            <table style="width:100%;font-size:11.5px;border-collapse:collapse">
              <tr style="background:#f0f9ff;color:#0c4a6e"><th style="padding:6px 8px;text-align:left">설정</th><th style="padding:6px 8px;text-align:right">일 소모(추정)</th><th style="padding:6px 8px;text-align:right">수명(추정)</th></tr>
              <tr style="background:#fff"><td style="padding:6px 8px"><strong>UDP · 8시간 · N16 (권고 · 관리자 최적)</strong></td><td style="padding:6px 8px;text-align:right">~6.1mAh</td><td style="padding:6px 8px;text-align:right"><strong>~3년</strong></td></tr>
              <tr><td style="padding:6px 8px">UDP · 12시간 · N24 (배터리 우선)</td><td style="padding:6px 8px;text-align:right">~4.2mAh</td><td style="padding:6px 8px;text-align:right">~4년</td></tr>
              <tr style="background:#fff"><td style="padding:6px 8px">신호 약함(-110dBm) · UDP+JSON 8h</td><td style="padding:6px 8px;text-align:right">~15mAh</td><td style="padding:6px 8px;text-align:right">~1.2년</td></tr>
            </table>
            <div style="font-size:11.5px;color:#555;line-height:1.75;margin-top:8px">
              모델 가정: NB-IoT 세션(접속+전송+ACK 대기) ≈ 2mAh, PSM 휴면 ≈ 15µA, SHT31 측정은 무시 수준, 가용 용량 80%.
              Dragino 는 모델별 수명표를 제공하지 않아 <strong>파일럿의 전압 추세로 실측</strong>한다(업링크마다 battery 값이 온다).
            </div>
            <div style="background:#fff;padding:10px 12px;border-radius:8px;margin-top:10px;font-size:11.5px;font-family:var(--font-mono);color:#0c4a6e;line-height:1.8">
              AT+PRO=2,5 · AT+SERVADDR=&lt;host&gt;,&lt;port&gt; · AT+IOTMOD=0(LTE-M) · AT+APN=iot.1nce.net<br/>
              AT+CLOCKLOG=1,65535,<strong>60,16</strong> · AT+TDC=<strong>28800</strong> · AT+REDPT=1 · AT+SHTEMP=0,0 · AT+SHHUM=50,50
            </div>
            <div style="font-size:11.5px;color:#555;margin-top:8px;line-height:1.7">
              <strong>N=16 (8h 분량의 2배)</strong> — 업링크 1회가 유실돼도 다음 업링크가 전 구간을 다시 싣는다. 이 조건으로 100KB 에 드는 가장 짧은 간격이 8시간(85.6KB, 86%). 마지막 ATZ 를 08시 무렵에 하면 00·08·16시 업로드.
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide 14-H: 하우스 온습도로거 ③ Phase 0 시뮬레이터 검증 결과 ──
  {
    title: '14-H. 하우스 온습도로거 ③ Phase 0 — 하드웨어 구매 전 시뮬레이터 수신 검증 (2026-11-03 실측)',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div>
          <div style="background:linear-gradient(135deg,#e0e7ff20,#c7d2fe20);padding:20px;border-radius:16px;border:1px solid #6366f133;margin-bottom:16px">
            <h3 style="color:#3730a3;margin:0 0 12px">🧪 검증 구성 — <code>sappt/thlogger/</code> (표준 라이브러리만, 운영 DB 무접촉)</h3>
            <div style="display:flex;flex-direction:column;gap:7px;font-size:12.5px">
              <div style="background:#fff;padding:9px 12px;border-radius:8px;border-left:4px solid #6366f1">
                <div><strong>sim/s31cb_sim.py</strong> — 장치 모델: CLOCKLOG(TR·N) · TDC · REDPT 재전송 · RTC 오차 · 전압 하강 · 가상 시계로 D일 압축</div>
                <div style="color:#666;margin-top:3px">페이로드는 공식 매뉴얼 2.2.1 JSON 예시와 같은 키 구조(IMEI·battery·signal·time·"1".."N")</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px;border-left:4px solid #0ea5e9">
                <div><strong>receiver/receiver.py</strong> — UDP(기본)/MQTT 수신 · 디코드 · IMEI 허용목록 · (imei,ts) 중복 제거 · ACK · 거절 이력</div>
                <div style="color:#666;margin-top:3px">sink: <code>sqlite:</code>(Phase 0) / <code>http:</code>(Phase 1 → tapo-api /ingest/logger)</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px;border-left:4px solid #22c55e">
                <div><strong>receiver/decoder.py</strong> + <strong>tests/</strong> 단위테스트 10건 — 문서 예시·숫자키 정렬·32 상한·범위·IMEI·HEX 미지원·쓰레기 입력</div>
              </div>
              <div style="background:#fff;padding:9px 12px;border-radius:8px;border-left:4px solid #f59e0b">
                <div><strong>receiver/report.py</strong> — 장치별 수신·중복·<strong>결손(1h 격자)</strong>·수신 지연 등급(TDC×2+1h CRIT) · <strong>sim/run_all.sh</strong> 전 시나리오 재현</div>
              </div>
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#fecaca20,#fed7aa20);padding:20px;border-radius:16px;border:1px solid #f8717133">
            <h3 style="color:#9f1239;margin:0 0 12px">❓ 시뮬레이터로는 확정 못 한 것 — 실기기 1대로 확인 (Phase 1 입구)</h3>
            <div style="font-size:12.5px;color:#444;line-height:1.9">
              <div>① <strong>HEX 페이로드</strong> 데이터로그 바이트 배치 — 문서에 없어 JSON 모드로 시작, HEX 는 실패킷으로 디코더 추가</div>
              <div>② <code>"time"</code> 의 시간대 — 표기가 없어 <strong>UTC 로 가정</strong>(디코더가 가정 플래그를 남긴다)</div>
              <div>③ UDP ACK 문자열(<code>Confirm=200</code>)과 <strong>REDPT 가 여러 패킷을 되돌리는지</strong> — 결손 복구 폭을 정한다</div>
              <div>④ 공개 UDP 포트 도달성 — 노트북 LTE 테더링에서 <code>s31cb_sim.py remote --server host:port</code> 로 ACK 왕복 확인</div>
              <div>⑤ 실제 세션 전력·현장 CSQ — 전압·signal 값이 업링크마다 와서 7일 추세로 본다</div>
            </div>
          </div>
        </div>
        <div>
          <div style="background:linear-gradient(135deg,#bbf7d020,#86efac20);padding:20px;border-radius:16px;border:1px solid #22c55e33;margin-bottom:16px">
            <h3 style="color:#14532d;margin:0 0 12px">✅ 시나리오 결과 — 3대 × 3~4일 · 1h 측정 · 수신기 예외 0건</h3>
            <table style="width:100%;font-size:11.5px;border-collapse:collapse">
              <tr style="background:#f0fdf4;color:#14532d"><th style="padding:5px 8px;text-align:left">시나리오</th><th style="padding:5px 8px;text-align:left">조건</th><th style="padding:5px 8px;text-align:right">중복 제거</th><th style="padding:5px 8px;text-align:right">결손</th></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">normal</td><td style="padding:5px 8px">2회/일 · N24</td><td style="padding:5px 8px;text-align:right">198/417</td><td style="padding:5px 8px;text-align:right;color:#15803d"><strong>0</strong></td></tr>
              <tr><td style="padding:5px 8px">normal</td><td style="padding:5px 8px">1회/일 · N24</td><td style="padding:5px 8px;text-align:right">9/225</td><td style="padding:5px 8px;text-align:right;color:#15803d"><strong>0</strong></td></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">missed (업링크 1회 유실)</td><td style="padding:5px 8px">2회/일 · N24</td><td style="padding:5px 8px;text-align:right">198</td><td style="padding:5px 8px;text-align:right;color:#15803d"><strong>0</strong> — N 여유로 복구</td></tr>
              <tr><td style="padding:5px 8px">missed</td><td style="padding:5px 8px">N12 · REDPT=1</td><td style="padding:5px 8px;text-align:right">18</td><td style="padding:5px 8px;text-align:right;color:#15803d"><strong>0</strong> — 재전송으로 복구</td></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">missed</td><td style="padding:5px 8px">N12 · REDPT=0</td><td style="padding:5px 8px;text-align:right">15</td><td style="padding:5px 8px;text-align:right;color:#b91c1c"><strong>12h</strong> 결손 → 보인다</td></tr>
              <tr><td style="padding:5px 8px">overflow (2회 연속 유실)</td><td style="padding:5px 8px">1회/일 · N24 · REDPT=0</td><td style="padding:5px 8px;text-align:right">3</td><td style="padding:5px 8px;text-align:right;color:#b91c1c"><strong>47h</strong> 결손 → 보인다</td></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">retrans (ACK 2회 유실)</td><td style="padding:5px 8px">재전송 6건 도착</td><td style="padding:5px 8px;text-align:right">348/567</td><td style="padding:5px 8px;text-align:right;color:#15803d"><strong>0</strong> · 중복 0 저장</td></tr>
              <tr><td style="padding:5px 8px">rtc (장치 시계 -4.6일)</td><td style="padding:5px 8px">1대 · 2일</td><td style="padding:5px 8px;text-align:right">40/89</td><td style="padding:5px 8px;text-align:right">49건 전부 <code>clock_suspect</code> 표시·보존</td></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">rogue (미등록 IMEI)</td><td style="padding:5px 8px">3 패킷</td><td style="padding:5px 8px;text-align:right">—</td><td style="padding:5px 8px;text-align:right">저장 0 · 거절 이력 3</td></tr>
              <tr><td style="padding:5px 8px">garbage (비JSON·HEX·범위밖)</td><td style="padding:5px 8px">5 패킷</td><td style="padding:5px 8px;text-align:right">—</td><td style="padding:5px 8px;text-align:right">거절 5 · ACK 0 · 예외 0</td></tr>
            </table>
            <div style="background:#fff;padding:10px 14px;border-radius:8px;margin-top:10px;font-size:12px;color:#166534;border-left:4px solid #22c55e">
              <strong>판정: 수신 체인 성립.</strong> 1시간 로그를 1일 1~2회 묶어 보내는 방식은 중복(정상 동작)과 유실을 서버가 모두 처리하며,
              복구 불가한 결손은 <strong>숨겨지지 않고 결손으로 집계</strong>된다. 하드웨어 발주 게이트의 소프트웨어 조건은 충족.
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#bae6fd20,#7dd3fc20);padding:16px 20px;border-radius:16px;border:1px solid #0ea5e933;margin-bottom:16px">
            <h3 style="color:#0c4a6e;margin:0 0 10px">🌐 Phase 0.5 — 운영 서버 수신기 가동 + 외부 수신 검증 (2026-11-03)</h3>
            <div style="font-size:12px;color:#444;line-height:1.8">
              <div><strong>엔드포인트</strong> 158.180.83.199:<strong>1999/udp</strong> · 컨테이너 <code>sappt-thlogger-receiver</code> · OCI 보안목록 UDP 1999 개방 · 상태 <code>127.0.0.1:18080/status</code></div>
              <div><strong>외부 검증</strong> 싱가포르 OCI 호스트 → 춘천: 1,199B 업링크 <strong>ACK 왕복 98~148ms</strong> · 미등록 IMEI 거절 · <code>devices.txt</code> 줄 추가 즉시 반영(재시작 없음)</div>
              <div><strong>PC 확인용 패키지</strong> <code>thlogger/client/</code> — <code>check_reception.bat [1~10]</code> → <code>[OK]/[FAIL]</code>. 회사 망이 UDP 를 막으면 테더링으로 재시도</div>
              <div style="color:#7c2d12;margin-top:4px">계획: S31-CB 1~2대 구매·테스트 → 약 20대 구축. 호스트 자신에서 공인 IP 로 보내는 테스트는 NAT 헤어핀 불가로 실패하니 판단 근거로 쓰지 말 것</div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#f1f5f920,#e2e8f020);padding:16px 20px;border-radius:16px;border:1px solid #94a3b833">
            <h3 style="color:#334155;margin:0 0 10px">▶ 재현 명령</h3>
            <div style="background:#0f172a;color:#e2e8f0;padding:12px 14px;border-radius:8px;font-size:11.5px;font-family:var(--font-mono);line-height:1.8">
              cd sappt/thlogger<br/>
              python3 -m unittest discover -s tests&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;# 디코더 10건<br/>
              sim/run_all.sh /tmp/thlogger_sim&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;# 전 시나리오<br/>
              python3 receiver/receiver.py --udp 0.0.0.0:1999 --sink sqlite:x.db --register IMEI:이름:H01<br/>
              python3 sim/s31cb_sim.py remote --server 158.180.83.199:1999&nbsp;&nbsp;# PC: 외부 도달성(테더링)
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide 14-I: 하우스 온습도로거 ④ 단계별 계획 · 데이터 모델 · 리스크 ──
  {
    title: '14-I. 하우스 온습도로거 ④ 단계별 계획 · 데이터 모델 · 리스크',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div>
          <div style="background:linear-gradient(135deg,#ddd6fe20,#bae6fd20);padding:20px;border-radius:16px;border:1px solid #818cf833;margin-bottom:16px">
            <h3 style="color:#3730a3;margin:0 0 12px">🗺️ 단계별 계획</h3>
            <div style="display:flex;flex-direction:column;gap:8px;font-size:12.5px">
              <div style="background:#fff;padding:10px 14px;border-radius:8px;border-left:4px solid #22c55e">
                <div><strong>Phase 0 · 시뮬레이터 검증</strong> — <span style="color:#15803d">✅ 2026-11-03 완료</span></div>
                <div style="color:#666;margin-top:3px">장치 에뮬레이터 · 수신기 · 디코더 · 결손/중복/지연 리포트 · 10 시나리오 통과</div>
              </div>
              <div style="background:#fff;padding:10px 14px;border-radius:8px;border-left:4px solid #6366f1">
                <div><strong>Phase 0.5 · 서버 가동 + 외부 수신 검증</strong> — <span style="color:#15803d">✅ 2026-11-03 완료</span> (UDP 1999 공개 · PC 확인 패키지)</div>
              </div>
              <div style="background:#fff;padding:10px 14px;border-radius:8px;border-left:4px solid #6366f1">
                <div><strong>Phase 1 · 파일럿 1~2대 (2~3주)</strong> — 구매 게이트 통과 후, 이후 약 20대 구축</div>
                <div style="color:#666;margin-top:3px">S31-CB 1~2대 + KT NB-IoT SIM(330원) + 예비 LTE-M SIM · 수신기는 이미 가동 중(sqlite → tapo-api 연동으로 전환) ·
                tapo-api <code>/ingest/logger</code> + 테이블 2개 · <strong>실패킷으로 디코더 확정(HEX·time 시간대·ACK·REDPT)</strong> · 하우스 설치 7일 연속 수신 · 전압/CSQ 추세</div>
              </div>
              <div style="background:#fff;padding:10px 14px;border-radius:8px;border-left:4px solid #f59e0b">
                <div><strong>Phase 2 · 등록관리 화면</strong> — <span style="color:#15803d">✅ 2026-11-03 가동</span> (lab 시험관리 → 온습도로거)</div>
                <div style="color:#666;margin-top:3px">장치 대장(IMEI 등록 = 수신 허용) · 시험 배정(기간) · 측정 데이터(차트·평균/최저/최고·결손·엑셀) · 업링크/거절 로그 ·
                설정 가이드(서버 주소·AT 명령 복사) · 수신기는 원문을 <code>/api/thlogger/ingest</code> 로 전달하고 해석·중복제거는 백엔드 ·
                감시 <code>thlogger_health_check.sh</code>(수신지연·저전압 → Discord+알림센터). 남은 것: 시험일정 상세 '환경조건' 탭 · 파트너 노출</div>
              </div>
              <div style="background:#fff;padding:10px 14px;border-radius:8px;border-left:4px solid #0ea5e9">
                <div><strong>Phase 3 · 확대 운영</strong></div>
                <div style="color:#666;margin-top:3px">하우스 수만큼 증설 · 작기별 장치 재배정 이력 · Grafana 대시보드 · 배터리 교체 주기 확정 · (선택) MQTTS 전환</div>
              </div>
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#bbf7d020,#86efac20);padding:20px;border-radius:16px;border:1px solid #22c55e33">
            <h3 style="color:#14532d;margin:0 0 10px">💵 비용 윤곽</h3>
            <div style="font-size:12.5px;color:#444;line-height:1.85">
              <div><strong>통신</strong> — 330원/월/대 (10대 3,300원/월 · 연 39,600원). 1NCE 가 한국 커버리지면 10년 선납 ~2만원/대</div>
              <div><strong>단말</strong> — S31-CB 판매처 견적(해외 리셀러 유통) + 나노 SIM 발급비 · 배터리는 비충전이라 수년 뒤 팩 교체</div>
              <div><strong>서버</strong> — 0원 (기존 OCI 호스트 · tapo TimescaleDB 재활용, 데이터 24~48행/일/대)</div>
            </div>
          </div>
        </div>
        <div>
          <div style="background:linear-gradient(135deg,#fef3c720,#fde68a20);padding:20px;border-radius:16px;border:1px solid #f59e0b33;margin-bottom:16px">
            <h3 style="color:#92400e;margin:0 0 12px">🗄️ 데이터 모델 (tapo TimescaleDB · sensors 스키마)</h3>
            <div style="background:#fff;padding:12px 14px;border-radius:10px;font-size:11.5px;line-height:1.8;color:#444;font-family:var(--font-mono)">
              <div><strong>logger_devices</strong> (imei PK, name, house_code, location_id→mst_locations, tr_min, tdc_sec, installed_at, removed_at, active)</div>
              <div><strong>logger_assignments</strong> (imei, schedule_id→test_schedules, valid_from, valid_to) — 작기별 재배정 이력</div>
              <div><strong>logger_uplinks</strong> (received_at, imei, device_time, battery_v, signal_dbm, n_records, n_inserted, raw jsonb) — '안 들어옴' 감시의 원천</div>
              <div><strong>readings</strong> → 기존 <code>sensors.readings</code> (site_id='house', room_id=house_code, device_id=imei) + <strong>UNIQUE(device_id, ts)</strong></div>
              <div><strong>rejects</strong> (received_at, peer, reason, imei, raw) — 미등록·해석실패를 남긴다</div>
            </div>
            <div style="font-size:12px;color:#7c2d12;margin-top:8px;line-height:1.7">
              시험번호와의 연결은 장치가 아니라 <strong>기간이 있는 배정</strong>으로 둔다 — 한 하우스가 작기마다 다른 시험을 받고, 장치는 하우스 사이를 옮겨 다닌다.
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#fecaca20,#fed7aa20);padding:20px;border-radius:16px;border:1px solid #f8717133">
            <h3 style="color:#9f1239;margin:0 0 12px">⚠️ 리스크 & 대응</h3>
            <table style="width:100%;font-size:11.5px;border-collapse:collapse">
              <tr style="background:#fff"><td style="padding:6px 8px;width:36%"><strong>KC 인증·단말 등록 불가</strong></td><td style="padding:6px 8px">구매 게이트에서 판매처·통신사에 선확인. 불가 시 국내 인증 NB-IoT 단말 또는 LoRaWAN(S31-LB+게이트웨이) 대안</td></tr>
              <tr><td style="padding:6px 8px"><strong>하우스 내부 신호 약함</strong></td><td style="padding:6px 8px">1대로 CSQ 측정 → LTE-M SIM 교체 · 안테나 위치 조정. CSQ 는 업링크마다 기록된다</td></tr>
              <tr style="background:#fff"><td style="padding:6px 8px"><strong>결손이 안 보임</strong> (TAPO 교훈)</td><td style="padding:6px 8px">업링크 이력 기반 지연 감시(TDC×2+1h CRIT) + 1h 격자 결손 집계. 건수가 아니라 <strong>마지막 수신 경과</strong>를 본다</td></tr>
              <tr><td style="padding:6px 8px"><strong>UDP 무인증</strong></td><td style="padding:6px 8px">등록 IMEI 만 저장 · 값 범위·시각 검증 · 거절 이력. 필요 시 AT 명령으로 MQTTS 전환(요금 1,100원 등급·배터리 절반)</td></tr>
              <tr style="background:#fff"><td style="padding:6px 8px"><strong>장치 RTC 오차</strong></td><td style="padding:6px 8px">버리지 않고 <code>clock_suspect</code> 표시 → 수신 시각 기준 보정 가능. NTP(<code>AT+NTP</code>) 설정으로 예방</td></tr>
              <tr><td style="padding:6px 8px"><strong>요금 자료 노후</strong></td><td style="padding:6px 8px">330원·1,100원은 2018 상용화 자료와 현행 상품페이지 혼재 — 가입 시 재확인(이 슬라이드 갱신)</td></tr>
            </table>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide 14-J: 하우스 온습도로거 ⑤ 전송 간격·시각 최적화 · 배터리 조합 · 원격 주기 변경 ──
  {
    title: '14-J. 하우스 온습도로거 ⑤ 8시간 권장안 · 배터리 조합 검토 · 장치 대장 원격 주기 변경 (2026-11-03)',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div>
          <div style="background:linear-gradient(135deg,#bbf7d020,#86efac20);padding:20px;border-radius:16px;border:1px solid #22c55e33;margin-bottom:16px">
            <h3 style="color:#14532d;margin:0 0 12px">⏱️ 100KB 요금제 최적 간격 — 31일 실측</h3>
            <table style="width:100%;font-size:11.5px;border-collapse:collapse">
              <tr style="background:#f0fdf4;color:#14532d"><th style="padding:5px 8px;text-align:left">간격</th><th style="padding:5px 8px;text-align:right">N</th><th style="padding:5px 8px;text-align:right">월 데이터</th><th style="padding:5px 8px;text-align:left">판정</th></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">4시간</td><td style="padding:5px 8px;text-align:right">8</td><td style="padding:5px 8px;text-align:right">113.8KB</td><td style="padding:5px 8px;color:#b91c1c">초과</td></tr>
              <tr><td style="padding:5px 8px">6시간</td><td style="padding:5px 8px;text-align:right">12</td><td style="padding:5px 8px;text-align:right">95.0KB</td><td style="padding:5px 8px;color:#b45309">빠듯함 (여유 5회)</td></tr>
              <tr style="background:#fff"><td style="padding:5px 8px"><strong>8시간</strong></td><td style="padding:5px 8px;text-align:right"><strong>16</strong></td><td style="padding:5px 8px;text-align:right"><strong>85.6KB</strong></td><td style="padding:5px 8px;color:#15803d"><strong>최적</strong></td></tr>
              <tr><td style="padding:5px 8px">12시간</td><td style="padding:5px 8px;text-align:right">24</td><td style="padding:5px 8px;text-align:right">76.2KB</td><td style="padding:5px 8px">배터리 우선</td></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">24시간</td><td style="padding:5px 8px;text-align:right">32</td><td style="padding:5px 8px;text-align:right">47.7KB</td><td style="padding:5px 8px">하루 늦음</td></tr>
            </table>
            <div style="font-size:12px;color:#444;line-height:1.8;margin-top:10px">
              N(동봉 건수) ≥ 간격의 2배를 지키며 100KB 에 드는 가장 짧은 간격이 <strong>8시간</strong>이다. 업로드 1회가 유실돼도 다음 업로드가 복구한다.
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#e0f2fe20,#bae6fd20);padding:20px;border-radius:16px;border:1px solid #0ea5e933">
            <h3 style="color:#0c4a6e;margin:0 0 12px">🕗 관리자 기준 전송 시각 — 00 · 08 · 16시</h3>
            <div style="font-size:12.5px;color:#444;line-height:1.85">
              <div>하우스 <strong>최저온은 해 뜨기 전(05~06시)</strong>, <strong>최고온은 한낮(13~15시)</strong>이다.</div>
              <div>08시 업로드 → 아침 확인(08:30)에 밤사이 최저온·결로 / 16시 업로드 → 퇴근 전(16:30)에 같은 날 최고온</div>
              <div>업로드가 빠지면 수신 지연 경보가 <strong>근무 시간(17시)</strong>에 뜬다 — 12시간(08·20시)이면 21시라 다음 날 인지</div>
              <div>방법: 업로드 간격은 마지막 재시작부터 센다 → 설치 마지막 <code>ATZ</code> 를 08시 무렵, 20대는 1~2분씩 어긋나게</div>
            </div>
          </div>
        </div>
        <div>
          <div style="background:linear-gradient(135deg,#fef3c720,#fde68a20);padding:20px;border-radius:16px;border:1px solid #f59e0b33;margin-bottom:16px">
            <h3 style="color:#92400e;margin:0 0 12px">🔋 데이터 타입 × 통신 방식 — 배터리 (8h·N16, 추정)</h3>
            <table style="width:100%;font-size:11.5px;border-collapse:collapse">
              <tr style="background:#fff7ed;color:#7c2d12"><th style="padding:5px 8px;text-align:left">조합</th><th style="padding:5px 8px;text-align:right">신호 양호</th><th style="padding:5px 8px;text-align:right">신호 약함</th></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">UDP + JSON (현재)</td><td style="padding:5px 8px;text-align:right">3.0년</td><td style="padding:5px 8px;text-align:right">1.2년</td></tr>
              <tr><td style="padding:5px 8px"><strong>UDP + HEX</strong></td><td style="padding:5px 8px;text-align:right"><strong>3.1년</strong></td><td style="padding:5px 8px;text-align:right"><strong>1.6년</strong></td></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">MQTT + HEX</td><td style="padding:5px 8px;text-align:right">3.0년</td><td style="padding:5px 8px;text-align:right">1.1년</td></tr>
              <tr><td style="padding:5px 8px">MQTT + JSON</td><td style="padding:5px 8px;text-align:right">3.0년</td><td style="padding:5px 8px;text-align:right">1.0년</td></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">MQTTS + HEX</td><td style="padding:5px 8px;text-align:right">2.8년</td><td style="padding:5px 8px;text-align:right">0.5년</td></tr>
            </table>
            <div style="font-size:12px;color:#444;line-height:1.8;margin-top:10px">
              <strong>HEX + MQTT 는 배터리 안정화에 도움이 안 된다</strong> — HEX 로 줄인 바이트를 MQTT 연결 왕복(5회)이 다시 쓴다. 신호가 좋으면 조합 차이가 없고,
              약할 때 NB-IoT 반복 전송으로 바이트·왕복 비용이 커진다. 최적은 <strong>UDP + HEX</strong>, 더 큰 변수는 <strong>신호 세기</strong>(설치 위치).
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#ddd6fe20,#bae6fd20);padding:20px;border-radius:16px;border:1px solid #818cf833">
            <h3 style="color:#3730a3;margin:0 0 12px">📡 장치 대장 → 기기 원격 주기 변경</h3>
            <div style="background:#fff;padding:12px 14px;border-radius:10px;font-size:11.5px;line-height:1.9;color:#444;font-family:var(--font-mono)">
              <div>① lab 장치 대장 → 전송 버튼 → 간격·N 선택 (월 데이터 추정 표시) → <strong>전송 대기</strong></div>
              <div>② 다음 업링크 → 서버 응답에 ACK + <code>{"Config":"[AT+TDC=..;AT+CLOCKLOG=1,65535,60,N]"}</code> → <strong>확인 대기</strong></div>
              <div>③ 기기 확인 응답(Downlink_Ack) 또는 업로드 간격이 새 주기(±15%)로 바뀜 → <strong>반영됨</strong> (장치 대장 주기 갱신)</div>
              <div>④ 6회 보내도 확인 없음 → <strong>반영 실패</strong> (현장 BLE 설정) · 대기 중엔 수신 지연 판정을 긴 주기 기준으로</div>
            </div>
            <div style="font-size:12px;color:#7c2d12;margin-top:8px;line-height:1.7">
              단말은 업링크 직후 약 3초만 수신 창을 열어 서버가 먼저 보낼 수 없다. 격리 환경에서 예약→다운링크→적용→확인 응답→반영, 무응답 6회→실패, 취소·범위 검증까지 확인했다.
              <strong>실기기 수용 여부는 파일럿에서 확인</strong>(JSON 다운링크 형식, HEX 는 <code>01 TDC · 08 CLOCKLOG</code> 코드로 대체 가능).
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide 14-K: 하우스 온습도로거 ⑥ 절전·데이터 최적화 배지 · 전문가 검토 ──
  {
    title: '14-K. 하우스 온습도로거 ⑥ 절전·데이터 최적화 배지 · 전문가 검토 (2026-11-03)',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px">
        <div>
          <div style="background:linear-gradient(135deg,#ddd6fe20,#bae6fd20);padding:20px;border-radius:16px;border:1px solid #818cf833;margin-bottom:16px">
            <h3 style="color:#3730a3;margin:0 0 10px">🏷️ 장치 대장 '최적화' 배지 — 최적 · 개선 권장 · 조치 필요</h3>
            <table style="width:100%;font-size:11.5px;border-collapse:collapse">
              <tr style="background:#eef2ff;color:#3730a3"><th style="padding:5px 8px;text-align:left">판정 항목</th><th style="padding:5px 8px;text-align:left">조치 필요</th><th style="padding:5px 8px;text-align:left">개선 권장</th></tr>
              <tr style="background:#fff"><td style="padding:5px 8px"><strong>업로드 간격 (절전)</strong></td><td style="padding:5px 8px"><span style="color:#b91c1c">6시간 미만</span></td><td style="padding:5px 8px"><span style="color:#92400e">8시간 미만</span></td></tr>
              <tr><td style="padding:5px 8px"><strong>동봉 건수 N</strong></td><td style="padding:5px 8px"><span style="color:#b91c1c">분량 &lt; 간격 → 사이 측정값 미전송</span></td><td style="padding:5px 8px"><span style="color:#92400e">간격의 2배 미만 · 2.6배 초과(낭비)</span></td></tr>
              <tr style="background:#fff"><td style="padding:5px 8px"><strong>월 데이터 (30일 실측 우선)</strong></td><td style="padding:5px 8px"><span style="color:#b91c1c">100KB 초과</span></td><td style="padding:5px 8px"><span style="color:#92400e">92KB 초과</span></td></tr>
              <tr><td style="padding:5px 8px"><strong>신호</strong></td><td style="padding:5px 8px"><span style="color:#b91c1c">-105dBm 미만</span></td><td style="padding:5px 8px"><span style="color:#92400e">-105 ~ -95dBm</span></td></tr>
              <tr style="background:#fff"><td style="padding:5px 8px"><strong>배터리</strong></td><td style="padding:5px 8px"><span style="color:#b91c1c">3.0V 미만</span></td><td style="padding:5px 8px"><span style="color:#92400e">3.3V 미만</span></td></tr>
            </table>
            <div style="font-size:12px;color:#444;line-height:1.8;margin-top:10px">
              월 데이터는 <strong>최근 30일 실제 수신량(업링크 원문 + 세션 67B)을 31일로 환산</strong>한 값이 우선이고, 관측 1일 미만이면 설정값으로 추정한다.
              배지에 근거 툴팁이 있고, 설정으로 고칠 수 있으면 <strong>배지 클릭 → 권장값(8시간·N16)이 채워진 주기 변경 창</strong> → 다음 업링크에 기기로 전송.
              색만으로 구분하지 않는다(기호 ✔ △ ! + 글자). 배터리·신호 배지도 칸 수·막대 수 아이콘을 함께 쓴다.
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#bbf7d020,#86efac20);padding:20px;border-radius:16px;border:1px solid #22c55e33">
            <h3 style="color:#14532d;margin:0 0 10px">🔋 배터리 절약 · 통신 데이터 최적화 조치</h3>
            <table style="width:100%;font-size:11.5px;border-collapse:collapse">
              <tr style="background:#f0fdf4;color:#14532d"><th style="padding:5px 8px;text-align:left">조치</th><th style="padding:5px 8px">배터리 효과</th><th style="padding:5px 8px;text-align:left">상태</th></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">업로드 8시간 · N=간격 2배</td><td style="padding:5px 8px;text-align:center"><strong>상</strong></td><td style="padding:5px 8px;color:#15803d"><strong>적용</strong></td></tr>
              <tr><td style="padding:5px 8px">UDP (MQTT·TLS 미사용)</td><td style="padding:5px 8px;text-align:center"><strong>중</strong></td><td style="padding:5px 8px;color:#15803d"><strong>적용</strong></td></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">경보 업링크 끔 · IP 주소 · 재전송 켬</td><td style="padding:5px 8px;text-align:center"><strong>중</strong></td><td style="padding:5px 8px;color:#15803d"><strong>적용</strong></td></tr>
              <tr><td style="padding:5px 8px">설치 위치로 신호 확보 (-105dBm 초과)</td><td style="padding:5px 8px;text-align:center"><strong>최상</strong></td><td style="padding:5px 8px;color:#0369a1"><strong>운영 규칙</strong></td></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">통신사 PSM(절전 모드) 지원</td><td style="padding:5px 8px;text-align:center"><strong>최상</strong></td><td style="padding:5px 8px;color:#b45309"><strong>파일럿 확인</strong></td></tr>
              <tr><td style="padding:5px 8px">GPS 끔 · 외부 인터럽트 끔</td><td style="padding:5px 8px;text-align:center"><strong>상</strong></td><td style="padding:5px 8px;color:#b45309"><strong>파일럿 확인</strong></td></tr>
              <tr style="background:#fff"><td style="padding:5px 8px">HEX 페이로드 (데이터 60~77% 감소)</td><td style="padding:5px 8px;text-align:center"><strong>하~중</strong></td><td style="padding:5px 8px;color:#b45309"><strong>파일럿 확인</strong></td></tr>
              <tr><td style="padding:5px 8px">시험용 짧은 주기 되돌리기</td><td style="padding:5px 8px;text-align:center"><strong>상</strong></td><td style="padding:5px 8px;color:#0369a1"><strong>운영 규칙</strong></td></tr>
            </table>
            <div style="font-size:12px;color:#7c2d12;margin-top:8px;line-height:1.7">
              가장 큰 변수는 설정이 아니라 <strong>신호 세기와 PSM 지원</strong>이다 — 약한 신호는 같은 데이터를 수십 번 반복 전송하고, PSM 이 없으면 대기 전류가 µA 가 아니라 mA 다.
              '파일럿 확인' 항목은 명령 이름·기본값·망 지원을 실기기와 통신사로 확인한 뒤 AT 명령에 넣는다.
            </div>
          </div>
        </div>
        <div>
          <div style="background:linear-gradient(135deg,#fef3c720,#fde68a20);padding:20px;border-radius:16px;border:1px solid #f59e0b33;margin-bottom:16px">
            <h3 style="color:#92400e;margin:0 0 10px">🧑‍🔬 전문가 검토 — 핵심 3가지</h3>
            <div style="font-size:12.5px;color:#444;line-height:1.9">
              <div>① <strong>일사 차폐 없는 값은 성적서 근거가 못 된다</strong> — 햇빛 받은 프로브는 실제보다 5~10℃ 높다</div>
              <div>② <strong>신호 세기가 배터리 수명을 가장 크게 좌우</strong>한다 — 같은 설정도 약한 신호에서 수명 절반 이하</div>
              <div>③ <strong>'값이 안 오는 것'을 감시</strong>해야 한다 — 수신 지연 경보는 끄지 않는다</div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#f1f5f920,#e2e8f020);padding:20px;border-radius:16px;border:1px solid #94a3b833">
            <h3 style="color:#334155;margin:0 0 10px">📋 주의사항 · 고려사항 8개 영역</h3>
            <div style="display:flex;flex-direction:column;gap:6px;font-size:12px;line-height:1.6">
              <div style="background:#fff;padding:7px 12px;border-radius:8px;border-left:4px solid #b91c1c"><strong style="color:#b91c1c">측정 정확도</strong> <span style="color:#555">— 일사 차폐 없으면 5~10℃ 높게 · 새벽 결로 뒤 99.9% 고착 · 생장점 높이·하우스 중앙</span></div>
              <div style="background:#fff;padding:7px 12px;border-radius:8px;border-left:4px solid #6b21a8"><strong style="color:#6b21a8">교정 · 추적성 (GLP)</strong> <span style="color:#555">— 연 1회 기준기 비교 · 원자료 불변(지우지 않고 주석) · 시험 배정(기간)으로 장치↔시험 연결 · UTC 저장/KST 표시</span></div>
              <div style="background:#fff;padding:7px 12px;border-radius:8px;border-left:4px solid #0369a1"><strong style="color:#0369a1">데이터 무결성</strong> <span style="color:#555">— '값이 안 오는 것' 감시(TAPO 11일 교훈) · 고착·튐 값 · 중복은 (장치,시각) 1건 · 시계 밀림 재정렬</span></div>
              <div style="background:#fff;padding:7px 12px;border-radius:8px;border-left:4px solid #b45309"><strong style="color:#b45309">배터리 · 전원</strong> <span style="color:#555">— 저온 용량 감소 · 장기 보관 전지는 설치 전 1~2회 업로드로 깨움 · 3.3V 아래면 남은 기간 짧음 · 정품 팩 교체</span></div>
              <div style="background:#fff;padding:7px 12px;border-radius:8px;border-left:4px solid #15803d"><strong style="color:#15803d">통신 · 망</strong> <span style="color:#555">— 보온 커튼 닫은 겨울 야간이 최악 신호 · 통신사 점검 시 동봉 2배+재전송이 복구 · 월 1회 KT 사용량 대조</span></div>
              <div style="background:#fff;padding:7px 12px;border-radius:8px;border-left:4px solid #334155"><strong style="color:#334155">보안</strong> <span style="color:#555">— UDP 무인증 → 등록 IMEI·값 범위·시각 검증 + 거절 로그 점검 · IMEI 라벨 비공개 · 필요 시 MQTTS</span></div>
              <div style="background:#fff;padding:7px 12px;border-radius:8px;border-left:4px solid #0f766e"><strong style="color:#0f766e">설치 · 운영</strong> <span style="color:#555">— 케이블 철골 고정(방제기·설치류) · 방제수 직접 노출 금지 · 라벨·예비 단말·예비 프로브 · 월간 점검</span></div>
              <div style="background:#fff;padding:7px 12px;border-radius:8px;border-left:4px solid #9f1239"><strong style="color:#9f1239">법규 · 인증</strong> <span style="color:#555">— KC 전파인증 · 리튬전지 항공 운송 제한</span></div>
            </div>
            <div style="font-size:11.5px;color:#666;margin-top:8px">상세 체크리스트: lab → 온습도로거 → 설정 가이드 → 전문가 검토 · 절전·데이터 최적화</div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: HWPX 업로드 기능 ──
  {
    title: 'HWPX 파일 업로드 & 자동 등록',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:24px">
        <div style="background:linear-gradient(135deg,#667eea15,#764ba215);padding:24px;border-radius:16px;border:1px solid #667eea22">
          <h3 style="color:#667eea;margin-top:0">기능 개요</h3>
          <ul style="color:#444;line-height:2;margin:0;padding-left:18px">
            <li><strong>HWPX 파일 업로드</strong> &mdash; 드래그앤드롭 또는 클릭</li>
            <li><strong>자동 파싱</strong> &mdash; HWPX(ZIP+XML) 구조 분석</li>
            <li><strong>미리보기</strong> &mdash; 등록 전 파싱 결과 확인</li>
            <li><strong>시험계획서 자동 등록</strong> &mdash; DB에 즉시 저장</li>
            <li><strong>편집 지원</strong> &mdash; 등록 후 바로 편집 가능</li>
          </ul>
        </div>
        <div style="background:linear-gradient(135deg,#f093fb15,#f5576c15);padding:24px;border-radius:16px;border:1px solid #f093fb22">
          <h3 style="color:#f5576c;margin-top:0">파싱 엔진 상세</h3>
          <ul style="color:#444;line-height:2;margin:0;padding-left:18px">
            <li>HWPX ZIP 내 <code>section0.xml</code> 자동 탐색</li>
            <li>XML 네임스페이스 기반 텍스트 추출</li>
            <li>정규식 패턴으로 필드 자동 매핑</li>
            <li>처리내용(약제) 테이블 자동 파싱</li>
            <li>시험구 배치 테이블 자동 파싱</li>
          </ul>
        </div>
      </div>
      <div class="mermaid">
sequenceDiagram
    actor U as 사용자
    participant F as Frontend
    participant B as Backend API
    participant P as HWPX Parser
    participant DB as PostgreSQL

    U->>F: HWPX 파일 드래그앤드롭
    F->>B: POST /api/protocols/preview-hwpx
    B->>P: parse_hwpx(file_bytes)
    P->>P: ZIP 해제 → XML 추출
    P->>P: 텍스트 파싱 → 필드 매핑
    P-->>B: parsed_data
    B-->>F: 미리보기 데이터
    F-->>U: 파싱 결과 테이블 표시
    U->>F: "등록" 버튼 클릭
    F->>B: POST /api/protocols/upload-hwpx
    B->>P: parse_hwpx(file_bytes)
    P-->>B: parsed_data
    B->>DB: INSERT protocol + treatments + plots
    DB-->>B: 생성된 계획서
    B-->>F: ProtocolResponse
    F-->>U: 등록 완료 화면
      </div>
      <div style="margin-top:20px;display:grid;grid-template-columns:1fr 1fr;gap:16px">
        <div style="background:#f0f7ff;padding:16px;border-radius:12px;border:1px solid #91caff">
          <h4 style="color:#1677ff;margin:0 0 8px">API 엔드포인트</h4>
          <table style="width:100%;font-size:13px;border-collapse:collapse">
            <tr style="border-bottom:1px solid #e8e8e8"><td style="padding:6px"><code>POST</code></td><td style="padding:6px"><code>/api/protocols/upload-hwpx</code></td><td style="padding:6px">파일 업로드 &rarr; 등록</td></tr>
            <tr><td style="padding:6px"><code>POST</code></td><td style="padding:6px"><code>/api/protocols/preview-hwpx</code></td><td style="padding:6px">파일 업로드 &rarr; 미리보기</td></tr>
          </table>
        </div>
        <div style="background:#f0fff4;padding:16px;border-radius:12px;border:1px solid #b7eb8f">
          <h4 style="color:#52c41a;margin:0 0 8px">추출 필드</h4>
          <div style="font-size:12px;color:#555;line-height:1.8">
            프로토콜 코드, 항목명, 시험기관, 시험년도, 담당자,<br/>
            실험시기, 시험장소, 시험목적, 대상병해, 학명,<br/>
            시험작물, 품종, 배치법, 반복수, 조사항목,<br/>
            처리내용(약제) 테이블, 시험구 배치 테이블
          </div>
        </div>
      </div>
    `,
  },
  {
    title: '시험일정총괄표 관리 시스템 (정규화)',
    content: `
      <div style="display:grid;gap:20px">
        <div style="background:linear-gradient(135deg,#f0fdf4,#dcfce7);padding:20px;border-radius:16px;border:1px solid #86efac">
          <h3 style="color:#16a34a;margin:0 0 12px">시험일정총괄표 모듈 개요 (DB 정규화 적용)</h3>
          <p style="color:#555;font-size:14px;line-height:1.8;margin:0">
            농약 시험일정을 총괄적으로 관리하는 모듈입니다. Excel 파일(시험일정총괄표)을 업로드하여 일괄 등록하거나,
            개별 시험일정을 등록/수정/삭제할 수 있습니다. 시험번호, 의뢰사, 시험년도, 구분, 시험장소별 다양한 필터와
            통계 분석 기능을 제공합니다.
          </p>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:#eff6ff;padding:16px;border-radius:12px;border:1px solid #93c5fd">
            <h4 style="color:#2563eb;margin:0 0 10px">주요 기능</h4>
            <ul style="margin:0;padding-left:20px;font-size:13px;color:#555;line-height:2">
              <li><b>Excel 업로드</b> - 시험일정총괄표 .xlsx 파일 일괄 등록</li>
              <li><b>Excel 다운로드</b> - 현재 데이터를 Excel로 내보내기</li>
              <li><b>CRUD</b> - 개별 시험일정 등록/수정/삭제</li>
              <li><b>다중 필터</b> - 년도/구분/의뢰사/검색어 복합 필터</li>
              <li><b>상세보기</b> - 처리일정, 조사일정, 관리현황 확인</li>
              <li><b>통계 분석</b> - 의뢰사별, 구분별, 시험장소별 차트</li>
              <li><b>상태 필터</b> - 처리완료/조사완료/성적서작성중/성적서완료/발급완료/최종종료</li>
              <li><b>파트너확인</b> - 의뢰사 파트너의 일정 확인 및 활동이력 기록</li>
            </ul>
          </div>
          <div style="background:#fef3c7;padding:16px;border-radius:12px;border:1px solid #fcd34d">
            <h4 style="color:#d97706;margin:0 0 10px">DB 테이블 구조 (test_schedules)</h4>
            <div style="font-size:11px;color:#555;line-height:1.9;font-family:monospace">
              &bull; id (PK), seq_no (연번)<br/>
              &bull; test_number (시험번호, 인덱스)<br/>
              &bull; title (항목명)<br/>
              &bull; client_company (의뢰사, 인덱스)<br/>
              &bull; test_year (시험년도, 인덱스)<br/>
              &bull; category (구분, 인덱스)<br/>
              &bull; test_location (시험장소)<br/>
              &bull; start_date (개시일)<br/>
              &bull; field_address (시험포장주소)<br/>
              &bull; field_owner (포장주/연락처)<br/>
              &bull; planting_date (정식시기)<br/>
              &bull; treatment_1~7 (1~7차처리)<br/>
              &bull; survey_1~4 (1~4차조사)<br/>
              &bull; report_date, cleanup, tims_status<br/>
              &bull; review_record, qrcode, status
            </div>
          </div>
        </div>

        <div style="background:#faf5ff;padding:16px;border-radius:12px;border:1px solid #c084fc">
          <h4 style="color:#7c3aed;margin:0 0 10px">API 엔드포인트</h4>
          <table style="width:100%;font-size:12px;border-collapse:collapse">
            <tr style="background:#f5f3ff;font-weight:600"><td style="padding:8px">메소드</td><td style="padding:8px">경로</td><td style="padding:8px">설명</td></tr>
            <tr style="border-bottom:1px solid #e9e5f5"><td style="padding:6px"><code>GET</code></td><td style="padding:6px"><code>/api/schedules</code></td><td style="padding:6px">일정 목록 (검색/필터 파라미터 지원)</td></tr>
            <tr style="border-bottom:1px solid #e9e5f5"><td style="padding:6px"><code>GET</code></td><td style="padding:6px"><code>/api/schedules/{id}</code></td><td style="padding:6px">일정 상세</td></tr>
            <tr style="border-bottom:1px solid #e9e5f5"><td style="padding:6px"><code>POST</code></td><td style="padding:6px"><code>/api/schedules</code></td><td style="padding:6px">일정 등록</td></tr>
            <tr style="border-bottom:1px solid #e9e5f5"><td style="padding:6px"><code>PUT</code></td><td style="padding:6px"><code>/api/schedules/{id}</code></td><td style="padding:6px">일정 수정</td></tr>
            <tr style="border-bottom:1px solid #e9e5f5"><td style="padding:6px"><code>DELETE</code></td><td style="padding:6px"><code>/api/schedules/{id}</code></td><td style="padding:6px">일정 삭제</td></tr>
            <tr style="border-bottom:1px solid #e9e5f5"><td style="padding:6px"><code>POST</code></td><td style="padding:6px"><code>/api/schedules/upload-excel</code></td><td style="padding:6px">Excel 파일 업로드 (일괄 등록)</td></tr>
            <tr style="border-bottom:1px solid #e9e5f5"><td style="padding:6px"><code>GET</code></td><td style="padding:6px"><code>/api/schedules/export/excel</code></td><td style="padding:6px">Excel 다운로드</td></tr>
            <tr style="border-bottom:1px solid #e9e5f5"><td style="padding:6px"><code>GET</code></td><td style="padding:6px"><code>/api/schedules/stats</code></td><td style="padding:6px">통계 데이터</td></tr>
            <tr><td style="padding:6px"><code>GET</code></td><td style="padding:6px"><code>/api/schedules/filters</code></td><td style="padding:6px">필터 옵션 목록</td></tr>
          </table>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:#fef2f2;padding:16px;border-radius:12px;border:1px solid #fca5a5">
            <h4 style="color:#dc2626;margin:0 0 8px">Excel 업로드 사용법</h4>
            <ol style="margin:0;padding-left:20px;font-size:13px;color:#555;line-height:2">
              <li>시험일정총괄표 메뉴 접속</li>
              <li>&lsquo;Excel 업로드&rsquo; 버튼 클릭</li>
              <li>.xlsx 파일 선택 (시험일정총괄표 형식)</li>
              <li>자동으로 헤더를 인식하여 데이터 매핑</li>
              <li>시험번호 기준 중복 체크 (기존 데이터 업데이트)</li>
              <li>결과 메시지 확인 (등록건수/건너뜀 건수)</li>
            </ol>
          </div>
          <div style="background:#ecfdf5;padding:16px;border-radius:12px;border:1px solid #6ee7b7">
            <h4 style="color:#059669;margin:0 0 8px">데이터 관리 포인트</h4>
            <ul style="margin:0;padding-left:20px;font-size:13px;color:#555;line-height:2">
              <li>시험번호는 고유 식별자로 중복 등록 방지</li>
              <li>구분: 품목등록-위탁/자체, 기초시험, 지역적응성시험</li>
              <li>처리일정(7차까지)과 조사일정(4차까지) 관리</li>
              <li>TIMS입력현황, 검토기록지 등 행정관리 추적</li>
              <li>진행상태 관리: 처리완료 → 조사완료 → 성적서작성중 → 성적서완료 → 발급완료 → 최종종료</li>
              <li>다운로드 시 현재 필터 조건 적용</li>
            </ul>
          </div>
        </div>
      </div>
    `,
  },
  {
    title: '파트너 사이트 공개 & 진행상태 업데이트 흐름',
    content: `
      <div style="display:grid;gap:20px">

        <!-- 핵심 원칙 -->
        <div style="background:linear-gradient(135deg,#fef3c7,#fde68a);padding:18px 22px;border-radius:14px;border-left:5px solid #f59e0b">
          <h3 style="margin:0 0 8px;color:#92400e;font-size:15px">핵심 원칙 — "Visibility by Ownership, Not by Flag"</h3>
          <p style="margin:0;color:#78350f;font-size:13px;line-height:1.8">
            SAPPT는 별도의 <code>is_published</code> 플래그를 두지 않습니다. 시험일정의 <strong>company_id</strong> 가 곧 공개 키(key)이며,
            해당 의뢰사 파트너 계정이 로그인하면 자신의 <code>company_id</code>와 일치하는 일정만 자동으로 노출됩니다.
            이는 <strong>Row-Level Tenancy</strong> 패턴으로, 데이터 격리(isolation)와 즉시 가시성(real-time visibility)을 동시에 달성합니다.
          </p>
        </div>

        <!-- 전체 흐름도 -->
        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 16px;color:#1e293b;font-size:15px">① End-to-End 흐름도 (관리자 등록 → 파트너 노출 → 상태 갱신)</h3>
          <div class="mermaid">
flowchart LR
    classDef admin fill:#eef2ff,stroke:#4f46e5,color:#312e81,stroke-width:2px
    classDef db fill:#ecfeff,stroke:#0891b2,color:#0e7490,stroke-width:2px
    classDef api fill:#f0fdf4,stroke:#16a34a,color:#14532d,stroke-width:2px
    classDef partner fill:#fef3c7,stroke:#f59e0b,color:#92400e,stroke-width:2px

    A[관리자<br/>SchedulePage]:::admin
    A1[Excel 업로드<br/>또는 개별 등록]:::admin
    B[(test_schedules<br/>company_id=42<br/>status=처리완료)]:::db
    C[POST/PUT<br/>/api/schedules]:::api
    D[GET<br/>/api/partner/schedules<br/>WHERE company_id=42]:::api
    E[파트너 포털<br/>partner.sappt.co.kr]:::partner
    F[알림 배지<br/>NotificationCenter]:::partner

    A --> A1 --> C --> B
    B -.JWT company_id 매칭.-> D
    D --> E
    B -.상태 변경 트리거.-> F
    F --> E

    style A stroke-width:2px
          </div>
          <p style="margin:12px 0 0;color:#64748b;font-size:11px;line-height:1.7">
            관리자가 등록·수정한 즉시 파트너 사이트에서도 조회됩니다. <strong>중간 캐시·승인 단계가 없으므로 입력 품질이 곧 신뢰성</strong>입니다.
            이를 보완하기 위해 향후 Phase 2 로드맵에 <code>draft / review / published</code> 워크플로 도입이 예정되어 있습니다.
          </p>
        </div>

        <!-- 진행상태 라이프사이클 -->
        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 16px;color:#1e293b;font-size:15px">② 진행상태 라이프사이클 (State Machine)</h3>
          <div class="mermaid">
stateDiagram-v2
    [*] --> 처리완료: 처리 완료<br/>(관리자 변경)
    처리완료 --> 조사완료: 조사 완료
    조사완료 --> 성적서작성중: 성적서 작성 시작
    성적서작성중 --> 성적서완료: 성적서 완료
    성적서완료 --> 발급완료: 성적서 발급
    발급완료 --> 최종종료: 최종 업무 종료
    최종종료 --> [*]

    note right of 처리완료
      파트너에게
      알림 발송 시점
    end note
          </div>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:12px;margin-top:12px">
            <tr style="background:#f1f5f9">
              <th style="padding:8px 12px;text-align:left;color:#475569;border-radius:8px 0 0 8px">상태</th>
              <th style="padding:8px 12px;text-align:left;color:#475569">의미</th>
              <th style="padding:8px 12px;text-align:left;color:#475569">파트너 화면</th>
              <th style="padding:8px 12px;text-align:left;color:#475569;border-radius:0 8px 8px 0">전이 트리거</th>
            </tr>
            <tr style="background:#dbeafe">
              <td style="padding:10px 12px;font-weight:700;color:#1d4ed8;border-radius:8px 0 0 8px">처리완료</td>
              <td style="padding:10px 12px">처리 일정 완료</td>
              <td style="padding:10px 12px">파란 배지</td>
              <td style="padding:10px 12px;border-radius:0 8px 8px 0">관리자 수동 변경 (기본값)</td>
            </tr>
            <tr style="background:#fef3c7">
              <td style="padding:10px 12px;font-weight:700;color:#d97706;border-radius:8px 0 0 8px">조사완료</td>
              <td style="padding:10px 12px">조사 일정 완료</td>
              <td style="padding:10px 12px">노란 배지</td>
              <td style="padding:10px 12px;border-radius:0 8px 8px 0">관리자 PUT /api/schedules/{id}</td>
            </tr>
            <tr style="background:#ede9fe">
              <td style="padding:10px 12px;font-weight:700;color:#6d28d9;border-radius:8px 0 0 8px">성적서작성중</td>
              <td style="padding:10px 12px">성적서 작성 진행 중</td>
              <td style="padding:10px 12px">보라 배지</td>
              <td style="padding:10px 12px;border-radius:0 8px 8px 0">관리자 PUT</td>
            </tr>
            <tr style="background:#dcfce7">
              <td style="padding:10px 12px;font-weight:700;color:#15803d;border-radius:8px 0 0 8px">성적서완료</td>
              <td style="padding:10px 12px">성적서 작성 완료</td>
              <td style="padding:10px 12px">초록 배지, 성적서 다운로드 활성화</td>
              <td style="padding:10px 12px;border-radius:0 8px 8px 0">PUT + ScheduleDocument 연결</td>
            </tr>
            <tr style="background:#e0f2fe">
              <td style="padding:10px 12px;font-weight:700;color:#0369a1;border-radius:8px 0 0 8px">발급완료</td>
              <td style="padding:10px 12px">성적서 발급 완료</td>
              <td style="padding:10px 12px">하늘 배지</td>
              <td style="padding:10px 12px;border-radius:0 8px 8px 0">관리자 PUT</td>
            </tr>
            <tr style="background:#f1f5f9">
              <td style="padding:10px 12px;font-weight:700;color:#475569;border-radius:8px 0 0 8px">최종종료</td>
              <td style="padding:10px 12px">모든 업무 완료·종결</td>
              <td style="padding:10px 12px">회색 배지</td>
              <td style="padding:10px 12px;border-radius:0 8px 8px 0">관리자 PUT</td>
            </tr>
          </table>
        </div>

        <!-- 시퀀스 다이어그램 -->
        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 16px;color:#1e293b;font-size:15px">③ 상태 변경 시 파트너 알림 시퀀스</h3>
          <div class="mermaid">
sequenceDiagram
    autonumber
    participant Admin as 👤 관리자
    participant FE as SchedulePage
    participant API as FastAPI<br/>backend
    participant DB as PostgreSQL
    participant Notif as Notification 테이블
    participant PApp as 📱 파트너 포털
    participant PUser as 🧑‍🌾 의뢰사

    Admin->>FE: 진행상태 변경 (예: "조사완료")
    FE->>API: PUT /api/schedules/{id}<br/>{status: "조사완료"}
    API->>DB: UPDATE test_schedules<br/>SET status, updated_at
    API->>Notif: INSERT (company_id,<br/>"SR22I-APPS01 상태변경: 처리완료→조사완료")
    API-->>FE: 200 OK
    Note over API,Notif: 동일 트랜잭션 내 처리<br/>(원자성 보장)

    PUser->>PApp: 로그인 / 새로고침
    PApp->>API: GET /api/partner/notifications<br/>(JWT: company_id=42)
    API->>Notif: SELECT WHERE company_id=42<br/>ORDER BY created_at DESC
    Notif-->>API: 미읽음 알림 목록
    API-->>PApp: NotificationResponse[]
    PApp->>PUser: 🔔 빨간 배지 + 토스트
    PUser->>PApp: 알림 클릭
    PApp->>API: POST /api/partner/notifications/{id}/read
    API->>Notif: UPDATE is_read=true
          </div>
          <p style="margin:12px 0 0;color:#64748b;font-size:11px;line-height:1.7">
            현재는 <strong>Polling 기반</strong>(파트너 페이지 로드/새로고침 시 fetch)입니다.
            실시간 푸시는 Phase 2에서 <strong>WebSocket 또는 Server-Sent Events</strong>로 전환 예정.
            메일/SMS 발송은 <code>Notification.created_at</code> 트리거 + 별도 worker(<code>celery-beat</code>)로 분리 설계됩니다.
          </p>
        </div>

        <!-- 권한·격리 모델 -->
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#eef2ff,#e0e7ff);padding:18px;border-radius:12px;border:1px solid #c7d2fe">
            <h4 style="margin:0 0 10px;color:#4338ca;font-size:14px">🔐 데이터 격리 (Tenant Isolation)</h4>
            <ul style="margin:0;padding-left:18px;color:#3730a3;font-size:12px;line-height:1.9">
              <li>JWT payload 에 <code>company_id</code> 클레임 내장</li>
              <li>모든 <code>/api/partner/*</code> 엔드포인트는 <code>get_current_partner_user</code> 의존성으로 강제 필터링</li>
              <li>경로변수 ID로 직접 조회 시에도 <code>s.company_id != partner.company_id</code> 검증 → 403</li>
              <li>SQLAlchemy 쿼리에 자동 WHERE 절 주입 (IDOR 방어)</li>
            </ul>
          </div>
          <div style="background:linear-gradient(135deg,#f0fdf4,#dcfce7);padding:18px;border-radius:12px;border:1px solid #86efac">
            <h4 style="margin:0 0 10px;color:#15803d;font-size:14px">⚡ 즉시 가시성 (Real-time)</h4>
            <ul style="margin:0;padding-left:18px;color:#166534;font-size:12px;line-height:1.9">
              <li>관리자 저장 → DB commit → 파트너 다음 fetch에 즉시 반영</li>
              <li>캐시 레이어 없음 (Redis 미사용) → Stale 데이터 문제 zero</li>
              <li>updated_at DESC 정렬 → 최신 변경분이 항상 상단</li>
              <li>알림은 미읽음 우선, 읽음 처리는 클라이언트 명시 호출</li>
            </ul>
          </div>
        </div>

        <!-- 전문가 권고 -->
        <div style="background:linear-gradient(135deg,#fff7ed,#ffedd5);padding:18px 22px;border-radius:14px;border-left:5px solid #ea580c">
          <h4 style="margin:0 0 10px;color:#9a3412;font-size:14px">🎯 전문가 권고 (Production Hardening)</h4>
          <ol style="margin:0;padding-left:20px;color:#7c2d12;font-size:12px;line-height:2">
            <li><strong>감사 로그</strong> — 상태 변경 이력을 <code>schedule_status_history</code> 테이블에 별도 적재 (누가/언제/이전→이후)</li>
            <li><strong>승인 워크플로</strong> — <code>draft → reviewed → published</code> 3단계로 분리, 파트너 노출은 <code>published</code> 부터</li>
            <li><strong>알림 채널 다중화</strong> — 인앱 / 이메일 / SMS / 카카오 알림톡, 의뢰사별 선호 채널 설정</li>
            <li><strong>Optimistic Locking</strong> — <code>version</code> 컬럼으로 동시 수정 충돌 방지</li>
            <li><strong>WebSocket 전환</strong> — Polling 제거, 평균 지연 30초 → 1초 미만</li>
            <li><strong>SLA 모니터링</strong> — 상태 변경 후 첫 파트너 조회까지의 latency 를 Prometheus 지표로 수집</li>
          </ol>
        </div>
      </div>
    `,
  },
  {
    title: 'DB 정규화 및 마스터 관리',
    content: `
      <div style="display:grid;gap:20px">
        <div style="background:linear-gradient(135deg,#eff6ff,#dbeafe);padding:20px;border-radius:16px;border:1px solid #93c5fd">
          <h3 style="color:#2563eb;margin:0 0 12px">DB 정규화 설계 (3NF)</h3>
          <p style="color:#555;font-size:13px;line-height:1.7;margin:0">
            기존 비정규화(Flat) 테이블을 3차 정규형(3NF)으로 재설계하였습니다.<br/>
            <b>반복 컬럼 제거:</b> treatment_1~7, survey_1~4 &rarr; 1:N 자식 테이블로 분리<br/>
            <b>이행적 종속 제거:</b> 의뢰사명, 시험장소, 구분, 포장주 &rarr; 마스터 FK 참조
          </p>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:#fef3c7;padding:16px;border-radius:12px;border:1px solid #fcd34d">
            <h4 style="color:#d97706;margin:0 0 10px">마스터 테이블 (4개)</h4>
            <table style="width:100%;font-size:12px;border-collapse:collapse">
              <tr style="background:#fef9c3;font-weight:600"><td style="padding:6px">테이블</td><td style="padding:6px">건수</td><td style="padding:6px">용도</td></tr>
              <tr style="border-bottom:1px solid #fde68a"><td style="padding:5px"><code>mst_companies</code></td><td style="padding:5px">10개사</td><td style="padding:5px">의뢰사 기준정보</td></tr>
              <tr style="border-bottom:1px solid #fde68a"><td style="padding:5px"><code>mst_locations</code></td><td style="padding:5px">33개소</td><td style="padding:5px">시험장소 (지역+시군)</td></tr>
              <tr style="border-bottom:1px solid #fde68a"><td style="padding:5px"><code>mst_categories</code></td><td style="padding:5px">4개</td><td style="padding:5px">시험구분</td></tr>
              <tr><td style="padding:5px"><code>mst_field_owners</code></td><td style="padding:5px">41명</td><td style="padding:5px">포장주 (이름/연락처 분리)</td></tr>
            </table>
          </div>
          <div style="background:#f0fdf4;padding:16px;border-radius:12px;border:1px solid #86efac">
            <h4 style="color:#16a34a;margin:0 0 10px">트랜잭션 테이블 (3개)</h4>
            <table style="width:100%;font-size:12px;border-collapse:collapse">
              <tr style="background:#dcfce7;font-weight:600"><td style="padding:6px">테이블</td><td style="padding:6px">건수</td><td style="padding:6px">관계</td></tr>
              <tr style="border-bottom:1px solid #bbf7d0"><td style="padding:5px"><code>test_schedules</code></td><td style="padding:5px">289건</td><td style="padding:5px">FK &rarr; 4개 마스터</td></tr>
              <tr style="border-bottom:1px solid #bbf7d0"><td style="padding:5px"><code>schedule_treatments</code></td><td style="padding:5px">561건</td><td style="padding:5px">1:N (차수별 처리)</td></tr>
              <tr><td style="padding:5px"><code>schedule_surveys</code></td><td style="padding:5px">377건</td><td style="padding:5px">1:N (차수별 조사)</td></tr>
            </table>
          </div>
        </div>

        <div style="background:#faf5ff;padding:16px;border-radius:12px;border:1px solid #c084fc">
          <h4 style="color:#7c3aed;margin:0 0 10px">마스터 관리 API</h4>
          <table style="width:100%;font-size:11px;border-collapse:collapse">
            <tr style="background:#f5f3ff;font-weight:600"><td style="padding:6px">리소스</td><td style="padding:6px">LIST</td><td style="padding:6px">CREATE</td><td style="padding:6px">UPDATE</td><td style="padding:6px">DELETE</td></tr>
            <tr style="border-bottom:1px solid #e9e5f5"><td style="padding:5px">의뢰사</td><td style="padding:5px"><code>GET /api/master/companies</code></td><td style="padding:5px"><code>POST</code></td><td style="padding:5px"><code>PUT /{id}</code></td><td style="padding:5px"><code>DELETE /{id}</code></td></tr>
            <tr style="border-bottom:1px solid #e9e5f5"><td style="padding:5px">시험장소</td><td style="padding:5px"><code>GET /api/master/locations</code></td><td style="padding:5px"><code>POST</code></td><td style="padding:5px"><code>PUT /{id}</code></td><td style="padding:5px"><code>DELETE /{id}</code></td></tr>
            <tr style="border-bottom:1px solid #e9e5f5"><td style="padding:5px">시험구분</td><td style="padding:5px"><code>GET /api/master/categories</code></td><td style="padding:5px"><code>POST</code></td><td style="padding:5px"><code>PUT /{id}</code></td><td style="padding:5px"><code>DELETE /{id}</code></td></tr>
            <tr><td style="padding:5px">포장주</td><td style="padding:5px"><code>GET /api/master/field-owners</code></td><td style="padding:5px"><code>POST</code></td><td style="padding:5px"><code>PUT /{id}</code></td><td style="padding:5px"><code>DELETE /{id}</code></td></tr>
          </table>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:#fef2f2;padding:16px;border-radius:12px;border:1px solid #fca5a5">
            <h4 style="color:#dc2626;margin:0 0 8px">업무 분리 구조</h4>
            <ul style="margin:0;padding-left:20px;font-size:12px;color:#555;line-height:2">
              <li><b>마스터 등록</b> - 의뢰사/장소/구분/포장주 기준정보 사전 등록</li>
              <li><b>개별 등록</b> - 마스터 Select + 캘린더 DatePicker 입력</li>
              <li><b>n건 일괄등록</b> - POST /api/schedules/bulk (마스터명 자동 매칭)</li>
              <li><b>Excel 업로드</b> - 마스터 미존재 시 자동 생성 후 FK 매핑</li>
              <li><b>참조 무결성</b> - 마스터 삭제 시 참조 건수 체크</li>
            </ul>
          </div>
          <div style="background:#ecfdf5;padding:16px;border-radius:12px;border:1px solid #6ee7b7">
            <h4 style="color:#059669;margin:0 0 8px">UX/정규화 이점</h4>
            <ul style="margin:0;padding-left:20px;font-size:12px;color:#555;line-height:2">
              <li>데이터 중복 제거 &rarr; 저장공간 절약</li>
              <li>마스터 변경 시 일괄 반영 (의뢰사명 등)</li>
              <li>처리/조사 횟수 제한 없음 (1:N 확장성)</li>
              <li>포장주 이름/연락처 분리 관리</li>
              <li>통계 쿼리 성능 향상 (인덱스 활용)</li>
              <li>참조 무결성으로 데이터 정합성 보장</li>
            </ul>
          </div>
        </div>

        <div style="background:#f0f9ff;padding:16px;border-radius:12px;border:1px solid #7dd3fc;margin-top:4px">
          <h4 style="color:#0284c7;margin:0 0 10px">캘린더 입력 컴포넌트 (DatePicker)</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
            <div>
              <p style="font-size:12px;color:#555;margin:0 0 8px">Ant Design DatePicker + dayjs 기반 캘린더 팝업으로 모든 일자 필드에 적용:</p>
              <ul style="margin:0;padding-left:20px;font-size:12px;color:#555;line-height:2">
                <li><b>개시일</b> - 시험 개시 일자</li>
                <li><b>정식시기</b> - 정식/파종 일자</li>
                <li><b>처리일자</b> (1~N차) - 처리일정 각 차수</li>
                <li><b>조사일자</b> (1~N차) - 조사일정 각 차수</li>
                <li><b>성적서발급일</b> - 성적서 발급 일자</li>
                <li><b>TIMS입력현황</b> - TIMS 입력 일자</li>
              </ul>
            </div>
            <div>
              <p style="font-size:12px;color:#555;margin:0 0 8px"><b>동작 방식:</b></p>
              <ul style="margin:0;padding-left:20px;font-size:12px;color:#555;line-height:2">
                <li>캘린더 팝업에서 날짜 클릭으로 선택</li>
                <li>YYYY-MM-DD 형식 직접 키보드 입력 가능</li>
                <li>수정 시 기존 일자가 자동으로 캘린더에 표시</li>
                <li>dayjs &harr; 문자열 자동 변환 (DB 저장은 문자열)</li>
                <li>파싱 불가한 기존 데이터(텍스트)는 빈 값으로 표시</li>
                <li>한국어 로케일 적용 (ko_KR)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 현재 시스템 구현 현황 ──
  {
    title: '시스템 구현 현황 & 고도화 진행 상태',
    content: `
      <div style="display:grid;gap:16px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#f0fdf4,#dcfce7);padding:18px;border-radius:14px;border:1px solid #86efac">
            <h4 style="color:#16a34a;margin:0 0 10px;font-size:14px">v2.1 구현 완료 항목</h4>
            <div style="display:grid;gap:6px;font-size:12px">
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#16a34a;font-weight:800">&#10003;</span> RBAC 권한 관리 (5 테이블, 역할/권한 매트릭스)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#16a34a;font-weight:800">&#10003;</span> 사용자 관리 (CRUD, 다중 역할 할당)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#16a34a;font-weight:800">&#10003;</span> 메뉴 순서 관리 (드래그앤드롭, 메뉴명 편집)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#16a34a;font-weight:800">&#10003;</span> 동적 사이드바 (DB 기반 메뉴 렌더링)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#16a34a;font-weight:800">&#10003;</span> 서버 모니터링 (CPU/메모리/디스크/컨테이너)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#16a34a;font-weight:800">&#10003;</span> Grafana + Loki + Promtail 로그 수집</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#16a34a;font-weight:800">&#10003;</span> DB 자동 백업 (pg_dump cron + gzip)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#16a34a;font-weight:800">&#10003;</span> 활동이력 & 감사 로그 (ActivityLog)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#16a34a;font-weight:800">&#10003;</span> 파트너 포털 (데이터 격리, 동/하절기 대시보드)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#16a34a;font-weight:800">&#10003;</span> 파트너확인 기능 (활동이력 기록, 관리자 확인)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#16a34a;font-weight:800">&#10003;</span> 6단계 상태 흐름 (처리완료→조사완료→…→최종종료)</div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fef2f2,#fecaca);padding:18px;border-radius:14px;border:1px solid #fca5a5">
            <h4 style="color:#dc2626;margin:0 0 10px;font-size:14px">미구현 / 개선 필요 영역</h4>
            <div style="display:grid;gap:6px;font-size:12px">
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#ef4444;font-weight:800">&#10007;</span> Backend 모듈화 (main.py 1,800+ 줄 단일 파일)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#ef4444;font-weight:800">&#10007;</span> Alembic DB 마이그레이션 (현재 create_all + 수동)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#ef4444;font-weight:800">&#10007;</span> 환경변수 보안 분리 (.env, JWT Secret)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#ef4444;font-weight:800">&#10007;</span> 자동화 테스트 (pytest / Playwright)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#ef4444;font-weight:800">&#10007;</span> CI/CD 파이프라인 (GitHub Actions)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#f59e0b;font-weight:800">&#9679;</span> 승인 워크플로우 (계획서 검토/승인)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#f59e0b;font-weight:800">&#9679;</span> 조사 결과 데이터 입력 모듈</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#f59e0b;font-weight:800">&#9679;</span> 알림 시스템 (Email / Telegram / 카카오톡)</div>
              <div style="background:#fff;padding:8px 12px;border-radius:8px;display:flex;align-items:center;gap:8px"><span style="color:#f59e0b;font-weight:800">&#9679;</span> 모바일 반응형 / PWA</div>
            </div>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:10px">
          <div style="background:#f0fdf4;padding:12px;border-radius:10px;text-align:center;border:1px solid #86efac">
            <div style="font-size:22px;font-weight:800;color:#16a34a">Phase 0</div>
            <div style="font-size:10px;color:#16a34a;font-weight:600;margin-top:2px">완료</div>
            <div style="font-size:10px;color:#555;margin-top:2px">기반 구축<br/>RBAC &amp; 모니터링</div>
          </div>
          <div style="background:#fef3c7;padding:12px;border-radius:10px;text-align:center;border:2px solid #fbbf24">
            <div style="font-size:22px;font-weight:800;color:#d97706">Phase 1</div>
            <div style="font-size:10px;color:#d97706;font-weight:600;margin-top:2px">Q2 2026</div>
            <div style="font-size:10px;color:#555;margin-top:2px">안정화 &amp; 보안<br/>모듈화 &amp; 테스트</div>
          </div>
          <div style="background:#fce7f3;padding:12px;border-radius:10px;text-align:center;border:1px solid #f9a8d4">
            <div style="font-size:22px;font-weight:800;color:#db2777">Phase 2</div>
            <div style="font-size:10px;color:#db2777;font-weight:600;margin-top:2px">Q3 2026</div>
            <div style="font-size:10px;color:#555;margin-top:2px">업무 고도화<br/>워크플로우 &amp; 알림</div>
          </div>
          <div style="background:#ede9fe;padding:12px;border-radius:10px;text-align:center;border:1px solid #a78bfa">
            <div style="font-size:22px;font-weight:800;color:#7c3aed">Phase 3</div>
            <div style="font-size:10px;color:#7c3aed;font-weight:600;margin-top:2px">Q3-Q4 2026</div>
            <div style="font-size:10px;color:#555;margin-top:2px">데이터 분석<br/>외부 연계 &amp; PWA</div>
          </div>
          <div style="background:#eef2ff;padding:12px;border-radius:10px;text-align:center;border:1px solid #a5b4fc">
            <div style="font-size:22px;font-weight:800;color:#4f46e5">Phase 4</div>
            <div style="font-size:10px;color:#4f46e5;font-weight:600;margin-top:2px">2027</div>
            <div style="font-size:10px;color:#555;margin-top:2px">엔터프라이즈<br/>AI &amp; GLP 인증</div>
          </div>
        </div>
      </div>
    `,
  },
  // ══════════════════════════════════════════════════════════════
  // 2026-07 전문가 정밀진단 (인프라/백엔드/프론트·UIUX) — 즉시적용 로드맵
  // ══════════════════════════════════════════════════════════════
  // ── Slide: 정밀진단 개요 & 복합 리스크 ──
  {
    title: '🔬 전문가 정밀진단 (2026-07) — 개요 & 복합 리스크 Top 3',
    content: `
      <div style="display:grid;gap:14px">
        <div style="background:linear-gradient(135deg,#eef2ff,#e0e7ff);padding:14px 18px;border-radius:12px;border:1px solid #a5b4fc">
          <div style="font-size:13px;color:#3730a3;line-height:1.7">
            인프라(SRE) · 백엔드 아키텍처 · 프론트/UIUX 3개 관점에서 실제 소스를 정독하여 근거(파일:라인) 기반으로 진단.
            <b>설계 골격은 성숙(무중단 롤링 · 이중 오프사이트 백업 · 복원 리허설 · Okta SSO · 디자인 토큰 SSOT)</b>하나,
            개별로는 견디지만 <b>조합되면 치명적인 복합 리스크</b>가 핵심 개선 대상.
          </div>
        </div>

        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px">
          <div style="background:#fef2f2;padding:14px;border-radius:12px;border:1px solid #fca5a5">
            <div style="font-size:11px;font-weight:800;color:#dc2626;background:#fee2e2;display:inline-block;padding:2px 8px;border-radius:6px">복합 ① 보안</div>
            <div style="font-size:13px;font-weight:700;color:#b91c1c;margin:8px 0 4px">시크릿 git 커밋 × JWT 하드코딩</div>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              <code>k8s/base/secrets.yaml</code> 가 git 추적(DB비번·OpenRouter/NCPMS/Oracle 실키 평문) + <code>auth.py:11</code> JWT secret 하드코딩 폴백.
              이 값에서 <b>MFA 시드·백업코드 암호화 키까지 파생</b> → 유출 시 전 계정 토큰 위조.
            </div>
          </div>
          <div style="background:#fff7ed;padding:14px;border-radius:12px;border:1px solid #fdba74">
            <div style="font-size:11px;font-weight:800;color:#ea580c;background:#ffedd5;display:inline-block;padding:2px 8px;border-radius:6px">복합 ② DoS</div>
            <div style="font-size:13px;font-weight:700;color:#c2410c;margin:8px 0 4px">업로드 무제한 × 이벤트루프 블로킹</div>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              HWPX/Excel 업로드 크기 상한 없음(<code>main.py:858, 2071</code>) + 파싱을 async 라우트에서 인라인 실행 → lxml/openpyxl 이 <b>이벤트루프 전체 점유</b>.
              대용량 몇 건이 API 전체를 마비.
            </div>
          </div>
          <div style="background:#f5f3ff;padding:14px;border-radius:12px;border:1px solid #c4b5fd">
            <div style="font-size:11px;font-weight:800;color:#7c3aed;background:#ede9fe;display:inline-block;padding:2px 8px;border-radius:6px">복합 ③ 가용성</div>
            <div style="font-size:13px;font-weight:700;color:#6d28d9;margin:8px 0 4px">단일 노드 SPOF × liveness-DB 결합</div>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              postgres 단일 인스턴스(SPOF)+PITR 없어 <b>최대 3h 데이터 유실</b>. <code>/api/health</code>(SELECT 1)를 liveness 에도 사용 → DB 일시장애 시 backend 2 Pod <b>CrashLoop 폭풍</b>.
            </div>
          </div>
        </div>

        <div style="background:#fff;padding:14px 16px;border-radius:12px;border:1px solid #e2e8f0">
          <h4 style="margin:0 0 8px;font-size:13px;color:#334155">우선순위 매트릭스 (P0=지혈 · P1=안정성 · P2=구조개선)</h4>
          <table style="width:100%;border-collapse:collapse;font-size:11.5px">
            <thead><tr style="background:#f8fafc;color:#475569">
              <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #e2e8f0">등급</th>
              <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #e2e8f0">인프라</th>
              <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #e2e8f0">백엔드</th>
              <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #e2e8f0">프론트 · UIUX</th>
            </tr></thead>
            <tbody>
              <tr>
                <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;font-weight:800;color:#dc2626">P0</td>
                <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9">시크릿 git제거+로테이션 · 포트 로컬바인딩 · latest→불변태그</td>
                <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9">JWT 폴백제거 · 로그인 레이트리밋 · 업로드 크기제한</td>
                <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9">401 refresh · 전역 에러 UX</td>
              </tr>
              <tr>
                <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;font-weight:800;color:#ea580c">P1</td>
                <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9">liveness 분리 · WAL 아카이빙(PITR) · Alertmanager · PDB</td>
                <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9">파싱 threadpool 오프로드 · Alembic · storage 지연초기화</td>
                <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9">코드 스플리팅(lazy) · React Query · Partner 모바일</td>
              </tr>
              <tr>
                <td style="padding:6px 8px;font-weight:800;color:#7c3aed">P2</td>
                <td style="padding:6px 8px">postgres HA/복제 · Trivy 스캔 · GitOps</td>
                <td style="padding:6px 8px">APIRouter 분리 · 목록 페이지네이션 · pytest · 날짜 정규화</td>
                <td style="padding:6px 8px">SchedulePage 분해 · MasterPage 제네릭 CRUD · hex→토큰</td>
              </tr>
            </tbody>
          </table>
          <div style="font-size:11px;color:#94a3b8;margin-top:8px">
            ※ 검토일 2026-07-06 · 검토 범위: main.py 6,340줄(엔드포인트 172) · Admin 23,684 LOC(23p, 번들 3.8MB) · Partner 2,622 LOC(6p). 상세는 다음 3개 슬라이드 참조.
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 인프라 정밀진단 (즉시적용) ──
  {
    title: '🔬 정밀진단 · 인프라(SRE) — 근거 & 즉시적용',
    content: `
      <div style="display:grid;gap:12px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">

          <div style="background:#fef2f2;padding:14px;border-radius:12px;border:1px solid #fca5a5">
            <h4 style="margin:0 0 8px;font-size:13px;color:#b91c1c">P0-1 · 시크릿 git 제거 + 전량 로테이션</h4>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              <b>근거:</b> <code>k8s/base/secrets.yaml</code> git 추적(커밋 7c7a291) — DB <code>sappt2026!</code> · OPENROUTER/NCPMS/ORACLE 실키 평문. private repo라도 <b>히스토리 영구 각인</b>.
            </div>
            <div style="background:#450a0a;color:#fecaca;padding:10px;border-radius:8px;margin-top:8px;font-family:monospace;font-size:10.5px;line-height:1.7">
              git rm --cached sappt/k8s/base/secrets.yaml<br/>
              echo 'k8s/base/secrets.yaml' &gt;&gt; .gitignore<br/>
              # 이후 전 시크릿 로테이션(DB/OpenRouter/NCPMS/Oracle/JWT)<br/>
              # 히스토리 정리: git filter-repo --path ...secrets.yaml --invert-paths<br/>
              # 여력 시: SealedSecrets/SOPS 로 암호화본만 커밋
            </div>
          </div>

          <div style="background:#fff7ed;padding:14px;border-radius:12px;border:1px solid #fdba74">
            <h4 style="margin:0 0 8px;font-size:13px;color:#c2410c">P0-2 · 인증없는 포트 호스트노출 차단</h4>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              <b>근거:</b> <code>docker-compose.yml</code> OpenSearch <code>9200:9200</code>(:150, 보안플러그인 DISABLE) · postgres <code>5432:5432</code>(:12). 방화벽 단일 의존.
            </div>
            <div style="background:#431407;color:#fed7aa;padding:10px;border-radius:8px;margin-top:8px;font-family:monospace;font-size:10.5px;line-height:1.7">
              # docker-compose.yml — 로컬 바인딩으로 변경<br/>
              ports:<br/>
              &nbsp;&nbsp;- "127.0.0.1:9200:9200"<br/>
              &nbsp;&nbsp;- "127.0.0.1:5432:5432"<br/>
              # k3s는 172.17.0.1 경유이므로 localhost 바인딩으로도 동작
            </div>
          </div>

          <div style="background:#f5f3ff;padding:14px;border-radius:12px;border:1px solid #c4b5fd">
            <h4 style="margin:0 0 8px;font-size:13px;color:#6d28d9">P0-3 · latest → 불변태그 배포</h4>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              <b>근거:</b> CI는 <code>sha-*/timestamp</code> 태그 생성하나 <code>deploy.sh:25</code> 가 <code>:latest</code> 고정 배포 → 운영 리비전 추적 불가·Pod간 드리프트.
            </div>
            <div style="background:#2e1065;color:#ddd6fe;padding:10px;border-radius:8px;margin-top:8px;font-family:monospace;font-size:10.5px;line-height:1.7">
              # deploy.sh — 태그 인자화<br/>
              TAG=&#36;{2:-latest}<br/>
              kubectl set image deploy/&#36;SVC \\<br/>
              &nbsp;&nbsp;&#36;SVC=ghcr.io/sappt/sappt-&#36;SVC:&#36;TAG -n sappt<br/>
              # 중장기: Argo CD/Flux GitOps 로 선언적 배포·롤백
            </div>
          </div>

          <div style="background:#eff6ff;padding:14px;border-radius:12px;border:1px solid #93c5fd">
            <h4 style="margin:0 0 8px;font-size:13px;color:#1d4ed8">P1-1 · liveness를 DB에서 분리</h4>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              <b>근거:</b> <code>/api/health</code>(<code>main.py:4318</code>, SELECT 1)를 liveness(<code>backend.yaml:156</code>)에 사용 → DB 장애 시 2 Pod CrashLoop 증폭.
            </div>
            <div style="background:#172554;color:#bfdbfe;padding:10px;border-radius:8px;margin-top:8px;font-family:monospace;font-size:10.5px;line-height:1.7">
              # backend: /livez (DB 비의존 200) 추가<br/>
              @app.get("/api/livez")<br/>
              def livez(): return {"ok": True}<br/>
              # backend.yaml — livenessProbe.path: /api/livez<br/>
              # readinessProbe 만 /api/health(DB) 유지
            </div>
          </div>

        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
          <div style="background:#f0fdf4;padding:12px 14px;border-radius:10px;border:1px solid #86efac">
            <b style="font-size:12px;color:#15803d">P1-2 · PITR(WAL 아카이빙)</b>
            <div style="font-size:11px;color:#555;line-height:1.7;margin-top:4px">
              현재 pg_dump 3h 주기(RPO 3h, RESTORE_RUNBOOK.md:20). <b>wal-g/pgBackRest</b>로 WAL을 B2/OCI 지속 아카이빙 → RPO 분·초 단위. 기존 rclone/OCI CLI 재사용.
            </div>
          </div>
          <div style="background:#f0fdf4;padding:12px 14px;border-radius:10px;border:1px solid #86efac">
            <b style="font-size:12px;color:#15803d">P1-3 · Alertmanager + PDB</b>
            <div style="font-size:11px;color:#555;line-height:1.7;margin-top:4px">
              현재 알림은 disk_alert/백업 Discord 애드혹. Prometheus 알람규칙(KubePodCrashLooping·메모리&gt;90%·cert D-14) 부재. 각 Deployment <code>PodDisruptionBudget minAvailable:1</code> 추가.
            </div>
          </div>
        </div>
        <div style="background:#f5f3ff;padding:10px 14px;border-radius:10px;border:1px dashed #c4b5fd;font-size:11px;color:#6d28d9">
          <b>P2 · SPOF 해소</b> — postgres 스트리밍 복제 standby 또는 OCI 관리형 PostgreSQL 이관(ADB 연동경로 존재). Trivy 이미지 스캔 CI 스텝 · Grafana 비번 Secret 분리 · Endpoints <code>172.17.0.1</code> 하드코딩(external-services.yaml) → ExternalName 전환.
        </div>
      </div>
    `,
  },
  // ── Slide: 백엔드 정밀진단 (즉시적용) ──
  {
    title: '🔬 정밀진단 · 백엔드 아키텍처 — 근거 & 즉시적용',
    content: `
      <div style="display:grid;gap:12px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">

          <div style="background:#fef2f2;padding:14px;border-radius:12px;border:1px solid #fca5a5">
            <h4 style="margin:0 0 8px;font-size:13px;color:#b91c1c">P0 · JWT 폴백 제거 + 로그인 방어</h4>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              <b>근거:</b> <code>auth.py:11</code> <code>getenv("SAPPT_SECRET_KEY", "sappt-...-production")</code> 폴백 → Fernet/MFA 시드(<code>auth.py:25</code>)까지 파생. 로그인(<code>main.py:522</code>)·파트너로그인 레이트리밋 없음(콘솔 MFA에만 존재).
            </div>
            <div style="background:#450a0a;color:#fecaca;padding:10px;border-radius:8px;margin-top:8px;font-family:monospace;font-size:10.5px;line-height:1.7">
              # auth.py — 폴백 제거, 미설정 시 기동 실패<br/>
              SECRET = os.environ["SAPPT_SECRET_KEY"]  # KeyError로 중단<br/>
              # 로그인: 기존 cache.incr 재사용 IP+계정 잠금<br/>
              # (콘솔 MFA _console_* 로직을 일반 로그인에 확장)
            </div>
          </div>

          <div style="background:#fff7ed;padding:14px;border-radius:12px;border:1px solid #fdba74">
            <h4 style="margin:0 0 8px;font-size:13px;color:#c2410c">P0 · 업로드 크기/타입 검증</h4>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              <b>근거:</b> 확장자만 검사(<code>main.py:858</code> HWPX, <code>:2071</code> Excel). 크기상한은 게시판(<code>:4089</code> 50MB)에만. MIME/매직바이트 미검증.
            </div>
            <div style="background:#431407;color:#fed7aa;padding:10px;border-radius:8px;margin-top:8px;font-family:monospace;font-size:10.5px;line-height:1.7">
              MAX = 30 * 1024 * 1024<br/>
              body = await file.read()<br/>
              if len(body) &gt; MAX: raise HTTPException(413)<br/>
              # + 매직바이트(zip 헤더 PK\\x03\\x04) 확인
            </div>
          </div>

          <div style="background:#fff7ed;padding:14px;border-radius:12px;border:1px solid #fdba74">
            <h4 style="margin:0 0 8px;font-size:13px;color:#c2410c">P1 · 무거운 파싱 이벤트루프 오프로드</h4>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              <b>근거:</b> <code>async upload_hwpx</code>(<code>:867</code>)·<code>upload_schedule_excel</code>(<code>:2065</code>)에서 lxml/openpyxl 인라인 실행. 챗봇 스트림 sync <code>def</code>(<code>:5312</code>)+동기 httpx로 워커 점유.
            </div>
            <div style="background:#431407;color:#fed7aa;padding:10px;border-radius:8px;margin-top:8px;font-family:monospace;font-size:10.5px;line-height:1.7">
              from fastapi.concurrency import run_in_threadpool<br/>
              data = await run_in_threadpool(parse_hwpx, body)<br/>
              # 챗봇: async def + httpx.AsyncClient 스트림 전환<br/>
              # _log_activity IP지오·OpenSearch색인 → BackgroundTasks
            </div>
          </div>

          <div style="background:#eff6ff;padding:14px;border-radius:12px;border:1px solid #93c5fd">
            <h4 style="margin:0 0 8px;font-size:13px;color:#1d4ed8">P1 · Alembic + storage 지연초기화</h4>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              <b>근거:</b> startup 수작업 ALTER/DO(<code>main.py:115~330</code>) → replicas:2 동시실행 레이스. <code>storage.py:14</code> import 시점 <code>oci.config.from_file()</code> 실패 시 <b>앱 전체 기동 불가</b>.
            </div>
            <div style="background:#172554;color:#bfdbfe;padding:10px;border-radius:8px;margin-top:8px;font-family:monospace;font-size:10.5px;line-height:1.7">
              alembic init migrations   # 현 DDL을 baseline 리비전으로<br/>
              # 마이그레이션은 initContainer/deploy.sh 1회 실행(부팅 분리)<br/>
              # storage.py: get_client() 지연초기화 패턴 + 명시 timeout
            </div>
          </div>

        </div>

        <div style="background:#f8fafc;padding:12px 14px;border-radius:10px;border:1px solid #e2e8f0">
          <b style="font-size:12px;color:#334155">P2 · 구조/성능</b>
          <div style="font-size:11px;color:#555;line-height:1.8;margin-top:4px">
            &bull; <b>APIRouter/서비스 계층 분리</b> — main.py 6,340줄·172 엔드포인트(async 23개뿐), <code>include_router</code> 0건 → 도메인별 <code>routers/*.py</code> 500~800줄 목표<br/>
            &bull; <b>목록 페이지네이션</b> — <code>list_schedules</code>(<code>:1834</code>) <code>.all()</code> 전량 반환 → limit/offset + X-Total-Count<br/>
            &bull; <b>권한 N+1 제거</b> — <code>permissions.py:64-70</code> 행마다 <code>Query.get()</code>(2.0 deprecated) → 조인 1회<br/>
            &bull; <b>날짜 문자열→Date</b> — <code>test_start_date</code> 등 <code>String(100)</code>(models.py) → 범위조회·정렬 최적화<br/>
            &bull; <b>pytest 스모크</b> — 테스트 0건. 인증/권한·파트너격리(<code>_resolve_company_ids</code>)·파서 골든파일부터
          </div>
        </div>
        <div style="font-size:10.5px;color:#94a3b8">※ 참고: 챗봇 실제 구현은 OpenRouter 폴백 체인(<code>llm_client.py</code>) — 문서상 "Claude API"와 상이. 문서 동기화 권장.</div>
      </div>
    `,
  },
  // ── Slide: 프론트/UIUX 정밀진단 (즉시적용) ──
  {
    title: '🔬 정밀진단 · 프론트엔드 & UI/UX — 근거 & 즉시적용',
    content: `
      <div style="display:grid;gap:12px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">

          <div style="background:#fef2f2;padding:14px;border-radius:12px;border:1px solid #fca5a5">
            <h4 style="margin:0 0 8px;font-size:13px;color:#b91c1c">P1 · 코드 스플리팅(React.lazy)</h4>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              <b>근거(실측):</b> Admin <code>main.js</code> <b>3.8MB(gzip 1.1MB)</b> 단일 청크 · Partner 1.2MB · <code>React.lazy</code> 사용 0. echarts/antd/@xyflow 전량 초기 로드.
            </div>
            <div style="background:#450a0a;color:#fecaca;padding:10px;border-radius:8px;margin-top:8px;font-family:monospace;font-size:10.5px;line-height:1.7">
              const SchedulePage = React.lazy(() =&gt; import('./pages/SchedulePage'))<br/>
              &lt;Suspense fallback={&lt;Spin/&gt;}&gt; ... &lt;/Suspense&gt;<br/>
              # 우선: SystemGuide(6,528L)·Monitoring·Schedule·ChatBot·ArchFlow
            </div>
          </div>

          <div style="background:#fff7ed;padding:14px;border-radius:12px;border:1px solid #fdba74">
            <h4 style="margin:0 0 8px;font-size:13px;color:#c2410c">P1 · 서버상태 캐싱(React Query)</h4>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              <b>근거:</b> <code>SchedulePage.tsx:162-171</code> fetchData가 전 필터에 의존 → 필터 1개 변경에 목록+filters+stats <b>3콜 전량 재조회</b>. 모든 mutation이 풀 리로드. 캐시 라이브러리 없음.
            </div>
            <div style="background:#431407;color:#fed7aa;padding:10px;border-radius:8px;margin-top:8px;font-family:monospace;font-size:10.5px;line-height:1.7">
              useQuery(['schedules', filters], ...)  # 필터키 캐싱<br/>
              # filters/stats 는 별도 쿼리키 → 재조회 방지<br/>
              # mutation 은 invalidateQueries 로 대상만 무효화
            </div>
          </div>

          <div style="background:#eff6ff;padding:14px;border-radius:12px;border:1px solid #93c5fd">
            <h4 style="margin:0 0 8px;font-size:13px;color:#1d4ed8">P1 · 401 refresh + 전역 에러 UX</h4>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              <b>근거:</b> <code>api.ts:17-26</code> 401=무조건 로그아웃(refresh 없음). Dashboard 로드 실패는 무음 catch(<code>DashboardPage.tsx:176</code>) → <code>if(!stats) return null</code>(<code>:402</code>) <b>빈 화면</b>. 무음 catch 14회.
            </div>
            <div style="background:#172554;color:#bfdbfe;padding:10px;border-radius:8px;margin-top:8px;font-family:monospace;font-size:10.5px;line-height:1.7">
              # 응답 인터셉터: 401 → refresh 재발급 큐잉(single-flight)<br/>
              # 5xx/네트워크 → 전역 fallback 토스트<br/>
              # 빈 화면 대신 "에러+재시도" UI (대시보드 우선)
            </div>
          </div>

          <div style="background:#fffbeb;padding:14px;border-radius:12px;border:1px solid #fcd34d">
            <h4 style="margin:0 0 8px;font-size:13px;color:#b45309">P1 · Partner 모바일 대응</h4>
            <div style="font-size:11.5px;color:#555;line-height:1.7">
              <b>근거:</b> 포털은 모바일 사용 개연 높음. <code>ScheduleListPage</code> 테이블 <code>scroll={{x}}</code> 부재(11컬럼 오버플로우) · 사이드바 &lt;768px 재확장 불가·Drawer 없이 64px 고정. aria/role/alt 0, 키보드로 상세진입 불가.
            </div>
            <div style="background:#451a03;color:#fde68a;padding:10px;border-radius:8px;margin-top:8px;font-family:monospace;font-size:10.5px;line-height:1.7">
              &lt;Table scroll={{ x: 'max-content' }} /&gt;  # 또는 카드형<br/>
              # 사이드바: &lt;768px AntD Drawer 오버레이 전환<br/>
              # 클릭행 role="button"/tabIndex/Enter · amber 대비 개선
            </div>
          </div>

        </div>

        <div style="background:#f8fafc;padding:12px 14px;border-radius:10px;border:1px solid #e2e8f0">
          <b style="font-size:12px;color:#334155">P2 · UI/UX 구조 개선 & 디자인 일관성</b>
          <div style="font-size:11px;color:#555;line-height:1.8;margin-top:4px">
            &bull; <b>SchedulePage 분해</b> — 1,786 LOC 단일·useState~30·inline Modal 6개 → 모달/훅(<code>useScheduleTable</code>) 파일 분리, 렌더내 서브컴포넌트 선언 제거<br/>
            &bull; <b>MasterPage 제네릭 CRUD</b> — 7엔티티 복붙 ~700 LOC → <code>&lt;MasterCrudTab/&gt;</code> · <code>useCrudResource</code> 훅<br/>
            &bull; <b>공유 패키지</b> — Admin↔Partner authToken/api/theme ~90% 중복 → <code>@sappt/ui-core</code> (~500 LOC 제거)<br/>
            &bull; <b>하드코딩 hex 2,180개 → 토큰</b> — <code>tokens.semantic</code>/<code>state</code> 이관 + ESLint hex 금지규칙. 차트는 <code>semantic.chart</code><br/>
            &bull; <b>타임라인 단일화</b> — Partner List(56px)/Detail(62px) 2~4중 복제·드리프트 → size prop 단일 컴포넌트<br/>
            &bull; <b>BusinessTrip 폼</b> — 평면 나열→섹션/스텝, work_type Segmented, 죽은 disabled DatePicker(<code>:1500</code>) 제거, 반복 미리보기 useWatch 반응화<br/>
            &bull; <b>피드백/빈상태 규약</b> — message/Modal/Popconfirm 혼재·무음 handleSave 통일, 공용 EmptyState 강제
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 즉시적용 실행 순서 체크리스트 ──
  {
    title: '🔬 정밀진단 · 즉시적용 실행 순서 (스프린트 체크리스트)',
    content: `
      <div style="display:grid;gap:12px">
        <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:12px">

          <div style="background:linear-gradient(135deg,#fef2f2,#fee2e2);padding:16px;border-radius:14px;border:1px solid #fca5a5">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
              <span style="background:#dc2626;color:#fff;font-weight:800;font-size:11px;padding:3px 10px;border-radius:20px">1주차 · 보안 지혈</span>
            </div>
            <div style="font-size:12px;color:#444;line-height:2">
              &#9744; secrets.yaml git 제거 + <b>전 시크릿 로테이션</b><br/>
              &#9744; JWT <code>auth.py:11</code> 폴백 제거(미설정 시 기동실패)<br/>
              &#9744; OpenSearch 9200 · postgres 5432 <code>127.0.0.1</code> 바인딩<br/>
              &#9744; HWPX/Excel 업로드 크기 상한 + 매직바이트<br/>
              &#9744; 로그인/파트너로그인 레이트리밋(cache.incr 재사용)
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#fff7ed,#ffedd5);padding:16px;border-radius:14px;border:1px solid #fdba74">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
              <span style="background:#ea580c;color:#fff;font-weight:800;font-size:11px;padding:3px 10px;border-radius:20px">2~3주차 · 가용성/안정성</span>
            </div>
            <div style="font-size:12px;color:#444;line-height:2">
              &#9744; liveness <code>/api/livez</code> 분리(DB 비의존)<br/>
              &#9744; WAL 아카이빙(wal-g/pgBackRest) → PITR<br/>
              &#9744; 파싱/생성 <code>run_in_threadpool</code> 오프로드<br/>
              &#9744; <code>storage.py</code> 지연초기화<br/>
              &#9744; Alembic 도입(부팅과 분리) · latest→불변태그 배포
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#eff6ff,#dbeafe);padding:16px;border-radius:14px;border:1px solid #93c5fd">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
              <span style="background:#2563eb;color:#fff;font-weight:800;font-size:11px;padding:3px 10px;border-radius:20px">1~2개월 · 구조 개선</span>
            </div>
            <div style="font-size:12px;color:#444;line-height:2">
              &#9744; 프론트 코드 스플리팅 + React Query 도입<br/>
              &#9744; 401 refresh + 전역 에러 UX<br/>
              &#9744; Partner 모바일(테이블 scroll·사이드바 Drawer)<br/>
              &#9744; main.py APIRouter/서비스 계층 분리<br/>
              &#9744; MasterPage/타임라인 공용화 · pytest 스모크
            </div>
          </div>

          <div style="background:linear-gradient(135deg,#f5f3ff,#ede9fe);padding:16px;border-radius:14px;border:1px solid #c4b5fd">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
              <span style="background:#7c3aed;color:#fff;font-weight:800;font-size:11px;padding:3px 10px;border-radius:20px">중장기</span>
            </div>
            <div style="font-size:12px;color:#444;line-height:2">
              &#9744; postgres HA/복제(또는 OCI 관리형 이관)<br/>
              &#9744; GitOps(Argo CD/Flux) 불변태그 선언 배포<br/>
              &#9744; Alertmanager + 핵심 알람규칙 · Trivy 스캔<br/>
              &#9744; <code>@sappt/ui-core</code> 공유 패키지 · hex→토큰 전량<br/>
              &#9744; 문서 동기화(main.py 규모·OpenRouter·가이드 차트)
            </div>
          </div>

        </div>

        <div style="background:#ecfdf5;padding:12px 16px;border-radius:12px;border:1px solid #6ee7b7">
          <b style="font-size:12px;color:#047857">유지 권장 (이미 우수한 부분)</b>
          <div style="font-size:11px;color:#065f46;line-height:1.8;margin-top:4px">
            maxUnavailable:0 무중단 롤링 · 이중 오프사이트 백업(OCI+B2)+실제 복원 리허설(2026-06-04) · Okta SSO 관리면 보호 · 2단계 disk_alert(쿨다운+상위볼륨) · CI 다중태깅·경로필터 매트릭스 · 디자인 토큰 SSOT(<code>theme/tokens.ts</code>) · 권한기반 동적 메뉴
          </div>
        </div>
        <div style="font-size:10.5px;color:#94a3b8;text-align:right">SAPPT 전문가 정밀진단 · 2026-07-06 · 인프라(SRE) / 백엔드 아키텍처 / 프론트·UIUX 3개 관점 통합</div>
      </div>
    `,
  },
  // ── Slide: 고도화 Phase 1 — 안정화 & 보안 ──
  {
    title: 'Phase 1 — 코드 안정화 & 보안 강화 (Q2 2026)',
    content: `
      <div style="display:grid;gap:16px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#ecfdf510,#d1fae510);padding:18px;border-radius:14px;border:1px solid #6ee7b7">
            <h4 style="color:#059669;margin:0 0 10px;font-size:14px">1-1. Backend 모듈화 (2주)</h4>
            <div style="font-size:12px;color:#444;line-height:1.8">
              <b>현재:</b> main.py 1,800+ 줄 단일 파일<br/>
              <b>목표:</b> FastAPI APIRouter 기반 모듈 분리
            </div>
            <div style="background:#f0fdf4;padding:10px;border-radius:8px;margin-top:10px;font-family:monospace;font-size:11px;line-height:1.7">
              app/routers/<br/>
              &nbsp;&nbsp;auth.py &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;# 인증 + /api/auth/me<br/>
              &nbsp;&nbsp;protocols.py &nbsp;&nbsp;# 계획서 CRUD + HWPX<br/>
              &nbsp;&nbsp;schedules.py &nbsp;&nbsp;# 일정 CRUD + Excel + bulk<br/>
              &nbsp;&nbsp;master.py &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;# 마스터 4종 CRUD<br/>
              &nbsp;&nbsp;permissions.py &nbsp;# RBAC + 메뉴순서 API<br/>
              &nbsp;&nbsp;users.py &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;# 사용자 관리<br/>
              &nbsp;&nbsp;partner.py &nbsp;&nbsp;&nbsp;&nbsp;# 파트너 전용<br/>
              &nbsp;&nbsp;monitoring.py &nbsp;&nbsp;# 서버상태 + 로그<br/>
              app/services/<br/>
              &nbsp;&nbsp;hwpx_service.py &nbsp;# HWPX 생성/파싱 통합<br/>
              &nbsp;&nbsp;excel_service.py &nbsp;# Excel 임포트/익스포트<br/>
              &nbsp;&nbsp;activity_service.py # 활동이력 헬퍼
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#eff6ff10,#dbeafe10);padding:18px;border-radius:14px;border:1px solid #93c5fd">
            <h4 style="color:#2563eb;margin:0 0 10px;font-size:14px">1-2. Alembic 마이그레이션 (1주)</h4>
            <div style="font-size:12px;color:#444;line-height:1.8">
              <b>현재:</b> create_all() + 수동 ALTER TABLE<br/>
              <b>목표:</b> Alembic 버전 관리 도입
            </div>
            <div style="background:#eff6ff;padding:10px;border-radius:8px;margin:10px 0;font-family:monospace;font-size:11px;line-height:1.7">
              pip install alembic<br/>
              alembic init migrations<br/>
              alembic revision --autogenerate -m "baseline"<br/>
              alembic upgrade head<br/>
              # docker-compose entrypoint:<br/>
              alembic upgrade head && uvicorn app.main:app
            </div>
            <h4 style="color:#2563eb;margin:10px 0;font-size:14px">1-3. 환경변수 보안 분리 (3일)</h4>
            <div style="background:#eff6ff;padding:10px;border-radius:8px;font-family:monospace;font-size:11px;line-height:1.7">
              # .env (gitignore)<br/>
              DATABASE_URL=postgresql://...<br/>
              JWT_SECRET_KEY=&#36;{openssl rand -hex 32}<br/>
              JWT_EXPIRE_MINUTES=480<br/>
              ADMIN_DEFAULT_PW=&#36;{강한_비밀번호}
            </div>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:#fff7ed;padding:16px;border-radius:12px;border:1px solid #fdba74">
            <h4 style="color:#ea580c;margin:0 0 8px;font-size:14px">1-4. 테스트 자동화 (2주)</h4>
            <div style="display:grid;gap:8px;font-size:12px">
              <div style="background:#fff;padding:10px;border-radius:8px;border-left:3px solid #16a34a">
                <b style="color:#16a34a">Unit Test (pytest)</b>
                <div style="color:#555;margin-top:2px">모델/스키마 검증, HWPX 파싱, 권한 캐시 로직, 유틸리티 함수 (40+ cases)</div>
              </div>
              <div style="background:#fff;padding:10px;border-radius:8px;border-left:3px solid #2563eb">
                <b style="color:#2563eb">Integration Test (httpx + TestClient)</b>
                <div style="color:#555;margin-top:2px">70+ API 엔드포인트 CRUD, RBAC 권한 검증, Excel/HWPX 업로드, 파트너 데이터 격리</div>
              </div>
              <div style="background:#fff;padding:10px;border-radius:8px;border-left:3px solid #7c3aed">
                <b style="color:#7c3aed">E2E Test (Playwright)</b>
                <div style="color:#555;margin-top:2px">로그인 &rarr; 계획서 등록 &rarr; HWPX 다운로드, 역할별 메뉴 접근 테스트, 메뉴 순서 변경</div>
              </div>
            </div>
          </div>
          <div style="background:#faf5ff;padding:16px;border-radius:12px;border:1px solid #d8b4fe">
            <h4 style="color:#7c3aed;margin:0 0 8px;font-size:14px">1-5. CI/CD 파이프라인 (1주)</h4>
            <div style="font-size:12px;color:#444;line-height:1.8">
              <div style="background:#fff;padding:10px;border-radius:8px;font-family:monospace;font-size:11px;line-height:1.7;margin-bottom:8px">
                # .github/workflows/deploy.yml<br/>
                on: push (main branch)<br/>
                jobs:<br/>
                &nbsp;&nbsp;test: pytest + lint<br/>
                &nbsp;&nbsp;build: docker build + push<br/>
                &nbsp;&nbsp;deploy: ssh + docker compose pull
              </div>
              <b>배포 전략:</b><br/>
              &bull; main push &rarr; 자동 테스트 &rarr; 이미지 빌드 &rarr; 서버 배포<br/>
              &bull; 실패 시 자동 롤백 (이전 이미지 복원)<br/>
              &bull; Slack/Discord 배포 결과 알림
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 고도화 Phase 2 — 업무 고도화 ──
  {
    title: 'Phase 2 — 핵심 업무 고도화 (Q3 2026)',
    content: `
      <div style="display:grid;gap:16px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#fce7f310,#fdf2f810);padding:18px;border-radius:14px;border:1px solid #f9a8d4">
            <h4 style="color:#db2777;margin:0 0 10px;font-size:14px">2-1. 승인 워크플로우 (3주)</h4>
            <div style="font-size:12px;color:#444;line-height:1.8">
              시험계획서의 상태 머신 기반 승인 프로세스 구현
            </div>
            <div style="background:#fff;border-radius:8px;padding:12px;margin-top:10px;text-align:center;border:1px solid #e2e8f0">
              <div style="display:flex;align-items:center;justify-content:center;gap:6px;font-size:11px;flex-wrap:wrap">
                <span style="background:#e2e8f0;padding:4px 10px;border-radius:6px">작성중</span><span style="color:#94a3b8">&rarr;</span>
                <span style="background:#fef3c7;padding:4px 10px;border-radius:6px">검토요청</span><span style="color:#94a3b8">&rarr;</span>
                <span style="background:#dbeafe;padding:4px 10px;border-radius:6px">검토중</span><span style="color:#94a3b8">&rarr;</span>
                <span style="background:#dcfce7;padding:4px 10px;border-radius:6px;font-weight:600">승인</span>
              </div>
              <div style="font-size:10px;color:#94a3b8;margin-top:6px">반려 시 작성중으로 복귀 + 반려 사유 기록</div>
            </div>
            <div style="font-size:11px;color:#555;margin-top:10px;line-height:1.8">
              <b>구현 항목:</b><br/>
              &bull; Protocol 모델에 status 필드 + 상태 전이 규칙<br/>
              &bull; 승인 이력 테이블 (approval_history)<br/>
              &bull; 역할별 승인 권한 (REVIEWER, APPROVER)<br/>
              &bull; 승인/반려 시 알림 발송
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fef3c710,#fff7ed10);padding:18px;border-radius:14px;border:1px solid #fdba74">
            <h4 style="color:#ea580c;margin:0 0 10px;font-size:14px">2-2. 조사 결과 데이터 입력 (3주)</h4>
            <div style="font-size:12px;color:#444;line-height:1.8">
              현장 조사 데이터를 직접 시스템에 입력하는 모듈
            </div>
            <div style="font-size:11px;color:#555;margin-top:10px;line-height:1.8">
              <b>신규 테이블:</b><br/>
              &bull; <code>survey_results</code> &mdash; 조사회차별 측정 데이터<br/>
              &bull; <code>survey_result_items</code> &mdash; 항목별 수치 (발병률, 방제가, 약해도)<br/>
              &bull; <code>survey_photos</code> &mdash; 현장 사진 첨부<br/><br/>
              <b>UI 기능:</b><br/>
              &bull; 조사 회차별 데이터 그리드 (시험구 &times; 측정항목)<br/>
              &bull; 통계 자동 계산 (평균, 표준편차, 방제가%)<br/>
              &bull; 이전 회차 대비 변화 추이 차트<br/>
              &bull; 모바일 터치 최적화 입력 폼
            </div>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#eef2ff10,#e0e7ff10);padding:18px;border-radius:14px;border:1px solid #a5b4fc">
            <h4 style="color:#4f46e5;margin:0 0 10px;font-size:14px">2-3. 자동 알림 시스템 (2주)</h4>
            <div style="font-size:11px;color:#555;line-height:1.8">
              <b>트리거 조건:</b><br/>
              &bull; 처리/조사 일정 D-3, D-1 사전 알림<br/>
              &bull; 계획서 승인/반려 시 관련자 알림<br/>
              &bull; 파트너사 시험 진행상태 변경 시 알림<br/>
              &bull; 성적서 완료 시 의뢰사 알림<br/><br/>
              <b>채널 (우선순위 순):</b><br/>
              &bull; <span style="background:#dcfce7;padding:2px 6px;border-radius:4px">1순위</span> 시스템 내 알림 (기존 Notification 확장)<br/>
              &bull; <span style="background:#dbeafe;padding:2px 6px;border-radius:4px">2순위</span> 이메일 (SMTP / AWS SES)<br/>
              &bull; <span style="background:#fef3c7;padding:2px 6px;border-radius:4px">3순위</span> Telegram Bot / 카카오 알림톡
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#f0fdf410,#dcfce710);padding:18px;border-radius:14px;border:1px solid #86efac">
            <h4 style="color:#16a34a;margin:0 0 10px;font-size:14px">2-4. 성적서 자동 생성 (2주)</h4>
            <div style="font-size:11px;color:#555;line-height:1.8">
              <b>프로세스:</b><br/>
              &bull; 조사 결과 데이터 + 시험계획서 병합<br/>
              &bull; HWPX 성적서 템플릿에 자동 채움<br/>
              &bull; 테이블/차트 자동 삽입 (효과 그래프 포함)<br/><br/>
              <b>구현 세부:</b><br/>
              &bull; <code>hwpx_report_generator.py</code> 신규 모듈<br/>
              &bull; 성적서 양식 템플릿 관리 (DB 저장)<br/>
              &bull; 미리보기 &rarr; 검토 &rarr; 확정 &rarr; 다운로드 플로우<br/>
              &bull; PDF 변환 (LibreOffice headless) 옵션
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 고도화 Phase 3 — 데이터 분석 & 외부 연계 ──
  {
    title: 'Phase 3 — 데이터 분석 & 외부 연계 (Q3-Q4 2026)',
    content: `
      <div style="display:grid;gap:16px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#ede9fe10,#ddd6fe10);padding:18px;border-radius:14px;border:1px solid #a78bfa">
            <h4 style="color:#7c3aed;margin:0 0 10px;font-size:14px">3-1. 효과 분석 대시보드 (3주)</h4>
            <div style="font-size:11px;color:#555;line-height:1.8">
              <b>분석 기능:</b><br/>
              &bull; 약효 데이터 추세 분석 (연도별/약제별/작물별)<br/>
              &bull; 약제 간 효과 비교 차트 (방제가 비교)<br/>
              &bull; 통계적 유의성 검정 (ANOVA, Duncan 다중비교)<br/>
              &bull; 약해도 분석 (작물별 안전성 평가)<br/><br/>
              <b>시각화:</b><br/>
              &bull; ECharts 기반 인터랙티브 차트<br/>
              &bull; 드릴다운: 차트 클릭 &rarr; 상세 목록 연동<br/>
              &bull; KPI 카드: 완료율, 지연율, 성적서 발급율<br/>
              &bull; Excel/PDF 보고서 내보내기
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fef3c710,#fff7ed10);padding:18px;border-radius:14px;border:1px solid #fdba74">
            <h4 style="color:#ea580c;margin:0 0 10px;font-size:14px">3-2. 파일 첨부 & 시료 관리 (3주)</h4>
            <div style="font-size:11px;color:#555;line-height:1.8">
              <b>파일 관리 (OCI Object Storage):</b><br/>
              &bull; OCI Object Storage(관리형) 연동 — ap-chuncheon-1<br/>
              &bull; 시험별 사진/문서 첨부 (드래그앤드롭)<br/>
              &bull; 성적서/계획서/보고서 버전 관리<br/>
              &bull; 썸네일 자동 생성, 미리보기<br/><br/>
              <b>시료 관리:</b><br/>
              &bull; 약제 샘플 입고/보관/사용 이력 추적<br/>
              &bull; 유효기간 관리 + 만료 자동 알림<br/>
              &bull; QR 바코드 스캔 연동
            </div>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#e0f2fe10,#bae6fd10);padding:18px;border-radius:14px;border:1px solid #7dd3fc">
            <h4 style="color:#0284c7;margin:0 0 10px;font-size:14px">3-3. TIMS 연계 & 외부 API (2주)</h4>
            <div style="font-size:11px;color:#555;line-height:1.8">
              <b>TIMS (농약관리통합정보시스템):</b><br/>
              &bull; 시험 결과 데이터 자동 전송 API 연동<br/>
              &bull; 농약 품목 정보 역방향 조회<br/>
              &bull; TIMS 입력현황 자동 갱신<br/><br/>
              <b>기상청 API:</b><br/>
              &bull; 시험 포장 지역 기상 데이터 자동 수집<br/>
              &bull; 시험 기간 중 기온/강수량/습도 이력<br/>
              &bull; 기상 조건과 약효 상관 분석 기반 데이터
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fce7f310,#fdf2f810);padding:18px;border-radius:14px;border:1px solid #f9a8d4">
            <h4 style="color:#db2777;margin:0 0 10px;font-size:14px">3-4. 모바일 PWA (3주)</h4>
            <div style="font-size:11px;color:#555;line-height:1.8">
              <b>목표:</b> 현장 조사원 전용 모바일 경험<br/><br/>
              <b>PWA 기능:</b><br/>
              &bull; Service Worker + 오프라인 캐시<br/>
              &bull; 홈 화면 추가 (설치형 앱 경험)<br/>
              &bull; 오프라인 데이터 입력 &rarr; 온라인 시 동기화<br/>
              &bull; 카메라 직접 촬영 &rarr; 사진 첨부<br/>
              &bull; GPS 좌표 자동 기록<br/>
              &bull; 푸시 알림 (Web Push API)<br/><br/>
              <b>반응형 UI:</b><br/>
              &bull; Ant Design Grid 브레이크포인트 적용<br/>
              &bull; 모바일 전용 하단 네비게이션 바<br/>
              &bull; 파트너 포털 터치 최적화
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 백업 & 재해복구 정책 ──
  {
    title: '백업 & 재해복구 정책 (Backup & DR Policy)',
    component: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ArchFlow
          height={340}
          groups={[
            { id: 'g_source', label: '운영 데이터', x: 0, y: 0, w: 250, h: 290, tone: 'green' },
            { id: 'g_local', label: '로컬 백업 — 1차 (14일)', x: 310, y: 0, w: 250, h: 290, tone: 'blue' },
            { id: 'g_remote', label: '원격 이중화 — 2·3차 (60일)', x: 620, y: 20, w: 240, h: 190, tone: 'orange' },
            { id: 'g_weekly', label: '주간 스냅샷 — Tier 3 (8주)', x: 910, y: 60, w: 240, h: 110, tone: 'red' },
          ]}
          nodes={[
            { id: 'pg', group: 'g_source', x: 16, y: 40, label: 'PostgreSQL 17', sub: 'sappt DB', emoji: '🗄️', tone: 'green' },
            { id: 'oci_docs', group: 'g_source', x: 16, y: 118, label: 'OCI Object Storage', sub: 'sappt-documents', emoji: '☁️', tone: 'green' },
            { id: 'src', group: 'g_source', x: 16, y: 205, label: '소스코드', sub: '/home/ubuntu/sappt', tone: 'green' },
            { id: 'ldb', group: 'g_local', x: 16, y: 40, label: 'DB', sub: 'sappt_DATE.sql.gz', tone: 'blue' },
            { id: 'lmn', group: 'g_local', x: 16, y: 118, label: 'OCI Docs', sub: 'sappt_oci_docs.tar.gz', tone: 'blue' },
            { id: 'lsc', group: 'g_local', x: 16, y: 205, label: '소스', sub: 'sappt_source.tar.gz', tone: 'blue' },
            { id: 'oci', group: 'g_remote', x: 16, y: 40, label: 'OCI Object Storage', sub: 'ap-chuncheon-1', emoji: '☁️', tone: 'orange' },
            { id: 'b2', group: 'g_remote', x: 16, y: 115, label: 'Backblaze B2', sub: 'US West', emoji: '🔵', tone: 'blue' },
            { id: 'wdb', group: 'g_weekly', x: 16, y: 40, label: 'weekly_db.sql.gz', sub: '매주 일요일 자동', tone: 'red' },
          ]}
          edges={[
            { source: 'pg', target: 'ldb', tone: 'green', animated: true },
            { source: 'oci_docs', target: 'lmn', tone: 'green' },
            { source: 'src', target: 'lsc', tone: 'green' },
            { source: 'ldb', target: 'oci', tone: 'orange' },
            { source: 'ldb', target: 'b2', tone: 'blue' },
            { source: 'lsc', target: 'oci', tone: 'orange' },
            { source: 'lsc', target: 'b2', tone: 'blue' },
            { source: 'lmn', target: 'b2', tone: 'blue' },
            { source: 'ldb', target: 'wdb', tone: 'red', dashed: true },
          ]}
        />
        <div dangerouslySetInnerHTML={{ __html: `
      <div style="display:grid;gap:20px">

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#ecfdf5,#d1fae5);padding:20px;border-radius:16px;border:1px solid #6ee7b7">
            <h4 style="color:#059669;margin:0 0 10px">백업 실행 스크립트 (현행)</h4>
            <div style="background:#064e3b;color:#a7f3d0;padding:14px;border-radius:10px;font-family:monospace;font-size:11px;line-height:2">
              # /home/ubuntu/sappt/backup/db_backup.sh<br/>
              # 3시간 간격 자동 실행 (cron)<br/>
              <br/>
              # 1. PostgreSQL Full Dump<br/>
              docker exec sappt-postgres pg_dump \\<br/>
              &nbsp;&nbsp;-U sappt -d sappt --no-owner \\<br/>
              &nbsp;&nbsp;| gzip &gt; sappt_DATE.sql.gz<br/>
              <br/>
              # 2. OCI Object Storage 목록 백업 (메타데이터)<br/>
              oci os object list --bucket-name sappt-documents \\<br/>
              &nbsp;&nbsp;| gzip &gt; sappt_oci_docs_DATE.json.gz<br/>
              <br/>
              # 3. OCI + B2 원격 업로드<br/>
              oci os object put ... --file sappt_DATE.sql.gz<br/>
              rclone copy sappt_DATE.sql.gz b2:sappt-backup/
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fef2f2,#fecaca);padding:20px;border-radius:16px;border:1px solid #f87171">
            <h4 style="color:#dc2626;margin:0 0 10px">복구 절차 (Disaster Recovery)</h4>
            <ol style="margin:0;padding-left:20px;font-size:12px;color:#444;line-height:2.2">
              <li><b>장애 범위 파악</b> &mdash; lab.sappt.co.kr/monitoring</li>
              <li><b>로컬 백업 확인</b><br/><code style="background:#fee2e2;padding:2px 6px;border-radius:4px;font-size:11px">ls /home/ubuntu/sappt/backup/*.sql.gz</code></li>
              <li><b>OCI/B2 다운로드</b> (로컬 없을 시)<br/><code style="background:#fee2e2;padding:2px 6px;border-radius:4px;font-size:11px">rclone copy b2:sappt-backup/daily/ ./restore/</code></li>
              <li><b>DB 복원</b><br/><code style="background:#fee2e2;padding:2px 6px;border-radius:4px;font-size:11px">gunzip -c sappt_DATE.sql.gz | docker exec -i sappt-postgres psql -U sappt -d sappt</code></li>
              <li><b>서비스 재시작</b><br/><code style="background:#fee2e2;padding:2px 6px;border-radius:4px;font-size:11px">docker compose up -d &amp;&amp; ./deploy.sh all</code></li>
              <li><b>정합성 검증</b> &mdash; 마스터/일정 건수 확인</li>
              <li><b>서비스 확인</b> &mdash; lab / partner / api 정상 응답</li>
            </ol>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px">
          <div style="background:#f0fdf4;padding:14px;border-radius:12px;text-align:center;border:1px solid #86efac">
            <div style="font-size:11px;color:#888">Full Backup</div>
            <div style="font-size:20px;font-weight:800;color:#16a34a">3시간</div>
            <div style="font-size:11px;color:#555">DB+소스코드 | 14일</div>
          </div>
          <div style="background:#eff6ff;padding:14px;border-radius:12px;text-align:center;border:1px solid #93c5fd">
            <div style="font-size:11px;color:#888">Weekly</div>
            <div style="font-size:20px;font-weight:800;color:#2563eb">Snapshot</div>
            <div style="font-size:11px;color:#555">매주 일요일 | 8주</div>
          </div>
          <div style="background:#fef3c7;padding:14px;border-radius:12px;text-align:center;border:1px solid #fbbf24">
            <div style="font-size:11px;color:#888">Remote</div>
            <div style="font-size:20px;font-weight:800;color:#d97706">OCI + B2</div>
            <div style="font-size:11px;color:#555">이중화 | 60일</div>
          </div>
          <div style="background:#fce7f3;padding:14px;border-radius:12px;text-align:center;border:1px solid #f9a8d4">
            <div style="font-size:11px;color:#888">목표 RTO / RPO</div>
            <div style="font-size:20px;font-weight:800;color:#db2777">30분 / 3h</div>
            <div style="font-size:11px;color:#555">복구 시간 / 데이터 손실</div>
          </div>
        </div>
      </div>
` }} />
      </div>
    ),
  },
  // ── Slide: 백업 정책 현행 운영 현황 ──
  {
    title: '백업 정책 운영 현황 (현행 적용, 2026.05~)',
    content: `
      <div style="display:grid;gap:18px">

        <!-- 2-Tier 구조 요약 (Tier 1 → Tier 2 통합) -->
        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px">
          <div style="background:linear-gradient(135deg,#eff6ff,#dbeafe);padding:16px;border-radius:14px;border:1px solid #93c5fd">
            <div style="font-size:11px;color:#1d4ed8;font-weight:700;letter-spacing:.05em;margin-bottom:6px">TIER 2 · 전체 백업 (통합 운영)</div>
            <div style="font-size:18px;font-weight:800;color:#1e3a8a;margin-bottom:6px">Full 3h</div>
            <div style="font-size:11px;color:#374151;line-height:1.8">
              📄 DB + OCI 메타 + 소스코드<br/>
              ⏰ 3시간 간격 (0·3·6·9·12·15·18·21시)<br/>
              📁 <code style="background:#dbeafe;padding:1px 5px;border-radius:3px">backup/</code><br/>
              🗑️ 14일 후 자동 삭제<br/>
              📊 06:15 일일 보고서 (Discord)
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fef3c7,#fde68a);padding:16px;border-radius:14px;border:1px solid #fbbf24">
            <div style="font-size:11px;color:#b45309;font-weight:700;letter-spacing:.05em;margin-bottom:6px">TIER 3 · 주간 스냅샷</div>
            <div style="font-size:18px;font-weight:800;color:#78350f;margin-bottom:6px">Weekly</div>
            <div style="font-size:11px;color:#374151;line-height:1.8">
              📄 DB + 소스코드 (일요일 자동)<br/>
              ⏰ 매주 일요일 전체 백업 시<br/>
              📁 <code style="background:#fde68a;padding:1px 5px;border-radius:3px">backup/weekly/</code><br/>
              🗑️ 8주(56일) 후 자동 삭제
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fff1f2,#fecdd3);padding:16px;border-radius:14px;border:1px solid #fca5a5">
            <div style="font-size:11px;color:#be123c;font-weight:700;letter-spacing:.05em;margin-bottom:6px">Discord 알림 정책</div>
            <div style="font-size:18px;font-weight:800;color:#9f1239;margin-bottom:6px">알림 통합</div>
            <div style="font-size:11px;color:#374151;line-height:1.8">
              🔴 오류 즉시 알림 (컨테이너·pg_dump)<br/>
              📊 06:15 백업 일일 보고서 (육하원칙)<br/>
              🛡️ 06:30 보안 일일 보고서 (Fail2Ban)<br/>
              ✅ 성공 알림 → 보고서로 통합
            </div>
          </div>
        </div>

        <!-- 원격 이중화 -->
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
          <div style="background:linear-gradient(135deg,#f0f9ff,#e0f2fe);padding:16px;border-radius:14px;border:1px solid #7dd3fc">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px">
              <span style="font-size:20px">☁️</span>
              <div>
                <div style="font-weight:800;color:#0369a1;font-size:13px">OCI Object Storage</div>
                <div style="font-size:10px;color:#0284c7">ap-chuncheon-1 (춘천) · 무료 20GB</div>
              </div>
            </div>
            <div style="font-size:11px;color:#374151;line-height:2">
              📦 백업: <b>sappt-backup</b> 버킷 (DB·소스·주간 스냅샷)<br/>
              📦 운영: <b>sappt-documents</b> 버킷 (문서 스토리지)<br/>
              📂 경로: <code style="background:#e0f2fe;padding:1px 5px;border-radius:3px">daily/YYYY/MM/</code> · <code style="background:#e0f2fe;padding:1px 5px;border-radius:3px">weekly/</code><br/>
              🧹 <b>20GB 한도 · 70% 초과 시 자동 정리</b> (cleanup 스크립트)<br/>
              🔗 콘솔: cloud.oracle.com → Storage → Object Storage → Buckets
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#eff6ff,#dbeafe);padding:16px;border-radius:14px;border:1px solid #6366f1">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px">
              <span style="font-size:20px">🔵</span>
              <div>
                <div style="font-weight:800;color:#4338ca;font-size:13px">Backblaze B2</div>
                <div style="font-size:10px;color:#6366f1">US West (us-west-004) · 무료 10GB</div>
              </div>
            </div>
            <div style="font-size:11px;color:#374151;line-height:2">
              📦 대상: DB + 소스코드 + 주간스냅샷<br/>
              📂 경로: <code style="background:#dbeafe;padding:1px 5px;border-radius:3px">daily/YYYY/MM/</code> · <code style="background:#dbeafe;padding:1px 5px;border-radius:3px">weekly/</code><br/>
              🧹 <b>10GB 한도 · 70% 초과 시 자동 정리</b> (cleanup 스크립트)<br/>
              ⚠️ 각 파일 독립 업로드 (장애 격리)
            </div>
          </div>
        </div>

        <!-- 크론탭 스케줄 + 복구 -->
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
          <div style="background:#1e293b;border-radius:12px;padding:16px">
            <div style="color:#94a3b8;font-size:10px;font-weight:700;margin-bottom:10px">📅 CRONTAB (KST 기준)</div>
            <div style="font-family:monospace;font-size:11px;color:#e2e8f0;line-height:2">
              <span style="color:#64748b"># Tier 2: 전체 백업 — 3시간 간격</span><br/>
              <span style="color:#7dd3fc">0 */3 * * *</span>&nbsp;&nbsp; <span style="color:#a7f3d0">db_backup.sh</span><br/>
              <br/>
              <span style="color:#64748b"># 백업 일일 보고서 — 06:15 KST</span><br/>
              <span style="color:#7dd3fc">15 6 * * *</span>&nbsp;&nbsp; <span style="color:#fbbf24">backup-daily-report.sh</span><br/>
              <br/>
              <span style="color:#64748b"># 보안 일일 보고서 — 06:30 KST</span><br/>
              <span style="color:#7dd3fc">30 6 * * *</span>&nbsp;&nbsp; <span style="color:#f87171">fail2ban-daily-report.sh</span><br/>
              <br/>
              <span style="color:#64748b"># OCI 캐시 갱신 — 10분 주기</span><br/>
              <span style="color:#7dd3fc">*/10 * * * *</span> <span style="color:#a7f3d0">update_oci_cache.sh</span>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fef2f2,#fecaca);padding:16px;border-radius:14px;border:1px solid #f87171">
            <div style="font-weight:700;color:#dc2626;font-size:12px;margin-bottom:10px">🆘 복구 절차</div>
            <ol style="margin:0;padding-left:18px;font-size:11px;color:#444;line-height:2.2">
              <li><b>장애 범위 파악</b> — lab.sappt.co.kr/monitoring</li>
              <li><b>로컬 백업 확인</b><br/>
                <code style="background:#fee2e2;padding:1px 5px;border-radius:3px;font-size:10px">ls /home/ubuntu/sappt/backup/*.sql.gz</code></li>
              <li><b>OCI/B2 다운로드</b> (로컬 없을 시)<br/>
                <code style="background:#fee2e2;padding:1px 5px;border-radius:3px;font-size:10px">rclone copy b2:sappt-backup/daily/ ./restore/</code></li>
              <li><b>DB 복원</b><br/>
                <code style="background:#fee2e2;padding:1px 5px;border-radius:3px;font-size:10px">gunzip -c sappt_DATE.sql.gz | docker exec -i sappt-postgres psql -U sappt -d sappt</code></li>
              <li><b>서비스 재시작</b><br/>
                <code style="background:#fee2e2;padding:1px 5px;border-radius:3px;font-size:10px">docker compose up -d && ./deploy.sh all</code></li>
              <li><b>정합성 검증</b> — 마스터/일정 건수 확인</li>
            </ol>
          </div>
        </div>

        <!-- KPI 요약 -->
        <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:10px">
          <div style="background:#f0fdf4;padding:12px;border-radius:10px;text-align:center;border:1px solid #86efac">
            <div style="font-size:10px;color:#888">로컬 보관</div>
            <div style="font-size:18px;font-weight:800;color:#16a34a">14일</div>
            <div style="font-size:10px;color:#555">Full Daily</div>
          </div>
          <div style="background:#eff6ff;padding:12px;border-radius:10px;text-align:center;border:1px solid #93c5fd">
            <div style="font-size:10px;color:#888">주간 보관</div>
            <div style="font-size:18px;font-weight:800;color:#2563eb">8주</div>
            <div style="font-size:10px;color:#555">Weekly Snapshot</div>
          </div>
          <div style="background:#fef3c7;padding:12px;border-radius:10px;text-align:center;border:1px solid #fbbf24">
            <div style="font-size:10px;color:#888">원격 용량 관리</div>
            <div style="font-size:18px;font-weight:800;color:#d97706">70%</div>
            <div style="font-size:10px;color:#555">초과 시 자동 정리</div>
          </div>
          <div style="background:#fdf4ff;padding:12px;border-radius:10px;text-align:center;border:1px solid #d8b4fe">
            <div style="font-size:10px;color:#888">이중화</div>
            <div style="font-size:18px;font-weight:800;color:#7c3aed">2곳</div>
            <div style="font-size:10px;color:#555">Korea + US West</div>
          </div>
          <div style="background:#fff7ed;padding:12px;border-radius:10px;text-align:center;border:1px solid #fdba74">
            <div style="font-size:10px;color:#888">추가 비용</div>
            <div style="font-size:18px;font-weight:800;color:#ea580c">$0</div>
            <div style="font-size:10px;color:#555">무료 한도 이내</div>
          </div>
        </div>

      </div>
    `,
  },
  // ── Slide: 성능 최적화 전략 ──
  {
    title: '성능 최적화 전략 (Performance Optimization)',
    content: `
      <div style="display:grid;gap:20px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#ecfdf5,#d1fae5);padding:20px;border-radius:16px;border:1px solid #6ee7b7">
            <h4 style="color:#059669;margin:0 0 12px">Backend 성능 최적화</h4>
            <div style="display:grid;gap:8px;font-size:12px">
              <div style="background:#fff;padding:10px;border-radius:8px;border-left:3px solid #22c55e">
                <b>DB 쿼리 최적화</b>
                <p style="margin:4px 0 0;color:#555">N+1 문제 해결: joinedload / selectinload 적용. 현재 일정 목록 조회 시 마스터 테이블 개별 조인 &rarr; eager loading으로 쿼리 수 1/N 감소</p>
              </div>
              <div style="background:#fff;padding:10px;border-radius:8px;border-left:3px solid #22c55e">
                <b>인덱스 전략</b>
                <p style="margin:4px 0 0;color:#555">복합 인덱스: (test_year, category_id), (company_id, status). 커버링 인덱스로 목록 조회 성능 향상</p>
              </div>
              <div style="background:#fff;padding:10px;border-radius:8px;border-left:3px solid #22c55e">
                <b>커넥션 풀링</b>
                <p style="margin:4px 0 0;color:#555">SQLAlchemy pool_size=10, max_overflow=20 설정. 동시 접속자 증가 대비</p>
              </div>
              <div style="background:#fff;padding:10px;border-radius:8px;border-left:3px solid #22c55e">
                <b>비동기 처리</b>
                <p style="margin:4px 0 0;color:#555">HWPX 생성/파싱, Excel 처리를 BackgroundTask로 전환. 대용량 파일 처리 시 응답 지연 방지</p>
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#eff6ff,#dbeafe);padding:20px;border-radius:16px;border:1px solid #93c5fd">
            <h4 style="color:#2563eb;margin:0 0 12px">Frontend 성능 최적화</h4>
            <div style="display:grid;gap:8px;font-size:12px">
              <div style="background:#fff;padding:10px;border-radius:8px;border-left:3px solid #3b82f6">
                <b>코드 스플리팅</b>
                <p style="margin:4px 0 0;color:#555">React.lazy + Suspense로 페이지별 지연 로딩. 초기 번들 사이즈 40% 절감 예상 (현재 ~800KB &rarr; ~480KB)</p>
              </div>
              <div style="background:#fff;padding:10px;border-radius:8px;border-left:3px solid #3b82f6">
                <b>ECharts 지연 로드</b>
                <p style="margin:4px 0 0;color:#555">대시보드 차트 라이브러리 동적 import. 로그인/목록 페이지 로딩 속도 개선</p>
              </div>
              <div style="background:#fff;padding:10px;border-radius:8px;border-left:3px solid #3b82f6">
                <b>API 캐싱</b>
                <p style="margin:4px 0 0;color:#555">마스터 데이터 localStorage 캐싱 (TTL 5분). 필터 옵션 반복 호출 제거</p>
              </div>
              <div style="background:#fff;padding:10px;border-radius:8px;border-left:3px solid #3b82f6">
                <b>Gzip + Brotli 압축</b>
                <p style="margin:4px 0 0;color:#555">Nginx gzip_static on; 정적 파일 사전 압축. 네트워크 전송량 70% 절감</p>
              </div>
            </div>
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#fff7ed,#ffedd5);padding:20px;border-radius:16px;border:1px solid #fdba74">
          <h4 style="color:#ea580c;margin:0 0 12px">성능 벤치마크 목표</h4>
          <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:12px;text-align:center">
            <div style="background:#fff;padding:14px;border-radius:10px">
              <div style="font-size:11px;color:#888">API 응답시간</div>
              <div style="font-size:24px;font-weight:800;color:#ea580c">&lt; 200ms</div>
              <div style="font-size:10px;color:#aaa">목록 조회 P95</div>
            </div>
            <div style="background:#fff;padding:14px;border-radius:10px">
              <div style="font-size:11px;color:#888">초기 로딩</div>
              <div style="font-size:24px;font-weight:800;color:#ea580c">&lt; 2s</div>
              <div style="font-size:10px;color:#aaa">FCP (First Contentful Paint)</div>
            </div>
            <div style="background:#fff;padding:14px;border-radius:10px">
              <div style="font-size:11px;color:#888">번들 사이즈</div>
              <div style="font-size:24px;font-weight:800;color:#ea580c">&lt; 500KB</div>
              <div style="font-size:10px;color:#aaa">gzip 압축 후</div>
            </div>
            <div style="background:#fff;padding:14px;border-radius:10px">
              <div style="font-size:11px;color:#888">HWPX 생성</div>
              <div style="font-size:24px;font-weight:800;color:#ea580c">&lt; 3s</div>
              <div style="font-size:10px;color:#aaa">문서 생성 + 다운로드</div>
            </div>
            <div style="background:#fff;padding:14px;border-radius:10px">
              <div style="font-size:11px;color:#888">동시 사용자</div>
              <div style="font-size:24px;font-weight:800;color:#ea580c">50+</div>
              <div style="font-size:10px;color:#aaa">동시 접속 처리</div>
            </div>
          </div>
        </div>

        <div style="background:#f0f9ff;padding:16px;border-radius:12px;border:1px solid #7dd3fc">
          <h4 style="color:#0284c7;margin:0 0 8px">Nginx 캐싱 설정 (권장)</h4>
          <div style="background:#0c4a6e;color:#bae6fd;padding:14px;border-radius:10px;font-family:monospace;font-size:11px;line-height:1.8">
            # 정적 파일 캐싱 (frontend nginx.conf)<br/>
            location ~* \\.(js|css|png|jpg|svg|ico|woff2)$ &#123;<br/>
            &nbsp;&nbsp;expires 30d;<br/>
            &nbsp;&nbsp;add_header Cache-Control "public, immutable";<br/>
            &nbsp;&nbsp;gzip_static on;<br/>
            &#125;<br/>
            <br/>
            # API 응답 캐싱 (마스터 데이터)<br/>
            location /api/master/ &#123;<br/>
            &nbsp;&nbsp;proxy_cache sappt_cache;<br/>
            &nbsp;&nbsp;proxy_cache_valid 200 5m;<br/>
            &nbsp;&nbsp;proxy_pass http://backend:8000;<br/>
            &#125;
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: UX/UI 디자인 가이드 ──
  {
    title: 'UX/UI 디자인 전략 (Design System)',
    content: `
      <div style="display:grid;gap:20px">
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#eef2ff,#e0e7ff);padding:20px;border-radius:16px;border:1px solid #a5b4fc">
            <h4 style="color:#4338ca;margin:0 0 10px">Admin Portal</h4>
            <div style="display:flex;gap:6px;margin-bottom:12px">
              <div style="width:28px;height:28px;border-radius:8px;background:#4f46e5" title="#4f46e5"></div>
              <div style="width:28px;height:28px;border-radius:8px;background:#6366f1" title="#6366f1"></div>
              <div style="width:28px;height:28px;border-radius:8px;background:#818cf8" title="#818cf8"></div>
              <div style="width:28px;height:28px;border-radius:8px;background:#f8fafc" title="#f8fafc"></div>
            </div>
            <ul style="margin:0;padding-left:16px;font-size:11px;color:#555;line-height:2">
              <li>Primary: <b>Indigo (#4f46e5)</b></li>
              <li>Font: Pretendard 14px</li>
              <li>전문적이고 신뢰감 있는 톤</li>
              <li>탭 기반 멀티태스킹 레이아웃</li>
            </ul>
          </div>
          <div style="background:linear-gradient(135deg,#fffbeb,#fef3c7);padding:20px;border-radius:16px;border:1px solid #fbbf24">
            <h4 style="color:#d97706;margin:0 0 10px">Partner Portal</h4>
            <div style="display:flex;gap:6px;margin-bottom:12px">
              <div style="width:28px;height:28px;border-radius:8px;background:#f59e0b" title="#f59e0b"></div>
              <div style="width:28px;height:28px;border-radius:8px;background:#d97706" title="#d97706"></div>
              <div style="width:28px;height:28px;border-radius:8px;background:#fbbf24" title="#fbbf24"></div>
              <div style="width:28px;height:28px;border-radius:8px;background:#fffbeb" title="#fffbeb"></div>
            </div>
            <ul style="margin:0;padding-left:16px;font-size:11px;color:#555;line-height:2">
              <li>Primary: <b>Amber (#f59e0b)</b></li>
              <li>따뜻하고 접근성 높은 톤</li>
              <li>읽기 중심 간결한 레이아웃</li>
              <li>모바일 친화적 카드형 UI</li>
            </ul>
          </div>
          <div style="background:linear-gradient(135deg,#f0fdf4,#dcfce7);padding:20px;border-radius:16px;border:1px solid #86efac">
            <h4 style="color:#16a34a;margin:0 0 10px">공통 디자인 원칙</h4>
            <ul style="margin:0;padding-left:16px;font-size:11px;color:#555;line-height:2">
              <li><b>일관성:</b> Ant Design 테마 토큰</li>
              <li><b>접근성:</b> WCAG 2.1 AA 기준</li>
              <li><b>반응형:</b> 1200/992/768 bp</li>
              <li><b>상태 색상:</b> 진행중(Blue), 완료(Green), 대기(Orange), 중단(Red)</li>
              <li><b>여백:</b> 밀도 키트 v3 &mdash; 층 두께를 <code>theme</code> 토큰이 정한다 (Content 8 &middot; 카드 본문 10 &middot; 표 셀 4/8)</li>
            </ul>
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#faf5ff,#ede9fe);padding:20px;border-radius:16px;border:1px solid #c4b5fd">
          <h4 style="color:#7c3aed;margin:0 0 12px">UX 개선 방안</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
            <div style="display:grid;gap:8px;font-size:12px">
              <div style="background:#fff;padding:12px;border-radius:8px;display:flex;align-items:flex-start;gap:10px">
                <div style="min-width:32px;height:32px;background:#dbeafe;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:16px">&#128269;</div>
                <div>
                  <b style="color:#2563eb">글로벌 검색</b>
                  <p style="margin:2px 0 0;color:#555;font-size:11px">Ctrl+K 단축키로 시험번호/항목명/의뢰사 통합 검색. 전체 메뉴 접근 없이 빠른 탐색</p>
                </div>
              </div>
              <div style="background:#fff;padding:12px;border-radius:8px;display:flex;align-items:flex-start;gap:10px">
                <div style="min-width:32px;height:32px;background:#dcfce7;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:16px">&#128190;</div>
                <div>
                  <b style="color:#16a34a">자동 저장</b>
                  <p style="margin:2px 0 0;color:#555;font-size:11px">시험계획서 작성 중 30초 간격 자동 임시 저장. 브라우저 종료/새로고침 시 복원 기능</p>
                </div>
              </div>
              <div style="background:#fff;padding:12px;border-radius:8px;display:flex;align-items:flex-start;gap:10px">
                <div style="min-width:32px;height:32px;background:#fef3c7;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:16px">&#127912;</div>
                <div>
                  <b style="color:#d97706">다크 모드</b>
                  <p style="margin:2px 0 0;color:#555;font-size:11px">Ant Design ConfigProvider theme 전환. 야간/저조도 환경 사용성 향상. 시스템 설정 연동</p>
                </div>
              </div>
            </div>
            <div style="display:grid;gap:8px;font-size:12px">
              <div style="background:#fff;padding:12px;border-radius:8px;display:flex;align-items:flex-start;gap:10px">
                <div style="min-width:32px;height:32px;background:#fce7f3;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:16px">&#128241;</div>
                <div>
                  <b style="color:#db2777">모바일 반응형</b>
                  <p style="margin:2px 0 0;color:#555;font-size:11px">파트너 포털: 현장 사용 최적화. 테이블 &rarr; 카드뷰 자동 전환. 터치 친화적 버튼 사이즈(44px+)</p>
                </div>
              </div>
              <div style="background:#fff;padding:12px;border-radius:8px;display:flex;align-items:flex-start;gap:10px">
                <div style="min-width:32px;height:32px;background:#e0f2fe;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:16px">&#128232;</div>
                <div>
                  <b style="color:#0284c7">토스트 &amp; 피드백</b>
                  <p style="margin:2px 0 0;color:#555;font-size:11px">저장/삭제/업로드 결과 즉시 피드백. 로딩 스켈레톤으로 체감 속도 향상. 에러 시 구체적 안내 메시지</p>
                </div>
              </div>
              <div style="background:#fff;padding:12px;border-radius:8px;display:flex;align-items:flex-start;gap:10px">
                <div style="min-width:32px;height:32px;background:#ede9fe;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:16px">&#9999;&#65039;</div>
                <div>
                  <b style="color:#7c3aed">인라인 편집</b>
                  <p style="margin:2px 0 0;color:#555;font-size:11px">테이블 셀 더블클릭으로 즉시 수정. 모달 없이 마스터 데이터 빠른 편집. 변경사항 하이라이트</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px">
          <div style="background:#fff;padding:16px;border-radius:12px;border:1px solid #e5e7eb;text-align:center">
            <div style="font-size:36px;margin-bottom:4px">&#9889;</div>
            <div style="font-size:18px;font-weight:800;color:#1f2937">Lighthouse 90+</div>
            <div style="font-size:11px;color:#888;margin-top:4px">Performance / Accessibility<br/>Best Practices / SEO</div>
          </div>
          <div style="background:#fff;padding:16px;border-radius:12px;border:1px solid #e5e7eb;text-align:center">
            <div style="font-size:36px;margin-bottom:4px">&#127919;</div>
            <div style="font-size:18px;font-weight:800;color:#1f2937">3-Click Rule</div>
            <div style="font-size:11px;color:#888;margin-top:4px">모든 핵심 기능에<br/>3클릭 이내 도달</div>
          </div>
          <div style="background:#fff;padding:16px;border-radius:12px;border:1px solid #e5e7eb;text-align:center">
            <div style="font-size:36px;margin-bottom:4px">&#128640;</div>
            <div style="font-size:18px;font-weight:800;color:#1f2937">Zero Training</div>
            <div style="font-size:11px;color:#888;margin-top:4px">직관적 UI로<br/>별도 교육 없이 사용</div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 운영 모니터링 & 가용성 ──
  {
    title: '운영 모니터링 & 가용성 (Operations & Availability)',
    component: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <ArchFlow
          height={400}
          groups={[
            { id: 'g_mon', label: '🔭 Monitoring Stack', x: 0, y: 0, w: 620, h: 95, tone: 'blue' },
            { id: 'g_alert', label: '🔔 Alert Channels', x: 120, y: 140, w: 380, h: 95, tone: 'amber' },
            { id: 'g_actions', label: '⚙️ Auto Actions', x: 120, y: 280, w: 380, h: 95, tone: 'green' },
          ]}
          nodes={[
            { id: 'hc', group: 'g_mon', x: 16, y: 38, label: 'readinessProbe', sub: 'Pod 상태 감시', tone: 'blue' },
            { id: 'grafana', group: 'g_mon', x: 210, y: 38, label: 'Grafana + Loki', sub: 'log.sappt.co.kr', tone: 'blue' },
            { id: 'log', group: 'g_mon', x: 400, y: 38, label: 'kubectl/docker logs', sub: '하이브리드 수집', tone: 'blue' },
            { id: 'discord', group: 'g_alert', x: 16, y: 38, label: 'Discord Webhook', sub: '백업/장애 알림', tone: 'amber' },
            { id: 'dash', group: 'g_alert', x: 200, y: 38, label: '모니터링 대시보드', sub: 'Docker + k3s', tone: 'amber' },
            { id: 'restart', group: 'g_actions', x: 16, y: 38, label: 'k3s Auto Restart', sub: 'crash → 재시작', tone: 'green' },
            { id: 'rollback', group: 'g_actions', x: 200, y: 38, label: 'Instant Rollback', sub: 'deploy.sh rollback', tone: 'green' },
          ]}
          edges={[
            { source: 'hc', target: 'discord', label: 'unhealthy', tone: 'amber' },
            { source: 'grafana', target: 'dash', label: 'error pattern', tone: 'amber' },
            { source: 'log', target: 'discord', label: 'level filter', tone: 'amber' },
            { source: 'discord', target: 'restart', tone: 'green', animated: true },
            { source: 'dash', target: 'rollback', tone: 'green', animated: true },
          ]}
        />
        <div dangerouslySetInnerHTML={{ __html: `
      <div style="display:grid;gap:20px">

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#f0f9ff,#e0f2fe);padding:20px;border-radius:16px;border:1px solid #7dd3fc">
            <h4 style="color:#0284c7;margin:0 0 10px">k3s Health Check (현재 적용)</h4>
            <div style="background:#0c4a6e;color:#bae6fd;padding:14px;border-radius:10px;font-family:monospace;font-size:11px;line-height:1.9">
              # backend Deployment<br/>
              readinessProbe:<br/>
              &nbsp;&nbsp;httpGet:<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;path: /api/storage/health<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;port: 8000<br/>
              &nbsp;&nbsp;initialDelaySeconds: 10<br/>
              &nbsp;&nbsp;periodSeconds: 5<br/>
              <br/>
              strategy:<br/>
              &nbsp;&nbsp;type: RollingUpdate<br/>
              &nbsp;&nbsp;rollingUpdate:<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;maxUnavailable: 0 &nbsp;<span style="color:#43e97b"># 무중단</span><br/>
              &nbsp;&nbsp;&nbsp;&nbsp;maxSurge: 1
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fef3c7,#fde68a);padding:20px;border-radius:16px;border:1px solid #fbbf24">
            <h4 style="color:#d97706;margin:0 0 10px">가용성 목표 (SLA)</h4>
            <div style="display:grid;gap:10px">
              <div style="background:#fff;padding:12px;border-radius:8px;display:flex;justify-content:space-between;align-items:center">
                <span style="font-size:13px"><b>Uptime</b></span>
                <span style="font-size:20px;font-weight:800;color:#16a34a">99.5%</span>
              </div>
              <div style="background:#fff;padding:12px;border-radius:8px;display:flex;justify-content:space-between;align-items:center">
                <span style="font-size:13px"><b>RTO (목표 복구 시간)</b></span>
                <span style="font-size:20px;font-weight:800;color:#d97706">30분</span>
              </div>
              <div style="background:#fff;padding:12px;border-radius:8px;display:flex;justify-content:space-between;align-items:center">
                <span style="font-size:13px"><b>RPO (목표 복구 시점)</b></span>
                <span style="font-size:20px;font-weight:800;color:#2563eb">24시간</span>
              </div>
              <div style="background:#fff;padding:12px;border-radius:8px;display:flex;justify-content:space-between;align-items:center">
                <span style="font-size:13px"><b>장애 알림 지연</b></span>
                <span style="font-size:20px;font-weight:800;color:#dc2626">&lt; 1분</span>
              </div>
              <div style="background:#fff;padding:12px;border-radius:8px;display:flex;justify-content:space-between;align-items:center">
                <span style="font-size:13px"><b>유지보수 윈도우</b></span>
                <span style="font-size:14px;font-weight:700;color:#7c3aed">일 02:00-04:00</span>
              </div>
            </div>
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#faf5ff,#ede9fe);padding:20px;border-radius:16px;border:1px solid #c4b5fd">
          <h4 style="color:#7c3aed;margin:0 0 12px">로그 관리 전략</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;font-size:12px">
            <div style="background:#fff;padding:12px;border-radius:8px">
              <b style="color:#0284c7">Application Log</b>
              <p style="margin:4px 0 0;color:#555">FastAPI 구조화 로깅 (JSON). 요청/응답 시간, 에러 스택트레이스. Docker stdout 수집</p>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px">
              <b style="color:#16a34a">Access Log</b>
              <p style="margin:4px 0 0;color:#555">Nginx access_log (combined). IP, UA, Status Code, 응답시간. NPM 프록시 로그 통합</p>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px">
              <b style="color:#d97706">Audit Log</b>
              <p style="margin:4px 0 0;color:#555">사용자 행위 추적 (CRUD). 로그인/로그아웃 기록. 데이터 변경 이력 (who/when/what)</p>
            </div>
          </div>
        </div>
      </div>
` }} />
      </div>
    ),
  },
  // ── Slide: 디스크 용량 관리 (2026-08-29 정리 실측) ──
  {
    title: '디스크 용량 관리 & 컨테이너 이미지 GC (Disk Capacity)',
    content: `
      <div style="display:grid;gap:20px">
        <div style="background:linear-gradient(135deg,#eef2ff,#e0e7ff);padding:20px;border-radius:16px;border:1px solid #a5b4fc">
          <h4 style="color:#4338ca;margin:0 0 10px">왜 찼는가 — 배포마다 이미지가 쌓인다</h4>
          <p style="margin:0 0 10px;font-size:13px;line-height:1.8;color:#312e81">
            세 프론트/백엔드 Deployment 는 모두 <b>:latest 태그 + imagePullPolicy: Always</b> 라
            배포할 때마다 새 다이제스트를 내려받는다. 그런데 containerd 는 <b>디스크 85% 부터</b> GC 를 시작하므로
            그 아래에서는 옛 이미지가 무한정 남는다. 2026-08-29 실측에서 <b>이미지 330개 중 실제 사용은 13개</b>,
            나머지가 22GB 를 점유하고 있었다.
          </p>
          <p style="margin:0;font-size:13px;line-height:1.8;color:#312e81">
            <b>롤백에는 쓰이지 않는다</b> — k8s revision 이 가리키는 이미지 참조도 똑같은 <code>:latest</code> 라
            옛 다이제스트는 롤백 경로에 등장하지 않는다. 그래서 삭제해도 <code>deploy.sh rollback</code> 은 그대로 동작한다.
            단 <b>console-frontend 만 예외</b> — GHCR 패키지가 비공개라 노드가 재pull 을 못 하므로 캐시된 이미지를 지우면 안 된다.
          </p>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:#f8fafc;padding:18px;border-radius:14px;border:1px solid #e2e8f0">
            <h4 style="color:#0f172a;margin:0 0 10px">2026-08-29 정리 실측 — 103G → 73G (54% → 38%)</h4>
            <table style="width:100%;border-collapse:collapse;font-size:12px">
              <tr style="background:#eef2ff"><th style="padding:8px;text-align:left">항목</th><th style="padding:8px;text-align:right">회수</th></tr>
              <tr><td style="padding:8px;border-top:1px solid #e2e8f0">k3s containerd 미사용 이미지 314개</td><td style="padding:8px;text-align:right;border-top:1px solid #e2e8f0"><b>19G</b></td></tr>
              <tr><td style="padding:8px;border-top:1px solid #e2e8f0">Docker 미사용 이미지 · 빌드캐시</td><td style="padding:8px;text-align:right;border-top:1px solid #e2e8f0"><b>4.7G</b></td></tr>
              <tr><td style="padding:8px;border-top:1px solid #e2e8f0">journald 아카이브 (상한 500M 설정)</td><td style="padding:8px;text-align:right;border-top:1px solid #e2e8f0"><b>1.3G</b></td></tr>
              <tr><td style="padding:8px;border-top:1px solid #e2e8f0">주간 백업 중복분 126개</td><td style="padding:8px;text-align:right;border-top:1px solid #e2e8f0"><b>1.65G</b></td></tr>
            </table>
          </div>
          <div style="background:linear-gradient(135deg,#fff7ed,#ffedd5);padding:18px;border-radius:14px;border:1px solid #fdba74">
            <h4 style="color:#c2410c;margin:0 0 10px">함께 고친 버그 — 주간 스냅샷이 주 8회</h4>
            <p style="margin:0 0 10px;font-size:13px;line-height:1.8;color:#7c2d12">
              <code>backup/db_backup.sh</code> 의 주간 스냅샷 조건이 <b>요일(일요일)만</b> 보고 있었다.
              백업은 3시간 주기라 일요일 하루에 8번 모두 복사돼 <b>주 1회가 아니라 주 8회</b>가 쌓였다
              (8주 보관 × 8배 = 144개 / 1.8GB).
            </p>
            <div style="background:#7c2d12;color:#fed7aa;padding:12px;border-radius:10px;font-family:monospace;font-size:11px;line-height:1.8">
              # before<br/>
              if [ "$DOW" = "7" ]; then<br/><br/>
              # after — 00시 실행분만<br/>
              if [ "$DOW" = "7" ] &amp;&amp; [ "$HOUR" = "00" ]; then
            </div>
          </div>
        </div>

        <div style="background:#0f172a;color:#e2e8f0;padding:18px;border-radius:14px">
          <h4 style="color:#93c5fd;margin:0 0 10px">정기 점검 절차 (디스크 60% 초과 시)</h4>
          <div style="font-family:monospace;font-size:11.5px;line-height:2">
            # 1. 무엇이 먹고 있는지<br/>
            sudo du -xh -d1 / | sort -rh | head<br/>
            docker system df<br/>
            sudo k3s crictl images -q | wc -l&nbsp;&nbsp;<span style="color:#94a3b8"># 30개 내외가 정상</span><br/><br/>
            # 2. 사용 중이 아닌 sappt 이미지 정리 (console-frontend 제외)<br/>
            <span style="color:#94a3b8"># 실행 중 이미지·:latest 태그는 crictl 이 알아서 거부하지만, 목록을 만들어 지우는 편이 안전하다</span><br/>
            sudo k3s crictl rmi &lt;미사용 이미지 ID들&gt;<br/><br/>
            # 3. Docker · 저널<br/>
            docker system prune -af --volumes<br/>
            sudo journalctl --vacuum-size=500M
          </div>
          <p style="margin:12px 0 0;font-size:12px;color:#cbd5e1">
            디스크 경보는 <code>backup/disk_alert.sh</code> 가 30분 주기로 감시한다 (WARN 80% / CRIT 90%, Discord + 알림센터).
            이번 정리 전 수치가 54% 였던 것처럼 <b>경보 임계 아래에서도 계속 증가</b>하므로, 배포가 잦은 달에는 위 1번만이라도 확인한다.
          </p>
        </div>
      </div>
    `,
  },
  // ── Slide: OCI 오브젝트 스토리지 과금 (2026-08-29 분석·조치) ──
  {
    title: 'OCI 오브젝트 스토리지 과금 구조 & 보관정책 (Object Storage Cost)',
    content: `
      <div style="display:grid;gap:20px">
        <div style="background:linear-gradient(135deg,#fef2f2,#fee2e2);padding:20px;border-radius:16px;border:1px solid #fca5a5">
          <h4 style="color:#b91c1c;margin:0 0 10px">무료 한도는 "10GB 보유"가 아니라 "월 10 GB-월 소진"이다</h4>
          <p style="margin:0 0 10px;font-size:13px;line-height:1.8;color:#7f1d1d">
            Always Free 의 Object Storage Standard 한도는 <b>월 10 GB-월</b>이고, 매일 (보유 용량 × 하루)만큼 소진된다.
            그래서 <b>누적 용량이 10GB를 넘는 날과 과금이 시작되는 날이 다르다</b> —
            2026년 실측에서 누적 10GB 통과는 7/19였지만, 실제 과금은 그 달 한도를 다 쓴 <b>8/21부터</b> 시작됐다.
          </p>
          <table style="width:100%;border-collapse:collapse;font-size:12px;background:#fff;border-radius:10px;overflow:hidden">
            <tr style="background:#fee2e2"><th style="padding:8px;text-align:left">월</th><th style="padding:8px;text-align:right">저장(GB-월)</th><th style="padding:8px;text-align:right">요청</th><th style="padding:8px;text-align:right">청구(SGD)</th></tr>
            <tr><td style="padding:7px">2026-05</td><td style="padding:7px;text-align:right">0.85</td><td style="padding:7px;text-align:right">64.8만</td><td style="padding:7px;text-align:right">0.281 <span style="color:#94a3b8">(요청)</span></td></tr>
            <tr><td style="padding:7px">2026-06</td><td style="padding:7px;text-align:right">4.20</td><td style="padding:7px;text-align:right">36.3만</td><td style="padding:7px;text-align:right">0.147 <span style="color:#94a3b8">(요청)</span></td></tr>
            <tr><td style="padding:7px">2026-07</td><td style="padding:7px;text-align:right">9.41</td><td style="padding:7px;text-align:right">0.5만</td><td style="padding:7px;text-align:right">0</td></tr>
            <tr style="background:#fef2f2"><td style="padding:7px"><b>2026-08</b></td><td style="padding:7px;text-align:right"><b>14.19</b></td><td style="padding:7px;text-align:right">0.7만</td><td style="padding:7px;text-align:right"><b>0.148 (저장)</b></td></tr>
          </table>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:#f8fafc;padding:18px;border-radius:14px;border:1px solid #e2e8f0">
            <h4 style="color:#0f172a;margin:0 0 10px">원인 — OCI 만 보관정책이 없었다</h4>
            <p style="margin:0 0 8px;font-size:13px;line-height:1.8;color:#334155">
              <code>backup/db_backup.sh</code> 는 OCI 에 <code>object put</code> 만 하고 삭제를 하지 않았다.
              로컬은 14일 정리, B2 는 별도 관리인데 <b>OCI 만 5월부터 한 번도 안 지워졌다</b>. 버킷 수명주기도 없었다.
            </p>
            <p style="margin:0;font-size:13px;line-height:1.8;color:#334155">
              그리고 <b>소스 tar 를 3시간마다</b> 올리고 있었다(평균 19.5MB × 8회/일 = 156MB/일).
              18.76GB 중 <b>소스 tar 가 16.97GB(90%)</b> 였다. 소스는 git 에도 있다.
            </p>
          </div>
          <div style="background:linear-gradient(135deg,#ecfdf5,#d1fae5);padding:18px;border-radius:14px;border:1px solid #6ee7b7">
            <h4 style="color:#047857;margin:0 0 10px">조치 (2026-08-29)</h4>
            <ol style="margin:0;padding-left:18px;font-size:13px;line-height:1.9;color:#065f46">
              <li><b>수명주기 정책</b> — <code>daily/</code> 30일 후 삭제 + 이전 버전 7일 후 정리</li>
              <li><b>소스 tar 는 1일 1회(00시)만 OCI 업로드</b> (로컬·B2 는 3시간 주기 유지)</li>
              <li><b>점검일지 PDF 는 저장하지 않고 다운로드 시 생성</b> — 원본은 DB 에 있다</li>
            </ol>
            <p style="margin:10px 0 0;font-size:12.5px;color:#065f46">
              증가율 <b>4.8 → 0.8 GB/월</b>, 회수 약 12GB → 무료 한도 안으로 복귀.
            </p>
          </div>
        </div>

        <div style="background:#fff7ed;padding:18px;border-radius:14px;border:1px solid #fdba74">
          <h4 style="color:#c2410c;margin:0 0 10px">함정 2가지 — 규칙을 만들어도 안 지워진다</h4>
          <p style="margin:0 0 8px;font-size:13px;line-height:1.8;color:#7c2d12">
            <b>① IAM 권한</b> — 수명주기 규칙은 Object Storage 서비스가 대신 실행하므로
            <code>Allow service objectstorage-&lt;region&gt; to manage object-family</code> 정책이 없으면
            <b>규칙이 만들어져도 조용히 실행되지 않는다</b>. (버킷 범위로 좁혀 <code>ObjectStorage-Lifecycle</code> 정책 생성)
          </p>
          <p style="margin:0;font-size:13px;line-height:1.8;color:#7c2d12">
            <b>② 버전관리</b> — 두 버킷 모두 버전관리가 켜져 있어 삭제해도 <b>이전 버전으로 남아 용량을 계속 차지</b>한다.
            실제로 삭제마커 700개가 0.35GB 를 붙잡고 있었다. 그래서 <b>previous-object-versions 정리 규칙을 함께</b> 넣어야 한다.
          </p>
        </div>

        <div style="background:#0f172a;color:#e2e8f0;padding:18px;border-radius:14px">
          <h4 style="color:#93c5fd;margin:0 0 10px">오브젝트 스토리지를 쓰는 곳 (보존이 필요한 것만 남긴다)</h4>
          <div style="font-size:12.5px;line-height:2">
            <b style="color:#86efac">보존 필요</b> — 시험일정 첨부문서 · 파트너 문의 첨부파일 · HWPX 템플릿(읽기) · 스토리지 관리 화면(수동 업로드/PAR)<br/>
            <b style="color:#fca5a5">보존 불필요 → 정리함</b> — 점검일지 PDF(다운로드 시 생성으로 변경)<br/>
            <b style="color:#fcd34d">상한 있음</b> — 화면테스트 캡처: 갤러리는 <code>uitests/latest/</code> 고정 키로 덮어써 늘지 않고,
            실패 캡처는 실행 이력 보관기간(기본 90일)과 함께 지워진다<br/>
            <b style="color:#93c5fd">백업</b> — <code>daily/</code> 30일 · <code>weekly/</code> 유지 · <code>tapo/</code> 는 TAPO 측 스크립트가 관리
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 신규 기능 - 활동이력 & 파트너 계정 & 모니터링 ──
  {
    title: '신규 기능 (v2.1 Update)',
    content: `
      <div style="display:grid;gap:20px">
        <div style="background:linear-gradient(135deg,#ecfdf5,#d1fae5);padding:20px;border-radius:16px;border:1px solid #6ee7b7">
          <h4 style="color:#059669;margin:0 0 12px">1. 활동이력 시스템 (Activity Log)</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;font-size:13px">
            <div style="background:#fff;padding:14px;border-radius:10px;line-height:1.8">
              <b style="color:#059669">KST 타임존</b><br/>
              모든 활동이력은 한국 표준시(KST, UTC+9) 기준으로 기록됩니다.
            </div>
            <div style="background:#fff;padding:14px;border-radius:10px;line-height:1.8">
              <b style="color:#059669">포괄적 이력 추적</b><br/>
              로그인, CRUD, HWPX 업로드/다운로드, Excel 내보내기, 마스터 관리, 페이지 이동 등 모든 사용자 행위 기록.
            </div>
          </div>
          <div style="background:#064e3b;color:#a7f3d0;padding:12px;border-radius:8px;font-family:monospace;font-size:11px;line-height:1.6;margin-top:12px">
            액션 유형: LOGIN | CREATE | UPDATE | DELETE | UPLOAD | DOWNLOAD | EXPORT | BULK_CREATE | VIEW
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#fef3c7,#fde68a);padding:20px;border-radius:16px;border:1px solid #fbbf24">
            <h4 style="color:#d97706;margin:0 0 12px">2. 파트너 계정 관리</h4>
            <ul style="color:#444;line-height:2;margin:0;padding-left:16px;font-size:13px">
              <li>의뢰사별 파트너 계정 <b>생성/수정/삭제</b></li>
              <li>의뢰사 연결 → 해당 시험건만 조회 가능</li>
              <li>partner.sappt.co.kr 에서 진행현황 확인</li>
              <li>비밀번호 개별 관리, 계정별 로그인 이력 추적</li>
            </ul>
          </div>
          <div style="background:linear-gradient(135deg,#ede9fe,#ddd6fe);padding:20px;border-radius:16px;border:1px solid #a78bfa">
            <h4 style="color:#7c3aed;margin:0 0 12px">3. 서버 모니터링</h4>
            <ul style="color:#444;line-height:2;margin:0;padding-left:16px;font-size:13px">
              <li><b>CPU</b> 사용률 및 코어 수</li>
              <li><b>메모리</b> 사용량/가용량/백분율</li>
              <li><b>디스크</b> 사용량/전체/잔여</li>
              <li><b>Docker</b> 컨테이너 상태 실시간 확인</li>
              <li><b>DB</b> 크기, 활성 연결 수</li>
              <li><b>백업</b> 마지막 백업 시점, 보관 수</li>
              <li>30초 자동 새로고침</li>
            </ul>
            <div style="margin-top:10px;background:#fff;border-radius:8px;padding:10px 12px;font-size:11.5px;color:#555;line-height:1.7">
              ⚙️ <b>Docker/k3s 접근 방식</b>: 백엔드 Pod이 호스트 <code>/run</code> 디렉토리를 <code>/host/run</code>(readOnly)으로 마운트하고 <code>DOCKER_HOST=unix:///host/run/docker.sock</code>로 <code>docker ps</code> 조회 · k3s Pod은 <code>kubectl</code>로 조회<br/>
              <span style="color:#7c3aed">※ 소켓 파일 직접 마운트(type:Socket) 대신 <b>디렉토리 마운트</b> 채택 — 도커 데몬 재시작 시 소켓 inode가 교체돼도 매 연결마다 새로 resolve되어 stale 없음 (Docker Services 0건 재발 방지)</span>
            </div>
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#f0f9ff,#e0f2fe);padding:20px;border-radius:16px;border:1px solid #7dd3fc">
          <h4 style="color:#0284c7;margin:0 0 12px">4. DB 자동백업</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;font-size:12px">
            <div style="background:#fff;padding:12px;border-radius:8px;text-align:center">
              <div style="font-size:24px;font-weight:800;color:#0284c7">3h</div>
              <div style="color:#666;margin-top:4px">백업 주기 (KST)</div>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px;text-align:center">
              <div style="font-size:24px;font-weight:800;color:#059669">14일</div>
              <div style="color:#666;margin-top:4px">로컬 보관</div>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px;text-align:center">
              <div style="font-size:24px;font-weight:800;color:#d97706">OCI+B2</div>
              <div style="color:#666;margin-top:4px">원격 이중화</div>
            </div>
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#faf5ff,#ede9fe);padding:20px;border-radius:16px;border:1px solid #c4b5fd">
          <h4 style="color:#7c3aed;margin:0 0 12px">5. OCI 오브젝트 스토리지 관리 <span style="background:#7c3aed;color:#fff;font-size:10px;padding:2px 8px;border-radius:10px;font-weight:600;margin-left:6px">NEW</span></h4>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;font-size:12px">
            <div style="background:#fff;padding:12px;border-radius:8px;line-height:1.9">
              <b style="color:#6d28d9">접근 경로</b><br/>
              서버관리 → OCI 스토리지<br/>
              버킷: <code style="background:#ede9fe;padding:1px 5px;border-radius:3px">sappt-documents</code><br/>
              리전: ap-chuncheon-1 (춘천)
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px;line-height:1.9">
              <b style="color:#6d28d9">주요 기능</b><br/>
              📁 폴더 탐색 (브레드크럼 경로)<br/>
              ⬆️ N개 파일 일괄 업로드<br/>
              ⬇️ N개 파일 일괄 다운로드 (PAR URL)<br/>
              🗑️ N개 항목 일괄 삭제
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 디자인 시스템 ──
  {
    title: '디자인 시스템 (Design System)',
    content: `
      <div style="display:grid;gap:20px">
        <div style="background:linear-gradient(135deg,#eef2ff,#e0e7ff);padding:24px;border-radius:16px;border:1px solid #a5b4fc">
          <h4 style="color:#4f46e5;margin:0 0 8px">선정 디자인킷: Ant Design 6.x + Custom Theme</h4>
          <p style="color:#555;font-size:14px;line-height:1.8;margin:0">
            <b>Ant Design</b>은 Alibaba에서 개발한 엔터프라이즈급 UI 라이브러리로, 업무용 시스템에 최적화되어 있습니다.
            B2B/ERP/관리 시스템에서 전 세계적으로 가장 널리 사용되는 React 컴포넌트 라이브러리입니다.
          </p>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e2e8f0">
            <h4 style="color:#4f46e5;margin:0 0 12px">선정 이유</h4>
            <ul style="color:#444;line-height:2;margin:0;padding-left:16px;font-size:13px">
              <li><b>풍부한 컴포넌트</b> — Table, Form, Modal, DatePicker 등 60+ 컴포넌트</li>
              <li><b>한국어 로케일</b> — koKR 완벽 지원, 날짜/페이지네이션 등</li>
              <li><b>테마 커스터마이징</b> — CSS Variables 기반 완전한 테마 오버라이드</li>
              <li><b>TypeScript 네이티브</b> — 완전한 타입 정의 제공</li>
              <li><b>반응형</b> — Grid System, Breakpoint 내장</li>
              <li><b>접근성</b> — WAI-ARIA 표준 준수</li>
            </ul>
          </div>
          <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e2e8f0">
            <h4 style="color:#4f46e5;margin:0 0 12px">테마 구성</h4>
            <div style="display:grid;gap:8px;font-size:13px">
              <div style="display:flex;align-items:center;gap:12px;padding:8px 12px;background:#f8fafc;border-radius:8px">
                <div style="width:32px;height:32px;border-radius:8px;background:#4f46e5"></div>
                <div><b>관리자 Primary</b><br/><span style="color:#888">Indigo #4f46e5</span></div>
              </div>
              <div style="display:flex;align-items:center;gap:12px;padding:8px 12px;background:#f8fafc;border-radius:8px">
                <div style="width:32px;height:32px;border-radius:8px;background:#f59e0b"></div>
                <div><b>파트너 Primary</b><br/><span style="color:#888">Amber #f59e0b</span></div>
              </div>
              <div style="display:flex;align-items:center;gap:12px;padding:8px 12px;background:#f8fafc;border-radius:8px">
                <div style="width:32px;height:32px;border-radius:8px;background:linear-gradient(135deg,#f8fafc,#f1f5f9);border:1px solid #e2e8f0"></div>
                <div><b>Background</b><br/><span style="color:#888">Slate #f1f5f9</span></div>
              </div>
              <div style="display:flex;align-items:center;gap:12px;padding:8px 12px;background:#f8fafc;border-radius:8px">
                <div style="width:32px;height:32px;border-radius:8px;background:#1e293b"></div>
                <div><b>Text Primary</b><br/><span style="color:#888">Slate #1e293b</span></div>
              </div>
              <div style="display:flex;align-items:center;gap:12px;padding:8px 12px;background:#f8fafc;border-radius:8px">
                <div style="width:32px;height:32px;border-radius:8px;background:#f8fafc;border:1px solid #e2e8f0"></div>
                <div><b>Surface</b><br/><span style="color:#888">#f8fafc — 표 헤더 &amp; 페이지네이션 푸터 공통 표면</span></div>
              </div>
              <div style="display:flex;align-items:center;gap:12px;padding:8px 12px;background:#f8fafc;border-radius:8px">
                <div style="width:32px;height:32px;border-radius:8px;background:#e0e7ff;box-shadow:inset 3px 0 0 #4f46e5"></div>
                <div><b>Selected Row</b><br/><span style="color:#888">#e0e7ff + 좌측 3px #4f46e5 액센트</span></div>
              </div>
              <div style="display:flex;align-items:center;gap:12px;padding:8px 12px;background:#eef2ff;border-radius:8px;border:1px solid #c7d2fe">
                <div style="width:32px;height:32px;border-radius:8px;background:#fff;border:1px solid #a5b4fc;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#4f46e5">13</div>
                <div><b>밀도 (Density)</b><br/><span style="color:#888">글자 13 &middot; 컨트롤 28 &middot; 표 행 31 &mdash; <code>tokens.density</code> 가 정본</span></div>
              </div>
            </div>
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#fff7ed,#ffedd5);padding:20px;border-radius:16px;border:1px solid #fdba74">
          <h4 style="color:#ea580c;margin:0 0 12px">디자인 원칙</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:12px;font-size:12px">
            <div style="background:#fff;padding:14px;border-radius:10px;text-align:center">
              <div style="font-size:24px;margin-bottom:6px">&#128200;</div>
              <b>정보 밀도</b>
              <p style="margin:4px 0 0;color:#666">장비 콘솔 수준의 밀도. 기준은 취향이 아니라 실측 &mdash; <b>첫 데이터 행까지 207px</b>(1366)</p>
            </div>
            <div style="background:#fff;padding:14px;border-radius:10px;text-align:center">
              <div style="font-size:24px;margin-bottom:6px">&#127912;</div>
              <b>일관성</b>
              <p style="margin:4px 0 0;color:#666">색상, 간격, 타이포그래피의 체계적 일관성</p>
            </div>
            <div style="background:#fff;padding:14px;border-radius:10px;text-align:center">
              <div style="font-size:24px;margin-bottom:6px">&#9889;</div>
              <b>탭 네비게이션</b>
              <p style="margin:4px 0 0;color:#666">브라우저 탭처럼 다중 작업 공간, 상태 보존</p>
            </div>
            <div style="background:#fff;padding:14px;border-radius:10px;text-align:center">
              <div style="font-size:24px;margin-bottom:6px">&#128274;</div>
              <b>역할 분리</b>
              <p style="margin:4px 0 0;color:#666">관리자(Indigo) vs 파트너(Amber) 시각적 구분</p>
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 디자인킷 · 목록 화면 UI 표준 ──
  {
    title: '디자인킷 · 목록 화면 UI 표준 (List Screen Standards)',
    content: `
      <div style="display:grid;gap:16px">
        <div style="background:linear-gradient(135deg,#eef2ff,#e0e7ff);padding:20px;border-radius:16px;border:1px solid #a5b4fc">
          <h4 style="color:#4f46e5;margin:0 0 8px">새 목록 화면은 아래 8개 규칙을 그대로 적용한다 <span style="font-size:11px;color:#6366f1">(2026-08 확정 · 전역 index.css + 공용 훅으로 강제 · 여백 값은 다음 장 '밀도 표준' 참조)</span></h4>
          <p style="color:#555;font-size:13px;line-height:1.8;margin:0">
            화면마다 제각각이던 높이 계산·페이지네이션 여백·선택 UI를 실측 기반으로 통일한 결과다.
            <b>페이지별 인라인 스타일로 다시 덮어쓰지 않는다</b> — 한 화면만 달라지는 순간 기준이 무너진다.
          </p>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">

          <div style="background:#fff;padding:16px;border-radius:14px;border:1px solid #e2e8f0">
            <b style="color:#4f46e5;font-size:13px">1. 높이는 실측, 매직넘버 금지</b>
            <p style="margin:6px 0 0;color:#555;font-size:12px;line-height:1.85">
              표 본문은 <code>useFitTableHeight</code>(scroll.y), 패널은 <code>useFillHeight</code>.
              <code>calc(100vh - 상수)</code>는 금지 — 헤더/필터바가 해상도·줄바꿈에 따라 달라져 하단이 잘린다.
              래퍼 2개(<code>cardWrapRef</code> 최하단 카드, <code>tableWrapRef</code> 표)를 반드시 함께 건다.
              <b>결과 기준: 문서 세로스크롤 0</b>.
            </p>
          </div>

          <div style="background:#fff;padding:16px;border-radius:14px;border:1px solid #e2e8f0">
            <b style="color:#4f46e5;font-size:13px">2. 페이지네이션 설정</b>
            <p style="margin:6px 0 0;color:#555;font-size:12px;line-height:1.85">
              <code>showSizeChanger</code> + 옵션 <code>20/50/100/200</code>,
              문구 <code>N–M / 총 T건</code>. 기본 20건(대장류·업무출장 월/주별만 50).
              <b>필터 변경·건수 변경 시 1페이지로 리셋</b>, 서버 페이징은 pageSize를 state로 두고 요청에 전달.
              백엔드 <code>page_size</code> 상한 <code>le=200</code>.
            </p>
          </div>

          <div style="background:#fff;padding:16px;border-radius:14px;border:1px solid #e2e8f0">
            <b style="color:#4f46e5;font-size:13px">3. 페이지네이션 = 푸터 바</b>
            <p style="margin:6px 0 0;color:#555;font-size:12px;line-height:1.85">
              표 헤더와 <b>같은 표면 <code>#f8fafc</code></b> + 상단 <code>1px #e2e8f0</code> 헤어라인 + 하단 라운드 8px.
              여백은 <b>margin이 아니라 padding 7px</b>(margin이면 배경·선이 표 폭에 안 걸린다. 12 &rarr; 7 은 밀도 키트 v3).
              조회 건수는 <b>좌측</b>(상태), 페이지 컨트롤은 <b>우측</b>(조작).
              lab은 우측 <code>64px</code>을 AI 도우미 버튼 자리로 비운다.
            </p>
          </div>

          <div style="background:#fff;padding:16px;border-radius:14px;border:1px solid #e2e8f0">
            <b style="color:#4f46e5;font-size:13px">4. 선택 UI는 떠 있는 툴바</b>
            <p style="margin:6px 0 0;color:#555;font-size:12px;line-height:1.85">
              <code>N건 선택됨</code> 바는 <b>문서 흐름에 넣지 않는다</b> — 체크할 때마다 표가 밀려 화면이 들썩인다.
              표 래퍼를 <code>position:relative</code>로 두고 공용 <code>.bulk-select-*</code> pill을 하단 중앙에 띄운다.
              선택 행은 배경 <code>#e0e7ff</code> + 첫 셀 좌측 <code>3px #4f46e5</code> 액센트 바(배경만으로는 hover와 혼동).
            </p>
          </div>

          <div style="background:#fff;padding:16px;border-radius:14px;border:1px solid #e2e8f0">
            <b style="color:#4f46e5;font-size:13px">5. 각주·범례는 표 위에</b>
            <p style="margin:6px 0 0;color:#555;font-size:12px;line-height:1.85">
              열 표식을 설명하는 각주는 <b>필터바 우측(조회 건수 옆)</b>에 둔다.
              표 아래에 두면 그 화면만 페이지네이션이 밀리고, 푸터 좌측의 조회 건수와 자리가 겹친다.
            </p>
          </div>

          <div style="background:#fff;padding:16px;border-radius:14px;border:1px solid #e2e8f0">
            <b style="color:#4f46e5;font-size:13px">6. 상단 요약은 '액션이 나오는 축'으로</b>
            <p style="margin:6px 0 0;color:#555;font-size:12px;line-height:1.85">
              분류 코드 분포(예: 작물폐기유무)는 보고도 할 일이 안 나오고, 같은 값의 필터가 이미 있으면 중복이다.
              그 화면이 답해야 하는 질문(<b>지금 무엇이 위험한가·무엇이 비어 있는가</b>)을 축으로 잡는다.
              집계 단위도 확인할 것 — 시료 단위로 세면 같은 시험이 중복 계산된다.
              <b>축을 이루는 조건은 요약 API 파라미터에서 제외</b>하고(미리 걸러 오면 하나를 고르는 순간 분포가 무너진다),
              축과 겹치는 필터 컨트롤은 두지 않는다.
            </p>
          </div>

          <div style="background:#fff;padding:16px;border-radius:14px;border:1px solid #e2e8f0">
            <b style="color:#4f46e5;font-size:13px">7. 색만으로 구분하지 않는다</b>
            <p style="margin:6px 0 0;color:#555;font-size:12px;line-height:1.85">
              상태·분류는 색 점 + <b>문자 라벨·건수를 항상 병기</b>하고, 상태색을 글자색으로 쓰지 않는다.
              범례는 0건 항목도 고정 순서로 노출(사라지면 토글 해제 대상이 없어진다).
              팔레트 추가 시 인접 색 <b>ΔE ≥ 15(정상)·≥ 8(색약)</b>을 검증한다.
            </p>
          </div>

        </div>

        <div style="background:linear-gradient(135deg,#f0fdf4,#dcfce7);padding:18px;border-radius:14px;border:1px solid #86efac">
          <b style="color:#16a34a;font-size:13px">8. 완료 기준은 눈이 아니라 실측 — CDP 회귀 테스트</b>
          <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-top:10px;font-size:11.5px">
            <div style="background:#fff;padding:10px 12px;border-radius:8px"><b>문서 스크롤</b><br/><span style="color:#666">docScroll === 0</span></div>
            <div style="background:#fff;padding:10px 12px;border-radius:8px"><b>푸터 위치</b><br/><span style="color:#666">푸터 하단 = 카드 하단, 뷰포트 안</span></div>
            <div style="background:#fff;padding:10px 12px;border-radius:8px"><b>선택 시</b><br/><span style="color:#666">thead·본문·푸터 좌표 이동 0px</span></div>
            <div style="background:#fff;padding:10px 12px;border-radius:8px"><b>겹침</b><br/><span style="color:#666">컨트롤 ∩ 플로팅 버튼 = 0</span></div>
          </div>
          <p style="margin:10px 0 0;color:#555;font-size:12px;line-height:1.8">
            스크립트 <code>test_footer.mjs</code>로 <b>해상도 4종</b>을 순환하며 라운드 반복 검사한다.
            2026-08-18: 10라운드 &times; 13화면 = <b>1,220건 전부 통과</b> 후 무중단 배포.
            2026-09-03 밀도 키트부터는 <code>uitests/density_audit.mjs</code>(층별 실측)를
            <b>1920 / 1600 / 1366 / 1280</b> 네 폭에 함께 돌려 <b>본문시작 y &middot; 행 높이 &middot; 표시 행수의 역행 0건</b>까지 확인한다.
            <b>좁은 폭이 진짜 기준선</b>이다 &mdash; 1600만 보면 개악을 통과시킨다.
          </p>
        </div>
      </div>
    `,
  },
  // ── Slide: 디자인킷 · 밀도 표준 (콘솔 밀도 키트 v3) ──
  {
    title: '디자인킷 · 밀도 표준 (Console Density Kit v3)',
    content: `
      <div style="display:grid;gap:16px">

        <div style="background:linear-gradient(135deg,#eef2ff,#e0e7ff);padding:20px;border-radius:16px;border:1px solid #a5b4fc">
          <h4 style="color:#4f46e5;margin:0 0 8px">밀도 값의 정본은 CSS 가 아니라 <span style="font-family:monospace">theme</span> 토큰이다 <span style="font-size:11px;color:#6366f1">(2026-09-03 적용 · lab &middot; partner 공통)</span></h4>
          <p style="color:#555;font-size:13px;line-height:1.85;margin:0">
            36화면 전수 실측에서 표 화면은 첫 데이터 행까지 <b>평균 358px(1600 기준, 뷰포트의 40%)</b>를 껍데기가 쓰고 있었다.
            1366&times;768 에서는 시험일정총괄표가 <b>533px(69%)</b>를 써 4행만 남았다.
            층이 겹칠수록 각 층의 기본값이 그대로 누적되므로 <b>페이지를 고치지 않고 층의 두께만 정한다</b>.
          </p>
        </div>

        <div style="display:grid;grid-template-columns:1.15fr 1fr;gap:14px">
          <div style="background:#fff;padding:16px;border-radius:14px;border:1px solid #e2e8f0">
            <b style="color:#4f46e5;font-size:13px">왜 CSS 에서 토큰으로 옮겼나</b>
            <p style="margin:6px 0 0;color:#555;font-size:12px;line-height:1.85">
              2026-08-30 까지는 <code>index.css</code> 말미의 <code>!important</code> 블록이 밀도를 정했다. 두 가지 구조적 문제가 있었다.<br/>
              &#9312; <b>특이도 싸움</b> — <code>.ant-menu-item</code> 만 겨냥하면 이미 있던
              <code>.ant-menu-inline .ant-menu-item</code> 에 져서 <b>조용히 무시된다</b>(실제로 헛쳤다).<br/>
              &#9313; <b>역행</b> — 페이지가 의도적으로 준 인라인 패딩(전폭 표의 <code>padding:0</code>)까지 덮어써 여백이 되레 늘어난다.<br/>
              antd 6.3.4 가 <code>Menu.itemHeight</code> &middot; <code>Card.headerHeight</code> &middot;
              <code>Table.cellPaddingBlockSM</code> &middot; <code>Form.itemMarginBottom</code> 을 정식 토큰으로 노출하므로
              규격을 옮길 자리가 있었다.
            </p>
          </div>
          <div style="background:#fff;padding:16px;border-radius:14px;border:1px solid #e2e8f0">
            <b style="color:#4f46e5;font-size:13px">어디를 고치나</b>
            <div style="margin-top:8px;display:grid;gap:6px;font-size:11.5px">
              <div style="background:#f8fafc;padding:8px 10px;border-radius:8px"><b>정본</b> &nbsp;<code>theme/tokens.ts</code> 의 <code>density</code>&middot;<code>layout</code></div>
              <div style="background:#f8fafc;padding:8px 10px;border-radius:8px"><b>주입</b> &nbsp;<code>theme/index.ts</code> 의 antd 컴포넌트 토큰</div>
              <div style="background:#f8fafc;padding:8px 10px;border-radius:8px"><b>CSS 잔여</b> &nbsp;색&middot;배경&middot;액센트&middot;제목 크기&middot;모달 패딩만</div>
              <div style="background:#fef3c7;padding:8px 10px;border-radius:8px;border:1px solid #fcd34d"><b>파트너는 사본</b> — 두 앱을 함께 고친다</div>
            </div>
          </div>
        </div>

        <div style="background:#fff;padding:16px;border-radius:14px;border:1px solid #e2e8f0">
          <b style="color:#4f46e5;font-size:13px">Tier 0&sim;6 &mdash; 바깥 층부터. 바깥일수록 모든 화면에 곱해진다</b>
          <table style="width:100%;border-collapse:collapse;font-size:11.5px;margin-top:10px">
            <thead>
              <tr style="background:#f8fafc;color:#64748b">
                <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #e2e8f0">층</th>
                <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #e2e8f0">항목</th>
                <th style="text-align:right;padding:6px 8px;border-bottom:1px solid #e2e8f0">전</th>
                <th style="text-align:right;padding:6px 8px;border-bottom:1px solid #e2e8f0">후</th>
                <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #e2e8f0">비고</th>
              </tr>
            </thead>
            <tbody style="color:#334155">
              <tr><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9"><b>0</b> 전역</td><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9">글자 / 컨트롤 높이 / 줄간 / 모서리</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#a1560a">14 / 32 / 1.57 / 8&middot;12</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#0d7369;font-weight:600">13 / 28 / 1.45 / 6&middot;8</td><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#64748b">버튼&middot;입력&middot;셀렉트&middot;날짜선택이 모두 이 하나를 본다</td></tr>
              <tr><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9"><b>1</b> 크롬</td><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9">헤더 / Content 패딩 / 사이드바 / 접힘</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#a1560a">56 / 12 / 230&middot;240 / 60&middot;64</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#0d7369;font-weight:600">44 / 8 / 208 / 48</td><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#64748b">사이드바 로고 줄 높이도 헤더와 맞춰 밑선이 이어진다</td></tr>
              <tr><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9"><b>2</b> 메뉴</td><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9">항목 높이 / 세로여백 / 내부패딩 / 그룹제목</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#a1560a">38 / 4 / 24 / 32</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#0d7369;font-weight:600">30 / 2 / 12 / 20</td><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#64748b">27개 항목 &times; 8px = 216px. 총높이 1326 &rarr; 1012px</td></tr>
              <tr><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9"><b>3</b> 카드</td><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9">머리 높이 / 머리 패딩 / 본문 패딩</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#a1560a">40 / 14 / 12</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#0d7369;font-weight:600">32 / 10 / 10</td><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#64748b">실측 머리 높이 8종(40&sim;61) &rarr; 3종(28&middot;32&middot;39)</td></tr>
              <tr><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9"><b>4</b> 표</td><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9">셀 세로 패딩 / 가로 패딩</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#a1560a">7 / 8</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#0d7369;font-weight:600">4 / 8 유지</td><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#64748b">행 39 &rarr; 31px. <b>가로 8은 상한</b> — 넓히면 줄바꿈으로 행이 되레 커진다</td></tr>
              <tr><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9"><b>5</b> 폼&middot;모달</td><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9">항목 하단여백 / 라벨 높이 / 세로라벨 패딩</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#a1560a">24&middot;8&sim;10 / 32 / 8</td><td style="text-align:right;padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#0d7369;font-weight:600">10 / 20 / 2</td><td style="padding:5px 8px;border-bottom:1px solid #f1f5f9;color:#64748b">시험일정 등록폼 28항목 849 &rarr; 약 620px</td></tr>
              <tr><td style="padding:5px 8px"><b>6</b> 탭&middot;타이포</td><td style="padding:5px 8px">탭 패딩 / 페이지네이션 푸터 / 제목 h4</td><td style="text-align:right;padding:5px 8px;color:#a1560a">8&middot;4 / 12 / 17</td><td style="text-align:right;padding:5px 8px;color:#0d7369;font-weight:600">5&middot;4 / 7 / 15</td><td style="padding:5px 8px;color:#64748b">사이드바에 이미 같은 메뉴명이 있다</td></tr>
            </tbody>
          </table>
        </div>

        <div style="background:linear-gradient(135deg,#f0fdf4,#dcfce7);padding:18px;border-radius:14px;border:1px solid #86efac">
          <b style="color:#16a34a;font-size:13px">적용 결과 &mdash; 배포 후 운영 화면 재실측 (34화면 &times; 2해상도)</b>
          <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-top:10px;font-size:11.5px">
            <div style="background:#fff;padding:10px 12px;border-radius:8px"><b>본문시작 평균</b><br/><span style="color:#a1560a">278px</span> &rarr; <b style="color:#0d7369">207px</b><br/><span style="color:#888">1366&times;768</span></div>
            <div style="background:#fff;padding:10px 12px;border-radius:8px"><b>표 표시행 합계</b><br/><span style="color:#a1560a">134행</span> &rarr; <b style="color:#0d7369">235행 (+75%)</b><br/><span style="color:#888">1600 기준 +65%</span></div>
            <div style="background:#fff;padding:10px 12px;border-radius:8px"><b>사이드바 메뉴</b><br/><span style="color:#a1560a">1326px</span> &rarr; <b style="color:#0d7369">1012px</b><br/><span style="color:#888">768 화면 넘침 558 &rarr; 328</span></div>
            <div style="background:#fff;padding:10px 12px;border-radius:8px"><b>역행 / 세로스크롤</b><br/><b style="color:#0d7369">0건 / 0건</b><br/><span style="color:#888">화면테스트 231통과 0실패</span></div>
          </div>
          <p style="margin:10px 0 0;color:#555;font-size:12px;line-height:1.8">
            가장 크게 바뀐 화면은 <b>활동이력(4 &rarr; 16행)</b>이다 &mdash; 여백이 아니라 목록 위에 놓인 310px짜리 차트 카드가 원인이었다.
            <b>상단에 큰 블록이 놓인 화면은 토큰이 아니라 화면에서 고친다</b>: 활동이력 차트는 기본 접힘(선택은 브라우저에 기억),
            시험일정총괄표&middot;시험물질관리의 요약 카드는 히어로 숫자 40px 을 없애고 <b>전폭 3줄</b>(머리줄 / 스택바 / 범례)로 쌓았다.
          </p>
        </div>

      </div>
    `,
  },
  // ── Slide: 디자인킷 · 밀도 표준 적용 함정 & 검증 ──
  {
    title: '디자인킷 · 밀도 표준 — 적용 함정 & 검증 방법',
    content: `
      <div style="display:grid;gap:16px">

        <div style="background:linear-gradient(135deg,#eef2ff,#e0e7ff);padding:18px;border-radius:14px;border:1px solid #a5b4fc">
          <h4 style="color:#4f46e5;margin:0 0 6px">층의 두께를 바꾸면 <b>모든 화면에 곱해진다</b> &mdash; 그래서 전수로 본다</h4>
          <p style="color:#555;font-size:12.5px;line-height:1.8;margin:0">
            앞 장의 값을 넣을 때 실제로 밟은 함정과, 완료 판정에 쓰는 실측 절차다.
            표본 몇 개만 보면 역행을 놓친다 &mdash; 이번에도 전수 실측이 카드 머리 61px, 제목 24px,
            <code>size</code> 미지정 표의 행 역행을 잡았다.
          </p>
        </div>

        <div style="background:linear-gradient(135deg,#fef2f2,#fee2e2);padding:18px;border-radius:14px;border:1px solid #fca5a5">
          <b style="color:#b91c1c;font-size:13px">함정 5가지 &mdash; 모두 이번 적용에서 실제로 밟았다</b>
          <div style="display:grid;gap:8px;margin-top:10px;font-size:12px;color:#555;line-height:1.8">
            <div style="background:#fff;padding:10px 12px;border-radius:8px">
              <b style="color:#b91c1c">&#9312; 표 셀 패딩은 세 크기를 모두 건다</b> &mdash;
              <code>cellPaddingBlockSM</code> 만 걸면 <code>size</code> 를 지정하지 않은 표가 antd 기본으로 되돌아가
              행이 <b>31 &rarr; 47px 로 역행</b>한다(ADB 데모&middot;점검일지&middot;파트너 시험일정에서 검출). 기본&middot;MD&middot;SM 전부.
            </div>
            <div style="background:#fff;padding:10px 12px;border-radius:8px">
              <b style="color:#b91c1c">&#9313; Content 패딩과 훅 상수는 같은 값을 읽게 한다</b> &mdash;
              <code>useViewportFit</code> 의 <code>CONTENT_PADDING_BOTTOM</code> 은 상수를 베끼지 않고
              <code>layout.contentPadding</code> 을 읽는다. 어긋나면 표 높이 실측이 틀어져 페이지네이션이 화면 밖으로 밀린다.
            </div>
            <div style="background:#fff;padding:10px 12px;border-radius:8px">
              <b style="color:#b91c1c">&#9314; antd 6 Modal 의 공개 토큰은 색&middot;제목뿐</b> &mdash;
              본문 패딩은 토큰으로 못 바꾼다. 이것만 <code>index.css</code> 에 남겼다.
            </div>
            <div style="background:#fff;padding:10px 12px;border-radius:8px">
              <b style="color:#b91c1c">&#9315; 표 가로 패딩 8은 여전히 상한</b> &mdash;
              10px 로 넓혔더니 활동이력 일시 컬럼이 2줄로 접혀 행이 42 &rarr; 59px 로 커진 적이 있다(2026-08-30). 밀도는 세로에서만 얻는다.
            </div>
            <div style="background:#fff;padding:10px 12px;border-radius:8px">
              <b style="color:#b91c1c">&#9316; 컨트롤이 작아지면 아이콘이 클릭 중앙을 차지한다</b> &mdash;
              글자가 13px 이 되며 태그(<code>2026년 &#10005;</code>)가 좁아져 <b>삭제 &#10005;(10px)가 124px 컨트롤의 가로 정중앙</b>에 놓였다.
              가운데를 누르면 목록이 열리는 대신 연도 필터가 지워진다(폭 156px 로 해결).
              화면테스트 <code>filter_interact</code> 가 잡았다 &mdash; 검사가 컨트롤 중앙을 클릭하기 때문에 사용자와 같은 지점을 밟는다.
              <b>밀도를 올릴 때는 높이만이 아니라 '무엇이 클릭 중앙에 오는가'를 함께 본다.</b>
            </div>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="background:#fff;padding:16px;border-radius:14px;border:1px solid #e2e8f0">
            <b style="color:#4f46e5;font-size:13px">검증 방법 &mdash; 눈이 아니라 실측</b>
            <p style="margin:6px 0 0;color:#555;font-size:12px;line-height:1.85">
              <code>uitests/density_audit.mjs</code> 층별 실측(본문시작&middot;행높이&middot;표시행&middot;메뉴 총높이)<br/>
              <code>uitests/density_stack.mjs</code> 본문시작 y 를 층으로 분해<br/>
              <code>uitests/density_sim.mjs</code> 제안값 주입 시뮬레이션<br/>
              + 화면테스트 36화면(<code>layout_fit</code>&middot;<code>pagination_fit</code>&middot;<code>filter_interact</code>)
              + <b>1920 / 1600 / 1366 / 1280 네 폭</b>.
              <b>좁은 폭이 진짜 기준선</b>이다 &mdash; 1600만 보면 개악을 통과시킨다.
            </p>
          </div>
          <div style="background:#fff;padding:16px;border-radius:14px;border:1px solid #e2e8f0">
            <b style="color:#4f46e5;font-size:13px">주입 시뮬레이션은 '하한'이다</b>
            <p style="margin:6px 0 0;color:#555;font-size:12px;line-height:1.85">
              기획 단계에서 제안값을 CSS 로 주입해 재봤을 때는 표시행 <b>+17%</b>였는데, 실제 토큰 적용은 <b>+75%</b>가 나왔다.
              주입으로는 닿지 못하는 층(앱 헤더&middot;탭 바&middot;메뉴&middot;페이지네이션, 그리고 <code>size</code> 미지정 표)이
              토큰에서는 함께 움직이기 때문이다.
              <b>시뮬레이션은 방향과 안전성을 확인하는 도구지 효과의 크기를 재는 도구가 아니다.</b>
            </p>
          </div>
        </div>

      </div>
    `,
  },
  // ── Slide: DBA 점검 & DB 품질 개선 ──
  {
    title: 'DBA 점검 & 데이터베이스 품질 개선 (v2.2)',
    content: `
      <div style="display:grid;gap:16px">
        <div style="background:linear-gradient(135deg,#fef2f2,#fee2e2);padding:18px;border-radius:14px;border:1px solid #fca5a5">
          <h4 style="color:#dc2626;margin:0 0 10px">CRITICAL 수정 완료</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;font-size:12px">
            <div style="background:#fff;padding:12px;border-radius:8px">
              <b style="color:#dc2626">FK 제약조건 15개 추가</b>
              <p style="margin:4px 0 0;color:#555">모든 외래키 컬럼에 실제 DB 제약조건 적용. ON DELETE CASCADE/SET NULL 정책 설정. 고아 레코드 방지.</p>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px">
              <b style="color:#dc2626">커넥션 풀 설정</b>
              <p style="margin:4px 0 0;color:#555">pool_size=20, max_overflow=40, pool_pre_ping=True, pool_recycle=3600. 커넥션 누수 및 고갈 방지.</p>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px">
              <b style="color:#dc2626">N+1 쿼리 해소</b>
              <p style="margin:4px 0 0;color:#555">relationship() + lazy="joined" 적용. 289건 목록 조회 시 1,734 → 3 쿼리로 개선 (99.8% 감소).</p>
            </div>
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#fff7ed,#ffedd5);padding:18px;border-radius:14px;border:1px solid #fdba74">
          <h4 style="color:#ea580c;margin:0 0 10px">HIGH 수정 완료</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;font-size:12px">
            <div style="background:#fff;padding:12px;border-radius:8px">
              <b>인덱스 5개 추가</b>
              <p style="margin:4px 0 0;color:#555">test_schedules(status), schedule_treatments(schedule_id, round_no), schedule_surveys(schedule_id, round_no), activity_logs(username), activity_logs(user_id, action, created_at)</p>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px">
              <b>CHECK 제약조건</b>
              <p style="margin:4px 0 0;color:#555">users.role: ADMIN/RESEARCHER/PARTNER/VIEWER만 허용. SQLAlchemy CheckConstraint 적용.</p>
            </div>
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#f0f9ff,#e0f2fe);padding:18px;border-radius:14px;border:1px solid #7dd3fc">
          <h4 style="color:#0284c7;margin:0 0 10px">적용된 FK 제약조건 목록 (15개)</h4>
          <div style="background:#0c4a6e;color:#bae6fd;padding:12px;border-radius:8px;font-family:monospace;font-size:10px;line-height:1.7;columns:2;column-gap:20px">
            fk_users_company_id<br/>
            fk_partner_accounts_company<br/>
            fk_partner_accounts_user<br/>
            fk_schedule_docs_schedule<br/>
            fk_notifications_company<br/>
            fk_notifications_schedule<br/>
            fk_treatments_protocol (CASCADE)<br/>
            fk_plot_designs_protocol (CASCADE)<br/>
            fk_schedules_company<br/>
            fk_schedules_category<br/>
            fk_schedules_location<br/>
            fk_schedules_field_owner<br/>
            fk_schedule_treats_sched (CASCADE)<br/>
            fk_schedule_surveys_sched (CASCADE)<br/>
            fk_field_owners_location
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 프론트엔드 품질 개선 ──
  {
    title: '프론트엔드 품질 개선 (v2.2)',
    content: `
      <div style="display:grid;gap:16px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#fef2f2,#fee2e2);padding:18px;border-radius:14px;border:1px solid #fca5a5">
            <h4 style="color:#dc2626;margin:0 0 10px">CRITICAL 수정</h4>
            <ul style="color:#444;line-height:2;margin:0;padding-left:16px;font-size:12px">
              <li><b>빈 catch 블록 제거</b> — SchedulePage, ProtocolListPage 등 모든 빈 catch를 에러 메시지 표시로 교체</li>
              <li><b>Axios 타임아웃 30초 설정</b> — Admin/Partner 양쪽 적용, 무한 대기 방지</li>
              <li><b>Error Boundary 추가</b> — React 컴포넌트 오류 시 전체 앱 크래시 대신 복구 UI 표시</li>
            </ul>
          </div>
          <div style="background:linear-gradient(135deg,#fff7ed,#ffedd5);padding:18px;border-radius:14px;border:1px solid #fdba74">
            <h4 style="color:#ea580c;margin:0 0 10px">HIGH 수정</h4>
            <ul style="color:#444;line-height:2;margin:0;padding-left:16px;font-size:12px">
              <li><b>API 에러 핸들링 강화</b> — 삭제/생성/수정 실패 시 서버 에러 메시지를 사용자에게 표시</li>
              <li><b>ProtocolListPage 삭제</b> — try-catch 추가, 실패 시 에러 메시지</li>
              <li><b>SchedulePage 폼 제출</b> — validation 에러와 API 에러 분리 처리</li>
            </ul>
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#ecfdf5,#d1fae5);padding:18px;border-radius:14px;border:1px solid #6ee7b7">
          <h4 style="color:#059669;margin:0 0 10px">품질 지표 개선 현황</h4>
          <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:10px;font-size:12px">
            <div style="background:#fff;padding:12px;border-radius:8px;text-align:center">
              <div style="font-size:11px;color:#666;margin-bottom:4px">에러 핸들링</div>
              <div style="display:flex;justify-content:center;gap:4px;align-items:baseline">
                <span style="font-size:14px;color:#94a3b8;text-decoration:line-through">4/10</span>
                <span style="font-size:10px;color:#94a3b8">&rarr;</span>
                <span style="font-size:20px;font-weight:800;color:#059669">7/10</span>
              </div>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px;text-align:center">
              <div style="font-size:11px;color:#666;margin-bottom:4px">DB 무결성</div>
              <div style="display:flex;justify-content:center;gap:4px;align-items:baseline">
                <span style="font-size:14px;color:#94a3b8;text-decoration:line-through">2/10</span>
                <span style="font-size:10px;color:#94a3b8">&rarr;</span>
                <span style="font-size:20px;font-weight:800;color:#059669">9/10</span>
              </div>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px;text-align:center">
              <div style="font-size:11px;color:#666;margin-bottom:4px">쿼리 성능</div>
              <div style="display:flex;justify-content:center;gap:4px;align-items:baseline">
                <span style="font-size:14px;color:#94a3b8;text-decoration:line-through">3/10</span>
                <span style="font-size:10px;color:#94a3b8">&rarr;</span>
                <span style="font-size:20px;font-weight:800;color:#059669">8/10</span>
              </div>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px;text-align:center">
              <div style="font-size:11px;color:#666;margin-bottom:4px">안정성</div>
              <div style="display:flex;justify-content:center;gap:4px;align-items:baseline">
                <span style="font-size:14px;color:#94a3b8;text-decoration:line-through">5/10</span>
                <span style="font-size:10px;color:#94a3b8">&rarr;</span>
                <span style="font-size:20px;font-weight:800;color:#059669">8/10</span>
              </div>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px;text-align:center">
              <div style="font-size:11px;color:#666;margin-bottom:4px">커넥션 관리</div>
              <div style="display:flex;justify-content:center;gap:4px;align-items:baseline">
                <span style="font-size:14px;color:#94a3b8;text-decoration:line-through">1/10</span>
                <span style="font-size:10px;color:#94a3b8">&rarr;</span>
                <span style="font-size:20px;font-weight:800;color:#059669">9/10</span>
              </div>
            </div>
          </div>
        </div>

        <div style="background:#f8fafc;padding:16px;border-radius:12px;border:1px solid #e2e8f0">
          <h4 style="color:#475569;margin:0 0 8px;font-size:13px">향후 개선 과제 (Backlog)</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:11px;color:#64748b">
            <div>&bull; Alembic 마이그레이션 시스템 도입</div>
            <div>&bull; TypeScript any 타입 제거 (strict mode)</div>
            <div>&bull; 날짜 컬럼 String &rarr; Date 타입 전환</div>
            <div>&bull; i18n 다국어 지원</div>
            <div>&bull; httpOnly 쿠키 기반 인증</div>
            <div>&bull; ARIA 접근성 라벨 추가</div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 로그분석 전문 시스템 추천 ──
  {
    title: '로그 모니터링 & 분석 시스템 추천',
    content: `
      <div style="display:grid;gap:16px">
        <div style="background:linear-gradient(135deg,#ecfdf5,#d1fae5);padding:18px;border-radius:14px;border:1px solid #6ee7b7">
          <h4 style="color:#059669;margin:0 0 10px">현재 구현: Built-in Container Log Viewer</h4>
          <p style="color:#555;font-size:13px;margin:0;line-height:1.8">
            서버 모니터링 페이지에서 Docker 컨테이너별 로그를 실시간 조회 가능.
            컨테이너 선택, 레벨 필터(ERROR/WARN/INFO), 키워드 검색, 라인 수 조절 지원.
            <b>소규모 운영에 적합하나, 대규모 로그 분석/장기 보관/알림에는 전문 시스템 필요.</b>
          </p>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px">
          <div style="background:#fff;padding:18px;border-radius:14px;border:2px solid #10b981;position:relative">
            <div style="position:absolute;top:-10px;right:12px;background:#10b981;color:#fff;padding:2px 10px;border-radius:10px;font-size:10px;font-weight:700">추천</div>
            <h4 style="color:#10b981;margin:0 0 8px">Loki + Grafana</h4>
            <div style="font-size:11px;color:#888;margin-bottom:10px">Grafana Labs (오픈소스)</div>
            <ul style="color:#444;line-height:1.9;margin:0;padding-left:14px;font-size:12px">
              <li>Docker 친화적 (컨테이너 자동 수집)</li>
              <li><b>경량</b> — 인덱싱 없이 라벨 기반</li>
              <li>Grafana 대시보드 통합</li>
              <li>알림 규칙 (Slack, Email)</li>
              <li>메모리 사용량 적음</li>
              <li><b>SAPPT 규모에 최적</b></li>
            </ul>
            <div style="margin-top:10px;background:#ecfdf5;padding:8px;border-radius:6px;font-size:11px;color:#059669">
              <b>리소스:</b> RAM 256MB~512MB<br/>
              <b>비용:</b> 무료 (Self-hosted)
            </div>
          </div>

          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e2e8f0">
            <h4 style="color:#6366f1;margin:0 0 8px">ELK Stack</h4>
            <div style="font-size:11px;color:#888;margin-bottom:10px">Elasticsearch + Logstash + Kibana</div>
            <ul style="color:#444;line-height:1.9;margin:0;padding-left:14px;font-size:12px">
              <li>풀텍스트 검색 강력</li>
              <li>복잡한 쿼리/시각화</li>
              <li>대규모 로그 분석 최적</li>
              <li>머신러닝 이상탐지</li>
              <li>높은 리소스 요구</li>
              <li>운영 복잡도 높음</li>
            </ul>
            <div style="margin-top:10px;background:#fef2f2;padding:8px;border-radius:6px;font-size:11px;color:#dc2626">
              <b>리소스:</b> RAM 4GB+ 필요<br/>
              <b>비용:</b> 무료~유료 (Elastic Cloud)
            </div>
          </div>

          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e2e8f0">
            <h4 style="color:#f59e0b;margin:0 0 8px">Datadog / New Relic</h4>
            <div style="font-size:11px;color:#888;margin-bottom:10px">SaaS (클라우드 관리형)</div>
            <ul style="color:#444;line-height:1.9;margin:0;padding-left:14px;font-size:12px">
              <li>설치/운영 부담 없음</li>
              <li>APM + 로그 + 메트릭 통합</li>
              <li>자동 이상탐지/알림</li>
              <li>분산 추적 지원</li>
              <li>Agent 기반 수집</li>
              <li>로그량 기반 과금</li>
            </ul>
            <div style="margin-top:10px;background:#fffbeb;padding:8px;border-radius:6px;font-size:11px;color:#d97706">
              <b>리소스:</b> Agent만 설치<br/>
              <b>비용:</b> 월 $15~/호스트 (유료)
            </div>
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#f0f9ff,#e0f2fe);padding:18px;border-radius:14px;border:1px solid #7dd3fc">
          <h4 style="color:#0284c7;margin:0 0 10px">SAPPT 권장 구성: Loki + Grafana + Promtail</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;font-size:12px">
            <div style="background:#fff;padding:12px;border-radius:8px;text-align:center">
              <b style="color:#0284c7">Promtail</b>
              <p style="margin:4px 0 0;color:#555">Docker 로그 자동 수집<br/>컨테이너별 라벨링</p>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px;text-align:center">
              <b style="color:#0284c7">Loki</b>
              <p style="margin:4px 0 0;color:#555">로그 저장/쿼리 엔진<br/>LogQL 쿼리 언어</p>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px;text-align:center">
              <b style="color:#0284c7">Grafana</b>
              <p style="margin:4px 0 0;color:#555">시각화 대시보드<br/>알림 규칙/Slack 연동</p>
            </div>
          </div>
          <div style="background:#0c4a6e;color:#bae6fd;padding:10px;border-radius:6px;font-family:monospace;font-size:10px;line-height:1.6;margin-top:10px">
            # docker-compose.yml 추가 예시<br/>
            loki: { image: grafana/loki:latest, ports: ["3100:3100"] }<br/>
            promtail: { image: grafana/promtail, volumes: ["/var/log:/var/log", "/var/lib/docker/containers:/var/lib/docker/containers:ro"] }<br/>
            grafana: { image: grafana/grafana, ports: ["3000:3000"] }
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 운영 서버 인프라 분석 ──
  {
    title: '운영 서버 인프라 분석 & 부하 현황',
    content: `
      <div style="display:grid;gap:16px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#eef2ff,#e0e7ff);padding:18px;border-radius:14px;border:1px solid #a5b4fc">
            <h4 style="color:#4f46e5;margin:0 0 12px">서버 사양</h4>
            <div style="display:grid;gap:6px;font-size:13px">
              <div style="display:flex;justify-content:space-between;background:#fff;padding:8px 12px;border-radius:6px">
                <span>CPU</span><b>ARM Neoverse-N1 x 4 cores</b>
              </div>
              <div style="display:flex;justify-content:space-between;background:#fff;padding:8px 12px;border-radius:6px">
                <span>RAM</span><b>24 GB</b>
              </div>
              <div style="display:flex;justify-content:space-between;background:#fff;padding:8px 12px;border-radius:6px">
                <span>Disk</span><b>145 GB SSD (사용 10%)</b>
              </div>
              <div style="display:flex;justify-content:space-between;background:#fff;padding:8px 12px;border-radius:6px">
                <span>Arch</span><b>aarch64 (Oracle Cloud)</b>
              </div>
              <div style="display:flex;justify-content:space-between;background:#fff;padding:8px 12px;border-radius:6px">
                <span>Swap</span><b>미설정 (권장: 4GB)</b>
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#ecfdf5,#d1fae5);padding:18px;border-radius:14px;border:1px solid #6ee7b7">
            <h4 style="color:#059669;margin:0 0 12px">컨테이너별 리소스 사용량</h4>
            <div style="display:grid;gap:4px;font-size:12px">
              <div style="display:flex;justify-content:space-between;background:#fff;padding:6px 10px;border-radius:6px">
                <span>nginx-proxy-manager</span><b style="color:#f59e0b">257 MB (1.1%)</b>
              </div>
              <div style="display:flex;justify-content:space-between;background:#fff;padding:6px 10px;border-radius:6px">
                <span>sappt-backend</span><b>74 MB (0.3%)</b>
              </div>
              <div style="display:flex;justify-content:space-between;background:#fff;padding:6px 10px;border-radius:6px">
                <span>sappt-loki</span><b>55 MB (0.2%)</b>
              </div>
              <div style="display:flex;justify-content:space-between;background:#fff;padding:6px 10px;border-radius:6px">
                <span>sappt-grafana</span><b>46 MB (0.2%)</b>
              </div>
              <div style="display:flex;justify-content:space-between;background:#fff;padding:6px 10px;border-radius:6px">
                <span>sappt-postgres</span><b>28 MB (0.1%)</b>
              </div>
              <div style="display:flex;justify-content:space-between;background:#fff;padding:6px 10px;border-radius:6px">
                <span>sappt-promtail</span><b>23 MB (0.1%)</b>
              </div>
              <div style="display:flex;justify-content:space-between;background:#fff;padding:6px 10px;border-radius:6px">
                <span>frontend x 2</span><b>5 MB x 2</b>
              </div>
              <div style="background:#f0fdf4;padding:8px 10px;border-radius:6px;margin-top:4px;text-align:center">
                <b style="color:#059669">총 메모리 사용: 493 MB / 24 GB (2.1%) — 매우 여유</b>
              </div>
            </div>
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#fff7ed,#ffedd5);padding:18px;border-radius:14px;border:1px solid #fdba74">
          <h4 style="color:#ea580c;margin:0 0 10px">보완 권장사항</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;font-size:12px">
            <div style="background:#fff;padding:12px;border-radius:8px">
              <b style="color:#dc2626">Swap 설정</b>
              <p style="margin:4px 0 0;color:#555">현재 0. OOM 방지를 위해 4GB swap 파일 생성 권장.<br/><code>fallocate -l 4G /swapfile</code></p>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px">
              <b style="color:#f59e0b">Docker 이미지 정리</b>
              <p style="margin:4px 0 0;color:#555">미사용 이미지 11GB. <code>docker system prune</code>로 정리 권장. 빌드 캐시 6.8GB 회수 가능.</p>
            </div>
            <div style="background:#fff;padding:12px;border-radius:8px">
              <b style="color:#0284c7">NPM 메모리 최적화</b>
              <p style="margin:4px 0 0;color:#555">Nginx PM이 257MB 사용 (최대). memory limit 설정 권장: <code>mem_limit: 256m</code></p>
            </div>
          </div>
        </div>

        <div style="background:#f8fafc;padding:14px;border-radius:12px;border:1px solid #e2e8f0;font-size:12px">
          <b>결론:</b> 현재 서버 리소스는 <span style="color:#059669;font-weight:700">매우 여유</span>로운 상태. 전체 메모리 사용 2.1%, 디스크 10%.
          Loki/Grafana/Promtail 추가 후에도 충분한 여유. 향후 사용자 100명 이상 동시 접속 시에도 현재 스펙으로 대응 가능.
        </div>
      </div>
    `,
  },
  // ── Slide: 고도화 Phase 4 — 엔터프라이즈 ──
  {
    title: 'Phase 4 — 엔터프라이즈 플랫폼 (2027)',
    component: (
      <div style={{ display: 'grid', gap: 16 }}>
        <div dangerouslySetInnerHTML={{ __html: `
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#eef2ff10,#e0e7ff10);padding:18px;border-radius:14px;border:1px solid #a5b4fc">
            <h4 style="color:#4f46e5;margin:0 0 10px;font-size:14px">4-1. AI/ML 연계</h4>
            <div style="font-size:11px;color:#555;line-height:1.8">
              &bull; <b>약효 예측 모델</b> &mdash; 과거 시험 데이터 기반 ML 모델 (scikit-learn / XGBoost)<br/>
              &bull; <b>이상치 자동 탐지</b> &mdash; 조사 데이터 입력 시 비정상 수치 경고<br/>
              &bull; <b>자연어 보고서</b> &mdash; LLM 기반 시험 결과 요약 자동 생성<br/>
              &bull; <b>이미지 분석</b> &mdash; 병해 사진 AI 분류 (발병도 자동 판정)
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#f0fdf410,#dcfce710);padding:18px;border-radius:14px;border:1px solid #86efac">
            <h4 style="color:#16a34a;margin:0 0 10px;font-size:14px">4-2. GLP 인증 대응</h4>
            <div style="font-size:11px;color:#555;line-height:1.8">
              &bull; <b>전자서명</b> &mdash; GPKI / 공동인증서 기반 법적 효력 서명<br/>
              &bull; <b>감사 추적 강화</b> &mdash; 필드 레벨 변경 이력 (JSON diff 저장)<br/>
              &bull; <b>데이터 무결성</b> &mdash; SHA-256 해시 체인 + 타임스탬프 검증<br/>
              &bull; <b>접근 로그</b> &mdash; 데이터 열람 이력까지 전수 기록
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#faf5ff10,#ede9fe10);padding:18px;border-radius:14px;border:1px solid #c4b5fd">
            <h4 style="color:#7c3aed;margin:0 0 10px;font-size:14px">4-3. 멀티테넌트</h4>
            <div style="font-size:11px;color:#555;line-height:1.8">
              &bull; <b>다기관 지원</b> &mdash; 여러 연구기관이 하나의 인스턴스 공유<br/>
              &bull; <b>데이터 격리</b> &mdash; Schema 분리 또는 Row-Level Security<br/>
              &bull; <b>기관별 커스터마이징</b> &mdash; 양식 템플릿, 조사항목, 워크플로우<br/>
              &bull; <b>관리자 콘솔</b> &mdash; SaaS 운영 대시보드
            </div>
          </div>
        </div>` }} />
        <div style={{ background: 'linear-gradient(135deg,#f0f9ff,#e0f2fe)', padding: 18, borderRadius: 14, border: '1px solid #7dd3fc' }}>
          <h4 style={{ color: '#0284c7', margin: '0 0 10px', fontSize: 14 }}>목표 아키텍처 (To-Be v3.0)</h4>
          <ArchFlow
            height={460}
            groups={[
              { id: 'g_ext', label: '🌐 External', x: 0, y: 0, w: 210, h: 410, tone: 'slate' },
              { id: 'g_app', label: '🧩 Application Layer', x: 380, y: 0, w: 230, h: 310, tone: 'green' },
              { id: 'g_data', label: '💾 Data Layer', x: 660, y: 0, w: 210, h: 240, tone: 'amber' },
              { id: 'g_ops', label: '⚙️ Operations', x: 660, y: 290, w: 210, h: 120, tone: 'purple' },
              { id: 'g_ai', label: '🤖 AI/ML', x: 920, y: 90, w: 200, h: 180, tone: 'red' },
            ]}
            nodes={[
              { id: 'user', group: 'g_ext', x: 16, y: 36, label: 'Admin / Partner', sub: 'Mobile', tone: 'slate' },
              { id: 'github', group: 'g_ext', x: 16, y: 108, label: 'GitHub Actions', sub: 'CI/CD', tone: 'slate' },
              { id: 'notify', group: 'g_ext', x: 16, y: 180, label: 'Telegram/Email', sub: 'KakaoTalk', tone: 'slate' },
              { id: 'tims', group: 'g_ext', x: 16, y: 252, label: 'TIMS', sub: '농약관리시스템', tone: 'slate' },
              { id: 'weather', group: 'g_ext', x: 16, y: 324, label: '기상청 API', tone: 'slate' },
              { id: 'npm_f', x: 270, y: 200, label: 'NPM + SSL', emoji: '🔀', tone: 'orange' },
              { id: 'fe', group: 'g_app', x: 16, y: 40, label: 'Admin Frontend', tone: 'green' },
              { id: 'pfe', group: 'g_app', x: 16, y: 110, label: 'Partner Frontend', tone: 'green' },
              { id: 'pwa', group: 'g_app', x: 16, y: 180, label: 'Mobile PWA', tone: 'green' },
              { id: 'be', group: 'g_app', x: 16, y: 250, label: 'FastAPI Backend', sub: 'Modular', tone: 'green' },
              { id: 'pg', group: 'g_data', x: 16, y: 40, label: 'PostgreSQL 17', sub: '+ Alembic', emoji: '🗄️', tone: 'amber' },
              { id: 'oci_obs', group: 'g_data', x: 16, y: 110, label: 'OCI Object Storage', emoji: '☁️', tone: 'amber' },
              { id: 'redis', group: 'g_data', x: 16, y: 180, label: 'Redis Cache', emoji: '🔴', tone: 'red' },
              { id: 'graf', group: 'g_ops', x: 16, y: 40, label: 'Grafana + Loki', tone: 'purple' },
              { id: 'cron', group: 'g_ops', x: 110, y: 40, label: 'Backup/Alert Cron', tone: 'purple' },
              { id: 'ml', group: 'g_ai', x: 16, y: 40, label: 'Prediction Model', tone: 'red' },
              { id: 'llm', group: 'g_ai', x: 16, y: 110, label: 'Report Generator', sub: 'LLM', tone: 'red' },
            ]}
            edges={[
              { source: 'user', target: 'npm_f', label: 'HTTPS', tone: 'slate', animated: true },
              { source: 'github', target: 'be', label: 'Deploy', tone: 'slate', dashed: true },
              { source: 'npm_f', target: 'fe', tone: 'orange' },
              { source: 'npm_f', target: 'pfe', tone: 'orange' },
              { source: 'npm_f', target: 'pwa', tone: 'orange' },
              { source: 'npm_f', target: 'be', tone: 'orange' },
              { source: 'be', target: 'pg', tone: 'amber', animated: true },
              { source: 'be', target: 'oci_obs', tone: 'amber' },
              { source: 'be', target: 'redis', tone: 'red' },
              { source: 'be', target: 'notify', label: 'Alert', tone: 'slate', dashed: true },
              { source: 'be', target: 'tims', tone: 'slate' },
              { source: 'be', target: 'weather', tone: 'slate' },
              { source: 'be', target: 'ml', tone: 'red', animated: true },
              { source: 'be', target: 'llm', tone: 'red' },
              { source: 'cron', target: 'pg', tone: 'purple', dashed: true },
              { source: 'graf', target: 'be', tone: 'purple', dashed: true },
            ]}
          />
        </div>
      </div>
    ),
  },
  // ── Slide: Grafana 통합 모니터링 구성 ──
  {
    title: 'Grafana 통합 모니터링 (log.sappt.co.kr)',
    content: `
      <div style="display:grid;gap:16px">
        <div style="background:linear-gradient(135deg,#fff7ed,#ffedd5);padding:18px;border-radius:14px;border:1px solid #fdba74">
          <h4 style="color:#ea580c;margin:0 0 10px">Loki + Grafana + Promtail 아키텍처</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;font-size:12px">
            <div style="background:#fff;padding:14px;border-radius:8px;text-align:center">
              <div style="font-size:22px;margin-bottom:6px">📡</div>
              <b style="color:#ea580c">Promtail</b>
              <p style="margin:4px 0 0;color:#555">Docker 컨테이너 로그 자동 수집<br/>레벨 자동 감지 (ERROR/WARN/INFO)</p>
            </div>
            <div style="background:#fff;padding:14px;border-radius:8px;text-align:center">
              <div style="font-size:22px;margin-bottom:6px">💾</div>
              <b style="color:#ea580c">Loki</b>
              <p style="margin:4px 0 0;color:#555">로그 저장/쿼리 엔진<br/>7일 보관, 자동 압축, LogQL</p>
            </div>
            <div style="background:#fff;padding:14px;border-radius:8px;text-align:center">
              <div style="font-size:22px;margin-bottom:6px">📊</div>
              <b style="color:#ea580c">Grafana</b>
              <p style="margin:4px 0 0;color:#555">시각화 대시보드 7개<br/>Loki + PostgreSQL 데이터소스</p>
            </div>
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#eef2ff,#e0e7ff);padding:18px;border-radius:14px;border:1px solid #a5b4fc">
          <h4 style="color:#4f46e5;margin:0 0 10px">대시보드 구성 (7개)</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:12px">
            <div style="background:#fff;padding:10px;border-radius:6px;display:flex;gap:8px;align-items:center">
              <span style="font-size:16px">🏠</span>
              <div><b>SAPPT 홈</b> — 통합 현황, 빠른 링크, 금일 활동, 최근 로그</div>
            </div>
            <div style="background:#fff;padding:10px;border-radius:6px;display:flex;gap:8px;align-items:center">
              <span style="font-size:16px">📈</span>
              <div><b>비즈니스 분석</b> — 의뢰사/구분/지역/약제 교차 분석</div>
            </div>
            <div style="background:#fff;padding:10px;border-radius:6px;display:flex;gap:8px;align-items:center">
              <span style="font-size:16px">🔐</span>
              <div><b>보안 감사 로그</b> — 시간대별 활동, 사용자/IP별, 실시간 로그</div>
            </div>
            <div style="background:#fff;padding:10px;border-radius:6px;display:flex;gap:8px;align-items:center">
              <span style="font-size:16px">💾</span>
              <div><b>PostgreSQL 분석</b> — 테이블 크기, 인덱스, 캐시 히트율, FK</div>
            </div>
            <div style="background:#fff;padding:10px;border-radius:6px;display:flex;gap:8px;align-items:center">
              <span style="font-size:16px">🌾</span>
              <div><b>시험 현장 분석</b> — 포장주, 처리/조사 차수, 약제, 지역 분포</div>
            </div>
            <div style="background:#fff;padding:10px;border-radius:6px;display:flex;gap:8px;align-items:center">
              <span style="font-size:16px">📋</span>
              <div><b>컨테이너 로그</b> — 볼륨 추이, 에러율, 실시간 에러/API 로그</div>
            </div>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
          <div style="background:#f8fafc;padding:14px;border-radius:12px;border:1px solid #e2e8f0">
            <h4 style="margin:0 0 8px;font-size:13px;color:#475569">접속 정보</h4>
            <div style="font-size:12px;line-height:2;color:#334155">
              <b>URL:</b> https://log.sappt.co.kr<br/>
              <b>계정:</b> admin / sappt2026!<br/>
              <b>시간대:</b> Asia/Seoul (KST)<br/>
              <b>자동 새로고침:</b> 10초~5분 (대시보드별)
            </div>
          </div>
          <div style="background:#f8fafc;padding:14px;border-radius:12px;border:1px solid #e2e8f0">
            <h4 style="margin:0 0 8px;font-size:13px;color:#475569">데이터소스</h4>
            <div style="font-size:12px;line-height:2;color:#334155">
              <b>Loki</b> — 컨테이너 로그 (실시간)<br/>
              <b>PostgreSQL</b> — 비즈니스 데이터, 감사 로그<br/>
              <b>보관:</b> 로그 7일 / DB 영구<br/>
              <b>수집:</b> Docker JSON 로그 자동
            </div>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: 권한 관리 시스템 (RBAC) ──
  {
    title: '권한 관리 시스템 (RBAC)',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:20px">
        <div style="background:linear-gradient(135deg,#667eea10,#764ba210);padding:20px;border-radius:14px;border:1px solid #667eea20">
          <h3 style="color:#667eea;margin:0 0 12px;font-size:15px">3단계 권한 체계</h3>
          <div style="background:#fff;border-radius:10px;padding:14px;margin-bottom:12px;text-align:center;border:1px solid #e2e8f0">
            <div style="display:flex;align-items:center;justify-content:center;gap:8px;font-size:14px;font-weight:600">
              <span style="background:#eef2ff;color:#4f46e5;padding:6px 14px;border-radius:8px">사용자</span>
              <span style="color:#94a3b8">&rarr;</span>
              <span style="background:#f0fdf4;color:#16a34a;padding:6px 14px;border-radius:8px">역할 (N개)</span>
              <span style="color:#94a3b8">&rarr;</span>
              <span style="background:#fef3c7;color:#d97706;padding:6px 14px;border-radius:8px">권한 (메뉴+액션)</span>
            </div>
          </div>
          <div style="font-size:12.5px;color:#555;line-height:2">
            &bull; <strong>이중 검증</strong> &mdash; 프론트(메뉴 숨김) + 백엔드(API 403 차단)<br/>
            &bull; <strong>인메모리 캐시</strong> &mdash; 5분 TTL, 변경 시 즉시 무효화<br/>
            &bull; <strong>다중 역할</strong> &mdash; 1인에 여러 역할 할당 가능<br/>
            &bull; <strong>감사 추적</strong> &mdash; 모든 권한 변경을 활동이력에 기록
          </div>
        </div>
        <div style="background:linear-gradient(135deg,#f093fb10,#f5576c10);padding:20px;border-radius:14px;border:1px solid #f093fb20">
          <h3 style="color:#f5576c;margin:0 0 12px;font-size:15px">DB 모델 (5개 테이블)</h3>
          <table style="width:100%;font-size:12px;border-collapse:separate;border-spacing:0 4px;color:#444">
            <tr><td style="padding:7px 10px;font-weight:700;background:#fdf2f8;border-radius:6px 0 0 6px;white-space:nowrap">permissions</td><td style="padding:7px 10px;background:#fdf2f8;border-radius:0 6px 6px 0">메뉴 권한 마스터 (13개 메뉴, sort_order + group_sort_order)</td></tr>
            <tr><td style="padding:7px 10px;font-weight:700;background:#fdf2f8;border-radius:6px 0 0 6px;white-space:nowrap">permission_actions</td><td style="padding:7px 10px;background:#fdf2f8;border-radius:0 6px 6px 0">메뉴별 액션 (READ / WRITE / DELETE / EXPORT)</td></tr>
            <tr><td style="padding:7px 10px;font-weight:700;background:#fdf2f8;border-radius:6px 0 0 6px;white-space:nowrap">roles</td><td style="padding:7px 10px;background:#fdf2f8;border-radius:0 6px 6px 0">역할 마스터 (시스템관리자 / 연구원 / 열람자 + 커스텀)</td></tr>
            <tr><td style="padding:7px 10px;font-weight:700;background:#fdf2f8;border-radius:6px 0 0 6px;white-space:nowrap">role_permissions</td><td style="padding:7px 10px;background:#fdf2f8;border-radius:0 6px 6px 0">역할-권한 매핑 (N:M)</td></tr>
            <tr><td style="padding:7px 10px;font-weight:700;background:#fdf2f8;border-radius:6px 0 0 6px;white-space:nowrap">user_roles</td><td style="padding:7px 10px;background:#fdf2f8;border-radius:0 6px 6px 0">사용자-역할 매핑 (N:M, 만료일 지원)</td></tr>
          </table>
        </div>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:12px;margin-bottom:20px">
        <div style="background:#eef2ff;padding:14px;border-radius:10px;border:1px solid #c7d2fe;text-align:center">
          <div style="font-size:26px;font-weight:800;color:#4f46e5">13</div>
          <div style="font-size:11px;color:#6366f1;margin-top:2px">메뉴 권한</div>
        </div>
        <div style="background:#f0fdf4;padding:14px;border-radius:10px;border:1px solid #86efac;text-align:center">
          <div style="font-size:26px;font-weight:800;color:#16a34a">3+</div>
          <div style="font-size:11px;color:#22c55e;margin-top:2px">기본 역할</div>
        </div>
        <div style="background:#fef3c7;padding:14px;border-radius:10px;border:1px solid #fcd34d;text-align:center">
          <div style="font-size:26px;font-weight:800;color:#d97706">4</div>
          <div style="font-size:11px;color:#f59e0b;margin-top:2px">액션 유형</div>
        </div>
        <div style="background:#fce7f3;padding:14px;border-radius:10px;border:1px solid #f9a8d4;text-align:center">
          <div style="font-size:26px;font-weight:800;color:#db2777">6</div>
          <div style="font-size:11px;color:#ec4899;margin-top:2px">메뉴 그룹</div>
        </div>
      </div>

      <div style="background:#f8fafc;padding:16px;border-radius:12px;border:1px solid #e2e8f0">
        <h4 style="margin:0 0 10px;font-size:13px;color:#475569">기본 역할별 권한 매트릭스</h4>
        <table style="width:100%;font-size:12px;border-collapse:separate;border-spacing:0 3px">
          <tr><th style="padding:7px 10px;text-align:left;background:#f1f5f9;border-radius:6px 0 0 6px;color:#64748b">역할</th><th style="padding:7px 10px;text-align:center;background:#f1f5f9;color:#64748b">시험관리</th><th style="padding:7px 10px;text-align:center;background:#f1f5f9;color:#64748b">데이터</th><th style="padding:7px 10px;text-align:center;background:#f1f5f9;color:#64748b">서버</th><th style="padding:7px 10px;text-align:center;background:#f1f5f9;color:#64748b">운영</th><th style="padding:7px 10px;text-align:center;background:#f1f5f9;border-radius:0 6px 6px 0;color:#64748b">시스템</th></tr>
          <tr><td style="padding:7px 10px;font-weight:600;background:#fff;border-radius:6px 0 0 6px">시스템관리자</td><td style="padding:7px;text-align:center;background:#f0fdf4;color:#16a34a;font-weight:700">FULL</td><td style="padding:7px;text-align:center;background:#f0fdf4;color:#16a34a;font-weight:700">FULL</td><td style="padding:7px;text-align:center;background:#f0fdf4;color:#16a34a;font-weight:700">FULL</td><td style="padding:7px;text-align:center;background:#f0fdf4;color:#16a34a;font-weight:700">FULL</td><td style="padding:7px;text-align:center;background:#f0fdf4;color:#16a34a;font-weight:700;border-radius:0 6px 6px 0">FULL</td></tr>
          <tr><td style="padding:7px 10px;font-weight:600;background:#fff;border-radius:6px 0 0 6px">연구원</td><td style="padding:7px;text-align:center;background:#eef2ff;color:#4f46e5;font-weight:600">R/W/D/E</td><td style="padding:7px;text-align:center;background:#f8fafc;color:#94a3b8">R</td><td style="padding:7px;text-align:center;background:#fef2f2;color:#ef4444">&mdash;</td><td style="padding:7px;text-align:center;background:#fef2f2;color:#ef4444">&mdash;</td><td style="padding:7px;text-align:center;background:#f8fafc;color:#94a3b8;border-radius:0 6px 6px 0">R</td></tr>
          <tr><td style="padding:7px 10px;font-weight:600;background:#fff;border-radius:6px 0 0 6px">열람자</td><td style="padding:7px;text-align:center;background:#f8fafc;color:#94a3b8">R</td><td style="padding:7px;text-align:center;background:#fef2f2;color:#ef4444">&mdash;</td><td style="padding:7px;text-align:center;background:#fef2f2;color:#ef4444">&mdash;</td><td style="padding:7px;text-align:center;background:#fef2f2;color:#ef4444">&mdash;</td><td style="padding:7px;text-align:center;background:#f8fafc;color:#94a3b8;border-radius:0 6px 6px 0">R</td></tr>
        </table>
      </div>
    `,
  },
  // ── Slide: 메뉴 순서 & 사용자 관리 ──
  {
    title: '메뉴 순서 관리 & 사용자/역할 관리',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:20px">
        <div style="background:linear-gradient(135deg,#667eea10,#764ba210);padding:20px;border-radius:14px;border:1px solid #667eea20">
          <h3 style="color:#667eea;margin:0 0 14px;font-size:15px">메뉴 순서 관리</h3>
          <div style="font-size:12.5px;color:#555;line-height:2">
            &bull; <strong>드래그앤드롭</strong>으로 그룹/메뉴 순서 변경 (@dnd-kit)<br/>
            &bull; <strong>그룹 간 이동</strong> &mdash; 그룹 헤더 핸들을 드래그<br/>
            &bull; <strong>그룹 내 메뉴 정렬</strong> &mdash; 메뉴 핸들을 드래그<br/>
            &bull; <strong>메뉴명/그룹명 인라인 편집</strong> &mdash; 클릭 즉시 수정<br/>
            &bull; 수정 시 <span style="background:#fef3c7;color:#92400e;padding:1px 6px;border-radius:4px;font-size:11px;font-weight:600">수정됨</span> 태그 표시<br/>
            &bull; <strong>초기화</strong> &mdash; 기본 순서/이름으로 일괄 복원
          </div>
          <div style="margin-top:14px;background:#fff;border-radius:10px;padding:12px;border:1px solid #e2e8f0">
            <div style="font-size:11px;color:#64748b;font-weight:600;margin-bottom:6px">정렬 기준 (DB)</div>
            <code style="font-size:12px;background:#f1f5f9;padding:6px 10px;border-radius:6px;display:block;color:#334155">ORDER BY group_sort_order ASC, sort_order ASC</code>
          </div>
        </div>
        <div style="background:linear-gradient(135deg,#43e97b10,#38f9d710);padding:20px;border-radius:14px;border:1px solid #43e97b20">
          <h3 style="color:#16a34a;margin:0 0 14px;font-size:15px">사용자 & 역할 관리 UI</h3>
          <div style="display:grid;gap:10px">
            <div style="background:#fff;border-radius:10px;padding:12px;border:1px solid #e2e8f0">
              <div style="font-weight:600;color:#334155;margin-bottom:4px;font-size:13px">사용자 관리</div>
              <div style="font-size:12px;color:#64748b;line-height:1.8">
                사용자 CRUD, 다중 역할 할당, 활성/비활성 전환,<br/>비밀번호 재설정
              </div>
            </div>
            <div style="background:#fff;border-radius:10px;padding:12px;border:1px solid #e2e8f0">
              <div style="font-weight:600;color:#334155;margin-bottom:4px;font-size:13px">역할/권한 관리 (2탭)</div>
              <div style="font-size:12px;color:#64748b;line-height:1.8">
                <strong>권한 매트릭스 탭</strong> &mdash; 역할별 메뉴&times;액션 체크박스<br/>
                <strong>메뉴 순서 탭</strong> &mdash; 드래그앤드롭 + 이름 편집
              </div>
            </div>
            <div style="background:#fff;border-radius:10px;padding:12px;border:1px solid #e2e8f0">
              <div style="font-weight:600;color:#334155;margin-bottom:4px;font-size:13px">동적 사이드바 메뉴</div>
              <div style="font-size:12px;color:#64748b;line-height:1.8">
                로그인 시 <code>/api/auth/me</code>에서 권한 로드 &rarr;<br/>
                DB 순서 기준으로 사이드바 메뉴 자동 구성
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style="background:#f8fafc;padding:16px;border-radius:12px;border:1px solid #e2e8f0">
        <h4 style="margin:0 0 10px;font-size:13px;color:#475569">권한/메뉴 관리 API 전체 목록</h4>
        <table style="width:100%;font-size:12px;border-collapse:separate;border-spacing:0 3px">
          <tr><th style="padding:6px 10px;text-align:left;background:#f1f5f9;border-radius:6px 0 0 6px;color:#64748b;width:60px">메서드</th><th style="padding:6px 10px;text-align:left;background:#f1f5f9;color:#64748b">엔드포인트</th><th style="padding:6px 10px;text-align:left;background:#f1f5f9;border-radius:0 6px 6px 0;color:#64748b">설명</th></tr>
          <tr><td style="padding:6px 10px;background:#eef2ff;border-radius:6px 0 0 6px"><code style="color:#4f46e5;font-weight:600">GET</code></td><td style="padding:6px 10px;background:#fff"><code>/api/auth/me</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">내 정보 + 역할 + 메뉴 권한 목록</td></tr>
          <tr><td style="padding:6px 10px;background:#eef2ff;border-radius:6px 0 0 6px"><code style="color:#4f46e5;font-weight:600">GET</code></td><td style="padding:6px 10px;background:#fff"><code>/api/permissions/matrix</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">역할&times;권한 매트릭스 (PA ID 포함)</td></tr>
          <tr><td style="padding:6px 10px;background:#f0fdf4;border-radius:6px 0 0 6px"><code style="color:#16a34a;font-weight:600">CRUD</code></td><td style="padding:6px 10px;background:#fff"><code>/api/roles</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">역할 생성/조회/수정/삭제</td></tr>
          <tr><td style="padding:6px 10px;background:#fef3c7;border-radius:6px 0 0 6px"><code style="color:#d97706;font-weight:600">PUT</code></td><td style="padding:6px 10px;background:#fff"><code>/api/roles/{id}/permissions</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">역할별 권한 일괄 설정</td></tr>
          <tr><td style="padding:6px 10px;background:#f0fdf4;border-radius:6px 0 0 6px"><code style="color:#16a34a;font-weight:600">CRUD</code></td><td style="padding:6px 10px;background:#fff"><code>/api/users</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">사용자 생성/조회/수정/삭제 + 역할 할당</td></tr>
          <tr><td style="padding:6px 10px;background:#eef2ff;border-radius:6px 0 0 6px"><code style="color:#4f46e5;font-weight:600">GET</code></td><td style="padding:6px 10px;background:#fff"><code>/api/permissions/menu-order</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">메뉴 순서 조회 (그룹별 + 메뉴명)</td></tr>
          <tr><td style="padding:6px 10px;background:#fef3c7;border-radius:6px 0 0 6px"><code style="color:#d97706;font-weight:600">PUT</code></td><td style="padding:6px 10px;background:#fff"><code>/api/permissions/menu-order</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">메뉴 순서 + 이름 일괄 저장</td></tr>
          <tr><td style="padding:6px 10px;background:#fce7f3;border-radius:6px 0 0 6px"><code style="color:#db2777;font-weight:600">POST</code></td><td style="padding:6px 10px;background:#fff"><code>/api/permissions/menu-order/reset</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">메뉴 순서/이름 기본값 초기화</td></tr>
        </table>
      </div>
    `,
  },
  // ── Slide: TIMS 연동 준비 ──
  {
    title: 'TIMS 연동 준비 (농약관리통합정보시스템)',
    content: `
      <div style="display:grid;gap:20px">
        <div style="padding:16px 20px;background:linear-gradient(135deg,#eef2ff,#e0e7ff);border-radius:12px;border-left:4px solid #4f46e5">
          <h3 style="margin:0 0 8px;color:#4f46e5">연동 목적</h3>
          <ul style="margin:0;padding-left:20px;line-height:2;color:#334155">
            <li><b>농약 품목 정보 역방향 조회</b> — TIMS 등록번호 기반 자동 매핑</li>
            <li><b>시험 결과 데이터 자동 전송</b> — 약효/약해 결과 API 제출</li>
            <li><b>TIMS 입력현황 자동 갱신</b> — 제출 상태 실시간 추적</li>
          </ul>
        </div>
        <h3 style="margin:0;color:#1e293b">신규 데이터 모델 (3개 테이블)</h3>
        <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:13px">
          <tr style="background:#f1f5f9"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;width:200px">tims_pesticides</td><td style="padding:8px 12px;border-radius:0 8px 8px 0">TIMS 농약 품목 마스터 캐시 (등록번호, 상품명, 유효성분, 적용 작물/병해충, 안전사용기준, 독성등급)</td></tr>
          <tr style="background:#f1f5f9"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px">tims_submissions</td><td style="padding:8px 12px;border-radius:0 8px 8px 0">시험결과 제출 이력 (스케줄 연계, 제출 상태 추적, 응답코드, 재시도)</td></tr>
          <tr style="background:#f1f5f9"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px">tims_api_logs</td><td style="padding:8px 12px;border-radius:0 8px 8px 0">API 호출 감사 로그 (요청/응답, 실행시간, 에러 추적)</td></tr>
        </table>
        <h3 style="margin:0;color:#1e293b">API 엔드포인트</h3>
        <table style="width:100%;border-collapse:separate;border-spacing:0 4px;font-size:13px">
          <tr><td style="padding:6px 10px;background:#eef2ff;border-radius:6px 0 0 6px;width:80px"><code style="color:#4f46e5;font-weight:600">GET/PUT</code></td><td style="padding:6px 10px;background:#fff"><code>/api/tims/config</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">연동 설정 조회/수정</td></tr>
          <tr><td style="padding:6px 10px;background:#f0fdf4;border-radius:6px 0 0 6px"><code style="color:#16a34a;font-weight:600">GET</code></td><td style="padding:6px 10px;background:#fff"><code>/api/tims/health</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">TIMS API 연결 테스트</td></tr>
          <tr><td style="padding:6px 10px;background:#eef2ff;border-radius:6px 0 0 6px"><code style="color:#4f46e5;font-weight:600">GET</code></td><td style="padding:6px 10px;background:#fff"><code>/api/tims/pesticides</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">농약 품목 검색 (키워드, 작물, 병해충)</td></tr>
          <tr><td style="padding:6px 10px;background:#fef3c7;border-radius:6px 0 0 6px"><code style="color:#d97706;font-weight:600">POST</code></td><td style="padding:6px 10px;background:#fff"><code>/api/tims/pesticides/sync</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">TIMS 품목 동기화</td></tr>
          <tr><td style="padding:6px 10px;background:#fef3c7;border-radius:6px 0 0 6px"><code style="color:#d97706;font-weight:600">POST</code></td><td style="padding:6px 10px;background:#fff"><code>/api/tims/submissions</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">시험결과 TIMS 제출</td></tr>
          <tr><td style="padding:6px 10px;background:#f0fdf4;border-radius:6px 0 0 6px"><code style="color:#16a34a;font-weight:600">GET</code></td><td style="padding:6px 10px;background:#fff"><code>/api/tims/api-logs</code></td><td style="padding:6px 10px;background:#fff;border-radius:0 6px 6px 0">API 호출 이력 조회</td></tr>
        </table>
        <div style="padding:14px 18px;background:#fffbeb;border-radius:10px;border:1px solid #fbbf24;font-size:13px;color:#92400e">
          <b>현재 상태:</b> 백엔드 모델·스키마·API·HTTP 클라이언트 준비 완료. TIMS API URL/Key 설정 후 즉시 연동 가능.<br/>
          <b>클라이언트:</b> <code>tims_client.py</code> — httpx 비동기 HTTP 클라이언트, 환경변수(<code>TIMS_API_BASE_URL</code>, <code>TIMS_API_KEY</code>) 주입 방식.
        </div>
      </div>
    `,
  },
  // ── Slide: Seed 순서 보존 정책 ──
  {
    title: '메뉴 순서 보존 정책 & 버그 수정 이력',
    content: `
      <div style="display:grid;gap:20px">
        <div style="padding:16px 20px;background:linear-gradient(135deg,#fef2f2,#fee2e2);border-radius:12px;border-left:4px solid #ef4444">
          <h3 style="margin:0 0 8px;color:#ef4444">해결된 이슈</h3>
          <p style="margin:0;color:#334155;line-height:1.8">배포(Pod 재시작) 시 <code>seed_permissions()</code>가 매번 메뉴 순서를 하드코딩 값으로 덮어쓰는 문제 수정.</p>
        </div>
        <h3 style="margin:0;color:#1e293b">변경 전 (문제)</h3>
        <div style="padding:14px;background:#1e293b;border-radius:10px;font-family:monospace;font-size:12px;color:#e2e8f0;line-height:1.8;overflow-x:auto">
          <span style="color:#94a3b8">// permissions.py — seed_permissions()</span><br/>
          <span style="color:#f87171">if perm exists:</span><br/>
          &nbsp;&nbsp;perm.sort_order = <span style="color:#fbbf24">SEED값</span> &nbsp;<span style="color:#f87171">← 사용자 변경 무시, 매번 원복</span><br/>
          &nbsp;&nbsp;perm.group_sort_order = <span style="color:#fbbf24">SEED값</span>
        </div>
        <h3 style="margin:0;color:#1e293b">변경 후 (수정)</h3>
        <div style="padding:14px;background:#1e293b;border-radius:10px;font-family:monospace;font-size:12px;color:#e2e8f0;line-height:1.8;overflow-x:auto">
          <span style="color:#94a3b8">// permissions.py — seed_permissions()</span><br/>
          <span style="color:#4ade80">if perm exists:</span><br/>
          &nbsp;&nbsp;<span style="color:#4ade80">// 기존 권한은 sort_order를 건드리지 않음 (사용자 커스텀 순서 보존)</span><br/>
          <span style="color:#4ade80">if perm NOT exists:</span><br/>
          &nbsp;&nbsp;새로 생성 시에만 SEED 기본값 적용
        </div>
        <h3 style="margin:0;color:#1e293b">기타 수정 사항</h3>
        <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:13px">
          <tr style="background:#fef2f2"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;width:200px;color:#ef4444">권한관리 500 에러</td><td style="padding:8px 12px;border-radius:0 8px 8px 0"><code>@app.get("/api/permissions")</code> 데코레이터가 헬퍼함수에 잘못 부착 → 실제 핸들러로 이동</td></tr>
          <tr style="background:#fef2f2"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;color:#ef4444">is_active NULL 에러</td><td style="padding:8px 12px;border-radius:0 8px 8px 0">terminal 메뉴의 <code>is_active=NULL</code> → 스키마를 <code>Optional[bool]</code>로 방어 처리</td></tr>
          <tr style="background:#fef2f2"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;color:#ef4444">대시보드 지도 깨짐</td><td style="padding:8px 12px;border-radius:0 8px 8px 0">탭 전환 시 Naver Map <code>display:none</code> 복귀 → <code>ResizeObserver</code>로 <code>setSize()</code> 자동 호출</td></tr>
        </table>
      </div>
    `,
  },
  // ── Slide: OpenSearch 고도화 전략 (개요) ──
  {
    title: 'OpenSearch 검색엔진 — 적용 완료 (v2.17.1 + nori)',
    content: `
      <div style="display:grid;gap:20px">
        <div style="padding:16px 20px;background:linear-gradient(135deg,#dcfce7,#bbf7d0);border-radius:12px;border-left:4px solid #16a34a">
          <h3 style="margin:0 0 8px;color:#166534">적용 완료</h3>
          <p style="margin:0;color:#14532d;line-height:1.8">OpenSearch 2.17.1 + nori 한글 분석기 + 농약 동의어 사전(50+항목) 적용 완료. 글로벌 통합검색, 챗봇 OpenSearch 연동, 모니터링 대시보드 추가.</p>
        </div>

        <h3 style="margin:0;color:#1e293b">AI 전문가 관점 — 지능형 검색 & 분석</h3>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
          <div style="padding:14px;background:#eef2ff;border-radius:10px;border:1px solid #c7d2fe">
            <h4 style="margin:0 0 6px;color:#4338ca">시맨틱 검색 (Semantic Search)</h4>
            <p style="margin:0;font-size:12px;color:#334155;line-height:1.7">OpenSearch의 <strong>k-NN 벡터 검색</strong>으로 "병해충 방제"를 검색하면 "약효 시험", "살균 테스트" 등 의미적으로 유사한 결과도 반환. 농약/시험 도메인 특화 임베딩 모델 적용 가능.</p>
          </div>
          <div style="padding:14px;background:#eef2ff;border-radius:10px;border:1px solid #c7d2fe">
            <h4 style="margin:0 0 6px;color:#4338ca">이상 탐지 (Anomaly Detection)</h4>
            <p style="margin:0;font-size:12px;color:#334155;line-height:1.7">사용자 활동 패턴의 이상 감지 — 비정상 로그인, 대량 다운로드, 시간외 접속 등을 ML 기반으로 자동 탐지. OpenSearch의 내장 AD 플러그인 활용.</p>
          </div>
          <div style="padding:14px;background:#eef2ff;border-radius:10px;border:1px solid #c7d2fe">
            <h4 style="margin:0 0 6px;color:#4338ca">자연어 쿼리 (NLQ)</h4>
            <p style="margin:0;font-size:12px;color:#334155;line-height:1.7">"지난달 A사 의뢰 벼 시험 몇건?" 같은 자연어를 OpenSearch 쿼리로 변환. 챗봇과 연동하여 비개발자도 복잡한 데이터 조회 가능.</p>
          </div>
          <div style="padding:14px;background:#eef2ff;border-radius:10px;border:1px solid #c7d2fe">
            <h4 style="margin:0 0 6px;color:#4338ca">추천 시스템</h4>
            <p style="margin:0;font-size:12px;color:#334155;line-height:1.7">검색어 자동완성, "이 프로토콜을 참조한 사용자들이 함께 본 프로토콜" 등 협업 필터링 기반 추천.</p>
          </div>
        </div>

        <h3 style="margin:0;color:#1e293b">현재 vs OpenSearch 적용 후 비교</h3>
        <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:12px">
          <tr style="background:#fee2e2"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;width:140px;color:#ef4444">현재</td><td style="padding:8px 12px">SQL LIKE '%keyword%' — 풀스캔, 한글 분리 불가, 관련성 정렬 없음</td></tr>
          <tr style="background:#dcfce7"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;color:#16a34a">OpenSearch</td><td style="padding:8px 12px">인버티드 인덱스 + nori 한글 분석기, 밀리초 검색, BM25 관련성 정렬, 하이라이팅</td></tr>
          <tr style="background:#fee2e2"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;color:#ef4444">현재 로그</td><td style="padding:8px 12px">Loki 7일 보관, 정규표현식 기반 검색만 가능</td></tr>
          <tr style="background:#dcfce7"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;color:#16a34a">OpenSearch</td><td style="padding:8px 12px">장기 보관 (90일+), 구조화된 쿼리, 실시간 대시보드, 알림</td></tr>
        </table>
      </div>
    `,
  },
  // ── Slide: OpenSearch 고도화 — 웹/연구소 전문가 관점 ──
  {
    title: 'OpenSearch 고도화 — 웹 & 연구소 전문가 관점',
    content: `
      <div style="display:grid;gap:20px">
        <h3 style="margin:0;color:#1e293b">웹 프로그램 전문가 관점 — 검색 UX & 성능</h3>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px">
          <div style="padding:14px;background:#f0fdf4;border-radius:10px;border:1px solid #bbf7d0">
            <h4 style="margin:0 0 6px;color:#16a34a">통합 검색 바</h4>
            <p style="margin:0;font-size:12px;color:#334155;line-height:1.7">헤더에 <strong>글로벌 검색</strong> 추가 — 일정/계획서/의뢰사/농약을 한번에 검색. 자동완성(suggest) + 하이라이팅 + 패싯(facet) 필터.</p>
          </div>
          <div style="padding:14px;background:#f0fdf4;border-radius:10px;border:1px solid #bbf7d0">
            <h4 style="margin:0 0 6px;color:#16a34a">실시간 동기화</h4>
            <p style="margin:0;font-size:12px;color:#334155;line-height:1.7">PostgreSQL CDC(Change Data Capture)로 DB 변경 시 OpenSearch 자동 동기화. <strong>Debezium + Logstash</strong> 파이프라인 또는 앱 레벨 듀얼 라이트.</p>
          </div>
          <div style="padding:14px;background:#f0fdf4;border-radius:10px;border:1px solid #bbf7d0">
            <h4 style="margin:0 0 6px;color:#16a34a">검색 성능</h4>
            <p style="margin:0;font-size:12px;color:#334155;line-height:1.7">현재 SQL LIKE: <strong>~100ms+</strong> (데이터 증가 시 선형 저하)<br/>OpenSearch: <strong>&lt;10ms</strong> (인덱스 기반, 데이터 무관)</p>
          </div>
        </div>

        <h3 style="margin:0;color:#1e293b">연구소 프로그램 전문가 관점 — 도메인 특화</h3>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
          <div style="padding:14px;background:#faf5ff;border-radius:10px;border:1px solid #e9d5ff">
            <h4 style="margin:0 0 6px;color:#7c3aed">농약 전문 검색 엔진</h4>
            <ul style="margin:4px 0 0;padding-left:16px;font-size:12px;color:#334155;line-height:1.8">
              <li>농약 품목명 동의어 사전 (예: 에마멕틴 = 에마벤조산)</li>
              <li>유효성분 → 품목 역방향 검색</li>
              <li>작물-병해충-농약 관계 그래프 검색</li>
              <li>TIMS 데이터 전문 인덱싱</li>
            </ul>
          </div>
          <div style="padding:14px;background:#faf5ff;border-radius:10px;border:1px solid #e9d5ff">
            <h4 style="margin:0 0 6px;color:#7c3aed">시험 데이터 분석 강화</h4>
            <ul style="margin:4px 0 0;padding-left:16px;font-size:12px;color:#334155;line-height:1.8">
              <li>시험 결과 전문 검색 (보고서 PDF/HWPX 내용 인덱싱)</li>
              <li>연도별/작물별/병해충별 시험 트렌드 분석</li>
              <li>유사 시험 프로토콜 자동 추천</li>
              <li>시험 데이터 집계 대시보드 (OpenSearch Dashboards)</li>
            </ul>
          </div>
          <div style="padding:14px;background:#faf5ff;border-radius:10px;border:1px solid #e9d5ff">
            <h4 style="margin:0 0 6px;color:#7c3aed">감사 & 컴플라이언스</h4>
            <ul style="margin:4px 0 0;padding-left:16px;font-size:12px;color:#334155;line-height:1.8">
              <li>활동이력 장기 보관 (Loki 7일 → OpenSearch 1년+)</li>
              <li>사용자별 행동 분석 대시보드</li>
              <li>데이터 변경 이력 추적 (감사 증적)</li>
              <li>규정 준수 리포트 자동 생성</li>
            </ul>
          </div>
          <div style="padding:14px;background:#faf5ff;border-radius:10px;border:1px solid #e9d5ff">
            <h4 style="margin:0 0 6px;color:#7c3aed">문서 검색 (첨부파일)</h4>
            <ul style="margin:4px 0 0;padding-left:16px;font-size:12px;color:#334155;line-height:1.8">
              <li>OCI Object Storage에 저장된 성적서/보고서 전문 인덱싱</li>
              <li>Ingest Pipeline으로 PDF/HWPX 텍스트 자동 추출</li>
              <li>첨부파일 내용 기반 검색 (현재 불가능)</li>
              <li>관련 문서 자동 그룹핑</li>
            </ul>
          </div>
        </div>
      </div>
    `,
  },
  // ── Slide: OpenSearch 고도화 — 구현 아키텍처 & 로드맵 ──
  {
    title: 'OpenSearch 구현 아키텍처 & 단계별 로드맵',
    content: `
      <div style="display:grid;gap:20px">
        <h3 style="margin:0;color:#1e293b">아키텍처 구성도</h3>
        <div style="padding:16px;background:#1e293b;border-radius:12px;font-family:monospace;font-size:12px;color:#e2e8f0;line-height:2;overflow-x:auto">
          <span style="color:#fbbf24">[PostgreSQL]</span> ──CDC──&gt; <span style="color:#34d399">[Logstash]</span> ──&gt; <span style="color:#60a5fa">[OpenSearch]</span> &lt;── <span style="color:#34d399">[Logstash]</span> &lt;── <span style="color:#fbbf24">[Promtail/Loki 로그]</span><br/>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#c084fc">↑</span><br/>
          <span style="color:#fbbf24">[FastAPI]</span> ──dual write──&gt; <span style="color:#60a5fa">[OpenSearch]</span> &lt;── <span style="color:#fb923c">[OpenSearch Dashboards]</span><br/>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#c084fc">↓</span><br/>
          <span style="color:#fbbf24">[React Frontend]</span> ──검색 API──&gt; <span style="color:#fbbf24">[FastAPI]</span> ──&gt; <span style="color:#60a5fa">[OpenSearch]</span>
        </div>

        <h3 style="margin:0;color:#1e293b">인덱스 설계</h3>
        <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:12px">
          <tr style="background:#e0f2fe"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;width:200px;color:#0369a1">sappt-schedules</td><td style="padding:8px 12px;border-radius:0 8px 8px 0">시험일정 (test_number, title, company, location, status, 처리/조사 일정)</td></tr>
          <tr style="background:#e0f2fe"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;color:#0369a1">sappt-protocols</td><td style="padding:8px 12px;border-radius:0 8px 8px 0">시험계획서 (title, purpose, disease, crop, treatments, institution)</td></tr>
          <tr style="background:#e0f2fe"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;color:#0369a1">sappt-pesticides</td><td style="padding:8px 12px;border-radius:0 8px 8px 0">농약 마스터 (품목명, 유효성분, 작물, 병해충 — TIMS 연동)</td></tr>
          <tr style="background:#e0f2fe"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;color:#0369a1">sappt-activity-logs</td><td style="padding:8px 12px;border-radius:0 8px 8px 0">활동이력 (장기 보관, 사용자 행동 분석, 감사 증적)</td></tr>
          <tr style="background:#e0f2fe"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;color:#0369a1">sappt-app-logs</td><td style="padding:8px 12px;border-radius:0 8px 8px 0">애플리케이션 로그 (Loki 대체, 구조화된 검색 + 장기 보관)</td></tr>
          <tr style="background:#e0f2fe"><td style="padding:8px 12px;font-weight:700;border-radius:8px 0 0 8px;color:#0369a1">sappt-documents</td><td style="padding:8px 12px;border-radius:0 8px 8px 0">첨부문서 전문 인덱스 (PDF/HWPX 텍스트 추출 후 인덱싱)</td></tr>
        </table>

        <h3 style="margin:0;color:#1e293b">단계별 로드맵</h3>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px">
          <div style="padding:14px;background:linear-gradient(135deg,#dcfce7,#bbf7d0);border-radius:10px;border-top:3px solid #16a34a">
            <h4 style="margin:0 0 8px;color:#16a34a">Phase 1 (2주)</h4>
            <p style="margin:0;font-size:11px;font-weight:600;color:#166534">기반 구축</p>
            <ul style="margin:6px 0 0;padding-left:14px;font-size:11px;color:#334155;line-height:1.8">
              <li>Docker Compose에 OpenSearch + Dashboards 추가</li>
              <li>nori 한글 분석기 설치</li>
              <li>activity_logs 인덱스 생성 & 초기 마이그레이션</li>
              <li>FastAPI opensearch-py 클라이언트 연동</li>
              <li>리소스: 4GB RAM, 20GB SSD</li>
            </ul>
          </div>
          <div style="padding:14px;background:linear-gradient(135deg,#e0f2fe,#bae6fd);border-radius:10px;border-top:3px solid #0284c7">
            <h4 style="margin:0 0 8px;color:#0284c7">Phase 2 (3주)</h4>
            <p style="margin:0;font-size:11px;font-weight:600;color:#0c4a6e">검색 고도화</p>
            <ul style="margin:6px 0 0;padding-left:14px;font-size:11px;color:#334155;line-height:1.8">
              <li>schedules/protocols 인덱스 구축</li>
              <li>글로벌 통합 검색 API 구현</li>
              <li>자동완성 (Completion Suggester)</li>
              <li>프론트엔드 통합 검색 바 추가</li>
              <li>검색 결과 하이라이팅</li>
            </ul>
          </div>
          <div style="padding:14px;background:linear-gradient(135deg,#faf5ff,#e9d5ff);border-radius:10px;border-top:3px solid #7c3aed">
            <h4 style="margin:0 0 8px;color:#7c3aed">Phase 3 (4주)</h4>
            <p style="margin:0;font-size:11px;font-weight:600;color:#4c1d95">AI & 분석</p>
            <ul style="margin:6px 0 0;padding-left:14px;font-size:11px;color:#334155;line-height:1.8">
              <li>농약 동의어 사전 구축</li>
              <li>문서 전문 인덱싱 (Ingest Pipeline)</li>
              <li>이상 탐지 대시보드</li>
              <li>시맨틱 검색 (벡터 임베딩)</li>
              <li>OpenSearch Dashboards 연동</li>
            </ul>
          </div>
        </div>
      </div>
    `,
  },

  // ── Slide: OpenSearch 동기화 & 운영 방안 (2026-08 적용) ──
  {
    title: 'OpenSearch 동기화 & 운영 방안 — 실시간 색인 + 야간 자가치유 (2026-08 적용)',
    content: `
      <div style="display:grid;gap:14px">

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="padding:14px;background:#f8fafc;border-radius:12px;border:1px solid #e2e8f0">
            <div style="font-size:13px;font-weight:700;color:#1e293b;margin-bottom:8px">⚡ 실시간 동기화 (이벤트 기반)</div>
            <div style="font-size:12px;line-height:1.95;color:#475569">
              데이터 변경 API가 호출되는 즉시 색인 반영:<br/>
              • 일정: 생성 · 수정 · 삭제 · <b style="color:#047857">일괄등록(bulk) · 엑셀 업로드</b><br/>
              • 계획서: 생성 · 수정 · 삭제 · <b style="color:#047857">HWPX 업로드</b><br/>
              • 활동이력: 기록 시 즉시 색인<br/>
              <span style="color:#94a3b8">※ 굵은 3개 경로는 2026-08 색인 누락 수정으로 보강됨</span>
            </div>
          </div>
          <div style="padding:14px;background:#ecfdf5;border-radius:12px;border:1px solid #a7f3d0">
            <div style="font-size:13px;font-weight:700;color:#047857;margin-bottom:8px">🌙 야간 자가치유 (매일 06:20 KST)</div>
            <div style="font-size:12px;line-height:1.95;color:#065f46">
              <code style="background:#d1fae5;padding:1px 6px;border-radius:5px">opensearch_sync_check.sh</code> 크론:<br/>
              ① 전체 재동기화 (sync_all — DB 기준 upsert)<br/>
              ② <b>고아 문서 자동 삭제</b> (DB에 없는 인덱스 문서 제거)<br/>
              ③ DB↔인덱스 건수 대조 → 불일치 시에만 Discord 알림<br/>
              <span style="color:#059669">※ 유령 문서 심기 테스트로 자동 제거 검증 완료</span>
            </div>
          </div>
        </div>

        <div style="padding:14px;background:#fff;border-radius:12px;border:1px solid #e2e8f0">
          <div style="font-size:13px;font-weight:700;color:#1e293b;margin-bottom:10px">🔧 2026-08 정합성 사건 회고 & 조치 (실측 기반)</div>
          <table style="width:100%;border-collapse:collapse;font-size:11.5px">
            <thead>
              <tr style="background:#f1f5f9;color:#334155">
                <th style="text-align:left;padding:7px 10px;border:1px solid #e2e8f0;width:26%">발견 문제</th>
                <th style="text-align:left;padding:7px 10px;border:1px solid #e2e8f0;width:34%">원인</th>
                <th style="text-align:left;padding:7px 10px;border:1px solid #e2e8f0">조치 및 결과</th>
              </tr>
            </thead>
            <tbody style="color:#475569">
              <tr>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">일정 인덱스 <b style="color:#b91c1c">유령 570건</b> (68%)</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">과거 데이터 일괄 정리 시 인덱스 삭제 미연동</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">인덱스 재구축 → <b style="color:#047857">273/273 완전 일치</b> + 야간 고아 삭제로 재발 방지</td>
              </tr>
              <tr style="background:#fafbfc">
                <td style="padding:7px 10px;border:1px solid #e2e8f0">계획서 <b style="color:#b91c1c">118건 색인 누락</b> (97%)</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">① upload-hwpx 경로 색인 미호출 ② 유효성분 함량(float) join TypeError로 조용한 실패</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">색인 호출 추가 + str 변환 수정 → <b style="color:#047857">122/122 완전 일치</b>, 약제 유효성분 검색 정상화</td>
              </tr>
              <tr>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">bulk·엑셀 업로드 색인 누락</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">일괄 경로 3곳에 색인 호출 부재</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">commit 후 touched_ids 일괄 색인 추가</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px">
          <div style="padding:13px;background:#eff6ff;border-radius:10px;border:1px solid #bfdbfe">
            <div style="font-size:12px;font-weight:700;color:#1d4ed8;margin-bottom:6px">📊 운영 부하 실측 (2026-08)</div>
            <div style="font-size:11px;line-height:1.85;color:#1e3a8a">
              메모리 1.7GB (heap 1GB 고정) · CPU 0.5%<br/>
              인덱스 총 3.6MB (문서 ~8천 건)<br/>
              호스트 여유 17GB — <b>운영 무리 없음</b><br/>
              야간 resync 부하: 수 초, 무시 가능
            </div>
          </div>
          <div style="padding:13px;background:#fffbeb;border-radius:10px;border:1px solid #fde68a">
            <div style="font-size:12px;font-weight:700;color:#b45309;margin-bottom:6px">🛡️ 장애 대비 설계</div>
            <div style="font-size:11px;line-height:1.85;color:#92400e">
              단일 노드 · replica 0 구성<br/>
              장애 시 <b>SQL LIKE 폴백</b>으로 검색 지속<br/>
              파생 저장소 — DB에서 수 초 재구성 가능<br/>
              → 별도 백업 불필요
            </div>
          </div>
          <div style="padding:13px;background:#f5f3ff;border-radius:10px;border:1px solid #ddd6fe">
            <div style="font-size:12px;font-weight:700;color:#6d28d9;margin-bottom:6px">📌 향후 유의점</div>
            <div style="font-size:11px;line-height:1.85;color:#5b21b6">
              활동이력 증가 추세 (연 수만 건)<br/>
              → 수십만 건 도달 시 보존정책 검토<br/>
              CDC(Debezium 등)는 현 규모에 과잉<br/>
              → 이벤트 + 야간 자가치유로 충분
            </div>
          </div>
        </div>

      </div>
    `,
  },

  // ── Slide: 챗봇 & LLM 활용 가이드 — 전체 아키텍처 ──
  {
    title: 'AI 챗봇 아키텍처 & 활용 전략 (Expert Guide)',
    content: `
      <div style="display:grid;gap:16px">
        <div style="padding:18px 22px;background:linear-gradient(135deg,#eef2ff,#e0e7ff);border-radius:14px;border-left:5px solid #4f46e5">
          <h3 style="margin:0 0 8px;color:#4338ca;font-size:16px">3계층 하이브리드 AI 아키텍처</h3>
          <p style="margin:0;color:#334155;line-height:1.8;font-size:13px">SAPPT 챗봇은 <strong>규칙 기반(Intent) + 검색 엔진(RAG) + LLM(자연어 생성)</strong> 3계층 구조를 채택하여, 정확성과 유연성을 동시에 확보합니다. 각 계층이 독립적으로 fallback하므로 어떤 상황에서도 100% 응답을 보장합니다.</p>
        </div>
        <div style="background:#1a1a2e;color:#e2e8f0;padding:20px;border-radius:14px;font-family:monospace;font-size:12px;line-height:2;white-space:pre"><span style="color:#818cf8;font-weight:700">사용자 입력</span>
    │
    ▼
<span style="color:#fbbf24">① Intent 매칭</span> (regex 13종: 현황, 일정검색, 의뢰사...)
    │ 매칭 → 즉시 DB 조회 후 정형 응답 (가장 빠름, ~50ms)
    │ 미매칭 ↓
    ▼
<span style="color:#34d399">② OpenSearch RAG</span> (nori 한글분석 + 동의어 + fuzzy)
    │ 시험일정/계획서 컨텍스트 최대 5건 검색
    │ 검색결과 ↓
    ▼
<span style="color:#60a5fa">③ LLM 자연어 생성</span> (OpenRouter 멀티모델 fallback)
    │ 컨텍스트 + 질문 → 한국어 자연어 답변 (SSE 스트리밍)
    │ LLM 실패 시 → 검색 결과 직접 표시
    ▼
<span style="color:#f472b6">응답 캐시</span> (Redis, 동일 쿼리 5분 / 세션 이력 3턴 30분)</div>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px">
          <div style="padding:14px;background:#f0fdf4;border-radius:10px;border:1px solid #bbf7d0;text-align:center">
            <div style="font-size:28px;font-weight:800;color:#16a34a">~50ms</div>
            <div style="font-size:11px;color:#166534">Intent 규칙 응답</div>
          </div>
          <div style="padding:14px;background:#eff6ff;border-radius:10px;border:1px solid #bfdbfe;text-align:center">
            <div style="font-size:28px;font-weight:800;color:#2563eb">~200ms</div>
            <div style="font-size:11px;color:#1e40af">OpenSearch 검색</div>
          </div>
          <div style="padding:14px;background:#fef3c7;border-radius:10px;border:1px solid #fde68a;text-align:center">
            <div style="font-size:28px;font-weight:800;color:#d97706">2~5s</div>
            <div style="font-size:11px;color:#92400e">LLM 자연어 생성</div>
          </div>
        </div>
        <div style="padding:12px 16px;background:#faf5ff;border-radius:10px;border:1px solid #e9d5ff">
          <p style="margin:0;color:#6b21a8;font-size:12px;line-height:1.8"><strong>전문가 포인트:</strong> 정형 질의(현황/검색)는 Intent 레이어에서 DB 직접 조회로 즉답하고, 비정형 자연어만 LLM에 위임합니다. 이 구조 덕분에 LLM 장애/과부하 시에도 핵심 기능이 영향받지 않으며, LLM 비용이 최소화됩니다.</p>
        </div>
      </div>
    `,
  },

  // ── Slide: 챗봇 명령어 완전 가이드 ──
  {
    title: '챗봇 명령어 & 검색 완전 가이드',
    content: `
      <div style="display:grid;gap:14px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div>
            <h3 style="margin:0 0 10px;color:#4f46e5;font-size:14px">기본 명령어 (Intent 레이어)</h3>
            <table style="width:100%;border-collapse:separate;border-spacing:0 5px;font-size:12px">
              <tr style="background:#eef2ff"><td style="padding:8px 12px;font-weight:700;border-radius:6px 0 0 6px;color:#4f46e5;width:120px">현황</td><td style="padding:8px 12px;border-radius:0 6px 6px 0">계획서/일정/의뢰사/장소 건수 + 상태별 통계</td></tr>
              <tr style="background:#f0fdf4"><td style="padding:8px 12px;font-weight:700;border-radius:6px 0 0 6px;color:#16a34a">일정 현황</td><td style="padding:8px 12px;border-radius:0 6px 6px 0">상태별(진행중/완료/대기) + 연도별 집계</td></tr>
              <tr style="background:#eef2ff"><td style="padding:8px 12px;font-weight:700;border-radius:6px 0 0 6px;color:#4f46e5">계획서 현황</td><td style="padding:8px 12px;border-radius:0 6px 6px 0">기관별 시험계획서 등록 현황</td></tr>
              <tr style="background:#f0fdf4"><td style="padding:8px 12px;font-weight:700;border-radius:6px 0 0 6px;color:#16a34a">의뢰사</td><td style="padding:8px 12px;border-radius:0 6px 6px 0">전체 의뢰사 + 의뢰사별 시험 건수</td></tr>
              <tr style="background:#eef2ff"><td style="padding:8px 12px;font-weight:700;border-radius:6px 0 0 6px;color:#4f46e5">오늘 일정</td><td style="padding:8px 12px;border-radius:0 6px 6px 0">현재 진행 중인 시험 목록</td></tr>
              <tr style="background:#f0fdf4"><td style="padding:8px 12px;font-weight:700;border-radius:6px 0 0 6px;color:#16a34a">최근 활동</td><td style="padding:8px 12px;border-radius:0 6px 6px 0">최근 10건 활동이력</td></tr>
              <tr style="background:#eef2ff"><td style="padding:8px 12px;font-weight:700;border-radius:6px 0 0 6px;color:#4f46e5">인기 검색어</td><td style="padding:8px 12px;border-radius:0 6px 6px 0">최근 7일 인기 검색 순위</td></tr>
              <tr style="background:#f0fdf4"><td style="padding:8px 12px;font-weight:700;border-radius:6px 0 0 6px;color:#16a34a">메뉴 안내</td><td style="padding:8px 12px;border-radius:0 6px 6px 0">시스템 메뉴 구조 설명</td></tr>
            </table>
          </div>
          <div>
            <h3 style="margin:0 0 10px;color:#0e7490;font-size:14px">검색 명령어 (OpenSearch 레이어)</h3>
            <table style="width:100%;border-collapse:separate;border-spacing:0 5px;font-size:12px">
              <tr style="background:#ecfeff"><td style="padding:8px 12px;font-weight:700;border-radius:6px 0 0 6px;color:#0e7490;width:160px">일정 검색 수박</td><td style="padding:8px 12px;border-radius:0 6px 6px 0">시험일정에서 "수박" 검색 (동의어/오타보정)</td></tr>
              <tr style="background:#f0fdf4"><td style="padding:8px 12px;font-weight:700;border-radius:6px 0 0 6px;color:#16a34a">계획서 검색 벼</td><td style="padding:8px 12px;border-radius:0 6px 6px 0">계획서에서 작물/제목/코드 검색</td></tr>
              <tr style="background:#ecfeff"><td style="padding:8px 12px;font-weight:700;border-radius:6px 0 0 6px;color:#0e7490">검색 SR22</td><td style="padding:8px 12px;border-radius:0 6px 6px 0">일정+계획서 통합 검색 (시험번호 포함)</td></tr>
              <tr style="background:#f0fdf4"><td style="padding:8px 12px;font-weight:700;border-radius:6px 0 0 6px;color:#16a34a">검색 에마멕틴</td><td style="padding:8px 12px;border-radius:0 6px 6px 0">유효성분명으로 계획서 검색 (동의어 확장)</td></tr>
            </table>
            <h3 style="margin:10px 0 8px;color:#7c3aed;font-size:14px">자연어 질문 (LLM 레이어)</h3>
            <div style="font-size:12px;line-height:2;color:#334155">
              <div style="padding:4px 10px;background:#faf5ff;border-radius:6px;margin-bottom:4px">"진행중인 시험은 몇 건이야?"</div>
              <div style="padding:4px 10px;background:#faf5ff;border-radius:6px;margin-bottom:4px">"고추 관련 시험 현황 알려줘"</div>
              <div style="padding:4px 10px;background:#faf5ff;border-radius:6px;margin-bottom:4px">"협회에서 의뢰한 시험 종류는?"</div>
              <div style="padding:4px 10px;background:#faf5ff;border-radius:6px;margin-bottom:4px">"사과 부란병 계획서 요약해줘"</div>
            </div>
          </div>
        </div>
        <div style="padding:14px 18px;background:#fffbeb;border-radius:10px;border:1px solid #fde68a">
          <p style="margin:0;color:#92400e;font-size:12px;line-height:1.8"><strong>TIP:</strong> 구체적인 작물명/병해충명/시험번호를 포함하면 정확도가 높아집니다. 한글/영문 모두 지원되며, 오타가 있어도 fuzzy 매칭으로 유사 결과를 찾아줍니다. 챗봇 입력창 옆 <strong>? 버튼</strong>을 누르면 예시를 바로 확인하고 클릭 실행할 수 있습니다.</p>
        </div>
      </div>
    `,
  },

  // ── Slide: OpenSearch 동의어 & 검색 품질 ──
  {
    title: '농약 도메인 특화 검색 — 동의어 사전 & nori 분석기',
    content: `
      <div style="display:grid;gap:16px">
        <div style="padding:16px 20px;background:linear-gradient(135deg,#ecfeff,#cffafe);border-radius:12px;border-left:4px solid #0891b2">
          <h3 style="margin:0 0 8px;color:#0e7490;font-size:15px">왜 동의어 사전이 중요한가?</h3>
          <p style="margin:0;color:#164e63;line-height:1.8;font-size:13px">농약 도메인에서는 같은 개념을 다양한 이름으로 부릅니다. "에마멕틴"과 "에마멕틴벤조에이트", "진딧물"과 "목화진딧물"은 같은 검색 의도입니다. SAPPT는 <strong>65개 이상의 동의어 그룹</strong>을 OpenSearch synonym_graph 필터에 등록하여, 어떤 표현으로 검색해도 관련 결과를 모두 찾아냅니다.</p>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px">
          <div style="padding:14px;background:#fef2f2;border-radius:10px;border:1px solid #fecaca">
            <h4 style="margin:0 0 8px;color:#dc2626;font-size:13px">유효성분 동의어</h4>
            <div style="font-size:11px;line-height:1.9;color:#450a0a">
              <div>에마멕틴 = 에마멕틴벤조에이트</div>
              <div>이미다클로프리드 = 이미다</div>
              <div>클로르피리포스 = chlorpyrifos</div>
              <div>아족시스트로빈 = azoxystrobin</div>
              <div>카벤다짐 = carbendazim</div>
              <div><span style="color:#999">... 외 11종</span></div>
            </div>
          </div>
          <div style="padding:14px;background:#f0fdf4;border-radius:10px;border:1px solid #bbf7d0">
            <h4 style="margin:0 0 8px;color:#16a34a;font-size:13px">병해충 동의어</h4>
            <div style="font-size:11px;line-height:1.9;color:#052e16">
              <div>도열병 = 벼도열병 = 이모치병</div>
              <div>진딧물 = 목화진딧물 = aphid</div>
              <div>잿빛곰팡이병 = botrytis</div>
              <div>총채벌레 = thrips</div>
              <div>탄저병 = anthracnose</div>
              <div><span style="color:#999">... 외 10종</span></div>
            </div>
          </div>
          <div style="padding:14px;background:#eff6ff;border-radius:10px;border:1px solid #bfdbfe">
            <h4 style="margin:0 0 8px;color:#2563eb;font-size:13px">작물 동의어</h4>
            <div style="font-size:11px;line-height:1.9;color:#172554">
              <div>벼 = 수도 = rice = 미곡</div>
              <div>고추 = pepper = 청양고추</div>
              <div>토마토 = 방울토마토</div>
              <div>감귤 = 오렌지 = 귤</div>
              <div>잔디 = 한국잔디 = 들잔디</div>
              <div><span style="color:#999">... 외 11종</span></div>
            </div>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="padding:14px;background:#faf5ff;border-radius:10px;border:1px solid #e9d5ff">
            <h4 style="margin:0 0 8px;color:#7c3aed;font-size:13px">nori 한글 형태소 분석기</h4>
            <div style="font-size:12px;line-height:1.8;color:#3b0764">
              <div>• <strong>nori_tokenizer</strong> — 한글 형태소 분석 (복합어 분리)</div>
              <div>• <strong>nori_part_of_speech</strong> — 조사/접속사 등 불용어 제거</div>
              <div>• <strong>ngram (2-3)</strong> — 부분 일치 검색 (시험번호 등)</div>
              <div>• <strong>fuzziness: AUTO</strong> — 오타 자동 보정 (1~2글자 차이)</div>
            </div>
          </div>
          <div style="padding:14px;background:#fff7ed;border-radius:10px;border:1px solid #fed7aa">
            <h4 style="margin:0 0 8px;color:#c2410c;font-size:13px">검색 가중치(Boost) 전략</h4>
            <div style="font-size:12px;line-height:1.8;color:#431407">
              <div>• <strong>시험번호 ^3</strong> — 가장 높은 가중치 (정확 식별)</div>
              <div>• <strong>제목 ^2, 의뢰사 ^2</strong> — 주요 검색 필드</div>
              <div>• <strong>작물/병해충 ^2</strong> — 도메인 핵심 필드</div>
              <div>• <strong>유효성분 ^1</strong> — 보조 검색 필드</div>
            </div>
          </div>
        </div>
        <div style="padding:12px 16px;background:#f0f9ff;border-radius:10px;border:1px solid #bae6fd">
          <p style="margin:0;color:#0c4a6e;font-size:12px;line-height:1.8"><strong>실전 활용:</strong> "에마멕틴" 검색 시 "에마멕틴벤조에이트"가 포함된 계획서도 함께 검색됩니다. "rice" 검색 시 "벼" 관련 시험이 모두 표시됩니다. 한글 초성 검색은 미지원이나, 2글자 이상의 키워드면 ngram으로 부분 매칭됩니다.</p>
        </div>
      </div>
    `,
  },

  // ── Slide: LLM 멀티모델 전략 & RAG 최적화 ──
  {
    title: 'LLM 멀티모델 Fallback & RAG 최적화 전략',
    content: `
      <div style="display:grid;gap:16px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="padding:16px;background:linear-gradient(135deg,#ede9fe,#e0e7ff);border-radius:12px;border:1px solid #c7d2fe">
            <h3 style="margin:0 0 10px;color:#4338ca;font-size:14px">멀티모델 Fallback 체인</h3>
            <div style="font-size:12px;line-height:2.2;color:#312e81">
              <div style="display:flex;align-items:center;gap:8px"><span style="background:#4f46e5;color:#fff;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:700">1순위</span> DeepSeek Chat v3.1</div>
              <div style="display:flex;align-items:center;gap:8px"><span style="background:#2563eb;color:#fff;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:700">2순위</span> Google Gemini 2.0 Flash</div>
              <div style="display:flex;align-items:center;gap:8px"><span style="background:#7c3aed;color:#fff;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:700">3순위</span> Meta Llama 3.3 70B</div>
              <div style="display:flex;align-items:center;gap:8px"><span style="background:#a855f7;color:#fff;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:700">4순위</span> Qwen 2.5 72B</div>
            </div>
            <div style="margin-top:10px;padding:8px 12px;background:rgba(79,70,229,0.08);border-radius:8px;font-size:11px;color:#4338ca;line-height:1.7">
              429(Rate Limit) → 5분 쿨다운 후 다음 모델<br/>
              5xx(서버 에러) → 1분 쿨다운 후 다음 모델<br/>
              Timeout → 1분 쿨다운 후 다음 모델<br/>
              모두 실패 → OpenSearch 결과 직접 표시
            </div>
          </div>
          <div style="padding:16px;background:linear-gradient(135deg,#ecfdf5,#d1fae5);border-radius:12px;border:1px solid #a7f3d0">
            <h3 style="margin:0 0 10px;color:#065f46;font-size:14px">RAG 컨텍스트 최적화</h3>
            <div style="font-size:12px;line-height:1.8;color:#064e3b">
              <p style="margin:0 0 8px"><strong>System Prompt 핵심 규칙:</strong></p>
              <div style="padding:8px;background:rgba(6,95,70,0.06);border-radius:6px;font-size:11px;line-height:1.8">
                1. 반드시 한국어로, 친절하고 간결하게<br/>
                2. [검색결과] 컨텍스트 안의 사실만 사용<br/>
                3. 컨텍스트에 없는 수치/이름 추측 금지<br/>
                4. 답변은 5~8줄 이내, 불릿 정리<br/>
                5. 시험번호 등 식별자 정확히 인용
              </div>
              <p style="margin:8px 0 0;font-size:11px"><strong>Hallucination 방지:</strong> 컨텍스트 외 정보 생성을 system prompt에서 원천 차단. 데이터가 없으면 "찾지 못했습니다"로 안내.</p>
            </div>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px">
          <div style="padding:12px;background:#f0f9ff;border-radius:10px;border:1px solid #bae6fd;text-align:center">
            <div style="font-size:11px;color:#0369a1;margin-bottom:4px">온도(Temperature)</div>
            <div style="font-size:24px;font-weight:800;color:#0c4a6e">0.3</div>
            <div style="font-size:10px;color:#64748b">사실 중심 응답</div>
          </div>
          <div style="padding:12px;background:#fef3c7;border-radius:10px;border:1px solid #fde68a;text-align:center">
            <div style="font-size:11px;color:#92400e;margin-bottom:4px">Max Tokens</div>
            <div style="font-size:24px;font-weight:800;color:#78350f">500</div>
            <div style="font-size:10px;color:#64748b">간결한 답변 유도</div>
          </div>
          <div style="padding:12px;background:#faf5ff;border-radius:10px;border:1px solid #e9d5ff;text-align:center">
            <div style="font-size:11px;color:#7c3aed;margin-bottom:4px">대화 이력</div>
            <div style="font-size:24px;font-weight:800;color:#5b21b6">3턴</div>
            <div style="font-size:10px;color:#64748b">최근 6메시지 유지</div>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="padding:14px;background:#fff;border-radius:10px;border:1px solid #e2e8f0">
            <h4 style="margin:0 0 8px;color:#1e293b;font-size:13px">응답 마커로 출처 구분</h4>
            <div style="font-size:12px;line-height:2;color:#475569">
              <div>🤖 — AI(LLM)가 자연어로 생성한 답변</div>
              <div>🔍 — OpenSearch 검색 결과 직접 표시</div>
              <div>📊📋📝 — Intent 규칙 기반 DB 직접 조회</div>
            </div>
          </div>
          <div style="padding:14px;background:#fff;border-radius:10px;border:1px solid #e2e8f0">
            <h4 style="margin:0 0 8px;color:#1e293b;font-size:13px">캐싱 & 성능 최적화</h4>
            <div style="font-size:12px;line-height:2;color:#475569">
              <div>• <strong>응답 캐시 5분</strong> — 동일 질문 즉시 응답</div>
              <div>• <strong>모델 쿨다운</strong> — Redis로 공유 (5분/1분)</div>
              <div>• <strong>SSE 스트리밍</strong> — First token ~0.5초 체감</div>
              <div>• <strong>세션 이력</strong> — 맥락 유지 대화 가능</div>
            </div>
          </div>
        </div>
      </div>
    `,
  },

  // ── Slide: 챗봇 LLM 고도화 — Claude 적용 효과 분석 ──
  {
    title: 'AI 챗봇 고도화 — Claude 적용 효과 분석',
    content: `
      <div style="display:grid;gap:14px">

        <div style="display:grid;grid-template-columns:1.25fr 1fr;gap:14px">
          <div style="padding:14px;background:#f8fafc;border-radius:12px;border:1px solid #e2e8f0">
            <div style="font-size:13px;font-weight:700;color:#1e293b;margin-bottom:8px">🔎 현재 적용 LLM (운영 확인)</div>
            <div style="font-size:12px;line-height:1.95;color:#475569">
              • 공급자: <b style="color:#1e293b">OpenRouter</b> <span style="color:#94a3b8">(Claude 직접 연동 아님)</span><br/>
              • 1순위 모델: <code style="background:#eef2ff;color:#3730a3;padding:1px 6px;border-radius:5px">nvidia/nemotron-nano-9b-v2:free</code><br/>
              • 폴백 체인: deepseek → gemini → llama → qwen <span style="color:#94a3b8">(모두 free)</span><br/>
              • 파라미터: temp 0.3 · max_tokens 500 · RAG=OpenSearch top5
            </div>
          </div>
          <div style="padding:14px;background:#ecfdf5;border-radius:12px;border:1px solid #a7f3d0">
            <div style="font-size:13px;font-weight:700;color:#047857;margin-bottom:8px">✅ 결론: 효과 있음</div>
            <div style="font-size:12px;line-height:1.95;color:#065f46">
              9B 소형 free 모델 → Claude 전환 시 <b>신뢰성·한국어 정확도</b>가 향상되고,
              비용은 <b>월 1~2만원 미만</b>으로 미미합니다. ROI 양호.
            </div>
          </div>
        </div>

        <div style="padding:14px;background:#fff;border-radius:12px;border:1px solid #e2e8f0">
          <div style="font-size:13px;font-weight:700;color:#1e293b;margin-bottom:10px">📈 개선 효과 (현재 약점 1:1 매핑)</div>
          <table style="width:100%;border-collapse:collapse;font-size:11.5px">
            <thead>
              <tr style="background:#f1f5f9;color:#334155">
                <th style="text-align:left;padding:7px 10px;border:1px solid #e2e8f0;width:42%">현재 약점</th>
                <th style="text-align:left;padding:7px 10px;border:1px solid #e2e8f0">Claude 적용 효과</th>
              </tr>
            </thead>
            <tbody style="color:#475569">
              <tr>
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b style="color:#b91c1c">환각 위험</b> — 9B 모델은 "context 내 사실만" 지시 준수가 약함</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">Haiku도 한국어·RAG grounding이 강함 → 데이터봇 최대 리스크 직접 감소</td>
              </tr>
              <tr style="background:#fafbfc">
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b style="color:#b91c1c">불안정</b> — free tier 429 빈발(폴백·쿨다운이 그 보상 장치)</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">유료 키 = 안정적 레이트리밋, 취약한 폴백체인 불필요</td>
              </tr>
              <tr>
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b style="color:#b91c1c">비일관</b> — 폴백 따라 매턴 모델이 바뀜</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">단일 모델 고정 → 일관된 품질·톤</td>
              </tr>
              <tr style="background:#fafbfc">
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b style="color:#b91c1c">자연어 한계</b> — regex 인텐트 13개로 제한</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">tool_use로 복합 질의 처리(소형 free는 tool calling 불가) → 최대 도약</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px">
          <div style="padding:13px;background:#eff6ff;border-radius:10px;border:1px solid #bfdbfe">
            <div style="font-size:12px;font-weight:700;color:#1d4ed8;margin-bottom:6px">💰 비용 (Haiku 4.5)</div>
            <div style="font-size:11px;line-height:1.85;color:#1e3a8a">
              턴당 ≈ <b>5.5원</b> ($0.004)<br/>
              규칙기반 질의는 LLM 미호출<br/>
              실사용 산발적 → <b>월 1~2만원 미만</b><br/>
              프롬프트 캐싱 시 추가 절감
            </div>
          </div>
          <div style="padding:13px;background:#fffbeb;border-radius:10px;border:1px solid #fde68a">
            <div style="font-size:12px;font-weight:700;color:#b45309;margin-bottom:6px">⚠️ 한계 (과대평가 방지)</div>
            <div style="font-size:11px;line-height:1.85;color:#92400e">
              규칙기반 처리 질의는 변화 없음<br/>
              순수 모델 스왑만으론 인텐트 커버리지는 그대로<br/>
              사용량 낮아 "긴급"은 아님(ROI는 양호)
            </div>
          </div>
          <div style="padding:13px;background:#eef2ff;border-radius:10px;border:1px solid #c7d2fe">
            <div style="font-size:12px;font-weight:700;color:#4338ca;margin-bottom:6px">🚀 추천 2단계</div>
            <div style="font-size:11px;line-height:1.85;color:#3730a3">
              <b>1단계</b> Claude Haiku 4.5 교체<br/>
              <span style="color:#6366f1">(chat_stream만 교체 · SSE·RAG 재사용)</span><br/>
              <b>2단계</b> tool_use 에이전트화<br/>
              <span style="color:#6366f1">(regex 인텐트 테이블 통합)</span>
            </div>
          </div>
        </div>

      </div>
    `,
  },

  // ── Slide: 챗봇 고도화 및 LLM API 적용방안 (2026-08 분석) ──
  {
    title: '챗봇 고도화 및 LLM API 적용방안 — 단계별 로드맵 (2026-08)',
    content: `
      <div style="display:grid;gap:14px">

        <div style="display:grid;grid-template-columns:1.2fr 1fr;gap:14px">
          <div style="padding:14px;background:#f8fafc;border-radius:12px;border:1px solid #e2e8f0">
            <div style="font-size:13px;font-weight:700;color:#1e293b;margin-bottom:8px">🔎 현재 데이터 접근 구조 (하이브리드)</div>
            <div style="font-size:12px;line-height:1.95;color:#475569">
              • <b>통계·집계형</b> (현황/의뢰사/오늘일정) → <b style="color:#0369a1">PostgreSQL 직접 쿼리</b> — 정확한 실시간 수치<br/>
              • <b>키워드 검색형</b> (일정/계획서/통합검색) → <b style="color:#047857">OpenSearch</b> (nori + 농약 동의어 + ngram)<br/>
              • <b>자유 질문</b> → OpenSearch top5 컨텍스트 주입 → <b style="color:#7c3aed">LLM(RAG)</b> — DB 미사용<br/>
              • 앞단 방어: 규칙 인텐트 13종 + Redis 응답캐시 5분 → LLM 호출 최소화
            </div>
          </div>
          <div style="padding:14px;background:#fef2f2;border-radius:12px;border:1px solid #fecaca">
            <div style="font-size:13px;font-weight:700;color:#b91c1c;margin-bottom:8px">🚨 색인 정합성 진단 (2026-08 실측)</div>
            <div style="font-size:12px;line-height:1.95;color:#7f1d1d">
              • 일정 인덱스 843건 중 <b>유령 570건</b> (DB 삭제분 잔존, 68%)<br/>
              • 계획서 122건 중 <b>4건만 색인</b> (97% 누락)<br/>
              • 원인: upload-hwpx 경로 색인 미호출 + 일괄삭제 미연동<br/>
              • 미색인 도메인: 조사/처리일정 상세, 출장, NCPMS 등
            </div>
          </div>
        </div>

        <div style="padding:14px;background:#fff;border-radius:12px;border:1px solid #e2e8f0">
          <div style="font-size:13px;font-weight:700;color:#1e293b;margin-bottom:10px">⚖️ LLM API 적용방안 — OpenRouter 경유 vs Anthropic API 직접</div>
          <table style="width:100%;border-collapse:collapse;font-size:11.5px">
            <thead>
              <tr style="background:#f1f5f9;color:#334155">
                <th style="text-align:left;padding:7px 10px;border:1px solid #e2e8f0;width:20%">항목</th>
                <th style="text-align:left;padding:7px 10px;border:1px solid #e2e8f0">OpenRouter 경유 (1단계 권장)</th>
                <th style="text-align:left;padding:7px 10px;border:1px solid #e2e8f0">Anthropic API 직접 (2단계 전환)</th>
              </tr>
            </thead>
            <tbody style="color:#475569">
              <tr>
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b>비용</b></td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">Haiku 정가 $1/$5 + 충전수수료 ~5.5% (현 사용량 월 수백원)</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">정가 그대로 + Batch API 50% 할인 가능</td>
              </tr>
              <tr style="background:#fafbfc">
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b>전환 작업</b></td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b style="color:#047857">코드 무수정</b> — 시크릿(OPENROUTER_MODELS)만 변경</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">Messages API 어댑터 추가 (~50줄)</td>
              </tr>
              <tr>
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b>폴백 체인</b></td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">Haiku + 무료모델을 한 API로 혼용 (현행 구조 유지)</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">클라이언트 2개 유지 필요 (경로 이원화 = 장애 대비 장점)</td>
              </tr>
              <tr style="background:#fafbfc">
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b>Claude 전용 기능</b></td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">제한적 (OpenAI 호환 계층)</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b style="color:#4338ca">네이티브 tool use·구조화 출력</b> — 에이전트화 필수 기반</td>
              </tr>
              <tr>
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b>데이터 경유</b></td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">시험 데이터가 제3자 중개 서버 추가 경유</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">Anthropic만 경유 (의뢰사 비밀유지 관점 유리)</td>
              </tr>
              <tr style="background:#fafbfc">
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b>참고</b></td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0" colspan="2">Claude 구독 플랜(Pro/Max)은 웹서비스 API 호출에 사용 불가 (약관) — 챗봇은 별도 API 종량제 필요. 구독은 Claude Code 개발·운영 자동화에 활용</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style="padding:14px;background:#fff;border-radius:12px;border:1px solid #e2e8f0">
          <div style="font-size:13px;font-weight:700;color:#1e293b;margin-bottom:10px">🗺️ 단계별 로드맵</div>
          <table style="width:100%;border-collapse:collapse;font-size:11.5px">
            <thead>
              <tr style="background:#f1f5f9;color:#334155">
                <th style="text-align:left;padding:7px 10px;border:1px solid #e2e8f0;width:14%">단계</th>
                <th style="text-align:left;padding:7px 10px;border:1px solid #e2e8f0">내용</th>
                <th style="text-align:left;padding:7px 10px;border:1px solid #e2e8f0;width:22%">난이도/비용</th>
              </tr>
            </thead>
            <tbody style="color:#475569">
              <tr>
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b style="color:#b91c1c">0단계 (즉시)</b><br/>색인 정합성 복구</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">일정·계획서 인덱스 삭제 후 재동기화 (유령 570 제거 + 계획서 118 복구) · upload-hwpx에 색인 호출 추가 · 야간 정합성 크론 점검(Discord 알림)</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">낮음 — 코드 1줄 + resync</td>
              </tr>
              <tr style="background:#fafbfc">
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b style="color:#b45309">1단계</b><br/>Haiku 전환</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">OPENROUTER_MODELS 1순위를 anthropic/claude-haiku-4.5로 변경 (무료모델은 비상 폴백 유지) · RAG 컨텍스트에 시험장소·개시일·조사일정 요약 확장 · 조사/처리일정 색인 추가</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">낮음 — 월 1만원 미만</td>
              </tr>
              <tr>
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b style="color:#4338ca">2단계 (목표)</b><br/>Tool Use 에이전트</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">Anthropic API 직접 연동 + 도구 4종: search(OpenSearch) · get_schedule_detail(DB) · aggregate_schedules(DB 집계) · get_overview — 수치는 항상 DB에서 나와 환각 구조적 차단, 자연어 집계 질문 해결</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">중간 — 기존 핸들러 도구화</td>
              </tr>
              <tr style="background:#fafbfc">
                <td style="padding:7px 10px;border:1px solid #e2e8f0"><b style="color:#64748b">3단계 (선택)</b><br/>문서 QA</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">HWPX 계획서 본문 전문 색인 + 벡터(시맨틱) 검색 — 현 데이터 규모(일정 273·계획서 122)에선 nori+동의어로 충분하여 문서 QA 수요 발생 시에만</td>
                <td style="padding:7px 10px;border:1px solid #e2e8f0">높음 — 수요 확인 후</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px">
          <div style="padding:13px;background:#eff6ff;border-radius:10px;border:1px solid #bfdbfe">
            <div style="font-size:12px;font-weight:700;color:#1d4ed8;margin-bottom:6px">💰 비용 실측 근거</div>
            <div style="font-size:11px;line-height:1.85;color:#1e3a8a">
              LLM 경로 호출: 월 10~20건 (검색로그 실측)<br/>
              턴당 ~2K입력/400출력 ≈ <b>6원</b><br/>
              현 사용량 기준 <b>월 100~200원</b><br/>
              월 500건 증가해도 ~3천원
            </div>
          </div>
          <div style="padding:13px;background:#fffbeb;border-radius:10px;border:1px solid #fde68a">
            <div style="font-size:12px;font-weight:700;color:#b45309;margin-bottom:6px">⚠️ 무료 모델 체인 현황</div>
            <div style="font-size:11px;line-height:1.85;color:#92400e">
              등록 5종 중 <b>4종 OpenRouter 퇴출</b> 확인<br/>
              실효 모델 = nemotron-nano-9B 단일<br/>
              9B 소형 → 한국어·지시준수 약함<br/>
              무료 티어 일 50회 제한
            </div>
          </div>
          <div style="padding:13px;background:#ecfdf5;border-radius:10px;border:1px solid #a7f3d0">
            <div style="font-size:12px;font-weight:700;color:#047857;margin-bottom:6px">✅ 설계 원칙</div>
            <div style="font-size:11px;line-height:1.85;color:#065f46">
              수치·집계 = <b>DB(원본)</b>에서만<br/>
              자연어·동의어 검색 = <b>OpenSearch</b><br/>
              LLM = 라우터/요약 역할에 한정<br/>
              규칙 인텐트·캐시 방어선 유지
            </div>
          </div>
        </div>

      </div>
    `,
  },

  // ── Slide: 챗봇 실전 활용 시나리오 & 고급 팁 ──
  {
    title: '챗봇 실전 활용 시나리오 — 업무별 Best Practice',
    content: `
      <div style="display:grid;gap:14px">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="padding:16px;background:linear-gradient(135deg,#eef2ff,#e0e7ff);border-radius:12px;border:1px solid #c7d2fe">
            <h3 style="margin:0 0 12px;color:#4338ca;font-size:14px">시나리오 1: 아침 업무 시작</h3>
            <div style="font-size:12px;line-height:2;color:#312e81">
              <div style="padding:4px 8px;background:#fff;border-radius:6px;margin-bottom:4px"><strong style="color:#4f46e5">Q.</strong> "현황"</div>
              <div style="padding:4px 8px;background:#f8fafc;border-radius:6px;margin-bottom:4px;color:#475569;font-size:11px">→ 전체 시스템 현황 한눈에 파악</div>
              <div style="padding:4px 8px;background:#fff;border-radius:6px;margin-bottom:4px"><strong style="color:#4f46e5">Q.</strong> "오늘 일정"</div>
              <div style="padding:4px 8px;background:#f8fafc;border-radius:6px;margin-bottom:4px;color:#475569;font-size:11px">→ 진행 중인 시험 일정 확인</div>
              <div style="padding:4px 8px;background:#fff;border-radius:6px"><strong style="color:#4f46e5">Q.</strong> "최근 활동"</div>
              <div style="padding:4px 8px;background:#f8fafc;border-radius:6px;color:#475569;font-size:11px">→ 어제까지의 팀 활동이력 리뷰</div>
            </div>
          </div>
          <div style="padding:16px;background:linear-gradient(135deg,#ecfdf5,#d1fae5);border-radius:12px;border:1px solid #a7f3d0">
            <h3 style="margin:0 0 12px;color:#065f46;font-size:14px">시나리오 2: 특정 시험 조사</h3>
            <div style="font-size:12px;line-height:2;color:#064e3b">
              <div style="padding:4px 8px;background:#fff;border-radius:6px;margin-bottom:4px"><strong style="color:#16a34a">Q.</strong> "수박 관련 시험 알려줘"</div>
              <div style="padding:4px 8px;background:#f8fafc;border-radius:6px;margin-bottom:4px;color:#475569;font-size:11px">→ AI가 수박 시험을 병해충별로 분류 정리</div>
              <div style="padding:4px 8px;background:#fff;border-radius:6px;margin-bottom:4px"><strong style="color:#16a34a">Q.</strong> "계획서 검색 수박"</div>
              <div style="padding:4px 8px;background:#f8fafc;border-radius:6px;margin-bottom:4px;color:#475569;font-size:11px">→ 수박 관련 시험계획서 코드/제목/작물 검색</div>
              <div style="padding:4px 8px;background:#fff;border-radius:6px"><strong style="color:#16a34a">Q.</strong> "수박 흰가루병 계획서 요약해줘"</div>
              <div style="padding:4px 8px;background:#f8fafc;border-radius:6px;color:#475569;font-size:11px">→ AI가 해당 계획서 핵심 내용 자연어 요약</div>
            </div>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="padding:16px;background:linear-gradient(135deg,#fff7ed,#fed7aa);border-radius:12px;border:1px solid #fdba74">
            <h3 style="margin:0 0 12px;color:#c2410c;font-size:14px">시나리오 3: 의뢰사 분석</h3>
            <div style="font-size:12px;line-height:2;color:#431407">
              <div style="padding:4px 8px;background:#fff;border-radius:6px;margin-bottom:4px"><strong style="color:#ea580c">Q.</strong> "의뢰사"</div>
              <div style="padding:4px 8px;background:#f8fafc;border-radius:6px;margin-bottom:4px;color:#475569;font-size:11px">→ 전체 의뢰사 + 시험 건수 목록</div>
              <div style="padding:4px 8px;background:#fff;border-radius:6px"><strong style="color:#ea580c">Q.</strong> "협회에서 의뢰한 시험 종류는?"</div>
              <div style="padding:4px 8px;background:#f8fafc;border-radius:6px;color:#475569;font-size:11px">→ AI가 협회 시험을 카테고리별 분류 정리</div>
            </div>
          </div>
          <div style="padding:16px;background:linear-gradient(135deg,#faf5ff,#e9d5ff);border-radius:12px;border:1px solid #d8b4fe">
            <h3 style="margin:0 0 12px;color:#6b21a8;font-size:14px">시나리오 4: 다단계 대화</h3>
            <div style="font-size:12px;line-height:2;color:#3b0764">
              <div style="padding:4px 8px;background:#fff;border-radius:6px;margin-bottom:4px"><strong style="color:#7c3aed">Q1.</strong> "고추 시험 현황 알려줘"</div>
              <div style="padding:4px 8px;background:#f8fafc;border-radius:6px;margin-bottom:4px;color:#475569;font-size:11px">→ 고추 관련 시험 5건 요약</div>
              <div style="padding:4px 8px;background:#fff;border-radius:6px"><strong style="color:#7c3aed">Q2.</strong> "그중에 약해 시험만 보여줘"</div>
              <div style="padding:4px 8px;background:#f8fafc;border-radius:6px;color:#475569;font-size:11px">→ 이전 대화 맥락 기억 (3턴 유지)</div>
            </div>
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="padding:14px;background:#f0f9ff;border-radius:10px;border:1px solid #bae6fd">
            <h4 style="margin:0 0 6px;color:#0369a1;font-size:13px">검색 품질을 높이는 팁</h4>
            <div style="font-size:11px;line-height:1.9;color:#0c4a6e">
              <div>• <strong>구체적인 키워드 사용</strong> — "시험" 대신 "수박 흰가루병"</div>
              <div>• <strong>영문/한글 모두 가능</strong> — "aphid" = "진딧물"</div>
              <div>• <strong>시험번호 직접 검색</strong> — "검색 SR22" (가장 정확)</div>
              <div>• <strong>자연어로 의도 명시</strong> — "~요약해줘", "~알려줘"</div>
              <div>• <strong>? 버튼 활용</strong> — 예시 질문 클릭 즉시 실행</div>
            </div>
          </div>
          <div style="padding:14px;background:#fef2f2;border-radius:10px;border:1px solid #fecaca">
            <h4 style="margin:0 0 6px;color:#dc2626;font-size:13px">이런 질문은 피하세요</h4>
            <div style="font-size:11px;line-height:1.9;color:#450a0a">
              <div>• <strong>시스템 외부 지식</strong> — "에마멕틴 독성은?" (DB 외 정보)</div>
              <div>• <strong>너무 짧은 키워드</strong> — "시" (1글자, 검색 불가)</div>
              <div>• <strong>미래 예측</strong> — "다음달 시험 일정은?" (미등록 데이터)</div>
              <div>• <strong>계산/통계 요청</strong> — "평균 시험 기간은?" (미지원)</div>
              <div>• <strong>파일 다운로드</strong> — "계획서 파일 보내줘" (미지원)</div>
            </div>
          </div>
        </div>
      </div>
    `,
  },

  // ── Slide: GHCR 컨테이너 레지스트리 ──
  {
    title: 'GitHub Container Registry (GHCR) 기반 이미지 관리',
    component: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div dangerouslySetInnerHTML={{ __html: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:20px">
        <div style="background:linear-gradient(135deg,#e3f2fd15,#1565c015);padding:20px;border-radius:16px;border:1px solid #90caf9">
          <h3 style="color:#1565c0;margin-top:0">개요</h3>
          <p style="color:#444;line-height:1.8;margin:0">GitHub Container Registry(GHCR)를 공식 이미지 저장소로 사용합니다. <strong>GitHub Actions</strong>가 코드 push 시 ARM64 이미지를 자동 빌드/푸시하며, k3s containerd가 <strong>ghcr.io/sappt/*</strong>에서 직접 pull합니다. 로컬 registry 컨테이너 불필요.</p>
        </div>
        <div style="background:linear-gradient(135deg,#f0fdf415,#15803d15);padding:20px;border-radius:16px;border:1px solid #86efac">
          <h3 style="color:#15803d;margin-top:0">전환 이유</h3>
          <ul style="color:#444;line-height:2;margin:0;padding-left:18px">
            <li>기존 로컬 registry:2 → 서버 의존성 제거</li>
            <li>GitHub Actions로 빌드 오프로드 (서버 CPU 절약)</li>
            <li>ARM64 크로스 빌드 지원 (QEMU)</li>
            <li>Public 패키지 — 인증 없이 pull 가능</li>
            <li>이미지 버전 태그 이력 (SHA + 타임스탬프)</li>
          </ul>
        </div>
      </div>` }} />
        <ArchFlow
          height={280}
          groups={[
            { id: 'g_gh', label: 'GitHub (sappt/lab)', x: 0, y: 20, w: 210, h: 170, tone: 'green' },
            { id: 'g_ghcr', label: '📦 GHCR (ghcr.io/sappt)', x: 290, y: 0, w: 230, h: 210, tone: 'blue' },
            { id: 'g_k3s', label: '☸️ k3s (Oracle Cloud)', x: 600, y: 20, w: 220, h: 170, tone: 'orange' },
          ]}
          nodes={[
            { id: 'push_code', group: 'g_gh', x: 16, y: 40, label: 'git push', sub: 'main branch', tone: 'green' },
            { id: 'gha', group: 'g_gh', x: 16, y: 104, label: 'GitHub Actions', sub: 'build-and-push.yml', tone: 'green' },
            { id: 'i1', group: 'g_ghcr', x: 16, y: 40, label: 'sappt-backend', sub: ':latest', tone: 'blue' },
            { id: 'i2', group: 'g_ghcr', x: 16, y: 104, label: 'sappt-frontend', sub: ':latest', tone: 'blue' },
            { id: 'i3', group: 'g_ghcr', x: 16, y: 156, label: 'sappt-partner-fe', sub: ':latest', tone: 'blue' },
            { id: 'pull2', group: 'g_k3s', x: 16, y: 40, label: 'containerd pull', sub: 'ghcr.io/sappt/*', tone: 'orange' },
            { id: 'rolling', group: 'g_k3s', x: 16, y: 104, label: 'Rolling Update', sub: 'maxUnavailable:0', tone: 'orange' },
          ]}
          edges={[
            { source: 'push_code', target: 'gha', tone: 'green' },
            { source: 'gha', target: 'i2', label: 'push', tone: 'blue', animated: true },
            { source: 'i2', target: 'pull2', label: 'pull', tone: 'orange', animated: true },
            { source: 'pull2', target: 'rolling', tone: 'orange' },
          ]}
        />
        <div dangerouslySetInnerHTML={{ __html: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:20px">
        <div style="padding:18px;background:#f0fdf4;border-radius:12px;border:1px solid #bbf7d0">
          <h4 style="margin:0 0 10px;color:#16a34a">이미지 태그 전략</h4>
          <ul style="color:#444;line-height:2;margin:0;padding-left:16px;font-size:13px">
            <li><code>:latest</code> — 항상 최신 main 브랜치</li>
            <li><code>:sha-XXXXXXX</code> — 커밋 SHA (롤백 기준점)</li>
            <li><code>:YYYYMMDDHHmmss</code> — 빌드 타임스탬프</li>
            <li>k3s는 <code>:latest</code>를 사용하며 deploy.sh로 강제 재pull</li>
          </ul>
        </div>
        <div style="padding:18px;background:#eff6ff;border-radius:12px;border:1px solid #bfdbfe">
          <h4 style="margin:0 0 10px;color:#1d4ed8">k3s 연동 설정</h4>
          <ul style="color:#444;line-height:2;margin:0;padding-left:16px;font-size:13px">
            <li><code>/etc/rancher/k3s/registries.yaml</code> — ghcr.io 엔드포인트</li>
            <li>k8s Deployment: <code>imagePullPolicy: Always</code></li>
            <li>패키지 Public 설정 → 인증 없이 pull</li>
            <li>deploy.sh: kubectl patch로 재시작 annotation 주입</li>
          </ul>
        </div>
      </div>

      <div style="margin-top:16px;background:#1a1a2e;color:#e0e0e0;padding:16px 20px;border-radius:12px;font-family:monospace;font-size:12px;line-height:2">
        <span style="color:#667eea"># GHCR 이미지 확인 (public)</span><br/>
        <span style="color:#43e97b">$</span> docker pull ghcr.io/sappt/sappt-backend:latest<br/>
        <span style="color:#43e97b">$</span> docker pull ghcr.io/sappt/sappt-frontend:latest<br/>
        <span style="color:#43e97b">$</span> docker pull ghcr.io/sappt/sappt-partner-frontend:latest<br/>
        <span style="color:#667eea"># k3s registries 설정</span><br/>
        <span style="color:#43e97b">$</span> cat /etc/rancher/k3s/registries.yaml<br/>
        <span style="color:#43e97b">$</span> sudo systemctl restart k3s &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#888"># 설정 변경 후 재시작 필요</span>
      </div>
` }} />
      </div>
    ),
  },
  // ── Slide: NCPMS 병해충 정보 연동 ──
  {
    title: '🐛 병해충 정보 연동 — 농촌진흥청 NCPMS OpenAPI',
    content: `
      <div style="display:grid;gap:16px">

        <div style="padding:18px;background:linear-gradient(135deg,#ecfdf5,#d1fae520);border-radius:12px;border:1px solid #6ee7b7">
          <div style="font-size:14px;font-weight:700;color:#065f46;margin-bottom:8px">개요</div>
          <p style="margin:0;font-size:12px;color:#047857;line-height:1.9">
            농촌진흥청 <b>국가농작물병해충관리시스템(NCPMS)</b> OpenAPI를 연동하여, 농약 시험 업무 중
            병해충 <b>동정·예찰·상담·예측</b> 정보를 SAPPT 안에서 바로 조회합니다.
            예찰 데이터는 <b>매일 자동 수집</b>되어 신규 회차 등록 시 Discord로 통지됩니다.
            <br/>메뉴 위치: <b>데이터관리 → 병해충 정보(NCPMS)</b> · 접근 경로: <code>lab.sappt.co.kr/ncpms</code>
          </p>
        </div>

        <div style="font-size:14px;font-weight:700;color:#1e293b">4대 기능 (탭 구성)</div>
        <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:12px">

          <div style="padding:16px;background:#eff6ff;border-radius:10px;border:1px solid #bfdbfe">
            <div style="font-size:13px;font-weight:700;color:#1d4ed8;margin-bottom:8px">① 병해충 도감 (검색)</div>
            <div style="font-size:11px;color:#1e40af;line-height:1.8">
              작물명·병해충명 <b>통합검색</b> (SVC16) → 사진·증상·생태·<b>방제법</b> 상세.<br/>
              병/병원체/해충/곤충/잡초/천적곤충 6종 상세(SVC05~15) 실시간 조회.
            </div>
          </div>

          <div style="padding:16px;background:#f0fdf4;border-radius:10px;border:1px solid #86efac">
            <div style="font-size:13px;font-weight:700;color:#166534;margin-bottom:8px">② 예찰정보 (대시보드 + 목록)</div>
            <div style="font-size:11px;color:#14532d;line-height:1.8">
              작물·회차별 예찰(SVC51/52) <b>DB 캐시</b> 기반.<br/>
              <b>대시보드</b>: 발생순위·<b>회차별 발생추세 라인차트</b>·시도별 현황.<br/>
              <b>목록</b>: 회차 표 + 행클릭 시 시도별 상세.
            </div>
          </div>

          <div style="padding:16px;background:#fffbeb;border-radius:10px;border:1px solid #fcd34d">
            <div style="font-size:13px;font-weight:700;color:#92400e;margin-bottom:8px">③ 상담 Q&amp;A</div>
            <div style="font-size:11px;color:#78350f;line-height:1.8">
              전문가 병해충 상담 사례(SVC41/42) 약 <b>4,800여 건</b> 열람.<br/>
              질문·답변·방제방법을 상세 카드로 표시.
            </div>
          </div>

          <div style="padding:16px;background:#f5f3ff;border-radius:10px;border:1px solid #c4b5fd">
            <div style="font-size:13px;font-weight:700;color:#6d28d9;margin-bottom:8px">④ 예측 포털</div>
            <div style="font-size:11px;color:#5b21b6;line-height:1.8">
              예측은 <b>화면직접호출만 승인</b>(데이터 미연동) → 작물별 <b>NCPMS 공식 예측지도 딥링크</b>.<br/>
              + 예찰 데이터 기반 <b>발생추세(▲급증/▼감소) 인사이트</b>로 방제 대비 지원.
            </div>
          </div>

        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="padding:14px;background:#f8fafc;border-radius:10px;border:1px solid #e2e8f0">
            <div style="font-size:12px;font-weight:700;color:#334155;margin-bottom:6px">🔄 일일 자동 수집</div>
            <div style="font-size:11px;color:#475569;line-height:1.9">
              <code>scripts/ncpms_sync.sh</code> — 매일 <b>06:10 KST</b> 크론.<br/>
              <code>POST /api/ncpms/sync</code> → SVC51 upsert(insectKey 유니크) → SVC52 상세 백필.<br/>
              신규 회차 발견 시 <b>Discord webhook</b> 알림.
            </div>
          </div>
          <div style="padding:14px;background:#f8fafc;border-radius:10px;border:1px solid #e2e8f0">
            <div style="font-size:12px;font-weight:700;color:#334155;margin-bottom:6px">🔐 아키텍처</div>
            <div style="font-size:11px;color:#475569;line-height:1.9">
              <code>ncpms_client.py</code> (env 키 싱글턴) + main.py <code>/api/ncpms/*</code>.<br/>
              권한: <code>require_permission("ncpms")</code> — READ/WRITE.<br/>
              캐시: <code>ncpms_surveys</code> · 감사로그: <code>ncpms_api_logs</code>.
            </div>
          </div>
        </div>

        <div style="padding:12px;background:linear-gradient(135deg,#05966915,#10b98115);border-radius:10px;border:1px solid #10b98133;font-size:11px;color:#065f46;line-height:1.8">
          <b>인증키:</b> 도메인 lab.sappt.co.kr 승인(2026-07-05). 검색·상담은 실시간 프록시, 예찰은 캐시+주기수집.
        </div>

      </div>
    `,
  },
  {
    title: '🆕 2026-04-15 업그레이드 — 챗봇 v2 + 인프라 강화',
    content: `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:20px">
        <div style="padding:20px;background:linear-gradient(135deg,#ede9fe,#e0e7ff);border-radius:12px;border:1px solid #c7d2fe">
          <div style="font-size:18px;font-weight:700;color:#4338ca;margin-bottom:12px">🤖 챗봇 v2</div>
          <ul style="margin:0;padding-left:20px;line-height:2;font-size:13px;color:#312e81">
            <li><b>멀티 모델 Fallback</b> — DeepSeek → Gemini → Llama → Qwen (무료 4종, 429/5xx 자동 전환)</li>
            <li><b>Intent 정규화</b> — regex 테이블 기반 13종 라우팅 (기존 연산자 버그 수정)</li>
            <li><b>응답 캐시</b> — 동일 쿼리 5분 (Redis), 평균 응답 속도 대폭 감소</li>
            <li><b>SSE 스트리밍</b> — LLM 토큰 단위 실시간 표시 (0.5초 first token)</li>
            <li><b>세션 이력</b> — 최근 3턴 기억 (Redis, 30분 TTL)</li>
            <li><b>사용 예시 UI</b> — 챗봇 입력창 옆 ? 버튼 → 예시 패널 토글</li>
          </ul>
        </div>
        <div style="padding:20px;background:linear-gradient(135deg,#fef3c7,#fed7aa);border-radius:12px;border:1px solid #fcd34d">
          <div style="font-size:18px;font-weight:700;color:#92400e;margin-bottom:12px">🗄️ 인프라 추가</div>
          <ul style="margin:0;padding-left:20px;line-height:2;font-size:13px;color:#78350f">
            <li><b>Redis 7</b> — ClusterIP 서비스, 128MB LRU 캐시 (k8s sappt ns)</li>
            <li><b>GHCR 자동 빌드</b> — .github/workflows/build-and-push.yml (ARM64 크로스 빌드)</li>
            <li><b>MonitoringPage 개선</b> — Services 툴팁(서비스별 역할 설명) + GHCR 카드</li>
            <li><b>discord_server_report.sh CPU 측정</b> — top -bn2 -d 0.5 로 정확도 개선</li>
          </ul>
        </div>
      </div>

      <div style="padding:20px;background:#f8fafc;border-radius:12px;border:1px solid #e2e8f0">
        <div style="font-size:16px;font-weight:700;color:#1e293b;margin-bottom:12px">📁 파일 배치 (신규)</div>
        <table style="width:100%;font-size:12px;border-collapse:collapse">
          <tr style="background:#f1f5f9"><th style="padding:8px;border:1px solid #e2e8f0;text-align:left">경로</th><th style="padding:8px;border:1px solid #e2e8f0;text-align:left">역할</th></tr>
          <tr><td style="padding:8px;border:1px solid #e2e8f0"><code>sappt/backend/app/cache.py</code></td><td style="padding:8px;border:1px solid #e2e8f0">Redis 래퍼 + 세션 히스토리</td></tr>
          <tr><td style="padding:8px;border:1px solid #e2e8f0"><code>sappt/backend/app/llm_client.py</code></td><td style="padding:8px;border:1px solid #e2e8f0">Multi-model fallback + SSE 스트리밍</td></tr>
          <tr><td style="padding:8px;border:1px solid #e2e8f0"><code>sappt/k8s/base/redis.yaml</code></td><td style="padding:8px;border:1px solid #e2e8f0">Redis Deployment + ClusterIP Service</td></tr>
          <tr><td style="padding:8px;border:1px solid #e2e8f0"><code>.github/workflows/build-and-push.yml</code></td><td style="padding:8px;border:1px solid #e2e8f0">GitHub Actions ARM64 빌드 → GHCR</td></tr>
          <tr><td style="padding:8px;border:1px solid #e2e8f0"><code>GHCR_SETUP.md</code></td><td style="padding:8px;border:1px solid #e2e8f0">k3s ↔ ghcr.io 연동 가이드</td></tr>
          <tr><td style="padding:8px;border:1px solid #e2e8f0"><code>/api/chatbot/stream</code> (endpoint)</td><td style="padding:8px;border:1px solid #e2e8f0">SSE 토큰 스트리밍</td></tr>
          <tr><td style="padding:8px;border:1px solid #e2e8f0"><code>/api/monitoring/ghcr</code> (endpoint)</td><td style="padding:8px;border:1px solid #e2e8f0">GHCR 패키지/태그 조회</td></tr>
        </table>
      </div>

      <div style="margin-top:16px;padding:16px;background:#1e293b;color:#e2e8f0;border-radius:12px;font-family:monospace;font-size:12px;line-height:1.9">
        <span style="color:#60a5fa"># 신규 API 테스트</span><br/>
        <span style="color:#34d399">$</span> curl -N -X POST https://api.sappt.co.kr/api/chatbot/stream -H "Authorization: Bearer $TOKEN" -H "Content-Type: application/json" -d '{"message":"현황"}'<br/>
        <span style="color:#60a5fa"># 캐시/백오프 상태 확인</span><br/>
        <span style="color:#34d399">$</span> kubectl -n sappt exec deploy/redis -- redis-cli keys 'chatbot:*'<br/>
        <span style="color:#60a5fa"># GHCR 패키지 조회</span><br/>
        <span style="color:#34d399">$</span> curl https://api.sappt.co.kr/api/monitoring/ghcr?owner=sappt -H "Authorization: Bearer $TOKEN"
      </div>
    `,
  },
  {
    title: '📡 전체 구성도 (2026-04-15 기준)',
    content: `
      <div style="font-family:monospace;font-size:13px;line-height:1.8;color:#1e293b;background:#f8fafc;padding:24px;border-radius:12px;border:1px solid #e2e8f0;white-space:pre">
┌──────────────────────────────────────────────────────────────────────────────┐
│                      SAPPT 프로덕션 구성 (OCI ARM64)                           │
├──────────────────────────────────────────────────────────────────────────────┤
│                                                                              │
│  [사용자 브라우저]                                                            │
│       │ HTTPS                                                                │
│       ▼                                                                      │
│  [NGINX Proxy Manager] ──► SSL / 도메인 라우팅 (lab.sappt.co.kr)             │
│       │                                                                      │
│       ├─► frontend (React, 관리자) ─┐                                        │
│       ├─► partner-frontend (React) ─┤                                        │
│       └─► backend (FastAPI) ────────┤                                        │
│              │                      │                                        │
│              ├─► PostgreSQL 17     │  k3s Cluster (sappt ns)                │
│              ├─► Redis 7 ★신규      │                                        │
│              ├─► OCI Object Storage │                                        │
│              ├─► OpenSearch 2.17 ──┘                                        │
│              └─► OpenRouter API ──► [DeepSeek/Gemini/Llama/Qwen] ★멀티      │
│                                                                              │
│  [모니터링 스택]                                                              │
│       Loki ◄── promtail (docker logs)                                        │
│       Grafana ──► 대시보드 UI (Loki + PostgreSQL datasource)                  │
│                                                                              │
│  [CI/CD 파이프라인] ★신규                                                     │
│       로컬 빌드:  deploy.sh → localhost:5000 → k3s rolling update            │
│       자동 빌드:  GH Actions → ghcr.io → (수동 pull 가능)                    │
│                                                                              │
└──────────────────────────────────────────────────────────────────────────────┘</div>

      <div style="margin-top:20px;display:grid;grid-template-columns:repeat(4,1fr);gap:12px">
        <div style="padding:14px;background:#dbeafe;border-radius:8px;text-align:center">
          <div style="font-size:24px;font-weight:800;color:#1e40af">18</div>
          <div style="font-size:11px;color:#1e3a8a">실행 서비스</div>
        </div>
        <div style="padding:14px;background:#dcfce7;border-radius:8px;text-align:center">
          <div style="font-size:24px;font-weight:800;color:#166534">~6GB</div>
          <div style="font-size:11px;color:#14532d">메모리 사용</div>
        </div>
        <div style="padding:14px;background:#fef3c7;border-radius:8px;text-align:center">
          <div style="font-size:24px;font-weight:800;color:#92400e">~8%</div>
          <div style="font-size:11px;color:#78350f">CPU 사용</div>
        </div>
        <div style="padding:14px;background:#ffe4e6;border-radius:8px;text-align:center">
          <div style="font-size:24px;font-weight:800;color:#9f1239">0 원</div>
          <div style="font-size:11px;color:#881337">월 비용 (Always Free)</div>
        </div>
      </div>
    `,
  },
  // ── Slide: OCI 오브젝트 스토리지 관리 ──
  {
    title: '☁️ OCI 오브젝트 스토리지 관리',
    content: `
      <div style="display:grid;gap:18px">

        <!-- 개요 헤더 -->
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:linear-gradient(135deg,#eff6ff,#dbeafe);padding:20px;border-radius:16px;border:1px solid #93c5fd">
            <h4 style="color:#1d4ed8;margin:0 0 10px">버킷 구성</h4>
            <div style="font-size:12px;color:#374151;line-height:2">
              <div style="display:flex;gap:8px;align-items:center;margin-bottom:6px">
                <span style="background:#1d4ed8;color:#fff;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:700">운영</span>
                <code style="background:#dbeafe;padding:2px 8px;border-radius:4px">sappt-documents</code>
                <span style="color:#6b7280">첨부문서 · 계획서 · 사진</span>
              </div>
              <div style="display:flex;gap:8px;align-items:center">
                <span style="background:#059669;color:#fff;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:700">백업</span>
                <code style="background:#d1fae5;padding:2px 8px;border-radius:4px">sappt-backup</code>
                <span style="color:#6b7280">DB·소스 자동 백업</span>
              </div>
              <div style="margin-top:12px;font-size:11px;color:#6b7280;line-height:1.8">
                리전: <b>ap-chuncheon-1</b> (춘천)<br/>
                네임스페이스: <code style="background:#f1f5f9;padding:1px 5px;border-radius:3px">axk0sygwmfyx</code><br/>
                인증: OCI Python SDK + <code style="background:#f1f5f9;padding:1px 5px;border-radius:3px">~/.oci/config</code> (hostPath)
              </div>
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#f0fdf4,#dcfce7);padding:20px;border-radius:16px;border:1px solid #86efac">
            <h4 style="color:#16a34a;margin:0 0 10px">관리 메뉴 접근</h4>
            <div style="font-size:12px;color:#374151;line-height:2.2">
              <div style="background:#fff;padding:8px 12px;border-radius:8px;font-family:monospace;font-size:11px;color:#1d4ed8;border:1px solid #dbeafe;margin-bottom:8px">
                lab.sappt.co.kr → 서버관리 → OCI 스토리지
              </div>
              <div>🔗 OCI 콘솔: <b>cloud.oracle.com</b></div>
              <div style="font-size:11px;color:#6b7280;margin-left:16px">→ Oracle Cloud → Storage</div>
              <div style="font-size:11px;color:#6b7280;margin-left:16px">→ Object Storage → Buckets</div>
            </div>
          </div>
        </div>

        <!-- 주요 기능 4종 -->
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px">
          <div style="background:linear-gradient(135deg,#fef9c3,#fef08a);padding:14px;border-radius:12px;border:1px solid #fde047">
            <div style="font-size:18px;margin-bottom:6px">📁</div>
            <div style="font-weight:700;color:#854d0e;font-size:12px;margin-bottom:6px">폴더 탐색</div>
            <div style="font-size:11px;color:#713f12;line-height:1.8">
              폴더 클릭 → 진입<br/>
              브레드크럼 경로 표시<br/>
              상위 폴더 이동 가능<br/>
              <code style="background:#fef08a;padding:1px 4px;border-radius:3px">delimiter=/</code> 방식
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#ecfdf5,#d1fae5);padding:14px;border-radius:12px;border:1px solid #6ee7b7">
            <div style="font-size:18px;margin-bottom:6px">⬆️</div>
            <div style="font-weight:700;color:#065f46;font-size:12px;margin-bottom:6px">N개 업로드</div>
            <div style="font-size:11px;color:#064e3b;line-height:1.8">
              드래그&드롭 다중 선택<br/>
              파일 목록 미리보기<br/>
              순차 업로드 + 진행률<br/>
              현재 폴더 위치에 저장
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#fef2f2,#fecaca);padding:14px;border-radius:12px;border:1px solid #fca5a5">
            <div style="font-size:18px;margin-bottom:6px">🗑️</div>
            <div style="font-weight:700;color:#991b1b;font-size:12px;margin-bottom:6px">N개 삭제</div>
            <div style="font-size:11px;color:#7f1d1d;line-height:1.8">
              체크박스 다중 선택<br/>
              파란 툴바 → 일괄 삭제<br/>
              삭제 확인 모달<br/>
              개별 삭제도 지원
            </div>
          </div>
          <div style="background:linear-gradient(135deg,#eff6ff,#dbeafe);padding:14px;border-radius:12px;border:1px solid #93c5fd">
            <div style="font-size:18px;margin-bottom:6px">⬇️</div>
            <div style="font-weight:700;color:#1e40af;font-size:12px;margin-bottom:6px">N개 다운로드</div>
            <div style="font-size:11px;color:#1e3a8a;line-height:1.8">
              체크박스 선택 후<br/>
              OCI PAR URL 발급<br/>
              브라우저 순차 다운로드<br/>
              폴더는 자동 제외
            </div>
          </div>
        </div>

        <!-- API 엔드포인트 -->
        <div style="background:#0f172a;border-radius:12px;padding:16px">
          <div style="color:#94a3b8;font-size:10px;font-weight:700;letter-spacing:.08em;margin-bottom:12px">API ENDPOINTS — /api/storage/*</div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-family:monospace;font-size:11px">
            <div style="background:#1e293b;padding:8px 12px;border-radius:6px;color:#e2e8f0">
              <span style="color:#34d399">GET</span> &nbsp;/storage/health<br/>
              <span style="color:#94a3b8;font-size:10px">OCI 연결 상태 확인</span>
            </div>
            <div style="background:#1e293b;padding:8px 12px;border-radius:6px;color:#e2e8f0">
              <span style="color:#34d399">GET</span> &nbsp;/storage/objects<br/>
              <span style="color:#94a3b8;font-size:10px">?prefix=&delimiter=/ (폴더 탐색)</span>
            </div>
            <div style="background:#1e293b;padding:8px 12px;border-radius-6px;border-radius:6px;color:#e2e8f0">
              <span style="color:#fbbf24">POST</span> /storage/objects/upload<br/>
              <span style="color:#94a3b8;font-size:10px">?prefix= (현재 폴더 업로드)</span>
            </div>
            <div style="background:#1e293b;padding:8px 12px;border-radius:6px;color:#e2e8f0">
              <span style="color:#f87171">DEL</span> &nbsp;/storage/objects/{"{key}"}<br/>
              <span style="color:#94a3b8;font-size:10px">단일/폴더 마커 삭제</span>
            </div>
            <div style="background:#1e293b;padding:8px 12px;border-radius:6px;color:#e2e8f0">
              <span style="color:#fbbf24">POST</span> /storage/folders<br/>
              <span style="color:#94a3b8;font-size:10px">body: {"{folder_name, prefix}"}</span>
            </div>
            <div style="background:#1e293b;padding:8px 12px;border-radius:6px;color:#e2e8f0">
              <span style="color:#34d399">GET</span> &nbsp;/storage/objects/{"{key}"}/download-url<br/>
              <span style="color:#94a3b8;font-size:10px">OCI PAR URL 발급 (1시간 유효)</span>
            </div>
          </div>
        </div>

        <!-- 백업 용량 자동 정리 -->
        <div style="background:linear-gradient(135deg,#fff7ed,#ffedd5);padding:16px;border-radius:14px;border:1px solid #fdba74">
          <h4 style="color:#c2410c;margin:0 0 10px">🧹 백업 스토리지 자동 정리 (backup_storage_cleanup.sh)</h4>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;font-size:11px">
            <div style="background:#fff;padding:10px;border-radius:8px;line-height:1.9">
              <b style="color:#ea580c">발동 조건</b><br/>
              OCI sappt-backup ≥ <b>14GB</b> (70%)<br/>
              B2 sappt-backup &nbsp;≥ <b>7GB</b> (70%)<br/>
              → 자동 삭제 시작
            </div>
            <div style="background:#fff;padding:10px;border-radius:8px;line-height:1.9">
              <b style="color:#ea580c">삭제 순서</b><br/>
              ① 오래된 일별 date-group 먼저<br/>
              ② 그래도 부족 시 오래된 주간 삭제<br/>
              목표: <b>60% 이하</b>로 회복
            </div>
            <div style="background:#fff;padding:10px;border-radius:8px;line-height:1.9">
              <b style="color:#ea580c">보호 정책</b><br/>
              최근 <b>7일</b> 일별 파일 절대 보호<br/>
              최근 <b>4주</b> 주간 파일 절대 보호<br/>
              Discord 🔵완료 / 🔴불가 알림
            </div>
          </div>
        </div>

      </div>
    `,
  },
  // ── Slide: QR코드 활용 전략 (전문가 가이드) ──
  {
    title: '📱 QR코드 활용 전략 — 시험관리 전문가 가이드',
    content: `
      <div style="display:grid;gap:16px">

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="padding:18px;background:linear-gradient(135deg,#fef3c7,#fde68a20);border-radius:12px;border:1px solid #fcd34d">
            <div style="font-size:14px;font-weight:700;color:#92400e;margin-bottom:10px">⚠️ 현재 상태 (As-Is)</div>
            <ul style="margin:0;padding-left:18px;font-size:12px;color:#78350f;line-height:2">
              <li><b>qrcode 필드 = 단순 URL 텍스트</b> 수동 입력</li>
              <li>QR 이미지 생성 없음 (시스템이 생성하지 않음)</li>
              <li>현장 활용 시나리오 전무</li>
              <li>약제 시료·포장 라벨링 미연동</li>
              <li>HWPX 문서에 QR 미삽입</li>
            </ul>
          </div>
          <div style="padding:18px;background:linear-gradient(135deg,#dcfce7,#bbf7d020);border-radius:12px;border:1px solid #86efac">
            <div style="font-size:14px;font-weight:700;color:#166534;margin-bottom:10px">✅ 목표 (To-Be)</div>
            <ul style="margin:0;padding-left:18px;font-size:12px;color:#14532d;line-height:2">
              <li>시스템이 <b>QR 이미지 자동 생성</b></li>
              <li>포장 표지판 → 즉시 시험 정보 조회</li>
              <li>HWPX 문서 내 QR 코드 자동 삽입</li>
              <li>약제 시료 QR 라벨 인쇄</li>
              <li>파트너 포털 QR 접근 링크</li>
            </ul>
          </div>
        </div>

        <div style="font-size:14px;font-weight:700;color:#1e293b;margin-bottom:4px">🎯 6대 활용 시나리오 — 전문가 권장</div>
        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px">

          <div style="padding:16px;background:#eff6ff;border-radius:10px;border:1px solid #bfdbfe">
            <div style="font-size:13px;font-weight:700;color:#1d4ed8;margin-bottom:8px">① 시험 포장 표지판 QR</div>
            <div style="font-size:11px;color:#1e40af;line-height:1.8">
              <b>대상:</b> 각 시험 구역(포장) 표지판<br/>
              <b>내용:</b> 시험번호·약제명·처리일정 즉시 확인<br/>
              <b>방법:</b> A4/A5 표지판 출력 → 야외 부착<br/>
              <b>효과:</b> 현장 연구원 현황 파악 50% 단축<br/>
              <b>URL:</b> <code>lab.sappt.co.kr/field/{id}</code>
            </div>
          </div>

          <div style="padding:16px;background:#f5f3ff;border-radius:10px;border:1px solid #c4b5fd">
            <div style="font-size:13px;font-weight:700;color:#6d28d9;margin-bottom:8px">② HWPX 문서 QR 삽입</div>
            <div style="font-size:11px;color:#5b21b6;line-height:1.8">
              <b>대상:</b> 시험계획서(한글 문서) 표지<br/>
              <b>내용:</b> 인쇄 문서 → 최신 온라인 계획서 연결<br/>
              <b>방법:</b> HWPX 생성 시 QR 이미지 자동 삽입<br/>
              <b>효과:</b> 문서 버전 불일치 문제 근절<br/>
              <b>위치:</b> 우측 상단 30×30mm
            </div>
          </div>

          <div style="padding:16px;background:#fff7ed;border-radius:10px;border:1px solid #fed7aa">
            <div style="font-size:13px;font-weight:700;color:#c2410c;margin-bottom:8px">③ 약제 시료 QR 라벨</div>
            <div style="font-size:11px;color:#9a3412;line-height:1.8">
              <b>대상:</b> 약제 샘플 용기 스티커<br/>
              <b>내용:</b> 약제명·농도·유효기간·보관조건<br/>
              <b>방법:</b> 열전사 라벨 프린터 출력 (40×25mm)<br/>
              <b>효과:</b> 시료 오용/혼동 사고 방지<br/>
              <b>규격:</b> QR 20mm + 텍스트 영역
            </div>
          </div>

          <div style="padding:16px;background:#fdf2f8;border-radius:10px;border:1px solid #f9a8d4">
            <div style="font-size:13px;font-weight:700;color:#be185d;margin-bottom:8px">④ 파트너 포털 접근 QR</div>
            <div style="font-size:11px;color:#9d174d;line-height:1.8">
              <b>대상:</b> 의뢰사(파트너) 이메일/문서<br/>
              <b>내용:</b> QR 스캔 → 파트너 포털 자동 접근<br/>
              <b>방법:</b> 알림 발송 시 개인화 QR 첨부<br/>
              <b>효과:</b> 파트너 접근성 향상, 문의 감소<br/>
              <b>URL:</b> <code>partner.sappt.co.kr/auth?token=xxx</code>
            </div>
          </div>

          <div style="padding:16px;background:#ecfdf5;border-radius:10px;border:1px solid #6ee7b7">
            <div style="font-size:13px;font-weight:700;color:#065f46;margin-bottom:8px">⑤ 현장 조사 입력 QR</div>
            <div style="font-size:11px;color:#064e3b;line-height:1.8">
              <b>대상:</b> 처리일/조사일 현장 데이터 입력<br/>
              <b>내용:</b> QR 스캔 → 모바일 입력 폼 직접 오픈<br/>
              <b>방법:</b> 조사일정 QR → 해당 회차 조사 폼<br/>
              <b>효과:</b> 현장→시스템 입력 지연 제로<br/>
              <b>연동:</b> 카메라 촬영 → 사진 자동 첨부
            </div>
          </div>

          <div style="padding:16px;background:#f8fafc;border-radius:10px;border:1px solid #cbd5e1">
            <div style="font-size:13px;font-weight:700;color:#334155;margin-bottom:8px">⑥ 검토기록지 이력 QR</div>
            <div style="font-size:11px;color:#475569;line-height:1.8">
              <b>대상:</b> 검토기록지 용지 하단<br/>
              <b>내용:</b> 해당 시험의 전체 변경이력 조회<br/>
              <b>방법:</b> 검토기록지 출력 시 QR 자동 포함<br/>
              <b>효과:</b> 감사 추적성(Audit Trail) 강화<br/>
              <b>규정:</b> GLP 기준 문서 추적성 요건 충족
            </div>
          </div>
        </div>

        <div style="padding:14px;background:#1e293b;border-radius:10px;color:#94a3b8;font-size:11px;line-height:1.8">
          <span style="color:#f59e0b;font-weight:700">💡 전문가 포인트:</span>
          농약 시험은 <b>GLP(Good Laboratory Practice)</b> 기준상 현장-시스템 간 데이터 연속성이 핵심입니다.
          QR 코드는 <b>물리적 현장(포장·시료)과 디지털 시스템을 잇는 브릿지</b>로,
          수기 기록의 전사 오류 및 버전 불일치를 구조적으로 차단합니다.
          Dynamic QR(redirect URL) 방식을 채택하면 URL 변경 시에도 QR 라벨 재인쇄 없이 대응 가능합니다.
        </div>
      </div>
    `,
  },
  // ── Slide: QR코드 구현 로드맵 ──
  {
    title: '🗺️ QR코드 구현 로드맵 — 단계별 설계',
    content: `
      <div style="display:grid;gap:16px">

        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:14px">

          <div style="padding:18px;background:linear-gradient(135deg,#dbeafe,#eff6ff);border-radius:12px;border:2px solid #3b82f6">
            <div style="font-size:13px;font-weight:700;color:#1d4ed8;margin-bottom:4px">Phase 1 — 즉시 구현 (1~2주)</div>
            <div style="font-size:10px;color:#3b82f6;margin-bottom:10px">백엔드 QR 생성 + 프론트 표시</div>
            <div style="font-size:11px;color:#1e3a8a;line-height:1.9">
              <b>Backend</b><br/>
              • <code>pip install qrcode[pil] pillow</code><br/>
              • <code>GET /api/schedules/{id}/qrcode</code><br/>
              &nbsp;&nbsp;→ PNG 이미지 스트리밍 반환<br/>
              • <code>GET /api/protocols/{id}/qrcode</code><br/>
              • QR URL: <code>lab.sappt.co.kr/field/{id}</code><br/><br/>
              <b>Frontend</b><br/>
              • QR 이미지 미리보기 (현재 URL 텍스트 대체)<br/>
              • "QR 다운로드" 버튼 (PNG)<br/>
              • "포장 표지판 인쇄" 버튼 (A4 레이아웃)
            </div>
          </div>

          <div style="padding:18px;background:linear-gradient(135deg,#f0fdf4,#dcfce7);border-radius:12px;border:2px solid #22c55e">
            <div style="font-size:13px;font-weight:700;color:#15803d;margin-bottom:4px">Phase 2 — 단기 (2~4주)</div>
            <div style="font-size:10px;color:#22c55e;margin-bottom:10px">HWPX 삽입 + 약제 라벨 + 파트너 QR</div>
            <div style="font-size:11px;color:#14532d;line-height:1.9">
              <b>HWPX QR 삽입</b><br/>
              • python-hwpx에 QR PNG 이미지 삽입<br/>
              • 표지 우측 상단 30×30mm 고정 위치<br/>
              • 생성 시 "최신 온라인 계획서" URL 인코딩<br/><br/>
              <b>약제 시료 라벨</b><br/>
              • Treatment별 QR 라벨 PDF 출력<br/>
              • 40×25mm 열전사 규격 지원<br/><br/>
              <b>파트너 QR</b><br/>
              • 알림 발송 시 파트너 포털 QR 첨부<br/>
              • 시험별 고유 토큰 기반 접근
            </div>
          </div>

          <div style="padding:18px;background:linear-gradient(135deg,#fdf4ff,#fae8ff);border-radius:12px;border:2px solid #a855f7">
            <div style="font-size:13px;font-weight:700;color:#7e22ce;margin-bottom:4px">Phase 3 — 중기 (1~2개월)</div>
            <div style="font-size:10px;color:#a855f7;margin-bottom:10px">현장 모바일 + 스캔 로그 + GPS</div>
            <div style="font-size:11px;color:#581c87;line-height:1.9">
              <b>현장 조사 모바일 뷰</b><br/>
              • QR 스캔 → 모바일 최적화 입력 폼<br/>
              • 처리/조사 회차별 직접 데이터 입력<br/>
              • 사진 촬영 → OCI Object Storage 자동 업로드<br/><br/>
              <b>QR 접근 로그</b><br/>
              • 스캔 시각·위치·장치 기록<br/>
              • ActivityLog 테이블 연동<br/><br/>
              <b>GPS + QR 연동</b><br/>
              • 스캔 시 GPS 좌표 자동 기록<br/>
              • 시험 포장 위치 지도 표시
            </div>
          </div>
        </div>

        <div style="padding:16px;background:#f8fafc;border-radius:10px;border:1px solid #e2e8f0">
          <div style="font-size:13px;font-weight:700;color:#1e293b;margin-bottom:12px">⚙️ Phase 1 핵심 코드 설계</div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;font-size:11px">
            <div style="background:#1e293b;padding:14px;border-radius:8px;color:#e2e8f0;font-family:monospace;line-height:1.8">
              <span style="color:#60a5fa"># Backend: qrcode 의존성 추가</span><br/>
              <span style="color:#34d399">requirements.txt</span><br/>
              qrcode[pil]==8.0<br/>
              pillow==11.0.0<br/><br/>
              <span style="color:#60a5fa"># 엔드포인트 예시</span><br/>
              <span style="color:#fbbf24">@app.get</span>("/api/schedules/{id}/qrcode")<br/>
              <span style="color:#a78bfa">async def</span> get_schedule_qr(id: int):<br/>
              &nbsp;&nbsp;url = f"https://lab.sappt.co.kr/field/{id}"<br/>
              &nbsp;&nbsp;img = qrcode.make(url)<br/>
              &nbsp;&nbsp;<span style="color:#a78bfa">return</span> StreamingResponse(img_bytes, media_type="image/png")
            </div>
            <div style="background:#1e293b;padding:14px;border-radius:8px;color:#e2e8f0;font-family:monospace;line-height:1.8">
              <span style="color:#60a5fa">// Frontend: QR 이미지 표시</span><br/>
              <span style="color:#fbbf24">const</span> QRCodeViewer = ({ scheduleId }) =&gt; (<br/>
              &nbsp;&lt;div&gt;<br/>
              &nbsp;&nbsp;&lt;img src={<span style="color:#34d399">&#96;/api/schedules/&#36;{scheduleId}/qrcode&#96;</span>}<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;width={120} height={120} /&gt;<br/>
              &nbsp;&nbsp;&lt;Button onClick={handleDownload}&gt;<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;QR 다운로드<br/>
              &nbsp;&nbsp;&lt;/Button&gt;<br/>
              &nbsp;&lt;/div&gt;<br/>
              );
            </div>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="padding:16px;background:#fffbeb;border-radius:10px;border:1px solid #fde68a">
            <div style="font-size:13px;font-weight:700;color:#92400e;margin-bottom:10px">📐 QR코드 규격 선택 기준</div>
            <table style="width:100%;font-size:11px;border-collapse:collapse">
              <tr style="background:#fef3c7"><th style="padding:6px;border:1px solid #fde68a">용도</th><th style="padding:6px;border:1px solid #fde68a">유형</th><th style="padding:6px;border:1px solid #fde68a">크기</th><th style="padding:6px;border:1px solid #fde68a">권장</th></tr>
              <tr><td style="padding:5px;border:1px solid #fde68a">포장 표지판</td><td style="padding:5px;border:1px solid #fde68a">URL QR</td><td style="padding:5px;border:1px solid #fde68a">60×60mm</td><td style="padding:5px;border:1px solid #fde68a">✅ 즉시</td></tr>
              <tr><td style="padding:5px;border:1px solid #fde68a">HWPX 문서</td><td style="padding:5px;border:1px solid #fde68a">URL QR</td><td style="padding:5px;border:1px solid #fde68a">30×30mm</td><td style="padding:5px;border:1px solid #fde68a">✅ Phase2</td></tr>
              <tr><td style="padding:5px;border:1px solid #fde68a">약제 라벨</td><td style="padding:5px;border:1px solid #fde68a">URL QR</td><td style="padding:5px;border:1px solid #fde68a">20×20mm</td><td style="padding:5px;border:1px solid #fde68a">✅ Phase2</td></tr>
              <tr><td style="padding:5px;border:1px solid #fde68a">파트너 알림</td><td style="padding:5px;border:1px solid #fde68a">Dynamic QR</td><td style="padding:5px;border:1px solid #fde68a">50×50mm</td><td style="padding:5px;border:1px solid #fde68a">Phase2</td></tr>
              <tr><td style="padding:5px;border:1px solid #fde68a">현장 조사</td><td style="padding:5px;border:1px solid #fde68a">URL QR</td><td style="padding:5px;border:1px solid #fde68a">40×40mm</td><td style="padding:5px;border:1px solid #fde68a">Phase3</td></tr>
            </table>
          </div>
          <div style="padding:16px;background:#f0fdf4;border-radius:10px;border:1px solid #86efac">
            <div style="font-size:13px;font-weight:700;color:#15803d;margin-bottom:10px">📋 GLP 규정 준수 관점</div>
            <div style="font-size:11px;color:#14532d;line-height:2">
              <b>GLP 핵심 요건과 QR 연계:</b><br/>
              • <b>데이터 추적성</b> → QR 스캔 로그로 접근 이력 기록<br/>
              • <b>문서 무결성</b> → 인쇄 문서 ↔ 최신 온라인 버전 연결<br/>
              • <b>시료 식별성</b> → 약제 용기 QR로 혼동 원천 차단<br/>
              • <b>현장 기록 즉시성</b> → 스캔 후 즉시 데이터 입력<br/>
              • <b>감사 추적(Audit Trail)</b> → 검토기록지 QR 이력 연동<br/><br/>
              <span style="color:#059669;font-weight:600">→ QR 도입 시 GLP/GCP 감사 대응력 대폭 향상</span>
            </div>
          </div>
        </div>

        <div style="padding:14px;background:linear-gradient(135deg,#667eea15,#764ba215);border-radius:10px;border:1px solid #667eea33;font-size:12px;color:#4338ca;line-height:1.8">
          <b>🎯 우선순위 추천 (전문가 의견):</b>
          Phase 1 중 <b>"포장 표지판 인쇄 기능"</b>이 현장 연구원 생산성에 가장 즉각적인 효과를 줍니다.
          <code>GET /api/schedules/{id}/qrcode</code> 엔드포인트 하나로 시험일정 상세 → QR 이미지 표시 → 인쇄까지 완결됩니다.
          qrcode 라이브러리는 Python 표준 생태계로 의존성 리스크 없음.
          <b>약 3일 작업으로 핵심 워크플로우 개선 가능.</b>
        </div>
      </div>
    `,
  },
  // ── Slide: QR코드 현장 포장 표지판 설계 ──
  {
    title: '🏷️ 시험 포장 표지판 QR — 설계 & 운영 매뉴얼',
    content: `
      <div style="display:grid;gap:16px">

        <div style="display:grid;grid-template-columns:1fr 2fr;gap:16px">
          <div style="padding:20px;background:#f8fafc;border-radius:12px;border:2px dashed #cbd5e1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px">
            <div style="font-size:11px;color:#64748b;font-weight:600;text-align:center">포장 표지판 레이아웃 (A5)</div>
            <div style="width:160px;padding:12px;background:white;border:1px solid #e2e8f0;border-radius:6px;font-size:10px;color:#334155">
              <div style="text-align:center;font-weight:700;font-size:11px;border-bottom:1px solid #e2e8f0;padding-bottom:6px;margin-bottom:8px">농약 효과시험 포장</div>
              <div style="display:flex;gap:8px;align-items:flex-start">
                <div style="width:52px;height:52px;background:#f1f5f9;border:1px solid #cbd5e1;border-radius:4px;display:flex;align-items:center;justify-content:center;font-size:8px;color:#94a3b8;flex-shrink:0">QR<br/>CODE</div>
                <div style="line-height:1.7;font-size:9px">
                  <b>시험번호:</b> 2026-001<br/>
                  <b>약제명:</b> ○○유제<br/>
                  <b>시험작물:</b> 벼<br/>
                  <b>담당자:</b> 홍길동
                </div>
              </div>
              <div style="margin-top:8px;padding-top:6px;border-top:1px solid #e2e8f0;font-size:9px;color:#64748b;text-align:center">
                처리기간: 2026.05.01~06.30<br/>
                ☎ 010-0000-0000
              </div>
            </div>
            <div style="font-size:10px;color:#94a3b8;text-align:center">실제 출력 시 A5 단면 라미네이팅 권장</div>
          </div>

          <div style="display:grid;gap:12px">
            <div style="padding:16px;background:#eff6ff;border-radius:10px;border:1px solid #bfdbfe">
              <div style="font-size:13px;font-weight:700;color:#1d4ed8;margin-bottom:8px">📋 표지판 포함 정보 설계</div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:11px;color:#1e3a8a;line-height:1.8">
                <div>
                  <b>필수 항목:</b><br/>
                  • 시험번호 (test_number)<br/>
                  • 시험명/약제명 (title)<br/>
                  • 시험작물 (test_crop)<br/>
                  • 처리기간 (start~end_date)<br/>
                  • 담당 연구원 (researcher)
                </div>
                <div>
                  <b>선택 항목:</b><br/>
                  • 의뢰사명 (company)<br/>
                  • 시험장소 (location)<br/>
                  • 포장주 연락처 (field_owner)<br/>
                  • 처리 횟수 (treatment_count)<br/>
                  • 조사 횟수 (survey_count)
                </div>
              </div>
            </div>
            <div style="padding:16px;background:#fef3c7;border-radius:10px;border:1px solid #fcd34d">
              <div style="font-size:13px;font-weight:700;color:#92400e;margin-bottom:8px">🔗 QR URL 설계 원칙</div>
              <div style="font-size:11px;color:#78350f;line-height:2">
                <b>공개 URL (인증 불필요):</b> <code>lab.sappt.co.kr/field/{schedule_id}</code><br/>
                → 현장에서 스마트폰으로 바로 스캔, 로그인 없이 기본 정보 확인<br/>
                <b>상세 URL (로그인 필요):</b> <code>lab.sappt.co.kr/schedules?id={schedule_id}</code><br/>
                → 연구원 로그인 후 전체 데이터 수정/입력<br/>
                <b>파트너 URL:</b> <code>partner.sappt.co.kr/schedules/{schedule_id}</code><br/>
                → 의뢰사 파트너 계정으로 진행상태 조회
              </div>
            </div>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="padding:16px;background:#f0fdf4;border-radius:10px;border:1px solid #86efac">
            <div style="font-size:13px;font-weight:700;color:#15803d;margin-bottom:10px">🖨️ 인쇄 운영 가이드</div>
            <div style="font-size:11px;color:#14532d;line-height:2">
              <b>출력 절차:</b><br/>
              1. 시험일정 목록 → 해당 시험 선택<br/>
              2. "포장 표지판 인쇄" 버튼 클릭<br/>
              3. A5 PDF 미리보기 확인<br/>
              4. 프린터 출력 (A5 단면)<br/>
              5. 라미네이팅 후 포장 표지판에 부착<br/><br/>
              <b>교체 주기:</b> 처리 회차 변경 시 재출력<br/>
              <b>권장 소재:</b> 방수 라미네이팅 또는 야외용 PVC
            </div>
          </div>
          <div style="padding:16px;background:#fdf4ff;border-radius:10px;border:1px solid #e879f9">
            <div style="font-size:13px;font-weight:700;color:#7e22ce;margin-bottom:10px">📊 기대 효과 측정 지표</div>
            <div style="font-size:11px;color:#581c87;line-height:2">
              <b>효율 지표:</b><br/>
              • 현장 정보 조회 시간: 5분 → 10초 (97% 감소)<br/>
              • 포장 확인 오류율: 목표 0% (혼동 방지)<br/>
              • 처리일 누락 기록: 즉시 입력으로 0건 목표<br/><br/>
              <b>추적 지표 (QR 스캔 로그):</b><br/>
              • 일별 포장 접근 횟수<br/>
              • 시험별 현장 방문 패턴<br/>
              • 미접속 시험 자동 경보
            </div>
          </div>
        </div>

        <div style="padding:14px;background:#1e293b;border-radius:10px;color:#94a3b8;font-size:11px;line-height:1.9">
          <span style="color:#34d399;font-weight:700">🚀 즉시 시작 가능한 1단계 액션:</span><br/>
          <span style="color:#fbbf24">1)</span> <code style="color:#60a5fa">pip install qrcode[pil]==8.0 pillow</code> → requirements.txt 추가<br/>
          <span style="color:#fbbf24">2)</span> <code style="color:#60a5fa">GET /api/schedules/{id}/qrcode</code> 엔드포인트 추가 (약 20줄)<br/>
          <span style="color:#fbbf24">3)</span> SchedulePage 상세 모달에 QR 이미지 컴포넌트 교체 (URL 텍스트 → 이미지)<br/>
          <span style="color:#fbbf24">4)</span> "포장 표지판 인쇄" 버튼 → window.print() + CSS @media print 스타일<br/>
          <span style="color:#fbbf24">예상 소요:</span> <b style="color:#a78bfa">백엔드 1일 + 프론트엔드 1일 = 총 2일</b>
        </div>
      </div>
    `,
  },
  // ── Slide: Dynamic QR — 개념 · 구조 · 흐름 ──
  {
    title: '🔗 Dynamic QR — 개념 · 구조 · 시스템 흐름',
    content: `
      <div style="display:grid;gap:16px">

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="padding:16px;background:#fef3c7;border-radius:12px;border:2px solid #fcd34d">
            <div style="font-size:13px;font-weight:700;color:#92400e;margin-bottom:10px">⚠️ Static QR (현재 방식)</div>
            <div style="font-size:11px;color:#78350f;line-height:2">
              QR 이미지 자체에 <b>목적지 URL을 직접 인코딩</b><br/><br/>
              <code style="background:#fde68a;padding:2px 6px;border-radius:3px">
                lab.sappt.co.kr/schedules?id=123
              </code><br/><br/>
              <b style="color:#dc2626">❌ 문제점:</b><br/>
              &bull; URL 구조 변경 → QR 라벨 전량 재인쇄<br/>
              &bull; 스캔 통계 수집 불가<br/>
              &bull; 만료/비활성화 제어 불가<br/>
              &bull; 인쇄 후 목적지 변경 불가
            </div>
          </div>
          <div style="padding:16px;background:#dcfce7;border-radius:12px;border:2px solid #22c55e">
            <div style="font-size:13px;font-weight:700;color:#15803d;margin-bottom:10px">✅ Dynamic QR (도입 방식)</div>
            <div style="font-size:11px;color:#14532d;line-height:2">
              QR 이미지는 <b>고정 단축 URL(토큰)만 인코딩</b><br/><br/>
              <code style="background:#bbf7d0;padding:2px 6px;border-radius:3px">
                lab.sappt.co.kr/qr/aB3xY7
              </code><br/><br/>
              <b style="color:#15803d">✅ 장점:</b><br/>
              &bull; QR 라벨 한 번 인쇄 → 영구 사용<br/>
              &bull; 목적지 URL DB에서 자유 변경<br/>
              &bull; 스캔 횟수·시각·위치 통계 수집<br/>
              &bull; 만료일·비활성화로 보안 제어
            </div>
          </div>
        </div>

        <div style="padding:16px;background:#f8fafc;border-radius:12px;border:1px solid #e2e8f0">
          <div style="font-size:13px;font-weight:700;color:#1e293b;margin-bottom:12px">📡 SAPPT Dynamic QR 시스템 흐름</div>
          <div class="mermaid">
sequenceDiagram
    participant 현장 as 📱 현장 스마트폰
    participant QR as 🔲 QR 라벨<br/>/qr/aB3xY7
    participant Server as ⚡ FastAPI<br/>GET /qr/{token}
    participant DB as 🗄️ qr_redirects<br/>qr_scan_logs
    participant Target as 🎯 목적지<br/>(시험일정/계획서/파트너)

    현장->>QR: 카메라 스캔
    QR->>Server: GET /qr/aB3xY7
    Server->>DB: token 조회 (is_active, expires_at 확인)
    DB-->>Server: target_url 반환
    Server->>DB: scan_log INSERT (시각·IP·UA)
    Server-->>현장: 302 Redirect
    현장->>Target: 목적지 페이지 로드
          </div>
        </div>

        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px">
          <div style="padding:14px;background:#eff6ff;border-radius:10px;border:1px solid #bfdbfe">
            <div style="font-size:12px;font-weight:700;color:#1d4ed8;margin-bottom:8px">🗄️ qr_redirects 테이블</div>
            <div style="font-size:10px;color:#1e40af;line-height:2;font-family:monospace">
              id &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; INTEGER PK<br/>
              token &nbsp;&nbsp;&nbsp;&nbsp; VARCHAR(20) UNIQUE<br/>
              target_url &nbsp;VARCHAR(500)<br/>
              qr_type &nbsp;&nbsp;&nbsp;VARCHAR(50)<br/>
              <span style="color:#64748b">&nbsp;schedule/protocol/</span><br/>
              <span style="color:#64748b">&nbsp;partner/treatment</span><br/>
              ref_id &nbsp;&nbsp;&nbsp;&nbsp;INTEGER<br/>
              scan_count &nbsp;INTEGER DEFAULT 0<br/>
              expires_at &nbsp;DATETIME NULL<br/>
              is_active &nbsp;&nbsp;BOOLEAN DEFAULT T<br/>
              created_at &nbsp;DATETIME
            </div>
          </div>
          <div style="padding:14px;background:#f5f3ff;border-radius:10px;border:1px solid #c4b5fd">
            <div style="font-size:12px;font-weight:700;color:#6d28d9;margin-bottom:8px">📋 qr_scan_logs 테이블</div>
            <div style="font-size:10px;color:#5b21b6;line-height:2;font-family:monospace">
              id &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; INTEGER PK<br/>
              token &nbsp;&nbsp;&nbsp;&nbsp; VARCHAR(20) INDEX<br/>
              scanned_at &nbsp;DATETIME<br/>
              ip_address &nbsp;VARCHAR(50)<br/>
              user_agent &nbsp;VARCHAR(500)<br/>
              <span style="color:#64748b">&nbsp;(모바일/PC 구분)</span><br/>
              latitude &nbsp;&nbsp; FLOAT NULL<br/>
              longitude &nbsp; FLOAT NULL<br/>
              <span style="color:#64748b">&nbsp;(GPS 동의 시)</span><br/>
              referer &nbsp;&nbsp;&nbsp; VARCHAR(200)
            </div>
          </div>
          <div style="padding:14px;background:#fff7ed;border-radius:10px;border:1px solid #fed7aa">
            <div style="font-size:12px;font-weight:700;color:#c2410c;margin-bottom:8px">⚙️ API 엔드포인트</div>
            <div style="font-size:10px;color:#9a3412;line-height:2">
              <b>공개 (인증 불필요)</b><br/>
              <code>GET /qr/{token}</code><br/>
              &nbsp;→ 302 redirect + 로그 기록<br/><br/>
              <b>관리자 (JWT 필요)</b><br/>
              <code>POST /api/qr/generate</code><br/>
              &nbsp;→ 토큰 생성 + QR 이미지<br/>
              <code>PUT &nbsp;/api/qr/{token}</code><br/>
              &nbsp;→ target_url 변경<br/>
              <code>GET &nbsp;/api/qr/{token}/stats</code><br/>
              &nbsp;→ 스캔 통계<br/>
              <code>DELETE /api/qr/{token}</code><br/>
              &nbsp;→ 비활성화
            </div>
          </div>
        </div>

        <div style="padding:14px;background:#1e293b;border-radius:10px;color:#cbd5e1;font-size:11px;line-height:1.9">
          <span style="color:#fbbf24;font-weight:700">💡 전문가 포인트 — 토큰 설계 원칙:</span>
          토큰은 <b style="color:#e2e8f0">Base62 (A-Za-z0-9) 6자리</b>를 권장합니다. 62⁶ = 56억 조합으로 충돌 없음.
          URL에 포함되므로 특수문자 제외. 시험일정 수가 연간 수백 건이므로 6자리로 충분합니다.
          <span style="color:#60a5fa">secrets.token_urlsafe(4)[:6]</span> 한 줄로 생성 가능.
          만료일은 시험 종료일 + 30일을 기본값으로 설정하면 라벨 수명과 시험 주기가 자연스럽게 일치합니다.
        </div>
      </div>
    `,
  },
  // ── Slide: Dynamic QR — 단계별 구현 계획 ──
  {
    title: '📋 Dynamic QR — SAPPT 단계별 구현 계획',
    content: `
      <div style="display:grid;gap:16px">

        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:14px">
          <div style="padding:18px;background:linear-gradient(135deg,#dbeafe,#eff6ff);border-radius:12px;border:2px solid #3b82f6">
            <div style="font-size:13px;font-weight:700;color:#1d4ed8;margin-bottom:4px">Step 1 — 기반 구축 (1주)</div>
            <div style="font-size:10px;color:#3b82f6;margin-bottom:10px">DB 스키마 + redirect 엔드포인트</div>
            <div style="font-size:11px;color:#1e3a8a;line-height:1.9">
              <b>Backend</b><br/>
              &bull; <code>qr_redirects</code> 테이블 생성<br/>
              &bull; <code>qr_scan_logs</code> 테이블 생성<br/>
              &bull; <code>GET /qr/{token}</code> redirect 구현<br/>
              &bull; 토큰 생성 유틸 (Base62, 6자리)<br/>
              &bull; 시험일정 저장 시 QR 토큰 자동 생성<br/><br/>
              <b>검증</b><br/>
              &bull; curl 테스트: 302 Location 헤더 확인<br/>
              &bull; DB scan_log 기록 여부 확인
            </div>
          </div>
          <div style="padding:18px;background:linear-gradient(135deg,#f0fdf4,#dcfce7);border-radius:12px;border:2px solid #22c55e">
            <div style="font-size:13px;font-weight:700;color:#15803d;margin-bottom:4px">Step 2 — 관리 UI (1주)</div>
            <div style="font-size:10px;color:#22c55e;margin-bottom:10px">QR 이미지 표시 + 목적지 변경 UI</div>
            <div style="font-size:11px;color:#14532d;line-height:1.9">
              <b>Frontend</b><br/>
              &bull; 시험일정 상세에 Dynamic QR 이미지 표시<br/>
              &nbsp;&nbsp;<code>/api/schedules/{id}/qrcode</code><br/>
              &bull; "목적지 URL 변경" 버튼 + 모달<br/>
              &bull; "QR 만료일 설정" (시험 종료일 +30일 기본)<br/>
              &bull; QR 비활성화/재활성화 토글<br/><br/>
              <b>포장 표지판 인쇄</b><br/>
              &bull; Dynamic QR 이미지 + 시험정보 A5 PDF<br/>
              &bull; 인쇄 후 동일 QR로 영구 사용
            </div>
          </div>
          <div style="padding:18px;background:linear-gradient(135deg,#fdf4ff,#fae8ff);border-radius:12px;border:2px solid #a855f7">
            <div style="font-size:13px;font-weight:700;color:#7e22ce;margin-bottom:4px">Step 3 — 통계 & 확장 (2주)</div>
            <div style="font-size:10px;color:#a855f7;margin-bottom:10px">스캔 통계 대시보드 + 전체 확장</div>
            <div style="font-size:11px;color:#581c87;line-height:1.9">
              <b>스캔 통계 대시보드</b><br/>
              &bull; 시험별 스캔 횟수 · 시간대 분포<br/>
              &bull; 모바일/PC 비율 (User-Agent 파싱)<br/>
              &bull; 미스캔 시험 자동 알림<br/><br/>
              <b>QR 타입 확장</b><br/>
              &bull; 약제 시료 QR (treatment 단위)<br/>
              &bull; 파트너 포털 QR (만료 토큰)<br/>
              &bull; 시험계획서 QR (protocol 단위)<br/>
              &bull; HWPX 문서에 Dynamic QR 삽입
            </div>
          </div>
        </div>

        <div style="padding:16px;background:#f8fafc;border-radius:12px;border:1px solid #e2e8f0">
          <div style="font-size:13px;font-weight:700;color:#1e293b;margin-bottom:12px">⚙️ Step 1 핵심 코드 — redirect 엔드포인트</div>
          <div style="background:#1e293b;padding:16px;border-radius:8px;color:#e2e8f0;font-family:monospace;font-size:11px;line-height:2">
            <span style="color:#60a5fa"># models.py — 신규 테이블</span><br/>
            <span style="color:#a78bfa">class</span> <span style="color:#fbbf24">QRRedirect</span>(Base):<br/>
            &nbsp;&nbsp;__tablename__ = <span style="color:#34d399">"qr_redirects"</span><br/>
            &nbsp;&nbsp;id = Column(Integer, primary_key=True)<br/>
            &nbsp;&nbsp;token = Column(String(20), unique=True, index=True)<br/>
            &nbsp;&nbsp;target_url = Column(String(500))<br/>
            &nbsp;&nbsp;qr_type = Column(String(50))&nbsp;&nbsp;<span style="color:#64748b"># schedule/protocol/partner</span><br/>
            &nbsp;&nbsp;ref_id = Column(Integer)<br/>
            &nbsp;&nbsp;scan_count = Column(Integer, default=0)<br/>
            &nbsp;&nbsp;expires_at = Column(DateTime, nullable=True)<br/>
            &nbsp;&nbsp;is_active = Column(Boolean, default=True)<br/><br/>
            <span style="color:#60a5fa"># main.py — redirect 엔드포인트 (인증 불필요)</span><br/>
            <span style="color:#a78bfa">from</span> fastapi.responses <span style="color:#a78bfa">import</span> RedirectResponse<br/>
            <span style="color:#a78bfa">import</span> secrets, string<br/><br/>
            <span style="color:#fbbf24">@app.get</span>(<span style="color:#34d399">"/qr/{token}"</span>)<br/>
            <span style="color:#a78bfa">async def</span> <span style="color:#fbbf24">qr_redirect</span>(token: str, request: Request, db: Session = Depends(get_db)):<br/>
            &nbsp;&nbsp;qr = db.query(QRRedirect).filter(<br/>
            &nbsp;&nbsp;&nbsp;&nbsp;QRRedirect.token == token,<br/>
            &nbsp;&nbsp;&nbsp;&nbsp;QRRedirect.is_active == True<br/>
            &nbsp;&nbsp;).first()<br/>
            &nbsp;&nbsp;<span style="color:#a78bfa">if not</span> qr: <span style="color:#a78bfa">raise</span> HTTPException(404)<br/>
            &nbsp;&nbsp;<span style="color:#a78bfa">if</span> qr.expires_at <span style="color:#a78bfa">and</span> qr.expires_at &lt; datetime.now(KST): <span style="color:#a78bfa">raise</span> HTTPException(410)<br/>
            &nbsp;&nbsp;qr.scan_count += 1<br/>
            &nbsp;&nbsp;db.add(QRScanLog(token=token, ip_address=request.client.host,<br/>
            &nbsp;&nbsp;&nbsp;&nbsp;user_agent=request.headers.get(<span style="color:#34d399">"user-agent"</span>, "")))<br/>
            &nbsp;&nbsp;db.commit()<br/>
            &nbsp;&nbsp;<span style="color:#a78bfa">return</span> RedirectResponse(qr.target_url, status_code=302)
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="padding:14px;background:#ecfdf5;border-radius:10px;border:1px solid #6ee7b7">
            <div style="font-size:12px;font-weight:700;color:#065f46;margin-bottom:8px">🏷️ 토큰 자동 생성 로직</div>
            <div style="background:#1e293b;padding:12px;border-radius:6px;font-family:monospace;font-size:10px;color:#e2e8f0;line-height:1.9">
              <span style="color:#60a5fa"># Base62 6자리 충돌 없는 토큰 생성</span><br/>
              CHARS = string.ascii_letters + string.digits<br/><br/>
              <span style="color:#a78bfa">def</span> <span style="color:#fbbf24">gen_token</span>(db: Session) -&gt; str:<br/>
              &nbsp;&nbsp;<span style="color:#a78bfa">for</span> _ <span style="color:#a78bfa">in</span> range(10):<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;tok = <span style="color:#34d399">""</span>.join(secrets.choice(CHARS) <span style="color:#a78bfa">for</span> _ <span style="color:#a78bfa">in</span> range(6))<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#a78bfa">if not</span> db.query(QRRedirect).filter_by(token=tok).first():<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#a78bfa">return</span> tok<br/>
              &nbsp;&nbsp;<span style="color:#a78bfa">raise</span> ValueError(<span style="color:#34d399">"token 생성 실패"</span>)
            </div>
          </div>
          <div style="padding:14px;background:#fef3c7;border-radius:10px;border:1px solid #fcd34d">
            <div style="font-size:12px;font-weight:700;color:#92400e;margin-bottom:8px">📊 운영 시나리오별 활용</div>
            <div style="font-size:11px;color:#78350f;line-height:2">
              <b>시나리오 ①</b> 포장 주소 변경<br/>
              &nbsp;→ target_url만 수정, QR 라벨 유지<br/>
              <b>시나리오 ②</b> 시험 완료 후 보관<br/>
              &nbsp;→ 만료일 지정 또는 비활성화<br/>
              <b>시나리오 ③</b> 파트너 링크 교체<br/>
              &nbsp;→ 동일 QR, 신규 파트너 URL로 redirect<br/>
              <b>시나리오 ④</b> 스캔 0회 시험 감지<br/>
              &nbsp;→ 관리자 알림 (현장 미방문 체크)
            </div>
          </div>
        </div>

        <div style="padding:14px;background:linear-gradient(135deg,#667eea15,#764ba215);border-radius:10px;border:1px solid #667eea33;font-size:11px;color:#4338ca;line-height:1.9">
          <b>🎯 전문가 추천 최종 아키텍처:</b>
          SAPPT의 모든 QR 코드는 <b>Dynamic QR 단일 체계</b>로 통일합니다.
          시험일정 생성 시 토큰 자동 발급 → <code>/qr/{token}</code> QR 이미지 즉시 생성 → 라벨 인쇄.
          이후 URL 구조가 바뀌어도, 시험이 이전되어도, 파트너가 바뀌어도
          <b>현장 라벨은 교체 없이 DB 업데이트만으로 대응</b>됩니다.
          총 구현 비용: Step 1~2 기준 <b style="color:#667eea">백엔드 2일 + 프론트엔드 1.5일 = 3.5일</b>.
        </div>
      </div>
    `,
  },
  // ── Slide: 버전 비교 스냅샷 ──
  {
    title: '📸 시험계획서 버전 비교 스냅샷 — 설계 & 구현 가이드',
    content: `
      <div style="display:grid;gap:14px">

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="padding:16px;background:linear-gradient(135deg,#fef3c7,#fffbeb);border-radius:12px;border:2px solid #fcd34d">
            <div style="font-size:13px;font-weight:700;color:#92400e;margin-bottom:8px">⚠️ 해결하는 문제</div>
            <div style="font-size:11px;color:#78350f;line-height:2">
              계획서 인쇄 후 내용이 수정됐을 때<br/>
              현장·의뢰사가 <b>구버전으로 작업하는 사고</b>가 발생.<br/><br/>
              • 약제 희석배수 변경 → 인쇄본은 구값<br/>
              • 처리 시기 변경 → 연구원 미인지<br/>
              • 담당자 교체 → 의뢰사 연락 오류<br/>
              • GLP 감사 시 인쇄본 vs DB 불일치<br/><br/>
              <b>→ QR 스캔만으로 즉시 변경 감지</b>
            </div>
          </div>
          <div style="padding:16px;background:linear-gradient(135deg,#dcfce7,#f0fdf4);border-radius:12px;border:2px solid #86efac">
            <div style="font-size:13px;font-weight:700;color:#15803d;margin-bottom:8px">✅ 스냅샷 방식의 핵심 원리</div>
            <div style="font-size:11px;color:#14532d;line-height:2">
              QR 생성 시점의 계획서 핵심 필드를<br/>
              <b>SHA-256 해시 + JSON</b>으로 DB에 저장.<br/><br/>
              스캔 시 현재 계획서 해시를 재계산하여<br/>
              저장된 해시와 비교:<br/><br/>
              • <b style="color:#15803d">동일</b> → 정상 redirect (변경 없음)<br/>
              • <b style="color:#dc2626">다름</b> → ⚠️ 경고 + 변경 항목 시각화<br/><br/>
              <b>→ 해시 충돌 확률: 1/2⁵⁶ (사실상 0)</b>
            </div>
          </div>
        </div>

        <div style="padding:14px;background:#f8fafc;border-radius:12px;border:1px solid #e2e8f0">
          <div style="font-size:13px;font-weight:700;color:#1e293b;margin-bottom:10px">📡 전체 흐름 (Mermaid)</div>
          <div class="mermaid">
flowchart TD
    A["📝 계획서 작성/수정 완료"] --> B["QR 생성 버튼 클릭"]
    B --> C["핵심 필드 JSON 직렬화\n(약제·희석배수·처리시기 등)"]
    C --> D["SHA-256 해시 계산"]
    D --> E["protocol_snapshots 테이블\n토큰·해시·JSON 저장"]
    E --> F["QR 이미지 생성 → 인쇄"]

    F --> G["⏱️ 시간 경과"]
    G --> H{"계획서 수정?"}
    H -->|수정 없음| I["📱 QR 스캔"]
    H -->|수정됨| I

    I --> J["GET /qr/{token}"]
    J --> K["현재 계획서 해시 재계산"]
    K --> L{"해시 동일?"}
    L -->|✅ 동일| M["302 Redirect\n→ 계획서 상세 페이지"]
    L -->|⚠️ 다름| N["경고 페이지 표시\n변경 항목 diff 시각화"]
    N --> O["[계속 보기] 또는\n[최신 계획서 PDF]"]

    style A fill:#eef2ff,color:#3730a3,stroke:#4f46e5,stroke-width:2px
    style F fill:#ecfdf5,color:#047857,stroke:#10b981,stroke-width:2px
    style M fill:#eff6ff,color:#1d4ed8,stroke:#2563eb,stroke-width:2px
    style N fill:#fef2f2,color:#b91c1c,stroke:#dc2626,stroke-width:2px
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="padding:14px;background:#f8fafc;border-radius:10px;border:1px solid #e2e8f0">
            <div style="font-size:12px;font-weight:700;color:#1e293b;margin-bottom:8px">🗄️ DB 스키마 — protocol_snapshots</div>
            <div style="background:#1e293b;padding:12px;border-radius:6px;font-family:monospace;font-size:10px;color:#e2e8f0;line-height:1.9">
              <span style="color:#a78bfa">class</span> <span style="color:#fbbf24">ProtocolSnapshot</span>(Base):<br/>
              &nbsp;&nbsp;__tablename__ = <span style="color:#34d399">"protocol_snapshots"</span><br/>
              &nbsp;&nbsp;id = Column(Integer, primary_key=True)<br/>
              &nbsp;&nbsp;<span style="color:#60a5fa"># QR 토큰과 1:1 연결</span><br/>
              &nbsp;&nbsp;qr_token = Column(String(20), index=True)<br/>
              &nbsp;&nbsp;protocol_id = Column(Integer, FK...)<br/>
              &nbsp;&nbsp;<span style="color:#60a5fa"># 변경 감지 핵심</span><br/>
              &nbsp;&nbsp;snapshot_hash = Column(String(64))<br/>
              &nbsp;&nbsp;<span style="color:#60a5fa"># 변경 항목 diff용 원본</span><br/>
              &nbsp;&nbsp;snapshot_data = Column(JSON)<br/>
              &nbsp;&nbsp;<span style="color:#60a5fa"># 생성 메타</span><br/>
              &nbsp;&nbsp;created_at = Column(DateTime)<br/>
              &nbsp;&nbsp;created_by = Column(Integer, FK...)<br/>
              &nbsp;&nbsp;<span style="color:#60a5fa"># 인쇄 시점 protocol.updated_at</span><br/>
              &nbsp;&nbsp;protocol_updated_at = Column(DateTime)
            </div>
          </div>
          <div style="padding:14px;background:#f8fafc;border-radius:10px;border:1px solid #e2e8f0">
            <div style="font-size:12px;font-weight:700;color:#1e293b;margin-bottom:8px">⚙️ 해시 계산 대상 필드</div>
            <div style="background:#1e293b;padding:12px;border-radius:6px;font-family:monospace;font-size:10px;color:#e2e8f0;line-height:1.9">
              <span style="color:#a78bfa">def</span> <span style="color:#fbbf24">compute_hash</span>(protocol, treatments):<br/>
              &nbsp;&nbsp;data = {<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"title"</span>: protocol.title,<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"manager"</span>: protocol.manager,<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"planned_start"</span>: protocol.planned_start,<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"planned_end"</span>: protocol.planned_end,<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"test_location"</span>: protocol.test_location,<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"test_crop"</span>: protocol.test_crop,<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"replications"</span>: protocol.replications,<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"treatments"</span>: [{<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"pesticide"</span>: t.pesticide_name,<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"dilution"</span>: t.dilution_rate,<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"timing"</span>: t.treatment_timing,<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;} <span style="color:#a78bfa">for</span> t <span style="color:#a78bfa">in</span> treatments]<br/>
              &nbsp;&nbsp;}<br/>
              &nbsp;&nbsp;raw = json.dumps(data, sort_keys=True, ensure_ascii=False)<br/>
              &nbsp;&nbsp;<span style="color:#a78bfa">return</span> hashlib.sha256(raw.encode()).hexdigest()
            </div>
          </div>
        </div>

        <div style="padding:14px;background:#f8fafc;border-radius:10px;border:1px solid #e2e8f0">
          <div style="font-size:12px;font-weight:700;color:#1e293b;margin-bottom:8px">🔌 API 엔드포인트 설계</div>
          <table style="width:100%;font-size:11px;border-collapse:collapse">
            <tr style="background:#f1f5f9;font-weight:600">
              <td style="padding:7px;border:1px solid #e2e8f0">메서드</td>
              <td style="padding:7px;border:1px solid #e2e8f0">경로</td>
              <td style="padding:7px;border:1px solid #e2e8f0">역할</td>
              <td style="padding:7px;border:1px solid #e2e8f0">인증</td>
            </tr>
            <tr>
              <td style="padding:6px;border:1px solid #e2e8f0"><code>POST</code></td>
              <td style="padding:6px;border:1px solid #e2e8f0"><code>/api/protocols/{id}/qr/generate</code></td>
              <td style="padding:6px;border:1px solid #e2e8f0">스냅샷 생성 + Dynamic QR 발급</td>
              <td style="padding:6px;border:1px solid #e2e8f0">JWT</td>
            </tr>
            <tr>
              <td style="padding:6px;border:1px solid #e2e8f0"><code>GET</code></td>
              <td style="padding:6px;border:1px solid #e2e8f0"><code>/qr/{token}</code></td>
              <td style="padding:6px;border:1px solid #e2e8f0">해시 비교 → 동일: 302 / 변경: 경고 페이지</td>
              <td style="padding:6px;border:1px solid #e2e8f0">없음</td>
            </tr>
            <tr>
              <td style="padding:6px;border:1px solid #e2e8f0"><code>GET</code></td>
              <td style="padding:6px;border:1px solid #e2e8f0"><code>/api/qr/{token}/diff</code></td>
              <td style="padding:6px;border:1px solid #e2e8f0">스냅샷 vs 현재 diff JSON 반환</td>
              <td style="padding:6px;border:1px solid #e2e8f0">없음</td>
            </tr>
            <tr>
              <td style="padding:6px;border:1px solid #e2e8f0"><code>GET</code></td>
              <td style="padding:6px;border:1px solid #e2e8f0"><code>/api/protocols/{id}/qr/history</code></td>
              <td style="padding:6px;border:1px solid #e2e8f0">QR 발급 이력 전체 (버전별 스냅샷)</td>
              <td style="padding:6px;border:1px solid #e2e8f0">JWT</td>
            </tr>
          </table>
        </div>

      </div>
    `,
  },
  {
    title: '📸 버전 비교 스냅샷 — 단계별 구현 & UI 예시',
    content: `
      <div style="display:grid;gap:14px">

        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px">
          <div style="padding:16px;background:linear-gradient(135deg,#dbeafe,#eff6ff);border-radius:12px;border:2px solid #3b82f6">
            <div style="font-size:12px;font-weight:700;color:#1d4ed8;margin-bottom:4px">Step 1 — 스냅샷 저장 (2일)</div>
            <div style="font-size:10px;color:#3b82f6;margin-bottom:8px">QR 생성 시 해시·JSON 저장</div>
            <div style="font-size:10px;color:#1e3a8a;line-height:1.9">
              <b>models.py</b><br/>
              &bull; ProtocolSnapshot 테이블 추가<br/>
              &bull; QRRedirect에 snapshot_id FK 추가<br/><br/>
              <b>main.py</b><br/>
              &bull; <code>POST /api/protocols/{id}/qr/generate</code><br/>
              &bull; compute_hash() 유틸 함수<br/>
              &bull; snapshot_data JSON 직렬화<br/>
              &bull; gen_token() → QRRedirect 생성<br/><br/>
              <b>검증</b><br/>
              &bull; 계획서 저장 → QR 생성 → DB 확인<br/>
              &bull; snapshot_hash 64자 hex 검증
            </div>
          </div>
          <div style="padding:16px;background:linear-gradient(135deg,#f0fdf4,#dcfce7);border-radius:12px;border:2px solid #22c55e">
            <div style="font-size:12px;font-weight:700;color:#15803d;margin-bottom:4px">Step 2 — 비교 로직 (1일)</div>
            <div style="font-size:10px;color:#22c55e;margin-bottom:8px">스캔 시 해시 재계산 + diff 반환</div>
            <div style="font-size:10px;color:#14532d;line-height:1.9">
              <b>GET /qr/{token} 수정</b><br/>
              &bull; QRRedirect → ProtocolSnapshot JOIN<br/>
              &bull; 현재 계획서 compute_hash() 재계산<br/>
              &bull; 동일: 302 redirect (기존 동작)<br/>
              &bull; 다름: 302 with <code>?changed=1&token=xxx</code><br/><br/>
              <b>GET /api/qr/{token}/diff</b><br/>
              &bull; snapshot_data vs 현재 필드 비교<br/>
              &bull; 변경 필드 목록 + before/after 반환<br/>
              &bull; 무변경 필드는 결과에서 제외
            </div>
          </div>
          <div style="padding:16px;background:linear-gradient(135deg,#fdf4ff,#fae8ff);border-radius:12px;border:2px solid #a855f7">
            <div style="font-size:12px;font-weight:700;color:#7e22ce;margin-bottom:4px">Step 3 — 경고 UI (1.5일)</div>
            <div style="font-size:10px;color:#a855f7;margin-bottom:8px">변경 항목 시각화 페이지</div>
            <div style="font-size:10px;color:#581c87;line-height:1.9">
              <b>공개 경고 페이지</b> (로그인 불필요)<br/>
              &bull; <code>lab.sappt.co.kr/qr-changed?token=xxx</code><br/>
              &bull; diff API 호출 → 변경 항목 렌더링<br/>
              &bull; "인쇄 시점" vs "현재" 2컬럼 비교표<br/>
              &bull; [최신 계획서 보기] 버튼<br/><br/>
              <b>관리자 QR 이력 뷰</b><br/>
              &bull; 계획서 상세 → "QR 발급 이력" 탭<br/>
              &bull; 버전별 스냅샷 목록 + 변경 요약<br/>
              &bull; "이 버전으로 QR 재발급" 기능
            </div>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="padding:14px;background:#f8fafc;border-radius:10px;border:1px solid #e2e8f0">
            <div style="font-size:12px;font-weight:700;color:#1e293b;margin-bottom:8px">⚙️ Step 2 핵심 코드 — diff 엔드포인트</div>
            <div style="background:#1e293b;padding:12px;border-radius:6px;font-family:monospace;font-size:10px;color:#e2e8f0;line-height:1.9">
              <span style="color:#fbbf24">@app.get</span>(<span style="color:#34d399">"/api/qr/{token}/diff"</span>)<br/>
              <span style="color:#a78bfa">async def</span> <span style="color:#fbbf24">qr_diff</span>(token: str, db=Depends(get_db)):<br/>
              &nbsp;&nbsp;snap = db.query(ProtocolSnapshot)<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;.filter_by(qr_token=token).first()<br/>
              &nbsp;&nbsp;<span style="color:#a78bfa">if not</span> snap: <span style="color:#a78bfa">raise</span> HTTPException(404)<br/>
              &nbsp;&nbsp;proto = db.query(TestProtocol)<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;.filter_by(id=snap.protocol_id).first()<br/>
              &nbsp;&nbsp;treats = proto.treatments<br/>
              &nbsp;&nbsp;current_hash = compute_hash(proto, treats)<br/>
              &nbsp;&nbsp;changed = current_hash != snap.snapshot_hash<br/>
              &nbsp;&nbsp;diffs = []<br/>
              &nbsp;&nbsp;<span style="color:#a78bfa">if</span> changed:<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;old = snap.snapshot_data<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#60a5fa"># 필드별 비교</span><br/>
              &nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#a78bfa">for</span> field <span style="color:#a78bfa">in</span> TRACKED_FIELDS:<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#a78bfa">if</span> old.get(field) != getattr(proto, field):<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;diffs.append({<span style="color:#34d399">"field"</span>: field,<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"before"</span>: old.get(field),<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"after"</span>: getattr(proto, field)})<br/>
              &nbsp;&nbsp;<span style="color:#a78bfa">return</span> {<span style="color:#34d399">"changed"</span>: changed, <span style="color:#34d399">"diffs"</span>: diffs,<br/>
              &nbsp;&nbsp;&nbsp;&nbsp;<span style="color:#34d399">"snapshot_at"</span>: snap.created_at}
            </div>
          </div>

          <div style="padding:14px;background:#f8fafc;border-radius:10px;border:1px solid #e2e8f0">
            <div style="font-size:12px;font-weight:700;color:#1e293b;margin-bottom:8px">🖥️ 변경 경고 페이지 UI 예시</div>
            <div style="background:white;border:1px solid #fca5a5;border-radius:8px;padding:12px;font-size:10px">
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;padding-bottom:8px;border-bottom:1px solid #fee2e2">
                <span style="font-size:16px">⚠️</span>
                <span style="font-weight:700;color:#dc2626">계획서가 수정되었습니다</span>
              </div>
              <div style="font-size:9px;color:#6b7280;margin-bottom:8px">
                QR 생성: 2026-03-15 &nbsp;|&nbsp; 현재: 2026-04-25 &nbsp;|&nbsp; <b style="color:#dc2626">3개 항목 변경</b>
              </div>
              <table style="width:100%;font-size:9px;border-collapse:collapse;margin-bottom:8px">
                <tr style="background:#fef2f2">
                  <td style="padding:4px;border:1px solid #fecaca;color:#6b7280">항목</td>
                  <td style="padding:4px;border:1px solid #fecaca;color:#dc2626">인쇄 시점</td>
                  <td style="padding:4px;border:1px solid #fecaca;color:#15803d">현재</td>
                </tr>
                <tr>
                  <td style="padding:4px;border:1px solid #fecaca">희석배수</td>
                  <td style="padding:4px;border:1px solid #fecaca;color:#dc2626;text-decoration:line-through">1,000배</td>
                  <td style="padding:4px;border:1px solid #fecaca;color:#15803d"><b>500배</b></td>
                </tr>
                <tr>
                  <td style="padding:4px;border:1px solid #fecaca">처리시기</td>
                  <td style="padding:4px;border:1px solid #fecaca;color:#dc2626;text-decoration:line-through">발병 초기</td>
                  <td style="padding:4px;border:1px solid #fecaca;color:#15803d"><b>발병 전</b></td>
                </tr>
                <tr>
                  <td style="padding:4px;border:1px solid #fecaca">담당자</td>
                  <td style="padding:4px;border:1px solid #fecaca;color:#dc2626;text-decoration:line-through">홍길동</td>
                  <td style="padding:4px;border:1px solid #fecaca;color:#15803d"><b>김철수</b></td>
                </tr>
              </table>
              <div style="display:flex;gap:6px">
                <div style="padding:4px 8px;background:#2563eb;color:white;border-radius:4px;font-size:9px;font-weight:600">최신 계획서 보기</div>
                <div style="padding:4px 8px;background:#f1f5f9;color:#475569;border-radius:4px;font-size:9px">인쇄본으로 계속</div>
              </div>
            </div>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
          <div style="padding:14px;background:#ecfdf5;border-radius:10px;border:1px solid #6ee7b7">
            <div style="font-size:12px;font-weight:700;color:#065f46;margin-bottom:8px">📋 실제 운영 시나리오 3가지</div>
            <div style="font-size:11px;color:#064e3b;line-height:2">
              <b>① GLP 감사 대응</b><br/>
              감사자가 보관 인쇄본 QR 스캔<br/>
              → 변경 이력 즉시 확인 → 감사 통과<br/><br/>
              <b>② 의뢰사 확인 절차</b><br/>
              파트너에게 QR 공유 → 스캔 후 확인<br/>
              → 수정 항목 발견 → 재승인 요청 자동 안내<br/><br/>
              <b>③ 현장 연구원 당일 체크</b><br/>
              처리 전 포장 표지판 QR 스캔<br/>
              → 희석배수 변경 감지 → 즉시 담당자 연락
            </div>
          </div>
          <div style="padding:14px;background:#fffbeb;border-radius:10px;border:1px solid #fde68a">
            <div style="font-size:12px;font-weight:700;color:#92400e;margin-bottom:8px">📌 버전 관리 정책 권장</div>
            <div style="font-size:11px;color:#78350f;line-height:2">
              <b>QR 재발급 기준:</b><br/>
              약제·희석배수·처리시기 변경 시<br/>
              → 기존 QR 비활성화 + 신규 QR 자동 발급<br/><br/>
              <b>담당자·일정 변경 시:</b><br/>
              → 기존 QR 유지 (target_url만 갱신)<br/>
              → 경고 표시는 하되 redirect는 정상<br/><br/>
              <b>스냅샷 보존 기간:</b><br/>
              → 시험 종료 후 5년 (GLP 기록 보존 기준)
            </div>
          </div>
        </div>

        <div style="padding:12px;background:#1e293b;border-radius:10px;color:#94a3b8;font-size:11px;line-height:1.9">
          <span style="color:#f59e0b;font-weight:700">🎯 전문가 포인트 — 왜 SHA-256인가:</span>
          MD5(128bit)는 의도적 충돌 공격에 취약. SHA-256(256bit)은 실무 GLP 환경에서
          <b style="color:#e2e8f0">문서 위·변조 입증 수단</b>으로 법적 효력을 인정받습니다.
          해시 자체를 HWPX 문서 하단에 텍스트로 인쇄해두면,
          스캔 없이도 육안으로 DB 원본과 대조할 수 있어 이중 안전망이 됩니다.
          총 구현 비용: <b style="color:#a78bfa">백엔드 3일 + 프론트엔드 1.5일 = 4.5일</b>
        </div>

      </div>
    `,
  },
  // ══════════════════════════════════════════════════════════════
  // Qdrant 벡터 검색 도입 계획 (2026-08)
  // ══════════════════════════════════════════════════════════════
  {
    title: '🧠 Qdrant 도입 ① 현황 진단 — 왜, 그리고 무엇을 위해',
    content: `
      <div style="display:grid;gap:18px">

        <div style="background:linear-gradient(135deg,#eef2ff,#e0e7ff);padding:18px 22px;border-radius:14px;border-left:5px solid #4f46e5">
          <h3 style="margin:0 0 8px;color:#3730a3;font-size:15px">한 줄 요약 — "Qdrant는 OpenSearch를 대체하지 않고, 의미(semantic) 축을 더한다"</h3>
          <p style="margin:0;color:#312e81;font-size:13px;line-height:1.85">
            SAPPT의 검색은 현재 <strong>OpenSearch BM25 키워드 축 하나</strong>로만 동작합니다.
            여기에 <strong>Qdrant 벡터(의미) 축</strong>을 더해 두 결과를 <strong>RRF로 융합</strong>하면,
            "용어가 정확히 일치해야만 찾히는" 한계를 해소하고 <strong>챗봇 RAG의 근거 품질</strong>을 직접 끌어올릴 수 있습니다.
            기존 nori 분석기 + 농약 동의어 사전 자산은 <strong>그대로 유지</strong>됩니다.
          </p>
        </div>

        <div style="background:#fffbeb;padding:16px 20px;border-radius:12px;border:1px solid #fde68a">
          <h3 style="margin:0 0 10px;color:#92400e;font-size:14px">⚠️ 먼저 짚을 것 — 지금 SAPPT의 문제는 "속도"가 아니라 "의미"입니다</h3>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;align-items:start">
            <div>
              <div style="font-size:12px;color:#78350f;line-height:1.9">
                실측 데이터 규모(2026-08 기준)는 아래와 같이 <b>매우 작습니다</b>.
                이 규모에서 BM25 검색은 이미 <b>10ms 미만</b>이며, 벡터DB를 넣는다고 체감 속도가 빨라지지 않습니다.<br/><br/>
                따라서 이 계획서는 <b style="color:#b45309">"속도 개선"이 아니라 "검색 품질·RAG 정확도 개선"</b>을 도입 명분으로 삼습니다.
                속도를 근거로 도입을 정당화하면 <b>도입 후 효과 측정에서 반드시 실패</b>합니다.
              </div>
            </div>
            <table style="width:100%;border-collapse:separate;border-spacing:0 5px;font-size:12px">
              <tr style="background:#fef3c7"><th style="padding:7px 10px;text-align:left;color:#92400e;border-radius:8px 0 0 8px">대상</th><th style="padding:7px 10px;text-align:right;color:#92400e">현재 건수</th><th style="padding:7px 10px;text-align:left;color:#92400e;border-radius:0 8px 8px 0">비고</th></tr>
              <tr style="background:#fff"><td style="padding:7px 10px;border-radius:8px 0 0 8px">시험일정</td><td style="padding:7px 10px;text-align:right;font-weight:700">301</td><td style="padding:7px 10px;color:#78350f">연 +300 누적</td></tr>
              <tr style="background:#fff"><td style="padding:7px 10px;border-radius:8px 0 0 8px">시험계획서</td><td style="padding:7px 10px;text-align:right;font-weight:700">122</td><td style="padding:7px 10px;color:#78350f">HWPX 본문 보유</td></tr>
              <tr style="background:#fff"><td style="padding:7px 10px;border-radius:8px 0 0 8px">첨부문서</td><td style="padding:7px 10px;text-align:right;font-weight:700">0</td><td style="padding:7px 10px;color:#dc2626">본문 검색 불가 영역</td></tr>
              <tr style="background:#fff"><td style="padding:7px 10px;border-radius:8px 0 0 8px">활동이력</td><td style="padding:7px 10px;text-align:right;font-weight:700">8,444</td><td style="padding:7px 10px;color:#78350f">색인 대상 제외 권장</td></tr>
              <tr style="background:#f0fdf4"><td style="padding:7px 10px;border-radius:8px 0 0 8px;font-weight:700;color:#166534">예상 벡터 총량</td><td style="padding:7px 10px;text-align:right;font-weight:700;color:#166534">~2만</td><td style="padding:7px 10px;color:#166534">3년 누적 + 문서청크 포함</td></tr>
            </table>
          </div>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">① 현재 검색이 놓치는 것 — 실제 실패 사례</h3>
          <div class="mermaid">
flowchart TB
    classDef q fill:#fef2f2,stroke:#dc2626,color:#991b1b,stroke-width:2px
    classDef bm fill:#f1f5f9,stroke:#64748b,color:#334155,stroke-width:2px
    classDef bad fill:#fee2e2,stroke:#ef4444,color:#991b1b,stroke-width:2px
    classDef good fill:#dcfce7,stroke:#16a34a,color:#166534,stroke-width:2px

    Q1[질의A<br/>노균병 방제 효과 시험]:::q
    Q2[질의B<br/>작년 여름 벼 도열병 뭐 했지]:::q
    Q3[질의C<br/>이 성적서에 약해 등급 몇이었나]:::q

    BM[OpenSearch BM25<br/>nori + 동의어 사전]:::bm

    Q1 --> BM
    Q2 --> BM
    Q3 --> BM

    BM --> R1[제목에 노균병 이 없으면 미검출<br/>흰가루병 살균 시험 놓침]:::bad
    BM --> R2[연도 의미 해석 불가<br/>작년 을 토큰으로만 처리]:::bad
    BM --> R3[첨부문서 본문 미색인<br/>검색 자체가 불가능]:::bad

    R1 --> V[Qdrant 의미 벡터 축 추가]:::good
    R2 --> V
    R3 --> V
          </div>
          <p style="margin:12px 0 0;color:#64748b;font-size:11px;line-height:1.8">
            <b>핵심:</b> 동의어 사전은 <b>등록해 둔 단어만</b> 확장합니다. 신규 병해충명·시험 표현·의뢰사 구어체는 사전에 없으면 영원히 놓칩니다.
            벡터 검색은 사전 없이도 <b>의미가 가까운 문서</b>를 회수하므로, 사전 유지보수 부담 자체를 줄입니다.
          </p>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">② Qdrant가 실제로 푸는 문제 4가지 (우선순위 순)</h3>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
            <div style="padding:14px;background:#f0fdf4;border-radius:10px;border:1px solid #bbf7d0">
              <h4 style="margin:0 0 6px;color:#16a34a;font-size:13px">1️⃣ 챗봇 RAG 근거 품질 <span style="background:#16a34a;color:#fff;padding:1px 7px;border-radius:6px;font-size:10px">최우선</span></h4>
              <p style="margin:0;font-size:12px;color:#334155;line-height:1.8">
                현재 <code>_chatbot_auto_search()</code>는 BM25 결과를 그대로 LLM 컨텍스트로 넘깁니다.
                키워드가 빗나가면 LLM은 <b>"관련 데이터를 찾지 못했습니다"</b>를 반환합니다.
                하이브리드 검색으로 바꾸면 <b>회수율(Recall)</b>이 올라가 답변 실패율이 직접 감소합니다.
              </p>
            </div>
            <div style="padding:14px;background:#f0fdf4;border-radius:10px;border:1px solid #bbf7d0">
              <h4 style="margin:0 0 6px;color:#16a34a;font-size:13px">2️⃣ 문서 본문 검색 <span style="background:#0ea5e9;color:#fff;padding:1px 7px;border-radius:6px;font-size:10px">신규 능력</span></h4>
              <p style="margin:0;font-size:12px;color:#334155;line-height:1.8">
                OCI Object Storage의 성적서·HWPX 본문은 <b>현재 검색 수단이 전혀 없습니다</b>.
                청킹 후 벡터 색인하면 "어느 시험 성적서에 이 문장이 있나"가 처음으로 가능해집니다.
                도입 가치가 가장 큰 영역이며, 벡터 건수도 여기서 대부분 발생합니다.
              </p>
            </div>
            <div style="padding:14px;background:#eff6ff;border-radius:10px;border:1px solid #bfdbfe">
              <h4 style="margin:0 0 6px;color:#2563eb;font-size:13px">3️⃣ 유사 시험·계획서 추천</h4>
              <p style="margin:0;font-size:12px;color:#334155;line-height:1.8">
                신규 계획서 작성 시 <b>"이와 유사한 과거 시험 5건"</b>을 자동 제시.
                벡터 최근접 탐색 한 번이면 되며, 별도 학습이 필요 없습니다.
                작성 시간 단축 + 프로토콜 일관성 확보에 직결됩니다.
              </p>
            </div>
            <div style="padding:14px;background:#eff6ff;border-radius:10px;border:1px solid #bfdbfe">
              <h4 style="margin:0 0 6px;color:#2563eb;font-size:13px">4️⃣ 파트너 포털 자연어 조회</h4>
              <p style="margin:0;font-size:12px;color:#334155;line-height:1.8">
                파트너는 시험번호 체계를 모릅니다. "우리 회사 상반기 살균제 시험 진행상황" 같은 자연어 질의를
                <b>payload 필터로 테넌트 격리</b>한 상태로 처리합니다. 격리 설계는 ③번 슬라이드에서 상술합니다.
              </p>
            </div>
          </div>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">③ 왜 OpenSearch k-NN이 아니라 Qdrant인가 (정직한 비교)</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:12px">
            <tr style="background:#f1f5f9">
              <th style="padding:9px 12px;text-align:left;color:#475569;border-radius:8px 0 0 8px;width:150px">항목</th>
              <th style="padding:9px 12px;text-align:left;color:#475569">OpenSearch 2.17 k-NN (기존 활용)</th>
              <th style="padding:9px 12px;text-align:left;color:#475569;border-radius:0 8px 8px 0">Qdrant (신규 도입)</th>
            </tr>
            <tr style="background:#fff"><td style="padding:9px 12px;font-weight:700;border-radius:8px 0 0 8px">추가 컴포넌트</td><td style="padding:9px 12px;color:#16a34a">없음 ✅ (플러그인 내장)</td><td style="padding:9px 12px;color:#dc2626">컨테이너 1개 추가 ❌</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;font-weight:700;border-radius:8px 0 0 8px">메모리 부담</td><td style="padding:9px 12px;color:#dc2626">JVM 힙에 벡터 적재<br/>현재 이미 <b>1.17GB</b> 사용 중 ❌</td><td style="padding:9px 12px;color:#16a34a">Rust 네이티브, 예상 <b>~250MB</b><br/>int8 양자화 시 더 감소 ✅</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;font-weight:700;border-radius:8px 0 0 8px">필터 + 벡터 동시</td><td style="padding:9px 12px;color:#b45309">post-filter 특성상<br/>강한 필터에서 결과 부족 발생</td><td style="padding:9px 12px;color:#16a34a">filterable HNSW —<br/>파트너 격리에 유리 ✅</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;font-weight:700;border-radius:8px 0 0 8px">장애 격리</td><td style="padding:9px 12px;color:#dc2626">벡터 부하가 <b>기존 전문검색까지</b> 동반 영향 ❌</td><td style="padding:9px 12px;color:#16a34a">프로세스 분리 —<br/>죽어도 BM25 검색은 정상 ✅</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;font-weight:700;border-radius:8px 0 0 8px">운영 난이도</td><td style="padding:9px 12px;color:#b45309">인덱스 매핑 재설계 필요</td><td style="padding:9px 12px;color:#16a34a">REST API 단순, 스냅샷 백업 내장</td></tr>
          </table>
          <div style="margin-top:12px;padding:12px 14px;background:#1e293b;border-radius:10px;color:#cbd5e1;font-size:11.5px;line-height:1.9">
            <span style="color:#a78bfa;font-weight:700">🎯 판단:</span>
            결정적 근거는 <b style="color:#e2e8f0">"장애 격리"와 "OpenSearch JVM 힙 보호"</b>입니다.
            OpenSearch는 이미 전문검색·활동이력·검색로그를 담당하는 <b>단일 장애점</b>이며, 여기에 벡터 부하까지 얹는 것은 4코어 서버에서 위험합니다.
            다만 <b style="color:#fbbf24">"컴포넌트 1개 추가"라는 운영 비용은 실재</b>하므로, Phase 0 PoC에서 효과가 확인되지 않으면 도입을 중단하는 것이 옳습니다.
          </div>
        </div>

      </div>
    `,
  },
  {
    title: '🧠 Qdrant 도입 ② 아키텍처 & 하이브리드 검색 설계',
    content: `
      <div style="display:grid;gap:18px">

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">① 목표 아키텍처 — 기존 구성에 2개 컨테이너만 추가</h3>
          <div class="mermaid">
flowchart LR
    classDef edge fill:#fff7ed,stroke:#ea580c,color:#9a3412,stroke-width:2px
    classDef k3s fill:#f0fdf4,stroke:#16a34a,color:#14532d,stroke-width:2px
    classDef exist fill:#eff6ff,stroke:#2563eb,color:#1e3a8a,stroke-width:2px
    classDef new fill:#f5f3ff,stroke:#7c3aed,color:#5b21b6,stroke-width:3px

    U[사용자<br/>lab / partner]:::edge
    NPM[Nginx Proxy Manager]:::edge

    BE[backend Pod x2<br/>FastAPI]:::k3s

    PG[(PostgreSQL 17<br/>원본 진실)]:::exist
    OS[OpenSearch 2.17<br/>BM25 + nori]:::exist
    RD[(Redis<br/>임베딩 캐시)]:::exist
    OCI[OCI Object Storage<br/>성적서 원본]:::exist

    EMB[embedder :8081<br/>ONNX CPU 임베딩]:::new
    QD[(Qdrant :6333<br/>벡터 저장소)]:::new

    U --> NPM --> BE
    BE --> PG
    BE --> OS
    BE --> RD
    BE --> QD
    BE --> EMB
    EMB --> QD
    OCI -.문서 청킹 배치.-> EMB
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:14px">
            <div style="padding:12px 14px;background:#f5f3ff;border-radius:10px;border:1px solid #ddd6fe">
              <div style="font-size:12px;font-weight:700;color:#5b21b6;margin-bottom:6px">신규 ①: Qdrant</div>
              <div style="font-size:11.5px;color:#4c1d95;line-height:1.9">
                Docker Compose <code>sappt-net</code>에 배치, ARM64 이미지 사용.<br/>
                k3s의 backend Pod는 기존 패턴대로 <b>172.17.0.1:6333</b>(Docker 브릿지) 경유 접근.<br/>
                <b>외부 포트 미개방</b> — NPM 프록시 라우트를 만들지 않습니다.
              </div>
            </div>
            <div style="padding:12px 14px;background:#f5f3ff;border-radius:10px;border:1px solid #ddd6fe">
              <div style="font-size:12px;font-weight:700;color:#5b21b6;margin-bottom:6px">신규 ②: embedder</div>
              <div style="font-size:11.5px;color:#4c1d95;line-height:1.9">
                문장 → 벡터 변환 전용 경량 서비스. CPU ONNX 추론.<br/>
                backend에 직접 넣지 않는 이유: <b>모델 로딩 메모리를 Pod 2개가 중복 점유</b>하고,
                배포 시마다 모델을 다시 올려 롤링 업데이트가 느려지기 때문입니다.
              </div>
            </div>
          </div>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">② 하이브리드 검색 질의 흐름 (BM25 + 벡터 → RRF 융합)</h3>
          <div class="mermaid">
sequenceDiagram
    autonumber
    participant U as 사용자
    participant BE as FastAPI backend
    participant RD as Redis
    participant EM as embedder
    participant OS as OpenSearch
    participant QD as Qdrant

    U->>BE: GET /api/search?q=노균병 방제 시험
    BE->>RD: 질의 임베딩 캐시 조회 md5 해시
    alt 캐시 히트
        RD-->>BE: 벡터 반환 0ms
    else 캐시 미스
        BE->>EM: POST /embed
        EM-->>BE: 1024차원 벡터 약 150ms
        BE->>RD: 캐시 저장 TTL 7일
    end

    par 병렬 실행
        BE->>OS: BM25 검색 top 30
        OS-->>BE: 키워드 랭킹
    and
        BE->>QD: 벡터 검색 top 30 + payload 필터
        QD-->>BE: 의미 랭킹
    end

    BE->>BE: RRF 융합 후 top 10 산출
    BE-->>U: 통합 결과 + 근거 스니펫
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1.1fr 1fr;gap:16px">
          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb">
            <h3 style="margin:0 0 12px;color:#1e293b;font-size:14px">③ RRF(Reciprocal Rank Fusion) — 왜 점수 합산이 아닌가</h3>
            <div style="padding:14px;background:#0f172a;border-radius:10px;font-family:monospace;font-size:12px;color:#e2e8f0;line-height:1.9">
              score(d) = Σ 1 / (k + rank<sub>i</sub>(d))<br/>
              <span style="color:#94a3b8">k = 60 (표준값), rank는 1부터 시작</span>
            </div>
            <p style="margin:12px 0 0;font-size:12px;color:#334155;line-height:1.85">
              BM25 점수(무한대 범위)와 코사인 유사도(0~1)는 <b>스케일이 달라 직접 더할 수 없습니다</b>.
              정규화 후 가중합도 가능하지만 문서 분포에 따라 가중치를 계속 재조정해야 합니다.
              RRF는 <b>점수를 버리고 순위만</b> 사용하므로 튜닝 파라미터가 <code>k</code> 하나뿐이고,
              한쪽 엔진이 죽어도 <b>나머지 한 축만으로 자연스럽게 동작</b>합니다.
            </p>
            <div style="margin-top:10px;padding:10px 12px;background:#f0fdf4;border-radius:8px;border:1px solid #bbf7d0;font-size:11.5px;color:#166534;line-height:1.8">
              <b>운영상 이점:</b> Qdrant 장애 시 벡터 랭킹이 빈 배열로 반환 → RRF 결과 = BM25 단독 결과.
              <b>즉 자동 폴백(graceful degradation)이 공식 자체에 내장</b>됩니다. 별도 예외처리 분기가 필요 없습니다.
            </div>
          </div>

          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb">
            <h3 style="margin:0 0 12px;color:#1e293b;font-size:14px">④ 융합 예시 — 실제로 순위가 어떻게 바뀌는가</h3>
            <table style="width:100%;border-collapse:separate;border-spacing:0 5px;font-size:11.5px">
              <tr style="background:#f1f5f9"><th style="padding:7px 9px;text-align:left;color:#475569;border-radius:8px 0 0 8px">문서</th><th style="padding:7px 9px;color:#475569">BM25</th><th style="padding:7px 9px;color:#475569">벡터</th><th style="padding:7px 9px;color:#475569">RRF</th><th style="padding:7px 9px;color:#475569;border-radius:0 8px 8px 0">최종</th></tr>
              <tr style="background:#dcfce7"><td style="padding:8px 9px;border-radius:8px 0 0 8px">흰가루병 살균 시험</td><td style="padding:8px 9px;text-align:center;color:#94a3b8">—</td><td style="padding:8px 9px;text-align:center;font-weight:700">1위</td><td style="padding:8px 9px;text-align:center">0.0164</td><td style="padding:8px 9px;text-align:center;font-weight:700;color:#16a34a">1위 ⬆</td></tr>
              <tr style="background:#fff"><td style="padding:8px 9px;border-radius:8px 0 0 8px">노균병 방제 약효</td><td style="padding:8px 9px;text-align:center;font-weight:700">1위</td><td style="padding:8px 9px;text-align:center">5위</td><td style="padding:8px 9px;text-align:center">0.0318</td><td style="padding:8px 9px;text-align:center;font-weight:700">1위</td></tr>
              <tr style="background:#fff"><td style="padding:8px 9px;border-radius:8px 0 0 8px">노균병 관련 공지</td><td style="padding:8px 9px;text-align:center">2위</td><td style="padding:8px 9px;text-align:center;color:#94a3b8">—</td><td style="padding:8px 9px;text-align:center">0.0161</td><td style="padding:8px 9px;text-align:center;color:#dc2626">4위 ⬇</td></tr>
            </table>
            <p style="margin:10px 0 0;font-size:11px;color:#64748b;line-height:1.8">
              키워드만 겹치는 저품질 문서는 <b>내려가고</b>, 단어는 다르지만 의미가 맞는 문서는 <b>올라옵니다</b>.
              이것이 하이브리드 검색의 실질 효과이며, Phase 1의 측정 지표(Recall@5)로 검증합니다.
            </p>
          </div>
        </div>

        <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:14px">⑤ 배치 위치 결정 — 왜 k3s가 아니라 Docker Compose인가</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:12px">
            <tr style="background:#f1f5f9"><th style="padding:8px 12px;text-align:left;color:#475569;border-radius:8px 0 0 8px">후보</th><th style="padding:8px 12px;text-align:left;color:#475569">장점</th><th style="padding:8px 12px;text-align:left;color:#475569;border-radius:0 8px 8px 0">채택 여부</th></tr>
            <tr style="background:#f0fdf4"><td style="padding:9px 12px;font-weight:700;color:#166534;border-radius:8px 0 0 8px">Docker Compose</td><td style="padding:9px 12px">postgres·opensearch와 <b>동일한 상태저장 계층</b>. 볼륨 관리·백업 스크립트를 기존 패턴으로 재사용</td><td style="padding:9px 12px;color:#16a34a;font-weight:700">✅ 채택</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;font-weight:700;border-radius:8px 0 0 8px">k3s StatefulSet</td><td style="padding:9px 12px">배포 일원화</td><td style="padding:9px 12px;color:#dc2626">❌ PV 관리 부담 증가, 단일 노드에서 이점 없음</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;font-weight:700;border-radius:8px 0 0 8px">Qdrant Cloud</td><td style="padding:9px 12px">운영 부담 0</td><td style="padding:9px 12px;color:#dc2626">❌ <b>의뢰사 시험 데이터 외부 전송</b> — 연구소 데이터 정책상 부적합</td></tr>
          </table>
        </div>

      </div>
    `,
  },
  {
    title: '🧠 Qdrant 도입 ③ 컬렉션 설계 · 임베딩 모델 · 테넌트 격리',
    content: `
      <div style="display:grid;gap:18px">

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">① 컬렉션 설계 — 3개 컬렉션으로 분리</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:12px">
            <tr style="background:#f1f5f9">
              <th style="padding:9px 12px;text-align:left;color:#475569;border-radius:8px 0 0 8px">컬렉션</th>
              <th style="padding:9px 12px;text-align:left;color:#475569">색인 단위</th>
              <th style="padding:9px 12px;text-align:left;color:#475569">임베딩 대상 텍스트</th>
              <th style="padding:9px 12px;text-align:left;color:#475569">payload (필터 키)</th>
              <th style="padding:9px 12px;text-align:right;color:#475569;border-radius:0 8px 8px 0">예상 건수</th>
            </tr>
            <tr style="background:#eef2ff">
              <td style="padding:10px 12px;font-weight:700;color:#4338ca;border-radius:8px 0 0 8px"><code>sappt_schedules</code></td>
              <td style="padding:10px 12px">시험일정 1건</td>
              <td style="padding:10px 12px">시험명 + 작물 + 병해충 + 약제 + 장소 + 의뢰사</td>
              <td style="padding:10px 12px"><code>company_ids[]</code> <code>test_year</code> <code>category_id</code> <code>status</code> <code>request_type</code></td>
              <td style="padding:10px 12px;text-align:right;font-weight:700">~1,200<br/><span style="font-size:10px;color:#64748b">3년 누적</span></td>
            </tr>
            <tr style="background:#f0fdf4">
              <td style="padding:10px 12px;font-weight:700;color:#16a34a;border-radius:8px 0 0 8px"><code>sappt_protocols</code></td>
              <td style="padding:10px 12px">계획서 섹션 단위</td>
              <td style="padding:10px 12px">제목 + 시험목적 + 처리내용 + 시험구 배치</td>
              <td style="padding:10px 12px"><code>protocol_id</code> <code>institution</code> <code>section</code></td>
              <td style="padding:10px 12px;text-align:right;font-weight:700">~600</td>
            </tr>
            <tr style="background:#faf5ff">
              <td style="padding:10px 12px;font-weight:700;color:#7c3aed;border-radius:8px 0 0 8px"><code>sappt_documents</code></td>
              <td style="padding:10px 12px">문서 청크 (500자 / 오버랩 80자)</td>
              <td style="padding:10px 12px">HWPX·PDF 성적서 본문 청크</td>
              <td style="padding:10px 12px"><code>company_ids[]</code> <code>schedule_id</code> <code>object_key</code> <code>page</code></td>
              <td style="padding:10px 12px;text-align:right;font-weight:700">~15,000<br/><span style="font-size:10px;color:#64748b">대부분 여기서 발생</span></td>
            </tr>
          </table>
          <div style="margin-top:12px;padding:12px 14px;background:#f8fafc;border-radius:10px;border:1px solid #e2e8f0;font-size:11.5px;color:#334155;line-height:1.9">
            <b>설계 원칙 3가지</b><br/>
            • <b>컬렉션 분리</b> — 재색인 주기가 다릅니다(일정: 실시간 / 문서: 업로드 시 1회). 한 컬렉션에 섞으면 부분 재색인이 어렵습니다.<br/>
            • <b>payload에 원문 저장 금지</b> — 원본 진실은 PostgreSQL입니다. Qdrant에는 <b>ID와 필터 키만</b> 두고, 조회 결과는 DB에서 다시 읽습니다. 이래야 DB 수정이 즉시 반영되고 정합성 사고가 줄어듭니다.<br/>
            • <b>payload 인덱스 필수</b> — <code>company_ids</code>, <code>test_year</code>에 keyword 인덱스를 생성해야 필터 성능이 나옵니다. 인덱스 없이 필터하면 전수 스캔합니다.
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#fef2f2,#fee2e2);padding:20px;border-radius:14px;border-left:5px solid #dc2626">
          <h3 style="margin:0 0 12px;color:#991b1b;font-size:15px">② 🔒 파트너 테넌트 격리 — 이 계획서에서 가장 중요한 보안 설계</h3>
          <div class="mermaid">
flowchart TB
    classDef ok fill:#dcfce7,stroke:#16a34a,color:#166534,stroke-width:2px
    classDef ng fill:#fee2e2,stroke:#dc2626,color:#991b1b,stroke-width:3px
    classDef mid fill:#eff6ff,stroke:#2563eb,color:#1e3a8a,stroke-width:2px

    P[파트너 JWT<br/>company code 그룹]:::mid
    R[_resolve_company_ids<br/>접근 가능 company_ids 산출]:::mid
    P --> R

    R --> W1[방식 A 금지<br/>벡터 검색 후 결과를 앱에서 걸러내기]:::ng
    R --> W2[방식 B 채택<br/>Qdrant must 필터를 질의에 포함]:::ok

    W1 --> X[top 10 이 전부 타사 문서면<br/>필터 후 0건 반환<br/>+ 타사 유사도 정보 유출 위험]:::ng
    W2 --> Y[격리된 후보군 내에서 top 10 산출<br/>결과 품질과 보안 동시 확보]:::ok
          </div>
          <div style="margin-top:14px;padding:14px;background:#fff;border-radius:10px;border:1px solid #fecaca">
            <div style="font-size:12px;color:#7f1d1d;line-height:1.9">
              <b style="color:#dc2626">절대 규칙:</b> 파트너 경로의 모든 Qdrant 질의는 <b>서버에서 강제로</b> <code>must: [{"key":"company_ids","match":{"any": 허용목록}}]</code> 필터를 주입합니다.
              프론트엔드가 보낸 필터 값을 <b>신뢰하지 않습니다</b>.<br/><br/>
              기존 <code>_partner_scope_filter()</code> / <code>_resolve_company_ids()</code> 로직을 <b>그대로 재사용</b>해 필터를 생성하므로,
              SQL·OpenSearch·Qdrant 3계층의 접근 범위가 <b>단일 함수에서 일관되게</b> 결정됩니다.
              신규 격리 규칙을 만들지 않는 것이 핵심입니다 — 규칙이 늘어나면 반드시 어긋납니다.
            </div>
          </div>
          <div style="margin-top:10px;padding:12px 14px;background:#fff7ed;border-radius:10px;border:1px solid #fed7aa;font-size:11.5px;color:#9a3412;line-height:1.85">
            <b>⚠️ 검증 필수 항목:</b> Phase 4 배포 전 <b>파트너 A 계정으로 파트너 B 데이터가 단 1건도 회수되지 않음</b>을 자동화 테스트로 증명해야 합니다.
            <code>schedule_companies</code> 다중 연결(협회 경유 시험)까지 포함해 테스트 케이스를 구성합니다.
          </div>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">③ 임베딩 모델 선정 — ARM 4코어 서버 기준</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:11.5px">
            <tr style="background:#f1f5f9">
              <th style="padding:8px 10px;text-align:left;color:#475569;border-radius:8px 0 0 8px">후보</th>
              <th style="padding:8px 10px;text-align:center;color:#475569">차원</th>
              <th style="padding:8px 10px;text-align:center;color:#475569">한국어 품질</th>
              <th style="padding:8px 10px;text-align:center;color:#475569">질의 지연(추정)</th>
              <th style="padding:8px 10px;text-align:left;color:#475569">특성</th>
              <th style="padding:8px 10px;text-align:center;color:#475569;border-radius:0 8px 8px 0">판정</th>
            </tr>
            <tr style="background:#dcfce7">
              <td style="padding:9px 10px;font-weight:700;color:#166534;border-radius:8px 0 0 8px">BGE-M3 (ONNX int8)</td>
              <td style="padding:9px 10px;text-align:center">1024</td>
              <td style="padding:9px 10px;text-align:center">★★★★★</td>
              <td style="padding:9px 10px;text-align:center">~200-350ms</td>
              <td style="padding:9px 10px">다국어 강함, 긴 문서 처리 우수</td>
              <td style="padding:9px 10px;text-align:center;font-weight:700;color:#16a34a">1순위</td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">multilingual-e5-small</td>
              <td style="padding:9px 10px;text-align:center">384</td>
              <td style="padding:9px 10px;text-align:center">★★★☆☆</td>
              <td style="padding:9px 10px;text-align:center">~40-70ms</td>
              <td style="padding:9px 10px">가볍고 빠름, 메모리 1/5</td>
              <td style="padding:9px 10px;text-align:center;color:#2563eb">지연 초과 시 대안</td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">한국어 특화 파인튜닝 모델</td>
              <td style="padding:9px 10px;text-align:center">1024</td>
              <td style="padding:9px 10px;text-align:center">★★★★★</td>
              <td style="padding:9px 10px;text-align:center">~200-350ms</td>
              <td style="padding:9px 10px">국내 검색 벤치마크 우수</td>
              <td style="padding:9px 10px;text-align:center;color:#7c3aed">PoC에서 A/B</td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">외부 임베딩 API</td>
              <td style="padding:9px 10px;text-align:center">가변</td>
              <td style="padding:9px 10px;text-align:center">★★★★☆</td>
              <td style="padding:9px 10px;text-align:center">네트워크 의존</td>
              <td style="padding:9px 10px">서버 자원 0, 운영 단순</td>
              <td style="padding:9px 10px;text-align:center;color:#dc2626">보류 — 데이터 외부전송</td>
            </tr>
          </table>
          <div style="margin-top:12px;display:grid;grid-template-columns:1fr 1fr;gap:12px">
            <div style="padding:13px;background:#f0fdf4;border-radius:10px;border:1px solid #bbf7d0">
              <div style="font-size:12px;font-weight:700;color:#166534;margin-bottom:6px">✅ 로컬 임베딩 채택 근거</div>
              <div style="font-size:11.5px;color:#14532d;line-height:1.9">
                • <b>의뢰사 시험 데이터가 서버 밖으로 나가지 않음</b> — 연구소 데이터 정책상 가장 중요<br/>
                • 데이터량이 작아 <b>초기 전체 색인이 1~2시간 내</b> 완료<br/>
                • 추가 API 비용 0원, 외부 장애·요금 변동 리스크 없음<br/>
                • 질의 임베딩은 <b>Redis 캐시</b>로 반복 질의 시 0ms
              </div>
            </div>
            <div style="padding:13px;background:#fffbeb;border-radius:10px;border:1px solid #fde68a">
              <div style="font-size:12px;font-weight:700;color:#92400e;margin-bottom:6px">⚠️ 리스크와 대응</div>
              <div style="font-size:11.5px;color:#78350f;line-height:1.9">
                • 지연시간이 위 추정치는 <b>반드시 실측 검증</b> 필요 (Phase 0 게이트)<br/>
                • 4코어 공유 서버이므로 embedder에 <b>CPU 제한(1.5코어)·메모리 제한</b> 설정<br/>
                • 문서 대량 색인은 <b>야간 배치</b>로 분리해 주간 응답성 보호<br/>
                • p95 &gt; 500ms면 즉시 e5-small로 교체 (차원만 바뀌므로 재색인만 필요)
              </div>
            </div>
          </div>
        </div>

        <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:14px">④ 자원 산정 — 현 서버(23GB RAM / 4 vCPU / 101GB 여유)에서 충분한가</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:12px">
            <tr style="background:#f1f5f9"><th style="padding:8px 12px;text-align:left;color:#475569;border-radius:8px 0 0 8px">항목</th><th style="padding:8px 12px;text-align:right;color:#475569">산정</th><th style="padding:8px 12px;text-align:left;color:#475569;border-radius:0 8px 8px 0">근거</th></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;border-radius:8px 0 0 8px">벡터 원본 크기</td><td style="padding:9px 12px;text-align:right;font-weight:700">~80MB</td><td style="padding:9px 12px;color:#64748b">2만 × 1024차원 × 4byte</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;border-radius:8px 0 0 8px">int8 스칼라 양자화 적용</td><td style="padding:9px 12px;text-align:right;font-weight:700;color:#16a34a">~20MB</td><td style="padding:9px 12px;color:#64748b">1/4 압축, 정확도 손실 미미</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;border-radius:8px 0 0 8px">Qdrant 프로세스 총 RAM</td><td style="padding:9px 12px;text-align:right;font-weight:700">~250MB</td><td style="padding:9px 12px;color:#64748b">HNSW 그래프 + 런타임 포함</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;border-radius:8px 0 0 8px">embedder RAM (BGE-M3 int8)</td><td style="padding:9px 12px;text-align:right;font-weight:700">~1.2GB</td><td style="padding:9px 12px;color:#64748b">모델 상주, 제한 2GB 설정</td></tr>
            <tr style="background:#dcfce7"><td style="padding:9px 12px;border-radius:8px 0 0 8px;font-weight:700;color:#166534">추가 소요 합계</td><td style="padding:9px 12px;text-align:right;font-weight:700;color:#166534">~1.5GB</td><td style="padding:9px 12px;color:#166534">가용 17GB 대비 <b>9%</b> — 여유 충분 ✅</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;border-radius:8px 0 0 8px">디스크</td><td style="padding:9px 12px;text-align:right;font-weight:700">~2GB</td><td style="padding:9px 12px;color:#64748b">모델 파일 + 벡터 + 스냅샷, 101GB 여유</td></tr>
          </table>
        </div>

      </div>
    `,
  },
  {
    title: '🧠 Qdrant 도입 ④ 단계별 로드맵 — Phase 0 ~ 4',
    content: `
      <div style="display:grid;gap:18px">

        <div style="background:linear-gradient(135deg,#f5f3ff,#ede9fe);padding:16px 20px;border-radius:14px;border-left:5px solid #7c3aed">
          <h3 style="margin:0 0 6px;color:#5b21b6;font-size:15px">단계 설계 원칙 — "각 Phase는 독립적으로 가치를 내고, 독립적으로 폐기 가능해야 한다"</h3>
          <p style="margin:0;color:#4c1d95;font-size:12.5px;line-height:1.85">
            모든 Phase는 <strong>기존 검색을 끄지 않고 병행</strong>합니다. 각 단계 종료 시 <strong>Exit 기준</strong>을 충족하지 못하면
            다음 단계로 넘어가지 않고 <strong>해당 Phase를 되돌립니다</strong>(컨테이너 중지 → 코드상 하이브리드 플래그 OFF → 즉시 기존 동작 복귀).
          </p>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">① 전체 일정 (총 9주, 주 2~3일 투입 기준)</h3>
          <div class="mermaid">
gantt
    title Qdrant 단계별 적용 로드맵
    dateFormat YYYY-MM-DD
    axisFormat %m/%d

    section Phase 0 PoC
    Qdrant embedder 컨테이너 기동      :p0a, 2026-08-18, 3d
    임베딩 모델 실측 벤치마크           :p0b, after p0a, 4d
    도입 여부 게이트 판정               :milestone, m0, after p0b, 0d

    section Phase 1 하이브리드 검색
    색인 파이프라인 및 동기화 훅        :p1a, after m0, 5d
    RRF 융합 API 및 통합검색 적용       :p1b, after p1a, 5d
    Recall 측정 및 검증                :milestone, m1, after p1b, 0d

    section Phase 2 챗봇 RAG
    리트리버 교체 및 근거 인용          :p2a, after m1, 6d
    답변 품질 A/B 평가                 :milestone, m2, after p2a, 0d

    section Phase 3 문서 본문
    HWPX PDF 청킹 배치                 :p3a, after m2, 6d
    문서 검색 UI 및 야간 색인           :p3b, after p3a, 5d

    section Phase 4 파트너 및 추천
    파트너 격리 검색 및 유사시험 추천    :p4a, after p3b, 7d
          </div>
        </div>

        <div style="display:grid;gap:12px">

          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb;border-left:5px solid #64748b">
            <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
              <span style="background:#64748b;color:#fff;padding:3px 12px;border-radius:20px;font-size:12px;font-weight:700">Phase 0</span>
              <h3 style="margin:0;color:#1e293b;font-size:14px">PoC & 도입 게이트 (1주) — <span style="color:#dc2626">여기서 중단할 수 있어야 한다</span></h3>
            </div>
            <div style="display:grid;grid-template-columns:1.3fr 1fr;gap:14px;font-size:12px">
              <div style="color:#334155;line-height:1.9">
                <b>작업</b><br/>
                • <code>docker-compose.yml</code>에 <code>qdrant</code> + <code>embedder</code> 추가 (ARM64, sappt-net, 외부 미개방)<br/>
                • 실데이터 423건(일정 301 + 계획서 122)으로 시범 색인<br/>
                • 실제 업무 질의 <b>30개 평가셋</b> 작성 — 연구원이 직접 정답 문서를 라벨링<br/>
                • BM25 단독 / 벡터 단독 / RRF 하이브리드 <b>3방식 비교 측정</b>
              </div>
              <div style="padding:12px;background:#fef2f2;border-radius:10px;border:1px solid #fecaca">
                <div style="font-size:11.5px;font-weight:700;color:#991b1b;margin-bottom:6px">🚦 Exit 기준 (미달 시 도입 중단)</div>
                <div style="font-size:11px;color:#7f1d1d;line-height:1.9">
                  ① 하이브리드 Recall@5가 BM25 단독 대비 <b>+15%p 이상</b><br/>
                  ② 질의 임베딩 p95 <b>&lt; 500ms</b><br/>
                  ③ 추가 RAM 점유 <b>&lt; 2GB</b><br/>
                  ④ 기존 API 응답시간 <b>영향 없음</b>
                </div>
              </div>
            </div>
          </div>

          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb;border-left:5px solid #4f46e5">
            <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
              <span style="background:#4f46e5;color:#fff;padding:3px 12px;border-radius:20px;font-size:12px;font-weight:700">Phase 1</span>
              <h3 style="margin:0;color:#1e293b;font-size:14px">하이브리드 통합검색 (2주) — 관리자 검색부터</h3>
            </div>
            <div style="display:grid;grid-template-columns:1.3fr 1fr;gap:14px;font-size:12px">
              <div style="color:#334155;line-height:1.9">
                • 신규 <code>backend/app/qdrant_client.py</code> — 기존 <code>opensearch_client.py</code>와 동일 구조로 작성<br/>
                • 기존 <code>_sync_schedule_to_opensearch()</code> 옆에 <code>_sync_schedule_to_qdrant()</code> 훅 추가 (실패해도 무시 = 기존 패턴 준수)<br/>
                • <code>GET /api/search</code>를 RRF 융합으로 교체, <b>환경변수 플래그</b>로 즉시 OFF 가능<br/>
                • 관리자 화면에만 우선 적용 — 파트너 경로는 손대지 않음
              </div>
              <div style="padding:12px;background:#eef2ff;border-radius:10px;border:1px solid #c7d2fe">
                <div style="font-size:11.5px;font-weight:700;color:#3730a3;margin-bottom:6px">산출물</div>
                <div style="font-size:11px;color:#312e81;line-height:1.9">
                  • qdrant_client.py<br/>• RRF 융합 함수 + 단위테스트<br/>• 색인 정합성 점검 스크립트<br/>• 검색 품질 대시보드 지표
                </div>
              </div>
            </div>
          </div>

          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb;border-left:5px solid #16a34a">
            <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
              <span style="background:#16a34a;color:#fff;padding:3px 12px;border-radius:20px;font-size:12px;font-weight:700">Phase 2</span>
              <h3 style="margin:0;color:#1e293b;font-size:14px">챗봇 RAG 고도화 (1.5주) — <span style="color:#16a34a">체감 효과 최대 구간</span></h3>
            </div>
            <div style="font-size:12px;color:#334155;line-height:1.9">
              • <code>_chatbot_auto_search()</code>의 검색부를 하이브리드 리트리버로 교체 (상세: 다음 슬라이드)<br/>
              • LLM 컨텍스트에 <b>출처 ID·유사도</b>를 함께 전달 → 답변에 근거 인용 표기<br/>
              • <b>Exit 기준:</b> "관련 데이터를 찾지 못했습니다" 응답 비율 <b>50% 이상 감소</b> (chatbot 로그로 측정)
            </div>
          </div>

          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb;border-left:5px solid #7c3aed">
            <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
              <span style="background:#7c3aed;color:#fff;padding:3px 12px;border-radius:20px;font-size:12px;font-weight:700">Phase 3</span>
              <h3 style="margin:0;color:#1e293b;font-size:14px">문서 본문 RAG (2.5주) — <span style="color:#7c3aed">현재 불가능한 기능의 신설</span></h3>
            </div>
            <div style="font-size:12px;color:#334155;line-height:1.9">
              • 기존 <code>hwpx_parser.py</code> 재사용 → 본문 텍스트 추출 → 500자/오버랩 80자 청킹<br/>
              • OCI Object Storage 문서 <b>야간 배치 색인</b> (주간 CPU 보호), 신규 업로드는 업로드 시 큐 적재<br/>
              • 문서 검색 결과에서 <b>원문 PAR URL + 해당 페이지</b>로 바로 이동<br/>
              • <b>Exit 기준:</b> 성적서 본문 문장으로 검색 시 정답 문서 top-3 포함률 <b>80% 이상</b>
            </div>
          </div>

          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb;border-left:5px solid #f59e0b">
            <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
              <span style="background:#f59e0b;color:#fff;padding:3px 12px;border-radius:20px;font-size:12px;font-weight:700">Phase 4</span>
              <h3 style="margin:0;color:#1e293b;font-size:14px">파트너 적용 & 유사시험 추천 (2주)</h3>
            </div>
            <div style="font-size:12px;color:#334155;line-height:1.9">
              • 파트너 자연어 검색 — <b>payload must 필터 강제 주입</b> (③번 슬라이드 보안 설계 준수)<br/>
              • <b>격리 자동화 테스트 통과가 배포 선행조건</b> — 파트너 A가 파트너 B 데이터 0건 회수 증명<br/>
              • 계획서 작성 화면에 "유사 과거 시험 5건" 추천 패널 추가<br/>
              • <b>Exit 기준:</b> 격리 테스트 100% 통과 + 파트너 검색 p95 &lt; 400ms
            </div>
          </div>

        </div>

        <div style="padding:14px 16px;background:#0f172a;border-radius:12px;color:#cbd5e1;font-size:11.5px;line-height:1.95">
          <span style="color:#fbbf24;font-weight:700">🔙 롤백 전략 (모든 Phase 공통):</span>
          Qdrant 관련 코드는 전부 <b style="color:#e2e8f0">환경변수 <code>QDRANT_ENABLED</code> 단일 플래그</b> 뒤에 둡니다.
          문제 발생 시 <b style="color:#e2e8f0">플래그 OFF → 재배포</b>만으로 도입 이전 동작으로 완전히 복귀합니다.
          DB 스키마 변경이 없으므로 <b style="color:#4ade80">데이터 롤백은 불필요</b>하며, Qdrant 컨테이너를 삭제해도 원본 데이터는 PostgreSQL·OCI에 그대로 남습니다.
          이것이 "payload에 원문을 저장하지 않는다"는 설계 원칙의 실질적 이유입니다.
        </div>

      </div>
    `,
  },
  {
    title: '🧠 Qdrant 도입 ⑤ 챗봇 RAG 파이프라인 — 현재 vs 개선',
    content: `
      <div style="display:grid;gap:18px">

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #fecaca">
            <h3 style="margin:0 0 12px;color:#991b1b;font-size:14px">현재 파이프라인 (BM25 단일 축)</h3>
            <div class="mermaid">
flowchart TB
    classDef ng fill:#fee2e2,stroke:#dc2626,color:#991b1b,stroke-width:2px
    classDef n fill:#f8fafc,stroke:#94a3b8,color:#334155,stroke-width:1px

    A[사용자 질문]:::n
    B[규칙 인텐트 선매칭]:::n
    C[_chatbot_auto_search<br/>OpenSearch BM25]:::n
    D{키워드 일치?}:::n
    E[검색결과 없음]:::ng
    F[컨텍스트 전달]:::n
    G[LLM 답변]:::n
    H[관련 데이터를 찾지 못했습니다]:::ng

    A --> B --> C --> D
    D -- 실패 --> E --> H
    D -- 성공 --> F --> G
            </div>
            <div style="margin-top:10px;padding:11px;background:#fef2f2;border-radius:9px;font-size:11.5px;color:#7f1d1d;line-height:1.85">
              <b>병목:</b> 검색이 0건이면 LLM은 아무리 좋은 모델이어도 답할 수 없습니다.
              <b>답변 품질 문제의 대부분은 모델이 아니라 리트리버(검색) 문제</b>입니다.
            </div>
          </div>

          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #bbf7d0">
            <h3 style="margin:0 0 12px;color:#166534;font-size:14px">개선 파이프라인 (하이브리드 + 근거 인용)</h3>
            <div class="mermaid">
flowchart TB
    classDef ok fill:#dcfce7,stroke:#16a34a,color:#166534,stroke-width:2px
    classDef n fill:#f8fafc,stroke:#94a3b8,color:#334155,stroke-width:1px
    classDef new fill:#f5f3ff,stroke:#7c3aed,color:#5b21b6,stroke-width:2px

    A[사용자 질문]:::n
    B[규칙 인텐트 선매칭]:::n
    C1[BM25 top 30]:::n
    C2[벡터 top 30<br/>Qdrant]:::new
    R[RRF 융합 top 8]:::new
    S[PostgreSQL 원본 재조회<br/>최신 상태 보장]:::new
    F[출처 ID 포함 컨텍스트]:::ok
    G[LLM 답변 + 근거 인용]:::ok

    A --> B
    B --> C1
    B --> C2
    C1 --> R
    C2 --> R
    R --> S --> F --> G
            </div>
            <div style="margin-top:10px;padding:11px;background:#f0fdf4;border-radius:9px;font-size:11.5px;color:#14532d;line-height:1.85">
              <b>핵심 변화 2가지:</b> ① 두 축 융합으로 <b>회수율 상승</b> ② Qdrant payload가 아닌 <b>DB에서 원본 재조회</b>해 LLM에 항상 최신 상태 전달.
            </div>
          </div>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">① 코드 변경 지점 — 신규 파일 2개 + 기존 파일 최소 수정</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:12px">
            <tr style="background:#f1f5f9">
              <th style="padding:9px 12px;text-align:left;color:#475569;border-radius:8px 0 0 8px">파일</th>
              <th style="padding:9px 12px;text-align:center;color:#475569">구분</th>
              <th style="padding:9px 12px;text-align:left;color:#475569">변경 내용</th>
              <th style="padding:9px 12px;text-align:center;color:#475569;border-radius:0 8px 8px 0">Phase</th>
            </tr>
            <tr style="background:#f5f3ff"><td style="padding:9px 12px;border-radius:8px 0 0 8px"><code>app/qdrant_client.py</code></td><td style="padding:9px 12px;text-align:center;color:#7c3aed;font-weight:700">신규</td><td style="padding:9px 12px">컬렉션 생성·upsert·검색·payload 인덱스. <code>opensearch_client.py</code>와 동일한 함수 시그니처 유지</td><td style="padding:9px 12px;text-align:center">1</td></tr>
            <tr style="background:#f5f3ff"><td style="padding:9px 12px;border-radius:8px 0 0 8px"><code>app/retriever.py</code></td><td style="padding:9px 12px;text-align:center;color:#7c3aed;font-weight:700">신규</td><td style="padding:9px 12px">RRF 융합 + 파트너 필터 주입 + 폴백 처리. <b>검색 진입점 단일화</b></td><td style="padding:9px 12px;text-align:center">1</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;border-radius:8px 0 0 8px"><code>app/main.py</code></td><td style="padding:9px 12px;text-align:center;color:#2563eb">수정</td><td style="padding:9px 12px"><code>_sync_schedule_to_qdrant()</code> 훅 추가 · <code>_chatbot_auto_search()</code> 내부를 retriever 호출로 교체 · <code>/api/search</code> 융합 적용</td><td style="padding:9px 12px;text-align:center">1~2</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;border-radius:8px 0 0 8px"><code>app/llm_client.py</code></td><td style="padding:9px 12px;text-align:center;color:#2563eb">수정</td><td style="padding:9px 12px">SYSTEM_PROMPT에 <b>근거 인용 규칙</b> 1줄 추가 — 컨텍스트의 시험번호를 답변에 명시</td><td style="padding:9px 12px;text-align:center">2</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;border-radius:8px 0 0 8px"><code>docker-compose.yml</code></td><td style="padding:9px 12px;text-align:center;color:#2563eb">수정</td><td style="padding:9px 12px">qdrant · embedder 서비스 + 볼륨 정의, CPU/메모리 제한 설정</td><td style="padding:9px 12px;text-align:center">0</td></tr>
            <tr style="background:#fff"><td style="padding:9px 12px;border-radius:8px 0 0 8px"><code>scripts/qdrant_sync_check.sh</code></td><td style="padding:9px 12px;text-align:center;color:#7c3aed;font-weight:700">신규</td><td style="padding:9px 12px">야간 정합성 대조 — 기존 <code>opensearch_sync_check.sh</code> 패턴 복제, Discord 알림 연동</td><td style="padding:9px 12px;text-align:center">1</td></tr>
          </table>
          <div style="margin-top:12px;padding:12px 14px;background:#f0fdf4;border-radius:10px;border:1px solid #bbf7d0;font-size:11.5px;color:#14532d;line-height:1.9">
            <b>설계 의도:</b> 검색 로직을 <code>retriever.py</code> 한 곳에 모읍니다.
            현재 <code>main.py</code>(약 6,900줄) 안에 검색 코드가 흩어지면 파트너 격리 규칙이 누락되는 지점이 생깁니다.
            <b>"모든 검색은 retriever를 거친다"</b>는 단일 규칙이 곧 보안 경계입니다.
          </div>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">② LLM 비용·품질에 미치는 영향</h3>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
            <div>
              <table style="width:100%;border-collapse:separate;border-spacing:0 5px;font-size:11.5px">
                <tr style="background:#f1f5f9"><th style="padding:7px 10px;text-align:left;color:#475569;border-radius:8px 0 0 8px">모델</th><th style="padding:7px 10px;text-align:right;color:#475569">입력 $/MTok</th><th style="padding:7px 10px;text-align:right;color:#475569;border-radius:0 8px 8px 0">출력 $/MTok</th></tr>
                <tr style="background:#eef2ff"><td style="padding:8px 10px;font-weight:700;color:#4338ca;border-radius:8px 0 0 8px">Claude Sonnet 5 <span style="font-size:10px">(현재 1순위)</span></td><td style="padding:8px 10px;text-align:right">3.00</td><td style="padding:8px 10px;text-align:right">15.00</td></tr>
                <tr style="background:#fff"><td style="padding:8px 10px;border-radius:8px 0 0 8px">Claude Haiku 4.5 <span style="font-size:10px">(경량 라우팅용)</span></td><td style="padding:8px 10px;text-align:right">1.00</td><td style="padding:8px 10px;text-align:right">5.00</td></tr>
                <tr style="background:#fff"><td style="padding:8px 10px;border-radius:8px 0 0 8px">Claude Opus 5 <span style="font-size:10px">(고난도 분석용)</span></td><td style="padding:8px 10px;text-align:right">5.00</td><td style="padding:8px 10px;text-align:right">25.00</td></tr>
              </table>
              <p style="margin:8px 0 0;font-size:10.5px;color:#94a3b8;line-height:1.7">
                Anthropic 정가 기준(2026-08). 현재 SAPPT는 <b>OpenRouter 경유</b>이므로 실제 청구액은 중개 마진에 따라 다를 수 있습니다.
                Sonnet 5는 2026-08-31까지 도입가 $2/$10가 적용됩니다.
              </p>
            </div>
            <div style="padding:14px;background:#f8fafc;border-radius:10px;border:1px solid #e2e8f0">
              <div style="font-size:12px;font-weight:700;color:#334155;margin-bottom:8px">비용 방향은 "감소"입니다</div>
              <div style="font-size:11.5px;color:#334155;line-height:1.9">
                검색 정밀도가 오르면 <b>컨텍스트에 넣는 문서 수를 줄일 수 있습니다</b>.
                현재처럼 관련성이 불확실해 넉넉히 넣는 방식 대비, 정확한 top-8만 전달하면
                <b>입력 토큰이 오히려 감소</b>합니다.<br/><br/>
                또한 "찾지 못했습니다" 후 사용자가 <b>질문을 바꿔 재시도하는 호출</b>이 줄어드는 효과가 더 큽니다 —
                실패한 호출도 요금은 동일하게 발생하기 때문입니다.<br/><br/>
                <b style="color:#7c3aed">권장:</b> 단순 조회성 질의는 Haiku 4.5로 라우팅해 비용을 추가로 낮출 수 있습니다.
                검색 품질이 확보되면 <b>작은 모델로도 충분해지는</b> 것이 RAG의 본질적 이점입니다.
              </div>
            </div>
          </div>
        </div>

      </div>
    `,
  },
  {
    title: '🧠 Qdrant 도입 ⑥ 운영 · 동기화 · 리스크 · 성과지표',
    content: `
      <div style="display:grid;gap:18px">

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">① 동기화 전략 — 실시간 색인 + 야간 자가치유 (기존 OpenSearch 패턴 그대로)</h3>
          <div class="mermaid">
flowchart LR
    classDef w fill:#eef2ff,stroke:#4f46e5,color:#312e81,stroke-width:2px
    classDef n fill:#f0fdf4,stroke:#16a34a,color:#14532d,stroke-width:2px
    classDef db fill:#ecfeff,stroke:#0891b2,color:#0e7490,stroke-width:2px
    classDef al fill:#fef2f2,stroke:#dc2626,color:#991b1b,stroke-width:2px

    A[일정 등록 수정 삭제]:::w
    B[(PostgreSQL 커밋)]:::db
    C[OpenSearch 색인]:::w
    D[Qdrant upsert<br/>실패해도 무시]:::w

    E[야간 크론 06:20 및 06:40]:::n
    F[DB 건수 대 벡터 건수 대조]:::n
    G{불일치?}:::n
    H[누락분 재색인 및 고아 삭제]:::n
    I[Discord 알림]:::al

    A --> B
    B --> C
    B --> D
    E --> F --> G
    G -- 있음 --> H --> I
    G -- 없음 --> J[정상 종료]:::n
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:14px;font-size:11.5px">
            <div style="padding:12px;background:#f0fdf4;border-radius:10px;border:1px solid #bbf7d0;color:#14532d;line-height:1.9">
              <b>실시간 색인은 "실패 허용"</b><br/>
              Qdrant upsert 실패가 <b>일정 저장 트랜잭션을 롤백시키면 안 됩니다</b>.
              기존 <code>_sync_schedule_to_opensearch()</code>와 동일하게 예외를 삼키고 로그만 남깁니다.
              누락은 야간 배치가 반드시 복구합니다.
            </div>
            <div style="padding:12px;background:#eef2ff;border-radius:10px;border:1px solid #c7d2fe;color:#312e81;line-height:1.9">
              <b>크론 시각 분리</b><br/>
              기존 OpenSearch 정합성 점검이 <code>20 6 * * *</code>이므로,
              Qdrant는 <code>40 6 * * *</code>로 <b>20분 간격을 둡니다</b>.
              4코어 서버에서 두 배치가 겹치면 백업(<code>0 */3</code>)과 함께 CPU가 포화될 수 있습니다.
            </div>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb">
            <h3 style="margin:0 0 12px;color:#1e293b;font-size:14px">② 백업 · 복구</h3>
            <table style="width:100%;border-collapse:separate;border-spacing:0 5px;font-size:11.5px">
              <tr style="background:#f1f5f9"><th style="padding:7px 10px;text-align:left;color:#475569;border-radius:8px 0 0 8px">항목</th><th style="padding:7px 10px;text-align:left;color:#475569;border-radius:0 8px 8px 0">방침</th></tr>
              <tr style="background:#fff"><td style="padding:8px 10px;font-weight:700;border-radius:8px 0 0 8px">백업 대상</td><td style="padding:8px 10px"><b>포함하지 않음</b> — 벡터는 <b>파생 데이터</b>이며 원본(PostgreSQL·OCI)에서 언제든 재생성 가능</td></tr>
              <tr style="background:#fff"><td style="padding:8px 10px;font-weight:700;border-radius:8px 0 0 8px">복구 방법</td><td style="padding:8px 10px">전체 재색인 스크립트 1회 실행 (약 1~2시간, 야간 수행)</td></tr>
              <tr style="background:#fff"><td style="padding:8px 10px;font-weight:700;border-radius:8px 0 0 8px">스냅샷</td><td style="padding:8px 10px">Qdrant 내장 스냅샷은 <b>대규모 재색인 직전에만</b> 수동 생성 (되돌리기용)</td></tr>
              <tr style="background:#dcfce7"><td style="padding:8px 10px;font-weight:700;color:#166534;border-radius:8px 0 0 8px">이점</td><td style="padding:8px 10px;color:#166534">기존 백업 용량·소요시간에 <b>영향 없음</b>. 백업 정책 변경 불필요</td></tr>
            </table>
          </div>

          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb">
            <h3 style="margin:0 0 12px;color:#1e293b;font-size:14px">③ 모니터링 편입</h3>
            <div style="font-size:11.5px;color:#334155;line-height:2">
              • <b>Prometheus</b> — Qdrant <code>/metrics</code> 엔드포인트 스크레이프 대상 추가<br/>
              • <b>Grafana</b> — 벡터 수·검색 지연 p95·컬렉션 상태 패널 추가 (log.sappt.co.kr)<br/>
              • <b>점검일지</b> — <code>inspection.py</code>에 Qdrant 헬스체크 항목 추가 → 월간 보고서 자동 반영<br/>
              • <b>디스크 알람</b> — 기존 <code>disk_alert.sh</code>가 이미 전체 디스크를 감시하므로 <b>추가 작업 없음</b><br/>
              • <b>API</b> — <code>GET /api/qdrant/health</code> 신설, 모니터링 페이지에 노출
            </div>
          </div>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">④ 리스크 등록부</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:11.5px">
            <tr style="background:#f1f5f9">
              <th style="padding:8px 11px;text-align:left;color:#475569;border-radius:8px 0 0 8px">리스크</th>
              <th style="padding:8px 11px;text-align:center;color:#475569">영향</th>
              <th style="padding:8px 11px;text-align:center;color:#475569">가능성</th>
              <th style="padding:8px 11px;text-align:left;color:#475569;border-radius:0 8px 8px 0">대응</th>
            </tr>
            <tr style="background:#fef2f2">
              <td style="padding:9px 11px;font-weight:700;color:#991b1b;border-radius:8px 0 0 8px">파트너 데이터 교차 노출</td>
              <td style="padding:9px 11px;text-align:center;color:#dc2626;font-weight:700">치명적</td>
              <td style="padding:9px 11px;text-align:center">낮음</td>
              <td style="padding:9px 11px">서버측 must 필터 강제 + <b>격리 자동화 테스트를 배포 선행조건화</b> + retriever 단일 진입점</td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">CPU 경합으로 기존 서비스 지연</td>
              <td style="padding:9px 11px;text-align:center;color:#ea580c;font-weight:700">높음</td>
              <td style="padding:9px 11px;text-align:center;color:#dc2626">중간</td>
              <td style="padding:9px 11px">embedder에 <b>CPU 1.5코어 제한</b>, 대량 색인은 야간 배치, 크론 시각 분리</td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">검색 품질 개선 폭이 기대 미달</td>
              <td style="padding:9px 11px;text-align:center;color:#ea580c">중간</td>
              <td style="padding:9px 11px;text-align:center;color:#dc2626">중간</td>
              <td style="padding:9px 11px"><b>Phase 0 게이트에서 조기 판정</b> — 미달 시 도입 중단이 정상 결과</td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">임베딩 모델 교체 시 재색인 부담</td>
              <td style="padding:9px 11px;text-align:center">낮음</td>
              <td style="padding:9px 11px;text-align:center">중간</td>
              <td style="padding:9px 11px">데이터량이 작아 전체 재색인 1~2시간. 컬렉션명에 <b>모델 버전 접미사</b>를 붙여 무중단 교체</td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">운영 컴포넌트 증가로 관리 부담</td>
              <td style="padding:9px 11px;text-align:center">낮음</td>
              <td style="padding:9px 11px;text-align:center;color:#dc2626">높음</td>
              <td style="padding:9px 11px">점검일지·Grafana·Discord 알림에 <b>1일차부터 편입</b>. 별도 운영 절차를 만들지 않음</td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">Qdrant 장애</td>
              <td style="padding:9px 11px;text-align:center">낮음</td>
              <td style="padding:9px 11px;text-align:center">낮음</td>
              <td style="padding:9px 11px">RRF 구조상 <b>자동으로 BM25 단독 동작</b>으로 축소. 서비스 중단 없음</td>
            </tr>
          </table>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">⑤ 성과지표(KPI) — 도입 전 반드시 기준선을 먼저 측정</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:11.5px">
            <tr style="background:#f1f5f9">
              <th style="padding:8px 11px;text-align:left;color:#475569;border-radius:8px 0 0 8px">지표</th>
              <th style="padding:8px 11px;text-align:left;color:#475569">측정 방법</th>
              <th style="padding:8px 11px;text-align:center;color:#475569">기준선</th>
              <th style="padding:8px 11px;text-align:center;color:#475569;border-radius:0 8px 8px 0">목표</th>
            </tr>
            <tr style="background:#dcfce7"><td style="padding:9px 11px;font-weight:700;color:#166534;border-radius:8px 0 0 8px">Recall@5</td><td style="padding:9px 11px">연구원 라벨링 평가셋 30문항</td><td style="padding:9px 11px;text-align:center">Phase 0 측정</td><td style="padding:9px 11px;text-align:center;font-weight:700;color:#16a34a">+15%p 이상</td></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">챗봇 무응답률</td><td style="padding:9px 11px">chatbot 로그 중 "찾지 못했습니다" 비율</td><td style="padding:9px 11px;text-align:center">현재 로그 집계</td><td style="padding:9px 11px;text-align:center;font-weight:700;color:#16a34a">50% 감소</td></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">검색 p95 지연</td><td style="padding:9px 11px">API 응답시간 (Prometheus)</td><td style="padding:9px 11px;text-align:center">현재 &lt;50ms</td><td style="padding:9px 11px;text-align:center;font-weight:700">&lt;400ms 유지</td></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">문서 본문 검색 적중률</td><td style="padding:9px 11px">성적서 문장 질의 top-3 포함률</td><td style="padding:9px 11px;text-align:center;color:#dc2626">0% (기능 없음)</td><td style="padding:9px 11px;text-align:center;font-weight:700;color:#16a34a">80% 이상</td></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">파트너 격리 정확도</td><td style="padding:9px 11px">교차 접근 자동화 테스트</td><td style="padding:9px 11px;text-align:center">—</td><td style="padding:9px 11px;text-align:center;font-weight:700;color:#dc2626">100% (타협 불가)</td></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">추가 자원 점유</td><td style="padding:9px 11px">docker stats</td><td style="padding:9px 11px;text-align:center">—</td><td style="padding:9px 11px;text-align:center;font-weight:700">RAM &lt;2GB</td></tr>
          </table>
          <div style="margin-top:12px;padding:12px 14px;background:#fffbeb;border-radius:10px;border:1px solid #fde68a;font-size:11.5px;color:#78350f;line-height:1.9">
            <b>⚠️ 순서가 중요합니다:</b> Phase 0 착수 <b>이전에</b> 현재 시스템으로 기준선을 먼저 측정해야 합니다.
            도입 후에는 "좋아진 것 같다"는 인상만 남고 <b>정량 비교가 영원히 불가능</b>해집니다.
            평가셋 30문항 작성이 이 프로젝트에서 가장 먼저 해야 할 실무 작업입니다.
          </div>
        </div>

        <div style="padding:14px 16px;background:#0f172a;border-radius:12px;color:#cbd5e1;font-size:11.5px;line-height:1.95">
          <span style="color:#a78bfa;font-weight:700">🎯 총평:</span>
          SAPPT의 데이터 규모(현재 423건)에서 Qdrant의 가치는 <b style="color:#e2e8f0">속도가 아니라 "검색 품질과 신규 능력"</b>입니다.
          가장 큰 실익은 <b style="color:#4ade80">① 챗봇 RAG 회수율 개선</b>과 <b style="color:#4ade80">② 현재 전혀 불가능한 문서 본문 검색</b> 두 가지이며,
          이 둘만으로도 도입 명분은 충분합니다. 반대로 <b style="color:#fbbf24">유사도 추천·이상탐지 같은 부가 기능을 도입 명분으로 삼으면 안 됩니다</b> — 데이터가 쌓인 뒤의 과제입니다.
          추가 자원은 약 1.5GB로 <b style="color:#e2e8f0">가용량의 9%</b>에 불과하고, 단일 플래그로 완전 롤백이 가능하므로 <b style="color:#e2e8f0">시도 비용이 낮은 개선</b>입니다.
        </div>

      </div>
    `,
  },
  // ══════════════════════════════════════════════════════════════
  // 바코드 · QR · PDA 리더기 전사 적용 분석 (2026-08)
  // ══════════════════════════════════════════════════════════════
  {
    title: '📦 바코드·PDA ① 전사 업무 전수 분석 — 스캔 접점 도출',
    content: `
      <div style="display:grid;gap:18px">

        <div style="background:linear-gradient(135deg,#fef2f2,#fee2e2);padding:18px 22px;border-radius:14px;border-left:5px solid #dc2626">
          <h3 style="margin:0 0 8px;color:#991b1b;font-size:15px">🔍 가장 먼저 직시할 사실 — QR은 이미 있는데, 아무도 스캔하지 않습니다</h3>
          <div style="display:grid;grid-template-columns:auto 1fr;gap:18px;align-items:center">
            <div style="display:flex;gap:12px">
              <div style="text-align:center;padding:12px 18px;background:#fff;border-radius:12px;border:2px solid #fca5a5">
                <div style="font-size:26px;font-weight:800;color:#dc2626">309</div>
                <div style="font-size:11px;color:#7f1d1d">발급된 QR</div>
              </div>
              <div style="display:flex;align-items:center;font-size:20px;color:#dc2626">→</div>
              <div style="text-align:center;padding:12px 18px;background:#fff;border-radius:12px;border:2px solid #dc2626">
                <div style="font-size:26px;font-weight:800;color:#dc2626">0</div>
                <div style="font-size:11px;color:#7f1d1d">스캔 기록</div>
              </div>
            </div>
            <p style="margin:0;color:#7f1d1d;font-size:12.5px;line-height:1.85">
              <code>qrcodes</code> 309건, <code>qr_scan_logs</code> <b>0건</b>. QR 발급 기능은 완성돼 있으나 <b>현장에서 단 한 번도 스캔되지 않았습니다</b>.<br/>
              원인은 기술이 아니라 <b>동선</b>입니다 — 스캔할 <b>기기</b>가 없고, 스캔해야 할 <b>이유</b>(스캔하면 일이 줄어드는 구조)가 없었습니다.
              <b style="color:#dc2626">바코드·PDA 도입이 같은 실패를 반복하지 않으려면, "출력물을 늘리는 것"이 아니라 "스캔이 곧 입력이 되는 구조"를 만들어야 합니다.</b>
            </p>
          </div>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">① 회사 업무 전 과정과 스캔 접점 — 의뢰 접수부터 보관·폐기까지</h3>
          <div class="mermaid">
flowchart TB
    classDef sys fill:#eef2ff,stroke:#4f46e5,color:#312e81,stroke-width:2px
    classDef gap fill:#fee2e2,stroke:#dc2626,color:#991b1b,stroke-width:3px
    classDef scan fill:#dcfce7,stroke:#16a34a,color:#166534,stroke-width:2px
    classDef paper fill:#fef3c7,stroke:#f59e0b,color:#92400e,stroke-width:2px

    A1[의뢰 접수<br/>의뢰사 시험 요청]:::sys
    A2[시료 입고<br/>농약 검체 수령]:::gap
    A3[시험계획서 작성<br/>처리약제 시험구 설계]:::sys
    A4[포장 섭외 및 준비<br/>포장주 구획 확정]:::paper
    A5[처리 살포<br/>연 460회]:::paper
    A6[약효 약해 조사<br/>연 981회]:::paper
    A7[성적서 작성 발급]:::sys
    A8[문서 시료 보관<br/>보존 및 폐기]:::gap

    A1 --> A2 --> A3 --> A4 --> A5 --> A6 --> A7 --> A8

    S2[스캔접점 시료 라벨<br/>입고 불출 잔량 폐기]:::scan
    S4[스캔접점 포장 표지판<br/>구획 확인]:::scan
    S5[스캔접점 처리 실적<br/>약제 배수 시각 작업자]:::scan
    S6[스캔접점 조사 실적<br/>구획별 조사값 입력]:::scan
    S8[스캔접점 보관 박스<br/>위치 반출입]:::scan

    A2 -.-> S2
    A4 -.-> S4
    A5 -.-> S5
    A6 -.-> S6
    A8 -.-> S8
          </div>
          <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-top:14px;font-size:11px">
            <div style="padding:9px;background:#eef2ff;border-radius:8px;border:1px solid #c7d2fe;color:#312e81"><b>■ 시스템 관리 중</b><br/>DB에 구조화되어 있음</div>
            <div style="padding:9px;background:#fef3c7;border-radius:8px;border:1px solid #fde68a;color:#92400e"><b>■ 종이 → 재입력</b><br/>현장 수기 후 사무실 입력</div>
            <div style="padding:9px;background:#fee2e2;border-radius:8px;border:1px solid #fca5a5;color:#991b1b"><b>■ 시스템 공백</b><br/>테이블 자체가 없음</div>
            <div style="padding:9px;background:#dcfce7;border-radius:8px;border:1px solid #bbf7d0;color:#166534"><b>■ 스캔 도입 지점</b><br/>본 계획서 적용 대상</div>
          </div>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">② 업무 단계별 실측 진단 (DB 전수 조회 기준)</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:11.5px">
            <tr style="background:#f1f5f9">
              <th style="padding:8px 11px;text-align:left;color:#475569;border-radius:8px 0 0 8px">업무 단계</th>
              <th style="padding:8px 11px;text-align:left;color:#475569">현재 기록 방식</th>
              <th style="padding:8px 11px;text-align:right;color:#475569">실측 규모</th>
              <th style="padding:8px 11px;text-align:left;color:#475569;border-radius:0 8px 8px 0">진단</th>
            </tr>
            <tr style="background:#fee2e2">
              <td style="padding:9px 11px;font-weight:700;color:#991b1b;border-radius:8px 0 0 8px">시료(검체) 관리</td>
              <td style="padding:9px 11px">시스템 밖 — 엑셀·수기 추정</td>
              <td style="padding:9px 11px;text-align:right;font-weight:700;color:#dc2626">테이블 0개</td>
              <td style="padding:9px 11px;color:#991b1b"><b>최대 공백.</b> 38개 테이블 중 시료·재고·자산 관련 전무</td>
            </tr>
            <tr style="background:#fef3c7">
              <td style="padding:9px 11px;font-weight:700;color:#92400e;border-radius:8px 0 0 8px">처리(살포) 실적</td>
              <td style="padding:9px 11px">현장 수기 → 사무실 재입력</td>
              <td style="padding:9px 11px;text-align:right;font-weight:700">460회/년</td>
              <td style="padding:9px 11px;color:#78350f">계획은 DB에 있으나 <b>실적 확인 필드 없음</b></td>
            </tr>
            <tr style="background:#fef3c7">
              <td style="padding:9px 11px;font-weight:700;color:#92400e;border-radius:8px 0 0 8px">약효·약해 조사</td>
              <td style="padding:9px 11px">야장(野帳) 수기 → 재입력</td>
              <td style="padding:9px 11px;text-align:right;font-weight:700">981회/년</td>
              <td style="padding:9px 11px;color:#78350f"><b>최다 현장 이벤트.</b> 조사값 자체는 시스템 미보관</td>
            </tr>
            <tr style="background:#fef3c7">
              <td style="padding:9px 11px;font-weight:700;color:#92400e;border-radius:8px 0 0 8px">포장 구획 식별</td>
              <td style="padding:9px 11px">표지판 육안 확인</td>
              <td style="padding:9px 11px;text-align:right;font-weight:700">배치 213건</td>
              <td style="padding:9px 11px;color:#78350f">QR 표지판 설계는 완료, <b>스캔 미사용</b></td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">차량·장비</td>
              <td style="padding:9px 11px">업무/출장에 차량만 연결</td>
              <td style="padding:9px 11px;text-align:right;font-weight:700">차량 7대</td>
              <td style="padding:9px 11px;color:#64748b">분무기·계측기 교정이력 관리 없음</td>
            </tr>
            <tr style="background:#fee2e2">
              <td style="padding:9px 11px;font-weight:700;color:#991b1b;border-radius:8px 0 0 8px">시약·소모품</td>
              <td style="padding:9px 11px">시스템 밖</td>
              <td style="padding:9px 11px;text-align:right;font-weight:700;color:#dc2626">테이블 0개</td>
              <td style="padding:9px 11px;color:#991b1b">시약실 온습도는 감시하나 <b>내용물은 미관리</b></td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">문서 보관</td>
              <td style="padding:9px 11px">OCI 전자문서 + 물리 보관실</td>
              <td style="padding:9px 11px;text-align:right;font-weight:700">T315-DOC</td>
              <td style="padding:9px 11px;color:#64748b">물리 박스 위치·반출입 이력 없음</td>
            </tr>
          </table>
        </div>

        <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:14px">③ 연간 현장 이벤트 기준선 (2026년 실측)</h3>
          <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:10px;text-align:center">
            <div style="padding:13px;background:#eef2ff;border-radius:10px;border:1px solid #c7d2fe">
              <div style="font-size:22px;font-weight:800;color:#4338ca">250</div><div style="font-size:11px;color:#312e81">시험 건수</div>
            </div>
            <div style="padding:13px;background:#fef3c7;border-radius:10px;border:1px solid #fde68a">
              <div style="font-size:22px;font-weight:800;color:#b45309">460</div><div style="font-size:11px;color:#92400e">처리 이벤트</div>
            </div>
            <div style="padding:13px;background:#fee2e2;border-radius:10px;border:1px solid #fca5a5">
              <div style="font-size:22px;font-weight:800;color:#dc2626">981</div><div style="font-size:11px;color:#991b1b">조사 이벤트</div>
            </div>
            <div style="padding:13px;background:#dcfce7;border-radius:10px;border:1px solid #bbf7d0">
              <div style="font-size:22px;font-weight:800;color:#16a34a">1,441</div><div style="font-size:11px;color:#166534">연간 현장 이벤트</div>
            </div>
            <div style="padding:13px;background:#f5f3ff;border-radius:10px;border:1px solid #ddd6fe">
              <div style="font-size:22px;font-weight:800;color:#7c3aed">8명</div><div style="font-size:11px;color:#5b21b6">연구원 (PDA 대수 산정 기준)</div>
            </div>
          </div>
          <p style="margin:12px 0 0;font-size:11.5px;color:#64748b;line-height:1.85">
            시험 1건당 평균 <b>처리 1.84회 + 조사 3.92회</b>. 즉 <b>연구원 1인이 연간 약 180건의 현장 이벤트</b>를 처리하며,
            각 이벤트마다 <b>현장 수기 → 사무실 재입력</b>이 반복되고 있습니다. 이 이중 작업의 제거가 도입 효과의 본체입니다.
            <br/><span style="color:#94a3b8">※ 2025년(35건)·2027년(15건)은 부분/선입력 데이터이므로 2026년을 기준선으로 채택했습니다.</span>
          </p>
        </div>

        <div style="padding:13px 16px;background:#0f172a;border-radius:12px;color:#cbd5e1;font-size:11.5px;line-height:1.95">
          <span style="color:#fbbf24;font-weight:700">⚠️ 기존 QR 섹션과의 관계:</span>
          기존 <b style="color:#e2e8f0">QR코드 활용</b> 탭(7장)은 <b>QR 발급·리다이렉트·표지판·문서 버전 스냅샷</b>을 다룹니다.
          본 섹션은 그 위에 <b style="color:#4ade80">① 1D/DataMatrix를 포함한 코드 체계 ② 시료 라이프사이클 ③ PDA 현장 오프라인 운영 ④ 전사 적용 우선순위</b>를 더합니다.
          중복이 아니라 <b style="color:#e2e8f0">"발급된 코드를 실제로 스캔해 쓰이게 만드는 층"</b>입니다.
        </div>

      </div>
    `,
  },
  {
    title: '📦 바코드·PDA ② 적용 우선순위 — 가치·난이도 매트릭스',
    content: `
      <div style="display:grid;gap:18px">

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">① 후보 9개 영역 — 업무가치 대비 구현난이도</h3>
          <div class="mermaid">
quadrantChart
    title 바코드 PDA 적용 후보 우선순위
    x-axis 낮은 구현난이도 --> 높은 구현난이도
    y-axis 낮은 업무가치 --> 높은 업무가치
    quadrant-1 전략 과제
    quadrant-2 즉시 착수
    quadrant-3 보류
    quadrant-4 선택 적용
    시료 검체 관리: [0.42, 0.95]
    조사 실적 수집: [0.55, 0.90]
    처리 실적 확인: [0.35, 0.82]
    포장 구획 확인: [0.20, 0.70]
    시약 소모품: [0.30, 0.45]
    장비 교정이력: [0.35, 0.38]
    문서 박스 보관: [0.25, 0.35]
    차량 운행관리: [0.28, 0.30]
    출근 현장 체크인: [0.22, 0.18]
          </div>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">② 평가표 — 선정 근거와 선행조건</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:11.5px">
            <tr style="background:#f1f5f9">
              <th style="padding:8px 10px;text-align:left;color:#475569;border-radius:8px 0 0 8px">영역</th>
              <th style="padding:8px 10px;text-align:center;color:#475569">가치</th>
              <th style="padding:8px 10px;text-align:center;color:#475569">난이도</th>
              <th style="padding:8px 10px;text-align:left;color:#475569">핵심 효과</th>
              <th style="padding:8px 10px;text-align:left;color:#475569">선행조건</th>
              <th style="padding:8px 10px;text-align:center;color:#475569;border-radius:0 8px 8px 0">판정</th>
            </tr>
            <tr style="background:#dcfce7">
              <td style="padding:9px 10px;font-weight:700;color:#166534;border-radius:8px 0 0 8px">시료(검체) 관리</td>
              <td style="padding:9px 10px;text-align:center">★★★★★</td>
              <td style="padding:9px 10px;text-align:center">중</td>
              <td style="padding:9px 10px">입고~폐기 전 이력 추적, <b>GLP 대응</b>, 잔량·유효기간 관리</td>
              <td style="padding:9px 10px">신규 테이블 2종 설계</td>
              <td style="padding:9px 10px;text-align:center;font-weight:700;color:#16a34a">1순위</td>
            </tr>
            <tr style="background:#dcfce7">
              <td style="padding:9px 10px;font-weight:700;color:#166534;border-radius:8px 0 0 8px">조사 실적 수집</td>
              <td style="padding:9px 10px;text-align:center">★★★★★</td>
              <td style="padding:9px 10px;text-align:center">중상</td>
              <td style="padding:9px 10px">연 981회 재입력 제거, 조사값 <b>디지털 원본화</b></td>
              <td style="padding:9px 10px">오프라인 큐 + 조사값 스키마</td>
              <td style="padding:9px 10px;text-align:center;font-weight:700;color:#16a34a">1순위</td>
            </tr>
            <tr style="background:#dcfce7">
              <td style="padding:9px 10px;font-weight:700;color:#166534;border-radius:8px 0 0 8px">처리 실적 확인</td>
              <td style="padding:9px 10px;text-align:center">★★★★☆</td>
              <td style="padding:9px 10px;text-align:center">하</td>
              <td style="padding:9px 10px">연 460회, 처리 시각·작업자 자동 기록</td>
              <td style="padding:9px 10px">실적 컬럼 추가만</td>
              <td style="padding:9px 10px;text-align:center;font-weight:700;color:#16a34a">2순위</td>
            </tr>
            <tr style="background:#eff6ff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">포장 구획 확인</td>
              <td style="padding:9px 10px;text-align:center">★★★★☆</td>
              <td style="padding:9px 10px;text-align:center">하</td>
              <td style="padding:9px 10px"><b>처리구 오인 방지</b> — 시험 신뢰성 직결</td>
              <td style="padding:9px 10px">QR 표지판 이미 설계됨</td>
              <td style="padding:9px 10px;text-align:center;color:#2563eb">2순위</td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">시약·소모품</td>
              <td style="padding:9px 10px;text-align:center">★★★☆☆</td>
              <td style="padding:9px 10px;text-align:center">하</td>
              <td style="padding:9px 10px">유효기간 경과 사용 차단, 재고 파악</td>
              <td style="padding:9px 10px">시료 테이블 확장 재사용</td>
              <td style="padding:9px 10px;text-align:center;color:#64748b">3순위</td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">장비 교정이력</td>
              <td style="padding:9px 10px;text-align:center">★★★☆☆</td>
              <td style="padding:9px 10px;text-align:center">하</td>
              <td style="padding:9px 10px">분무기·저울 교정주기 도래 알림</td>
              <td style="padding:9px 10px">자산 테이블</td>
              <td style="padding:9px 10px;text-align:center;color:#64748b">3순위</td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">문서 박스 보관</td>
              <td style="padding:9px 10px;text-align:center">★★☆☆☆</td>
              <td style="padding:9px 10px;text-align:center">하</td>
              <td style="padding:9px 10px">보관 위치 즉시 확인, 반출입 이력</td>
              <td style="padding:9px 10px">보관 위치 체계 수립</td>
              <td style="padding:9px 10px;text-align:center;color:#64748b">3순위</td>
            </tr>
            <tr style="background:#f8fafc">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">차량 운행관리</td>
              <td style="padding:9px 10px;text-align:center">★★☆☆☆</td>
              <td style="padding:9px 10px;text-align:center">하</td>
              <td style="padding:9px 10px">차량 7대 — 업무/출장에 이미 연결됨</td>
              <td style="padding:9px 10px">—</td>
              <td style="padding:9px 10px;text-align:center;color:#dc2626">보류</td>
            </tr>
            <tr style="background:#f8fafc">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">현장 체크인</td>
              <td style="padding:9px 10px;text-align:center">★☆☆☆☆</td>
              <td style="padding:9px 10px;text-align:center">하</td>
              <td style="padding:9px 10px">8명 규모에서 실익 낮음</td>
              <td style="padding:9px 10px">—</td>
              <td style="padding:9px 10px;text-align:center;color:#dc2626">보류</td>
            </tr>
          </table>
          <div style="margin-top:12px;padding:12px 14px;background:#fffbeb;border-radius:10px;border:1px solid #fde68a;font-size:11.5px;color:#78350f;line-height:1.9">
            <b>⚠️ 보류 판정의 근거:</b> 차량 7대·연구원 8명 규모에서 차량 QR·출근 체크인은 <b>스캔 부담이 절약 시간을 초과</b>합니다.
            "할 수 있다"와 "해야 한다"는 다릅니다. 소규모 조직에서 스캔 지점을 남발하면 <b>현장이 스캔 자체를 회피</b>하게 되고,
            결국 <b>지금의 스캔 0건 상태가 재현</b>됩니다. 적용 지점은 의도적으로 좁게 시작합니다.
          </div>
        </div>

        <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:14px">③ "스캔이 곧 입력" 원칙 — 채택 여부를 가르는 단 하나의 기준</h3>
          <div class="mermaid">
flowchart LR
    classDef ng fill:#fee2e2,stroke:#dc2626,color:#991b1b,stroke-width:2px
    classDef ok fill:#dcfce7,stroke:#16a34a,color:#166534,stroke-width:2px

    A[스캔 지점 후보]:::ok
    B{스캔하면<br/>현장 일이 줄어드는가}:::ok
    C[채택<br/>스캔 = 기록 완료<br/>사무실 재입력 소멸]:::ok
    D[탈락<br/>스캔은 추가 노동일 뿐<br/>반드시 사용되지 않음]:::ng

    A --> B
    B -- 예 --> C
    B -- 아니오 --> D
          </div>
          <p style="margin:12px 0 0;font-size:11.5px;color:#334155;line-height:1.9">
            조사 실적을 예로 들면 — 현재는 <b>야장에 수기 → 사무실에서 다시 입력</b>합니다.
            PDA로 구획을 스캔하고 조사값을 바로 입력하면 <b>야장 작성과 재입력이 동시에 사라집니다</b>. 스캔이 노동을 늘리지 않고 <b>대체</b>합니다.
            반대로 차량 QR 스캔은 기존에 없던 노동을 <b>추가</b>하기만 합니다. 이 차이가 정착과 사장(死藏)을 가릅니다.
          </p>
        </div>

      </div>
    `,
  },
  {
    title: '📦 바코드·PDA ③ 코드 체계 설계 — 심볼로지 · 식별자 · 라벨 물성',
    content: `
      <div style="display:grid;gap:18px">

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">① 대상별 심볼로지 선택 — QR 하나로 통일하면 안 되는 이유</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:11.5px">
            <tr style="background:#f1f5f9">
              <th style="padding:8px 10px;text-align:left;color:#475569;border-radius:8px 0 0 8px">적용 대상</th>
              <th style="padding:8px 10px;text-align:center;color:#475569">권장 심볼</th>
              <th style="padding:8px 10px;text-align:center;color:#475569">최소 라벨</th>
              <th style="padding:8px 10px;text-align:left;color:#475569;border-radius:0 8px 8px 0">선택 근거</th>
            </tr>
            <tr style="background:#f5f3ff">
              <td style="padding:9px 10px;font-weight:700;color:#5b21b6;border-radius:8px 0 0 8px">시료 바이알·소분 용기</td>
              <td style="padding:9px 10px;text-align:center;font-weight:700">DataMatrix</td>
              <td style="padding:9px 10px;text-align:center">8×8mm</td>
              <td style="padding:9px 10px">좁은 곡면에 부착. <b>동일 정보량 대비 QR보다 작고</b>, 일부 손상돼도 복원됨</td>
            </tr>
            <tr style="background:#eff6ff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">포장 표지판(시험구)</td>
              <td style="padding:9px 10px;text-align:center;font-weight:700">QR</td>
              <td style="padding:9px 10px;text-align:center">40×40mm+</td>
              <td style="padding:9px 10px"><b>원거리·스마트폰 호환</b>. 장갑 낀 채 1m 밖에서도 인식 필요</td>
            </tr>
            <tr style="background:#f0fdf4">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">시약병·소모품</td>
              <td style="padding:9px 10px;text-align:center;font-weight:700">Code128</td>
              <td style="padding:9px 10px;text-align:center">30×10mm</td>
              <td style="padding:9px 10px">1D로 충분(ID만 담음). <b>인쇄 저렴·판독 빠름</b>, 기존 시약병 라벨 여백에 적합</td>
            </tr>
            <tr style="background:#fff7ed">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">장비·자산</td>
              <td style="padding:9px 10px;text-align:center;font-weight:700">Code128</td>
              <td style="padding:9px 10px;text-align:center">40×15mm</td>
              <td style="padding:9px 10px">자산번호 단순 식별. 금속 부착 시 <b>내열·내약품 라벨</b> 필요</td>
            </tr>
            <tr style="background:#eff6ff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">문서 보관 박스</td>
              <td style="padding:9px 10px;text-align:center;font-weight:700">QR</td>
              <td style="padding:9px 10px;text-align:center">30×30mm</td>
              <td style="padding:9px 10px">스마트폰으로도 조회 가능해야 함(감사 대응 시)</td>
            </tr>
            <tr style="background:#eff6ff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">성적서·계획서 문서</td>
              <td style="padding:9px 10px;text-align:center;font-weight:700">QR</td>
              <td style="padding:9px 10px;text-align:center">25×25mm</td>
              <td style="padding:9px 10px">기존 Dynamic QR 체계 그대로 사용 (이미 구현됨)</td>
            </tr>
          </table>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">② 식별자(코드 스킴) 설계 — 스캔 즉시 종류가 판별되어야 한다</h3>
          <div class="mermaid">
flowchart TB
    classDef t fill:#eef2ff,stroke:#4f46e5,color:#312e81,stroke-width:2px
    classDef v fill:#f0fdf4,stroke:#16a34a,color:#14532d,stroke-width:2px

    R[스캔된 코드 문자열]:::t
    P{접두 2자리<br/>타입 판별}:::t

    R --> P
    P -- SM --> S1[시료<br/>SM-26-0031-A]:::v
    P -- PL --> S2[포장 구획<br/>PL-26-0031-T3R2]:::v
    P -- RG --> S3[시약<br/>RG-26-00147]:::v
    P -- EQ --> S4[장비<br/>EQ-SPR-004]:::v
    P -- BX --> S5[보관 박스<br/>BX-DOC-2026-012]:::v
    P -- 그외 --> S6[기존 Dynamic QR<br/>단축코드 URL]:::v
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:14px">
            <div style="padding:13px;background:#f8fafc;border-radius:10px;border:1px solid #e2e8f0">
              <div style="font-size:12px;font-weight:700;color:#334155;margin-bottom:6px">설계 규칙</div>
              <div style="font-size:11.5px;color:#334155;line-height:1.95">
                • <b>접두 2자리로 타입 구분</b> — PDA가 스캔 즉시 어느 화면을 열지 결정<br/>
                • <b>연도 2자리 포함</b> — 기존 <code>test_year</code> 2자리 체계와 일관성 유지<br/>
                • <b>사람이 읽을 수 있게</b> — 라벨에 코드 문자열을 <b>육안 텍스트로 병기</b>. 스캐너 고장 시 수동 입력 가능해야 함<br/>
                • <b>대문자·숫자·하이픈만</b> — 손글씨 혼동 방지 위해 <b>I, O, 0, 1 회피</b> 규칙 적용
              </div>
            </div>
            <div style="padding:13px;background:#fffbeb;border-radius:10px;border:1px solid #fde68a">
              <div style="font-size:12px;font-weight:700;color:#92400e;margin-bottom:6px">⚠️ URL을 직접 굽지 마세요</div>
              <div style="font-size:11.5px;color:#78350f;line-height:1.95">
                시료·구획 라벨에 <code>https://lab.sappt.co.kr/...</code> 전체 URL을 인쇄하면
                <b>도메인이 바뀌는 순간 전량 재출력</b>해야 합니다.<br/><br/>
                라벨에는 <b>불변 식별자만</b> 굽고, URL 매핑은 서버가 담당합니다.
                기존 <code>qrcodes</code> 테이블의 <b>Dynamic QR(단축코드 → redirect_url)</b> 구조가 정확히 이 원칙으로 만들어져 있으므로 <b>그대로 재사용</b>합니다.
              </div>
            </div>
          </div>
        </div>

        <div style="background:linear-gradient(135deg,#fef2f2,#fee2e2);padding:20px;border-radius:14px;border-left:5px solid #dc2626">
          <h3 style="margin:0 0 12px;color:#991b1b;font-size:15px">③ 🏷️ 라벨 물성 — 현장 실패의 90%는 여기서 발생합니다</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:11.5px">
            <tr style="background:#fecaca">
              <th style="padding:8px 10px;text-align:left;color:#7f1d1d;border-radius:8px 0 0 8px">구분</th>
              <th style="padding:8px 10px;text-align:left;color:#7f1d1d">❌ 쓰면 안 되는 것</th>
              <th style="padding:8px 10px;text-align:left;color:#7f1d1d;border-radius:0 8px 8px 0">✅ 권장</th>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">인쇄 방식</td>
              <td style="padding:9px 10px"><b>감열(Direct Thermal)</b> — 직사광선·고온에서 <b>라벨 전체가 검게 변색</b>되어 판독 불가</td>
              <td style="padding:9px 10px"><b>열전사(Thermal Transfer)</b> + <b>수지(Resin) 리본</b></td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">라벨 재질</td>
              <td style="padding:9px 10px">일반 아트지 — 비·이슬·농약 분무에 <b>수 시간 내 훼손</b></td>
              <td style="padding:9px 10px"><b>합성지(PP/PET)</b> — 내수·내약품·UV 저항</td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">포장 표지판</td>
              <td style="padding:9px 10px">라벨 부착만 — 한 시즌을 못 버팀</td>
              <td style="padding:9px 10px"><b>PVC/알루미늄 판에 UV 인쇄</b> 또는 라벨 위 <b>투명 라미네이트</b></td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">시료 바이알</td>
              <td style="padding:9px 10px">일반 점착 — 냉장·결로에서 <b>가장자리부터 박리</b></td>
              <td style="padding:9px 10px"><b>저온용 강점착</b>, 곡면은 세로 부착 회피</td>
            </tr>
            <tr style="background:#fff">
              <td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">여백(Quiet Zone)</td>
              <td style="padding:9px 10px">코드를 라벨 가장자리까지 꽉 채움 → <b>인식률 급락</b></td>
              <td style="padding:9px 10px">코드 주변 <b>최소 4모듈 여백</b> 확보</td>
            </tr>
          </table>
          <div style="margin-top:12px;padding:12px 14px;background:#fff;border-radius:10px;border:1px solid #fecaca;font-size:11.5px;color:#7f1d1d;line-height:1.9">
            <b>필수 검증 절차:</b> 본 도입에서 가장 흔한 실패는 <b>"실내에서 잘 되던 라벨이 포장에서 안 읽히는 것"</b>입니다.
            Phase 1 착수 전 <b>실제 포장에 시험 라벨을 부착하고 한 시즌(최소 4주) 노출 시험</b>을 반드시 수행하고,
            <b>직사광선·강우·농약 분무 후 판독률</b>을 측정해 재질을 확정하십시오. 이 검증 없이 전량 출력하면 재작업 비용이 라벨비의 수십 배가 됩니다.
          </div>
        </div>

      </div>
    `,
  },
  {
    title: '📦 바코드·PDA ④ 시료(검체) 라이프사이클 — 최대 공백 영역',
    content: `
      <div style="display:grid;gap:18px">

        <div style="background:linear-gradient(135deg,#eef2ff,#e0e7ff);padding:16px 20px;border-radius:14px;border-left:5px solid #4f46e5">
          <h3 style="margin:0 0 6px;color:#3730a3;font-size:15px">왜 시료 관리가 1순위인가</h3>
          <p style="margin:0;color:#312e81;font-size:12.5px;line-height:1.85">
            SAPPT는 <b>시험 일정과 계획</b>은 정교하게 관리하지만, 정작 그 시험의 <b>물리적 대상인 농약 시료</b>는 시스템 밖에 있습니다.
            의뢰사가 보낸 검체가 <b>언제 도착해 어디에 보관되고 누가 얼마를 꺼내 썼으며 잔량이 얼마인지</b>를 시스템이 답하지 못합니다.
            시험 결과의 신뢰성은 <b>"그 시험에 정확히 그 시료가 쓰였다"</b>는 추적성에서 나오므로, 이는 기능 공백이자 <b>신뢰성 공백</b>입니다.
          </p>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">① 시료 상태 전이 — 각 전이는 1회 스캔으로 기록</h3>
          <div class="mermaid">
stateDiagram-v2
    [*] --> 입고접수: 의뢰사 검체 도착<br/>라벨 발행 및 부착
    입고접수 --> 보관중: 보관 위치 스캔<br/>시약실 냉장고 등
    보관중 --> 불출: 처리 당일 반출<br/>작업자 및 수량 기록
    불출 --> 사용완료: 살포 완료
    사용완료 --> 보관중: 잔량 반납<br/>재보관
    보관중 --> 폐기: 시험 종료 후<br/>보존기간 경과
    폐기 --> [*]

    보관중 --> 반송: 의뢰사 반환 요청
    반송 --> [*]

    note right of 불출
      이 지점이 핵심
      누가 언제 얼마를
      어느 시험에 썼는가
    end note
          </div>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">② 신규 테이블 설계 — 2개만 추가</h3>
          <div class="mermaid">
erDiagram
    MST_COMPANIES ||--o{ SAMPLES : "의뢰"
    SAMPLES ||--o{ SAMPLE_TRANSACTIONS : "이력"
    TEST_SCHEDULES ||--o{ SAMPLE_TRANSACTIONS : "사용처"
    USERS ||--o{ SAMPLE_TRANSACTIONS : "작업자"

    SAMPLES {
        int id PK
        string sample_code "SM-26-0031-A 라벨 인쇄값"
        int company_id FK
        string pesticide_name "농약 품목명"
        string lot_no "제조번호"
        date received_date "입고일"
        date expiry_date "유효기간"
        numeric qty_initial "입고량"
        numeric qty_remain "현재 잔량"
        string unit "단위 g mL"
        string storage_location "보관 위치"
        string status "보관중 폐기 반송"
    }

    SAMPLE_TRANSACTIONS {
        int id PK
        int sample_id FK
        string tx_type "입고 불출 반납 폐기"
        numeric qty "수량"
        int schedule_id FK "사용 시험"
        int user_id FK "작업자"
        datetime scanned_at "스캔 시각"
        string device_id "PDA 식별자"
        string memo
    }
          </div>
          <div style="margin-top:12px;padding:12px 14px;background:#f0fdf4;border-radius:10px;border:1px solid #bbf7d0;font-size:11.5px;color:#14532d;line-height:1.9">
            <b>설계 포인트</b><br/>
            • <code>qty_remain</code>은 <b>캐시 컬럼</b>입니다. 진실은 <code>sample_transactions</code>의 합계이며, 잔량은 트랜잭션 발생 시 갱신합니다.
              불일치 시 <b>트랜잭션 합계가 우선</b>이며 야간 배치로 재계산합니다 — 감사 대응 시 "계산 근거"를 제시할 수 있어야 하기 때문입니다.<br/>
            • <code>schedule_id</code> 연결이 <b>추적성의 핵심</b>입니다. 성적서에서 역방향으로 "이 시험에 사용된 시료 로트"를 즉시 제시할 수 있게 됩니다.<br/>
            • 기존 <code>mst_companies</code>(의뢰사)·<code>test_schedules</code>·<code>users</code>를 <b>그대로 참조</b>하므로 신규 마스터가 필요 없습니다.<br/>
            • 시약·소모품은 <b>같은 구조를 재사용</b>합니다(<code>tx_type</code>만 확장) — 3순위 과제를 별도 개발 없이 흡수합니다.
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb">
            <h3 style="margin:0 0 12px;color:#1e293b;font-size:14px">③ PDA 불출 화면 흐름 (3스캔 원칙)</h3>
            <div class="mermaid">
sequenceDiagram
    autonumber
    participant W as 연구원
    participant P as PDA
    participant S as 서버

    W->>P: ① 시료 라벨 스캔
    P->>S: 시료 조회
    S-->>P: 품목명 잔량 유효기간
    alt 유효기간 경과
        P-->>W: 경고 차단 사용 불가
    end
    W->>P: ② 시험번호 스캔 또는 선택
    W->>P: ③ 불출량 입력
    P->>S: 트랜잭션 등록 멱등키 포함
    S-->>P: 잔량 갱신 완료
    P-->>W: 완료 화면 잔량 표시
            </div>
            <p style="margin:10px 0 0;font-size:11px;color:#64748b;line-height:1.8">
              <b>3스캔 이내</b>로 끝나야 현장에서 정착합니다. 입력 항목이 4개를 넘으면 사용률이 급감합니다.
            </p>
          </div>

          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb">
            <h3 style="margin:0 0 12px;color:#1e293b;font-size:14px">④ 즉시 얻게 되는 관리 기능</h3>
            <div style="font-size:11.5px;color:#334155;line-height:2">
              ✅ <b>유효기간 경과 시료 사용 차단</b> — 스캔 시점 자동 검증<br/>
              ✅ <b>잔량 부족 사전 경고</b> — 처리 예정량 대비 부족분 알림<br/>
              ✅ <b>시험↔시료 역추적</b> — 성적서에서 사용 로트 즉시 제시<br/>
              ✅ <b>의뢰사별 보유 현황</b> — 파트너 포털 확장 여지<br/>
              ✅ <b>폐기 대상 자동 산출</b> — 보존기간 경과 목록<br/>
              ✅ <b>보관 위치 조회</b> — "그 시료 어디 있지"의 소멸<br/><br/>
              <span style="color:#7c3aed;font-weight:700">GLP 관점:</span> 검체 수불부(受拂簿)를 <b>수기 대장이 아닌 스캔 로그</b>로 대체하면,
              작성자·시각이 자동 기록되어 <b>사후 소급 작성이 불가능한 증적</b>이 됩니다.
            </div>
          </div>
        </div>

      </div>
    `,
  },
  {
    title: '📦 바코드·PDA ⑤ 현장 운영 아키텍처 — 오프라인 우선 설계',
    content: `
      <div style="display:grid;gap:18px">

        <div style="background:linear-gradient(135deg,#fffbeb,#fef3c7);padding:16px 20px;border-radius:14px;border-left:5px solid #f59e0b">
          <h3 style="margin:0 0 6px;color:#92400e;font-size:15px">전제: 시험 포장에는 통신이 없다고 가정한다</h3>
          <p style="margin:0;color:#78350f;font-size:12.5px;line-height:1.85">
            시험장소는 <b>56곳</b>이며 대부분 농촌 포장입니다. LTE 음영·약전계가 일상이고, 비닐하우스 내부는 더 심합니다.
            <b>온라인을 전제로 만든 PDA 앱은 현장에서 반드시 실패합니다.</b>
            따라서 "통신이 되면 좋고, 안 돼도 업무가 100% 진행되는" <b>오프라인 우선(offline-first)</b> 구조가 선택이 아니라 전제입니다.
          </p>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">① 오프라인 우선 동기화 아키텍처</h3>
          <div class="mermaid">
flowchart LR
    classDef dev fill:#f5f3ff,stroke:#7c3aed,color:#5b21b6,stroke-width:2px
    classDef net fill:#fef3c7,stroke:#f59e0b,color:#92400e,stroke-width:2px
    classDef srv fill:#f0fdf4,stroke:#16a34a,color:#14532d,stroke-width:2px

    S[출근 전 사전 동기화<br/>당일 일정 시료 구획 내려받기]:::dev
    L[(PDA 로컬 SQLite<br/>오프라인 작업 큐)]:::dev
    W[현장 작업<br/>스캔 및 입력<br/>통신 불필요]:::dev
    N{통신 가능?}:::net
    Q[큐에 적재 유지<br/>재시도 대기]:::net
    A[POST /api/pda/sync<br/>배치 업로드]:::srv
    D[(PostgreSQL<br/>멱등키로 중복 차단)]:::srv
    R[결과 회신<br/>성공 실패 충돌]:::srv

    S --> L --> W --> N
    N -- 아니오 --> Q --> N
    N -- 예 --> A --> D --> R --> L
          </div>
          <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-top:14px;font-size:11.5px">
            <div style="padding:12px;background:#f5f3ff;border-radius:10px;border:1px solid #ddd6fe;color:#4c1d95;line-height:1.9">
              <b>① 사전 동기화</b><br/>
              출근 시 Wi-Fi에서 <b>당일 일정·대상 시료·구획 목록</b>을 내려받습니다.
              현장에서는 서버 조회 없이 <b>로컬 데이터만으로</b> 스캔 검증이 가능합니다.
            </div>
            <div style="padding:12px;background:#fef3c7;border-radius:10px;border:1px solid #fde68a;color:#78350f;line-height:1.9">
              <b>② 무조건 로컬 기록</b><br/>
              모든 입력은 <b>먼저 로컬에 확정 저장</b>합니다. 전송 실패가 업무를 막지 않습니다.
              배터리 방전·앱 강제종료에도 <b>데이터가 남아야</b> 합니다.
            </div>
            <div style="padding:12px;background:#f0fdf4;border-radius:10px;border:1px solid #bbf7d0;color:#14532d;line-height:1.9">
              <b>③ 멱등 업로드</b><br/>
              각 기록에 <b>클라이언트 생성 UUID</b>를 부여합니다.
              통신 끊김으로 <b>같은 배치를 여러 번 보내도</b> 서버가 중복을 차단합니다.
            </div>
          </div>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">② 조사 실적 수집 시나리오 (연 981회 — 최대 효과 지점)</h3>
          <div class="mermaid">
sequenceDiagram
    autonumber
    participant W as 조사자
    participant P as PDA 오프라인
    participant L as 로컬 큐
    participant S as 서버

    Note over W,P: 출근 시 당일 조사 대상 사전 수신 완료

    W->>P: 포장 도착 구획 표지판 스캔
    P->>P: 로컬 검증 오늘 조사 대상 맞는지
    alt 대상 아님
        P-->>W: 경고 다른 시험 구획입니다
    end
    P-->>W: 시험명 처리약제 조사회차 표시
    W->>P: 약효 약해 조사값 입력
    P->>L: 즉시 로컬 저장 UUID 부여
    P-->>W: 다음 구획 안내

    Note over W,P: 구획 수만큼 반복 통신 불필요

    W->>P: 사무실 복귀 Wi-Fi 접속
    L->>S: 배치 업로드 일괄 전송
    S-->>L: 처리 결과 회신
    Note over S: 재입력 공정 소멸
          </div>
          <div style="margin-top:12px;padding:12px 14px;background:#f0fdf4;border-radius:10px;border:1px solid #bbf7d0;font-size:11.5px;color:#14532d;line-height:1.9">
            <b>이 시나리오의 핵심은 "구획 오인 방지"입니다.</b> 약효 시험에서 <b>처리구를 잘못 조사하면 시험 자체가 무효</b>가 됩니다.
            스캔은 조사값 입력의 부담을 늘리는 것이 아니라, <b>지금 조사 중인 구획이 맞는지 기계가 검증</b>해 주는 장치입니다.
            수기 야장에서는 불가능했던 <b>실시간 검증</b>이 도입 가치의 본질입니다.
          </div>
        </div>

        <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:14px">③ 충돌·예외 처리 규칙</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:11.5px">
            <tr style="background:#f1f5f9"><th style="padding:8px 11px;text-align:left;color:#475569;border-radius:8px 0 0 8px">상황</th><th style="padding:8px 11px;text-align:left;color:#475569">처리 방식</th><th style="padding:8px 11px;text-align:left;color:#475569;border-radius:0 8px 8px 0">근거</th></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">동일 배치 중복 전송</td><td style="padding:9px 11px">UUID 기준 <b>무시(멱등)</b></td><td style="padding:9px 11px;color:#64748b">통신 재시도의 정상적 부작용</td></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">사무실에서 이미 수정됨</td><td style="padding:9px 11px"><b>현장 기록 우선</b> + 변경 이력 남김</td><td style="padding:9px 11px;color:#64748b">현장이 1차 관측자</td></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">일정이 삭제된 뒤 업로드</td><td style="padding:9px 11px"><b>보류함(pending)에 격리</b> 후 관리자 확인</td><td style="padding:9px 11px;color:#64748b">데이터 유실 방지 — 절대 버리지 않음</td></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">미등록 코드 스캔</td><td style="padding:9px 11px">경고 + <b>원문 보존</b>, 사후 매핑 허용</td><td style="padding:9px 11px;color:#64748b">라벨 오류·타 기관 코드 대응</td></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">기기 분실·파손</td><td style="padding:9px 11px">미전송분 손실 → <b>일 1회 이상 동기화 의무화</b></td><td style="padding:9px 11px;color:#64748b">운영 규정으로 위험 통제</td></tr>
          </table>
        </div>

        <div style="padding:13px 16px;background:#0f172a;border-radius:12px;color:#cbd5e1;font-size:11.5px;line-height:1.95">
          <span style="color:#f87171;font-weight:700">🔐 보안 유의:</span>
          PDA는 <b style="color:#e2e8f0">공용 기기</b>가 되기 쉽습니다. 반드시 <b>개인 계정 로그인</b>을 강제하고(작업자 기록의 신뢰성이 여기서 나옵니다),
          기기별 <b style="color:#e2e8f0">device_id 화이트리스트</b>로 미등록 단말의 API 접근을 차단하며, 분실 대비로 <b>로컬 데이터 암호화와 원격 초기화</b>를 준비해야 합니다.
          현재 JWT 만료가 480분이므로 PDA 전용 토큰은 <b style="color:#fbbf24">더 짧게 + 리프레시</b> 구조로 분리하는 것이 안전합니다.
        </div>

      </div>
    `,
  },
  {
    title: '📦 바코드·PDA ⑥ 하드웨어 선정 — 농약시험 현장 요구조건',
    content: `
      <div style="display:grid;gap:18px">

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">① 전용 PDA vs 스마트폰 — 무엇을 살 것인가</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:11.5px">
            <tr style="background:#f1f5f9">
              <th style="padding:8px 10px;text-align:left;color:#475569;border-radius:8px 0 0 8px">항목</th>
              <th style="padding:8px 10px;text-align:left;color:#475569">산업용 PDA (핸디터미널)</th>
              <th style="padding:8px 10px;text-align:left;color:#475569;border-radius:0 8px 8px 0">일반 스마트폰 + 카메라 스캔</th>
            </tr>
            <tr style="background:#fff"><td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">스캔 속도</td><td style="padding:9px 10px;color:#16a34a">전용 이미저 — <b>연속 스캔 즉시 인식</b></td><td style="padding:9px 10px;color:#dc2626">초점 대기 발생, 반복 스캔 시 피로</td></tr>
            <tr style="background:#fff"><td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">장갑 착용</td><td style="padding:9px 10px;color:#16a34a"><b>물리 트리거 키</b> — 농약용 장갑 그대로</td><td style="padding:9px 10px;color:#dc2626">터치 인식 불량 — 장갑 벗어야 함</td></tr>
            <tr style="background:#fff"><td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">직사광선 시인성</td><td style="padding:9px 10px;color:#16a34a">고휘도 옥외형 패널</td><td style="padding:9px 10px;color:#b45309">모델에 따라 판독 곤란</td></tr>
            <tr style="background:#fff"><td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">방수·방진</td><td style="padding:9px 10px;color:#16a34a"><b>IP65 이상</b> — 분무 환경·물세척 가능</td><td style="padding:9px 10px;color:#b45309">방수 케이스 의존</td></tr>
            <tr style="background:#fff"><td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">낙하 내구성</td><td style="padding:9px 10px;color:#16a34a">1.2~1.8m 낙하 규격</td><td style="padding:9px 10px;color:#dc2626">파손 위험 높음</td></tr>
            <tr style="background:#fff"><td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">배터리</td><td style="padding:9px 10px;color:#16a34a">교체형 — <b>종일 외근 대응</b></td><td style="padding:9px 10px;color:#b45309">보조배터리 필요</td></tr>
            <tr style="background:#fff"><td style="padding:9px 10px;font-weight:700;border-radius:8px 0 0 8px">도입 비용</td><td style="padding:9px 10px;color:#dc2626">대당 고가 (수십~수백만원대)</td><td style="padding:9px 10px;color:#16a34a"><b>기존 보유 기기 활용 가능</b></td></tr>
            <tr style="background:#dcfce7"><td style="padding:9px 10px;font-weight:700;color:#166534;border-radius:8px 0 0 8px">권고</td><td style="padding:9px 10px;color:#166534" colspan="2"><b>Phase 1은 스마트폰 웹앱(PWA)으로 검증 → Phase 2에서 전용 PDA 소수 도입</b>. 하드웨어를 먼저 사면 "장비는 있는데 안 쓰는" 현재 QR 상황이 반복됩니다.</td></tr>
            </table>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">② 농약시험 현장 특수 요구조건 — 일반 물류창고와 다른 점</h3>
          <div class="mermaid">
mindmap
  root((농약시험 현장<br/>PDA 요구조건))
    환경
      직사광선 아래 화면 판독
      강우 이슬 결로 대응
      농약 분무 후 세척 가능
      한여름 고온 차량 보관
    작업
      농약용 고무장갑 착용 조작
      한 손 조작 가능
      물리 트리거 필수
      구획 이동하며 연속 스캔
    통신
      LTE 음영 지역 다수
      비닐하우스 내부 차폐
      종일 오프라인 가정
    운영
      연구원 8명 공용 사용
      개인 계정 로그인 강제
      일 1회 이상 동기화 의무
      차량 충전 거치대
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px">
          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb">
            <h3 style="margin:0 0 12px;color:#1e293b;font-size:14px">③ 대수 산정 (연구원 8명 기준)</h3>
            <table style="width:100%;border-collapse:separate;border-spacing:0 5px;font-size:11.5px">
              <tr style="background:#f1f5f9"><th style="padding:7px 10px;text-align:left;color:#475569;border-radius:8px 0 0 8px">구분</th><th style="padding:7px 10px;text-align:center;color:#475569">수량</th><th style="padding:7px 10px;text-align:left;color:#475569;border-radius:0 8px 8px 0">근거</th></tr>
              <tr style="background:#fff"><td style="padding:8px 10px;border-radius:8px 0 0 8px">동시 외근 팀</td><td style="padding:8px 10px;text-align:center;font-weight:700">2~3팀</td><td style="padding:8px 10px;color:#64748b">차량 7대 보유, 연구원 8명</td></tr>
              <tr style="background:#fff"><td style="padding:8px 10px;border-radius:8px 0 0 8px">팀당 기기</td><td style="padding:8px 10px;text-align:center;font-weight:700">1~2대</td><td style="padding:8px 10px;color:#64748b">조사 시 분담 병행 가능</td></tr>
              <tr style="background:#fff"><td style="padding:8px 10px;border-radius:8px 0 0 8px">예비기</td><td style="padding:8px 10px;text-align:center;font-weight:700">1대</td><td style="padding:8px 10px;color:#64748b">고장·충전 대응</td></tr>
              <tr style="background:#dcfce7"><td style="padding:8px 10px;font-weight:700;color:#166534;border-radius:8px 0 0 8px">권장 총 대수</td><td style="padding:8px 10px;text-align:center;font-weight:700;color:#166534">4~5대</td><td style="padding:8px 10px;color:#166534">전원 지급 불필요</td></tr>
            </table>
            <div style="margin-top:10px;padding:11px;background:#fffbeb;border-radius:9px;border:1px solid #fde68a;font-size:11px;color:#78350f;line-height:1.85">
              <b>라벨 프린터</b>는 별도로 <b>사무실 1대</b>(시료 입고 시 라벨 발행용)면 충분합니다.
              현장 휴대 프린터는 <b>초기 도입 대상이 아닙니다</b> — 라벨은 입고·준비 단계에서 미리 붙이는 것이 원칙입니다.
            </div>
          </div>

          <div style="background:#fff;padding:18px;border-radius:14px;border:1px solid #e5e7eb">
            <h3 style="margin:0 0 12px;color:#1e293b;font-size:14px">④ 앱 방식 선택 — PWA 우선</h3>
            <table style="width:100%;border-collapse:separate;border-spacing:0 5px;font-size:11.5px">
              <tr style="background:#f1f5f9"><th style="padding:7px 10px;text-align:left;color:#475569;border-radius:8px 0 0 8px">방식</th><th style="padding:7px 10px;text-align:left;color:#475569">장단점</th></tr>
              <tr style="background:#dcfce7"><td style="padding:8px 10px;font-weight:700;color:#166534;border-radius:8px 0 0 8px">PWA<br/>(권장)</td><td style="padding:8px 10px;color:#14532d">기존 React 자산 재사용, 스토어 심사 없음, <b>배포가 곧 반영</b>. IndexedDB로 오프라인 큐 구현. 카메라 스캔은 웹 API로 가능</td></tr>
              <tr style="background:#fff"><td style="padding:8px 10px;font-weight:700;border-radius:8px 0 0 8px">네이티브</td><td style="padding:8px 10px;color:#64748b">PDA 물리 트리거·전용 이미저 <b>완전 제어</b> 가능하나, 별도 개발·배포 체계 필요</td></tr>
              <tr style="background:#fff"><td style="padding:8px 10px;font-weight:700;border-radius:8px 0 0 8px">절충안</td><td style="padding:8px 10px;color:#334155">산업용 PDA 대부분은 스캔 결과를 <b>키보드 입력으로 주입</b>(키보드 웨지)합니다. → <b>PWA 입력창에 그대로 들어오므로 네이티브 없이도 전용 PDA 활용 가능</b></td></tr>
            </table>
            <div style="margin-top:10px;padding:11px;background:#eef2ff;border-radius:9px;border:1px solid #c7d2fe;font-size:11px;color:#312e81;line-height:1.85">
              <b>결론:</b> 키보드 웨지 방식 덕분에 <b>PWA 하나로 스마트폰과 전용 PDA를 모두 지원</b>할 수 있습니다.
              하드웨어 결정을 <b>나중으로 미룰 수 있다</b>는 점이 이 선택의 최대 이점입니다.
            </div>
          </div>
        </div>

      </div>
    `,
  },
  {
    title: '📦 바코드·PDA ⑦ 도입 로드맵 · ROI · 리스크',
    content: `
      <div style="display:grid;gap:18px">

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 14px;color:#1e293b;font-size:15px">① 단계별 로드맵 (총 14주)</h3>
          <div class="mermaid">
gantt
    title 바코드 PDA 도입 로드맵
    dateFormat YYYY-MM-DD
    axisFormat %m/%d

    section Phase 0 검증
    라벨 재질 옥외 노출시험     :p0a, 2026-09-01, 28d
    코드 체계 확정 및 PoC       :p0b, 2026-09-01, 10d

    section Phase 1 시료관리
    테이블 설계 및 API          :p1a, 2026-09-15, 10d
    라벨 발행 및 입고 화면      :p1b, after p1a, 7d
    PWA 불출 반납 기능          :p1c, after p1b, 10d

    section Phase 2 현장수집
    오프라인 큐 및 동기화       :p2a, after p1c, 12d
    처리 실적 스캔 확인         :p2b, after p2a, 7d
    조사 실적 입력              :p2c, after p2b, 12d

    section Phase 3 확장
    시약 장비 문서박스          :p3a, after p2c, 10d
    전용 PDA 도입 검토          :milestone, m3, after p3a, 0d
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1.2fr 1fr;gap:16px">
          <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb">
            <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">② ROI 산정 — 재입력 공정 제거 효과</h3>
            <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:11.5px">
              <tr style="background:#f1f5f9"><th style="padding:8px 10px;text-align:left;color:#475569;border-radius:8px 0 0 8px">항목</th><th style="padding:8px 10px;text-align:right;color:#475569">값</th><th style="padding:8px 10px;text-align:left;color:#475569;border-radius:0 8px 8px 0">비고</th></tr>
              <tr style="background:#fff"><td style="padding:8px 10px;border-radius:8px 0 0 8px">연간 현장 이벤트</td><td style="padding:8px 10px;text-align:right;font-weight:700">1,441건</td><td style="padding:8px 10px;color:#64748b">2026년 실측</td></tr>
              <tr style="background:#fff"><td style="padding:8px 10px;border-radius:8px 0 0 8px">건당 재입력 시간</td><td style="padding:8px 10px;text-align:right;font-weight:700">3분</td><td style="padding:8px 10px;color:#dc2626"><b>가정값</b> — 실측 필요</td></tr>
              <tr style="background:#fff"><td style="padding:8px 10px;border-radius:8px 0 0 8px">연간 재입력 시간</td><td style="padding:8px 10px;text-align:right;font-weight:700">72시간</td><td style="padding:8px 10px;color:#64748b">1,441 × 3분</td></tr>
              <tr style="background:#fff"><td style="padding:8px 10px;border-radius:8px 0 0 8px">오기입 정정·확인</td><td style="padding:8px 10px;text-align:right;font-weight:700">+α</td><td style="padding:8px 10px;color:#64748b">전화 확인·야장 재확인</td></tr>
              <tr style="background:#dcfce7"><td style="padding:8px 10px;font-weight:700;color:#166534;border-radius:8px 0 0 8px">연간 절감(보수적)</td><td style="padding:8px 10px;text-align:right;font-weight:700;color:#166534">약 9인일</td><td style="padding:8px 10px;color:#166534">72h ÷ 8h</td></tr>
            </table>
            <div style="margin-top:12px;padding:12px 14px;background:#fffbeb;border-radius:10px;border:1px solid #fde68a;font-size:11.5px;color:#78350f;line-height:1.9">
              <b>⚠️ 정직한 평가:</b> 연 9인일 절감은 <b>도입 비용을 정당화하기에 충분하지 않을 수 있습니다</b>.
              본 과제의 실제 명분은 시간 절감이 아니라 <b style="color:#b45309">① 시료 추적성 확보(현재 시스템 공백) ② 처리구 오인 방지(시험 무효화 리스크 제거) ③ 조사값의 디지털 원본화</b>입니다.
              <b>시험 1건이 구획 오인으로 무효화될 때의 손실</b>과 비교해 판단하는 것이 옳습니다.
            </div>
          </div>

          <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb">
            <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">③ 정량 목표(KPI)</h3>
            <table style="width:100%;border-collapse:separate;border-spacing:0 5px;font-size:11.5px">
              <tr style="background:#f1f5f9"><th style="padding:7px 9px;text-align:left;color:#475569;border-radius:8px 0 0 8px">지표</th><th style="padding:7px 9px;text-align:center;color:#475569">현재</th><th style="padding:7px 9px;text-align:center;color:#475569;border-radius:0 8px 8px 0">목표</th></tr>
              <tr style="background:#fee2e2"><td style="padding:8px 9px;font-weight:700;color:#991b1b;border-radius:8px 0 0 8px">스캔 실사용 건수</td><td style="padding:8px 9px;text-align:center;font-weight:700;color:#dc2626">0</td><td style="padding:8px 9px;text-align:center;font-weight:700;color:#16a34a">월 100+</td></tr>
              <tr style="background:#fff"><td style="padding:8px 9px;font-weight:700;border-radius:8px 0 0 8px">시료 추적 가능률</td><td style="padding:8px 9px;text-align:center;color:#dc2626">0%</td><td style="padding:8px 9px;text-align:center;font-weight:700">100%</td></tr>
              <tr style="background:#fff"><td style="padding:8px 9px;font-weight:700;border-radius:8px 0 0 8px">현장→입력 소요</td><td style="padding:8px 9px;text-align:center">1~3일</td><td style="padding:8px 9px;text-align:center;font-weight:700">당일</td></tr>
              <tr style="background:#fff"><td style="padding:8px 9px;font-weight:700;border-radius:8px 0 0 8px">라벨 판독 성공률</td><td style="padding:8px 9px;text-align:center">—</td><td style="padding:8px 9px;text-align:center;font-weight:700">99%+</td></tr>
              <tr style="background:#fff"><td style="padding:8px 9px;font-weight:700;border-radius:8px 0 0 8px">구획 오인 발생</td><td style="padding:8px 9px;text-align:center">미측정</td><td style="padding:8px 9px;text-align:center;font-weight:700">0건</td></tr>
            </table>
            <div style="margin-top:10px;padding:11px;background:#eef2ff;border-radius:9px;border:1px solid #c7d2fe;font-size:11px;color:#312e81;line-height:1.85">
              <b>1번 지표가 사실상 유일한 성패 기준</b>입니다. 스캔이 실제로 일어나지 않으면 나머지는 전부 무의미합니다.
            </div>
          </div>
        </div>

        <div style="background:#fff;padding:20px;border-radius:14px;border:1px solid #e5e7eb;box-shadow:0 1px 3px rgba(0,0,0,0.04)">
          <h3 style="margin:0 0 12px;color:#1e293b;font-size:15px">④ 리스크 등록부</h3>
          <table style="width:100%;border-collapse:separate;border-spacing:0 6px;font-size:11.5px">
            <tr style="background:#f1f5f9"><th style="padding:8px 11px;text-align:left;color:#475569;border-radius:8px 0 0 8px">리스크</th><th style="padding:8px 11px;text-align:center;color:#475569">영향</th><th style="padding:8px 11px;text-align:center;color:#475569">가능성</th><th style="padding:8px 11px;text-align:left;color:#475569;border-radius:0 8px 8px 0">대응</th></tr>
            <tr style="background:#fee2e2">
              <td style="padding:9px 11px;font-weight:700;color:#991b1b;border-radius:8px 0 0 8px">도입 후에도 스캔이 안 쓰임<br/><span style="font-size:10px">(현 QR 상황의 재현)</span></td>
              <td style="padding:9px 11px;text-align:center;color:#dc2626;font-weight:700">치명적</td>
              <td style="padding:9px 11px;text-align:center;color:#dc2626;font-weight:700">높음</td>
              <td style="padding:9px 11px"><b>"스캔이 곧 입력"인 지점만 채택</b>. 스캔해도 사무실 재입력이 남으면 절대 정착하지 않음. Phase 1은 연구원 2명 파일럿으로 <b>실사용 확인 후</b> 확대</td>
            </tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">라벨이 현장에서 판독 불가</td><td style="padding:9px 11px;text-align:center;color:#ea580c;font-weight:700">높음</td><td style="padding:9px 11px;text-align:center;color:#dc2626">중간</td><td style="padding:9px 11px">Phase 0에서 <b>4주 옥외 노출시험 필수</b>. 감열 라벨 사용 금지</td></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">오프라인 데이터 유실</td><td style="padding:9px 11px;text-align:center;color:#ea580c;font-weight:700">높음</td><td style="padding:9px 11px;text-align:center">낮음</td><td style="padding:9px 11px">로컬 확정 저장 + 멱등 업로드 + <b>일 1회 동기화 의무화</b></td></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">시료 잔량 불일치</td><td style="padding:9px 11px;text-align:center">중간</td><td style="padding:9px 11px;text-align:center;color:#dc2626">중간</td><td style="padding:9px 11px">트랜잭션 합계를 진실로 삼고 <b>야간 재계산</b>. 정기 실물 재고조사 병행</td></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">하드웨어 선구매 후 사장</td><td style="padding:9px 11px;text-align:center">중간</td><td style="padding:9px 11px;text-align:center;color:#dc2626">중간</td><td style="padding:9px 11px"><b>PWA로 먼저 검증</b>, 전용 PDA는 Phase 3 이후 결정</td></tr>
            <tr style="background:#fff"><td style="padding:9px 11px;font-weight:700;border-radius:8px 0 0 8px">공용 기기 계정 공유</td><td style="padding:9px 11px;text-align:center">중간</td><td style="padding:9px 11px;text-align:center;color:#dc2626">높음</td><td style="padding:9px 11px">개인 로그인 강제 + 짧은 세션. <b>작업자 기록의 신뢰성 근거</b></td></tr>
          </table>
        </div>

        <div style="padding:14px 16px;background:#0f172a;border-radius:12px;color:#cbd5e1;font-size:11.5px;line-height:1.95">
          <span style="color:#a78bfa;font-weight:700">🎯 총평 및 권고:</span>
          전사 분석 결과 <b style="color:#e2e8f0">가장 시급한 것은 바코드가 아니라 "시료 관리 시스템의 부재"</b>입니다.
          바코드·PDA는 그 시스템을 <b>현장에서 실제로 쓰이게 만드는 입력 수단</b>으로서 의미가 있습니다.
          따라서 권고 순서는 <b style="color:#4ade80">① 시료 테이블 신설 → ② 라벨·스캔으로 입력 수단 제공 → ③ 조사·처리 실적으로 확대</b>이며,
          <b style="color:#fbbf24">하드웨어 구매는 마지막</b>입니다.
          현재 QR 309건이 발급되고도 스캔 0건인 상태가 이 순서를 지키지 않으면 어떻게 되는지를 이미 보여주고 있습니다.
        </div>

      </div>
    `,
  }
];

const stripHtml = (html: string) =>
  html.replace(/<[^>]+>/g, ' ').replace(/&[a-z]+;/g, ' ').replace(/\s+/g, ' ').trim();

const SystemGuidePage: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.min.js';
    script.onload = () => {
      (window as any).mermaid?.initialize({
        startOnLoad: false,
        theme: 'base',
        themeVariables: {
          fontFamily: font.family,
          fontSize: '14px',
          primaryColor: '#eef2ff',
          primaryBorderColor: '#4f46e5',
          primaryTextColor: '#3730a3',
          lineColor: '#94a3b8',
          secondaryColor: '#f8fafc',
          tertiaryColor: '#f1f5f9',
          noteBkgColor: '#fef9c3',
          noteTextColor: '#713f12',
          noteBorderColor: '#fde68a',
        },
        flowchart: { curve: 'basis', padding: 16 },
        sequence: { actorMargin: 60, messageMargin: 40 },
      });
      renderMermaid();
    };
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, []);

  useEffect(() => {
    renderMermaid();
  }, [current]);

  const renderMermaid = async () => {
    const mermaid = (window as any).mermaid;
    if (!mermaid || !slideRef.current) return;
    const elements = slideRef.current.querySelectorAll('.mermaid');
    elements.forEach((el: any, i: number) => {
      if (el.getAttribute('data-processed')) {
        el.removeAttribute('data-processed');
        const code = el.getAttribute('data-original') || el.textContent;
        el.setAttribute('data-original', code);
        el.textContent = code;
      } else {
        el.setAttribute('data-original', el.textContent);
      }
    });
    try {
      await mermaid.run({ nodes: elements });
    } catch (e) {
      // retry once
      setTimeout(() => mermaid.run({ nodes: elements }).catch(() => {}), 300);
    }
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (searchOpen) return;
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        setCurrent((c) => Math.min(c + 1, slides.length - 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrent((c) => Math.max(c - 1, 0));
      } else if (e.key === 'Escape') {
        exitFullscreen();
      } else if (e.key === 'Home') {
        setCurrent(0);
      } else if (e.key === 'End') {
        setCurrent(slides.length - 1);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [searchOpen]);

  const enterFullscreen = () => {
    containerRef.current?.requestFullscreen?.();
    setIsFullscreen(true);
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    setIsFullscreen(false);
  };

  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement) setIsFullscreen(false);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const slide = slides[current];

  // 카테고리별 슬라이드 그룹
  //
  // ⚠️ 유지보수 규칙: 각 카테고리는 **시작 슬라이드 인덱스(start)만** 지정합니다.
  //    끝 인덱스는 "다음 카테고리 시작 - 1"로 자동 계산되며, 마지막 카테고리는
  //    항상 slides 배열 끝까지 확장됩니다.
  //    → 구간 누락·중복이 구조적으로 발생하지 않습니다.
  //
  //    슬라이드를 중간에 추가/삭제했다면 그 이후 카테고리의 start 값만 조정하세요.
  //    (과거 [시작, 끝]을 손으로 관리하던 방식에서 탭 범위가 어긋난 이력이 있어 변경)
  const categories = useMemo(() => {
    const defs = [
      { key: 'overview', label: '시스템 개요', icon: <AppstoreOutlined />, start: 0 },
      { key: 'tapo', label: '🌡️ TAPO 모니터링', icon: <LineChartOutlined />, start: 18 },
      { key: 'features', label: '기능 상세', icon: <ToolOutlined />, start: 32 },
      { key: 'audit', label: '전문가 정밀진단', icon: <SafetyCertificateOutlined />, start: 36 },
      { key: 'roadmap', label: '로드맵 · 운영 · 품질', icon: <RocketOutlined />, start: 41 },
      { key: 'admin', label: '권한 · 마스터 · TIMS', icon: <CrownOutlined />, start: 62 },
      { key: 'search', label: '검색 & 챗봇', icon: <MessageOutlined />, start: 66 },
      { key: 'integration', label: '연동 & 인프라', icon: <ThunderboltOutlined />, start: 77 },
      { key: 'qrcode', label: 'QR코드 활용', icon: <QrcodeOutlined />, start: 82 },
      { key: 'qdrant', label: 'Qdrant 벡터검색', icon: <NodeIndexOutlined />, start: 89 },
      { key: 'barcode', label: '📦 바코드 · PDA', icon: <BarcodeOutlined />, start: 95 },
    ];
    return defs.map((c, i) => ({
      key: c.key,
      label: c.label,
      icon: c.icon,
      range: [
        c.start,
        i + 1 < defs.length ? defs[i + 1].start - 1 : slides.length - 1,
      ] as [number, number],
    }));
  }, []);

  const activeCategory = useMemo(() => {
    return categories.find(c => current >= c.range[0] && current <= c.range[1])?.key || 'overview';
  }, [current, categories]);

  const handleCategoryChange = (key: string) => {
    const cat = categories.find(c => c.key === key);
    if (cat) setCurrent(cat.range[0]);
  };

  const currentCat = categories.find(c => c.key === activeCategory)!;
  const catSlides = slides.slice(currentCat.range[0], currentCat.range[1] + 1);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return slides
      .map((s, i) => ({ slide: s, idx: i }))
      .filter(({ slide }) => {
        if (slide.title.toLowerCase().includes(q)) return true;
        if (slide.content) return stripHtml(slide.content).toLowerCase().includes(q);
        return false;
      })
      .slice(0, 20);
  }, [searchQuery]);

  const getCatForIdx = useCallback((idx: number) =>
    categories.find(c => idx >= c.range[0] && idx <= c.range[1]), [categories]);

  const goToSlide = (idx: number) => {
    setCurrent(idx);
    setSearchOpen(false);
    setSearchQuery('');
  };

  const openSearch = () => {
    setSearchOpen(true);
    setSearchQuery('');
    setTimeout(() => searchInputRef.current?.focus(), 80);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };

  const getSnippet = (slide: Slide, q: string): string => {
    if (!slide.content) return '';
    const text = stripHtml(slide.content);
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx < 0) return text.slice(0, 80);
    const start = Math.max(0, idx - 30);
    return (start > 0 ? '…' : '') + text.slice(start, idx + q.length + 50) + '…';
  };

  return (
    <div
      ref={containerRef}
      style={{
        background: isFullscreen ? '#fff' : 'transparent',
        height: isFullscreen ? '100vh' : 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Category Tabs */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: isFullscreen ? '0 32px' : '0',
        background: isFullscreen ? '#fafbff' : 'transparent',
        borderBottom: isFullscreen ? '1px solid #f0f0f0' : 'none',
        position: 'relative',
      }}>
        <Tabs
          activeKey={activeCategory}
          onChange={handleCategoryChange}
          size="small"
          style={{ flex: 1, minWidth: 0 }}
          tabBarStyle={{ marginBottom: 0 }}
          items={categories.map(c => ({
            key: c.key,
            label: <span style={{ fontSize: 13 }}>{c.icon} {c.label}</span>,
          }))}
        />
        <div style={{ display: 'flex', gap: 6, marginLeft: 8, flexShrink: 0 }}>
          <Button
            size="small"
            icon={<SearchOutlined />}
            onClick={openSearch}
            title="검색 (Ctrl+F)"
          >
            검색
          </Button>
          <Button
            size="small"
            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={isFullscreen ? exitFullscreen : enterFullscreen}
          >
            {isFullscreen ? '창모드' : '전체화면'}
          </Button>
        </div>

        {/* Search Dropdown */}
        {searchOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              zIndex: 1000,
              width: 480,
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 8, alignItems: 'center' }}>
              <SearchOutlined style={{ color: '#94a3b8', fontSize: 16 }} />
              <Input
                ref={searchInputRef}
                placeholder="슬라이드 제목 또는 내용 검색... (Esc로 닫기)"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') closeSearch(); }}
                bordered={false}
                style={{ flex: 1, fontSize: 14, padding: 0 }}
                autoFocus
              />
              <span style={{ fontSize: 11, color: '#cbd5e1', flexShrink: 0 }}>
                {searchQuery && `${searchResults.length}건`}
              </span>
            </div>
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {!searchQuery && (
                <div style={{ padding: '24px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  키워드를 입력하면 전체 {slides.length}개 슬라이드에서 검색합니다
                </div>
              )}
              {searchQuery && searchResults.length === 0 && (
                <div style={{ padding: '24px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  검색 결과 없음
                </div>
              )}
              {searchResults.map(({ slide, idx }) => {
                const cat = getCatForIdx(idx);
                const q = searchQuery.trim();
                const snippet = getSnippet(slide, q);
                const titleHl = slide.title.replace(
                  new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
                  '<mark style="background:#fef08a;padding:0 2px;border-radius:2px">$1</mark>'
                );
                const snippetHl = snippet.replace(
                  new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
                  '<mark style="background:#fef08a;padding:0 2px;border-radius:2px">$1</mark>'
                );
                return (
                  <div
                    key={idx}
                    onClick={() => goToSlide(idx)}
                    style={{
                      padding: '10px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f8fafc',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      {cat && (
                        <span style={{
                          fontSize: 10,
                          padding: '1px 7px',
                          borderRadius: 10,
                          background: '#eef2ff',
                          color: '#4f46e5',
                          fontWeight: 600,
                          flexShrink: 0,
                        }}>
                          {cat.label}
                        </span>
                      )}
                      <span
                        style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}
                        dangerouslySetInnerHTML={{ __html: titleHl || '(표지)' }}
                      />
                    </div>
                    {snippet && (
                      <div
                        style={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.6 }}
                        dangerouslySetInnerHTML={{ __html: snippetHl }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Backdrop */}
        {searchOpen && (
          <div
            onClick={closeSearch}
            style={{ position: 'fixed', inset: 0, zIndex: 999 }}
          />
        )}
      </div>

      {/* Sub Tabs (slides within category) */}
      <div style={{
        padding: isFullscreen ? '0 32px' : '0',
        background: isFullscreen ? '#fafbff' : 'transparent',
      }}>
        <Tabs
          activeKey={String(current)}
          onChange={(key) => setCurrent(Number(key))}
          size="small"
          type="card"
          tabBarStyle={{ marginBottom: 0 }}
          items={catSlides.map((s, i) => {
            const globalIdx = currentCat.range[0] + i;
            return {
              key: String(globalIdx),
              label: <span style={{ fontSize: 12 }}>{s.title || '표지'}</span>,
            };
          })}
        />
      </div>

      {/* Slide Content */}
      <div
        ref={slideRef}
        style={{
          flex: 1,
          padding: isFullscreen ? '32px 64px' : '24px',
          background: '#fff',
          borderRadius: isFullscreen ? 0 : 12,
          boxShadow: isFullscreen ? 'none' : '0 2px 12px rgba(0,0,0,0.06)',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          marginTop: 8,
        }}
      >
        {slide.title && (
          <h2
            style={{
              margin: '0 0 24px',
              paddingBottom: 16,
              borderBottom: '3px solid transparent',
              borderImage: 'linear-gradient(90deg, #4f46e5, #7c3aed) 1',
              fontSize: isFullscreen ? 28 : 22,
              fontWeight: 700,
              color: '#1a1a2e',
            }}
          >
            {slide.title}
          </h2>
        )}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {slide.component ? (
            slide.component
          ) : (
            <div
              style={{ flex: 1 }}
              dangerouslySetInnerHTML={{ __html: slide.content || '' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemGuidePage;
